import { registerLib, registerCodes, createScopedErrors, listCodes, getCodeMeta, reportInvalidInput } from '@norkron/web-errors';
import { accessibleName as accessibleName$1 } from '@norkron/srequivz';
import { z } from 'zod';

// src/error-codes.ts
var WEB_CONFORMANCE_LIB = "@norkron/web-conformance";
var WEB_CONFORMANCE_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-conformance");
try {
  registerLib(WEB_CONFORMANCE_LIB, WEB_CONFORMANCE_TOKEN);
} catch {
}
try {
  registerCodes(WEB_CONFORMANCE_LIB, {
    WEB_CONFORMANCE_INVALID_INPUT: {
      description: "Runtime input validation rejected the value passed to a public web-conformance export. Payload follows WEB_INVALID_INPUT_SHAPE: { fieldPath, expected, received }.",
      severity: "warning",
      docsAnchor: "web-conformance/invalid-input",
      exampleFix: "Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib."
    },
    CF_AUDIT_REFUSED_EMPTY_SURFACE: {
      description: "The audit REFUSED to answer: the content census found nothing auditable under the root (or under the declared mirror). Every rule returned zero findings, which for a blank surface is indistinguishable from a clean bill of health \u2014 so the audit declines to issue one. Emitted only when the caller passes `requireContent: true`. A refusal is never a pass: it carries severity `error` so pre-existing `totals.critical > 0` gates and the CLI exit code fail on it.",
      severity: "error",
      docsAnchor: "web-conformance/audit-refused-empty-surface",
      exampleFix: "Await the render/hydration before auditing, or pass the element that actually holds the content. Lower `minAuditableNodes` only if the surface is legitimately near-empty and you can say why."
    },
    CF_AUDIT_REFUSED_RULE_NOT_RUN: {
      description: "The audit REFUSED to answer: at least one rule did not EXECUTE \u2014 a structural rule threw, a lib selfCheck threw, or a rule named in `requiredRuleCodes` is absent from the walker ledger. A rule that did not run has not found nothing; it has found nothing out. Emitted only when the caller passes `requireContent: true`. Severity `error` so the refusal fails every existing critical gate.",
      severity: "error",
      docsAnchor: "web-conformance/audit-refused-rule-not-run",
      exampleFix: "Read `extras.threwRules` / `extras.selfCheckThrows` / `extras.missingRules` and fix the offending rule. Do not silence it by removing the code from `requiredRuleCodes` unless the criterion is genuinely out of scope."
    },
    CF_SELFCHECK_THREW: {
      description: "A registered selfCheck threw an exception. The audit walker isolated it via try/catch; the lib is reported via CF_SELFCHECK_INTERNAL_ERROR finding rather than crashing the audit.",
      severity: "warning",
      docsAnchor: "web-conformance/selfcheck-threw",
      exampleFix: "Catch internally in the lib selfCheck and return a finding instead of throwing."
    },
    STR_HANDROLLED_DUPLICATES_LIB_PRIMITIVE: {
      description: "A hand-rolled widget (no data-norkron-lib stamp) duplicates a canonical lib-emitted primitive in the same viewport cluster. Rule Zero inverse: the product reimplemented a widget the ecosystem already owns.",
      severity: "warning",
      docsAnchor: "web-conformance/handrolled-duplicates-lib-primitive",
      exampleFix: "Replace the hand-rolled widget with the canonical lib primitive named in extras.lib_alternative; delete the duplicate."
    },
    STR_LIB_PRIMITIVE_DUPLICATE_EMITTERS: {
      description: "Two or more distinct stamped libs both emit the same UX primitive in the same viewport cluster. The ecosystem must designate one canonical owner per UX role.",
      severity: "warning",
      docsAnchor: "web-conformance/lib-primitive-duplicate-emitters",
      exampleFix: "Pick one canonical lib for the primitive named in extras.primitive; migrate every consumer to it and remove the other emitter."
    },
    STR_DUPLICATE_PRIMITIVE_BY_SIMILARITY: {
      description: "Two widgets matching the same primitive signature collide on form-fingerprint similarity (Levenshtein-normalised score). Severity scales with similarity: critical >= 0.9, major >= 0.7, minor >= 0.4. Distinct visual forms (e.g. stepper-dots vs linear-bar) score below 0.4 and emit no finding.",
      severity: "warning",
      docsAnchor: "web-conformance/duplicate-primitive-by-similarity",
      exampleFix: "Either collapse to a single canonical widget (preferably a lib-emitted one), or differentiate visual forms so the two indicators clearly communicate distinct processes."
    },
    STR_INLINE_STYLE_ON_LIB_STAMP: {
      description: 'A `data-norkron-lib` stamped root carries inline `style="..."` declarations that override themed appearance (background-color, color, border-color, etc.). CSS custom properties pass through; isolated animation properties (transition/transform/opacity) downgrade to info-level.',
      severity: "warning",
      docsAnchor: "web-conformance/inline-style-on-lib-stamp",
      exampleFix: "Move the declarations into the lib's theme tokens via web-themer, or remove the consumer override entirely."
    },
    STR_UTILITY_CLASS_ON_LIB_STAMP: {
      description: "A `data-norkron-lib` stamped root carries 3+ utility/atomic CSS classes (Tailwind-style). Violates the BEM-only mandate.",
      severity: "warning",
      docsAnchor: "web-conformance/utility-class-on-lib-stamp",
      exampleFix: "Replace utility classes with BEM lib-scoped classes, or move the utility classes onto a wrapper that is NOT the stamped lib root."
    },
    STR_TEXT_ELLIPSIS_TRUNCATED: {
      description: "Text inside an ellipsis-clamp element is actively being truncated (scrollWidth > clientWidth). Informational \u2014 verify the truncation is intentional and the full text is exposed via tooltip or expansion.",
      severity: "warning",
      docsAnchor: "web-conformance/text-ellipsis-truncated",
      exampleFix: "If the full text is important (CTA / form field / data cell), increase parent width or remove the ellipsis clamp. If a tooltip already exposes the full text, ignore this info-level finding."
    },
    STR_CHILD_OVERFLOWS_PARENT: {
      description: "A child element's bounding rect escapes its parent's border-box on at least one side by more than a 1px tolerance, while the parent's computed overflow is `visible`. Resolves _adversarial AUDIT.md GAP-1 \u2014 the historically uncovered case where a positioned or oversized child bleeds past an unclipped parent. The audit walker compares `el.getBoundingClientRect()` against `parent.getBoundingClientRect()`; degrades to STR_NEEDS_LAYOUT under headless DOM.",
      severity: "warning",
      docsAnchor: "web-conformance/child-overflows-parent",
      exampleFix: "Either constrain the child (max-width/100%, correct position offsets) or \u2014 if the visual overflow is intentional \u2014 set the parent overflow to hidden/clip/scroll/auto so the opt-in is explicit."
    },
    STR_LIB_NOT_INSTANTIATED: {
      description: 'A DOM element matches a known lib signature (ARIA role, BEM class root, or both) but no `data-norkron-lib="<suggestedLib>"` stamp exists on the element or within 3 ancestors. Resolves _adversarial AUDIT.md GAP-2 \u2014 a LONE unwired component (e.g. a bare `[role="combobox"]` rendered by a product without importing `@norkron/web-combobox`) that escapes STR_HANDROLLED_DUPLICATES_LIB_PRIMITIVE (which requires a stamped sibling). Opt out via `data-allow-unwired="true"` on the element or ancestor.',
      severity: "warning",
      docsAnchor: "web-conformance/lib-not-instantiated",
      exampleFix: 'Replace the hand-rolled markup with the suggested lib (e.g. `import { Combobox } from "@norkron/web-combobox"`) so the stamped root carries `data-norkron-lib`. If the hand-roll is intentional and reviewed, mark the element with `data-allow-unwired="true"`.'
    },
    STR_ACTUAL_BACKGROUND_CONTRAST_INSUFFICIENT: {
      description: "WCAG contrast (SC 1.4.3 / 1.4.11) computed against the ACTUAL displayed background \u2014 resolved per the DOM compositing chain (alpha stacks, gradients, backdrop-filter, image bgs, z-index intruders). Complements STR_CONTRAST_INSUFFICIENT, which uses a naive ancestor opaque-solid walk and misses these 6 compositing failure modes. Extras carry { ratio, threshold, fg, bg, bgKind, bgKindDetail, elTag, selector, fontSize, fontWeight, isLarge, isSvgGraphical }. Under headless DOM (no getComputedStyle / unmeasurable rect / no elementsFromPoint) degrades via STR_NEEDS_LAYOUT(forCode=STR_ACTUAL_BACKGROUND_CONTRAST_INSUFFICIENT) \u2014 never silently skip.",
      severity: "warning",
      docsAnchor: "web-conformance/actual-displayed-background-contrast",
      exampleFix: "Make the displayed background deterministic (drop alpha < 1, replace gradient with a solid behind the text, move the element off any z-index intruder), then re-run the audit. If the visual design requires the gradient/overlay, adjust the foreground color or font-size/weight so contrast against the SAMPLED bg meets the WCAG threshold the rule reports."
    },
    STR_RESPONSIVE_COLLAPSE_MISSING: {
      description: "A button/nav element with an icon AND a label has content (icon + label + gap) that overflows the element's available width at the current viewport, and no responsive collapse has been applied (the label is still visible despite not fitting). Indicates a missing `@media (max-width: ...)` collapse rule OR a wrong breakpoint. Detected via the viewport-sweep harness \u2014 `STR_RESPONSIVE_COLLAPSE_MISSING` fires per viewport where overflow is observed. Extras carry `{ viewport, overflowPx, suggestedBreakpoint }`.",
      severity: "warning",
      docsAnchor: "web-conformance/responsive-collapse-missing",
      exampleFix: "Hide the `btn__label` (or equivalent) below the suggested breakpoint via CSS, OR shorten the label, OR widen the parent. The label MUST be reachable via accessible name (aria-label / .sr-only) when visually hidden."
    },
    STR_TEXT_OVERFLOWS_AT_BREAKPOINT: {
      description: "A text element declares `text-overflow: ellipsis` and is actively truncating (scrollWidth > clientWidth) at one viewport but not at the next size up \u2014 the ellipsis kicks in at the wrong breakpoint, or the layout needs a different approach (wrap, larger parent, shortened copy). Detected via the viewport-sweep harness. Extras carry `{ breakpoint, truncatedTextSample, nextSizeUp }`.",
      severity: "warning",
      docsAnchor: "web-conformance/text-overflows-at-breakpoint",
      exampleFix: "Either lower the breakpoint at which the layout reflows (so truncation never kicks in for the affected sizes), or remove the ellipsis clamp on this element (let text wrap), or shorten the copy."
    },
    STR_CHILD_OVERFLOWS_VISUAL_PARENT: {
      description: "A child element's bounding rect escapes the CONTENT-BOX of its nearest VISUAL parent \u2014 the closest ancestor that sets itself apart with a contrasting displayed background and/or a visible border \u2014 by more than a 1px tolerance, while that visual parent does not intentionally clip. Distinct from STR_CHILD_OVERFLOWS_PARENT (which uses the immediate DOM parent border-box): this rule catches a child bleeding past the surface a human reads as the boundary (card/panel/callout) even when an unstyled intermediate wrapper technically still contains it. Honors `data-allow-overflow` on the child or any ancestor up to the visual parent. Degrades to STR_NEEDS_LAYOUT under headless DOM.",
      severity: "warning",
      docsAnchor: "web-conformance/child-overflows-visual-parent",
      exampleFix: "Constrain the child within the visible surface (max-width/100%, correct position offsets), move the surface boundary, or \u2014 if the bleed is intentional (avatar overlapping a card edge) \u2014 add `data-allow-overflow` or set the visual parent overflow to hidden/clip."
    },
    STR_INTRA_PRODUCT_PANEL_OVERLAPS_PEER: {
      description: 'Two intra-product panels (sidebar, drawer, sidekick, complementary region) visually overlap each other at the same z-layer with a non-trivial intersection (>=8px wide, >=32px tall). Neither panel is an ancestor of the other and no shared ancestor is itself a panel (nested panels are legitimate chrome layouts). The audit\'s parent/child overflow rules and keep-clear rules miss this \u2014 peer-on-peer panel overlap is a layout-shell bug, not a content-bleed bug. Opt out via `data-allow-panel-overlap="true"` on either panel or any shared ancestor. Degrades to STR_NEEDS_LAYOUT under headless DOM.',
      severity: "warning",
      docsAnchor: "web-conformance/intra-product-panel-overlaps-peer",
      exampleFix: "Constrain the panels so they do not share screen real estate at this breakpoint (collapse one, reflow into separate grid columns, switch to tabs). If the overlap is intentional, opt out with `data-allow-panel-overlap` on either panel or a shared ancestor."
    },
    STR_DECLARED_CHROME_NOT_VISIBLE: {
      description: 'An element that DECLARED itself as visible product chrome \u2014 either via `data-conformance-expect-visible="<label>"` (explicit consumer opt-in) or via the lib/region pair (`data-norkron-lib` + `data-region` in {legend, minimap, controls, panel-list, tab-list, nav, toolbar, sidebar, drawer, sidekick}) \u2014 fails to render visibly to the user. Fires when getComputedStyle reports display:none, visibility != visible, opacity:0 without an animation in flight, OR the bounding rect is < 2px on either axis, OR the rect sits fully outside the viewport with no overflow:auto/scroll ancestor that could scroll it in. Closes the "chrome silently disappeared" failure mode that the panel-overlap, viewport-overflow and container-renders-empty rules cannot see (those need the element to render at non-zero size first). Degrades to STR_NEEDS_LAYOUT under headless DOM.',
      severity: "warning",
      docsAnchor: "web-conformance/declared-chrome-not-visible",
      exampleFix: "Either (a) make the element render visibly at the declared region (remove display:none, set visibility:visible, give it a non-zero rect, scroll it into view, or animate opacity > 0), or (b) drop the visibility declaration if the element is intentionally hidden in this state \u2014 remove `data-conformance-expect-visible` / `data-region`, or replace with conditional rendering so the node is not in the DOM."
    },
    STR_KEEP_CLEAR_REGION_OVERLAPPED: {
      description: 'An element marked `data-keep-clear="true"` (a chrome overlay, modal, sidebar, sticky toolbar, etc.) has its bounding rect intersected by a non-ancestor / non-descendant element rendered at the same or lower z-layer \u2014 the canonical "two unrelated subtrees visually intersect" failure mode that STR_CHILD_OVERFLOWS_PARENT and STR_CHILD_OVERFLOWS_VISUAL_PARENT cannot see (both only handle parent\u2194child overflow). Fires per (keep-clear, leaf-intruder) pair, deduped by selector. Opt-out via `data-allow-overlap` on the intruder or any ancestor up to the common ancestor with the keep-clear element. Degrades to STR_NEEDS_LAYOUT under headless DOM.',
      severity: "warning",
      docsAnchor: "web-conformance/keep-clear-region-overlapped",
      exampleFix: "Either constrain the intruder so it stays out of the keep-clear rect, raise the keep-clear element's z-index above the intruder, or mark the overlap explicit via `data-allow-overlap` on the intruder or an ancestor."
    },
    STR_INTERACTIVE_ELEMENT_VISUALLY_EMPTY: {
      description: 'An interactive element (button, [role=button], a[href], etc.) occupies non-trivial visible space yet renders zero visible content: no text, no inline image/svg/canvas, no background-image/mask-image, no `::before`/`::after` glyph with a loaded font. Almost always the icon-font silent failure \u2014 a `<button class="nf-icn ...">` shipped with `aria-label` but the icon-font CSS or font file never loaded, so sighted users see a blank clickable rectangle while screen readers cheerfully announce the label. Opt-out via `data-allow-empty="true"`. Skips `disabled` / `hidden` / `inert`. Degrades to STR_NEEDS_LAYOUT under headless DOM.',
      severity: "warning",
      docsAnchor: "web-conformance/interactive-element-visually-empty",
      exampleFix: 'Verify the icon CSS/font asset imports \u2014 check the network panel for a 404 on the font file, and the consumer\'s entrypoint for the missing `import "@<lib>/styles.css"` (or icon-font equivalent). If the empty render is intentional, set `data-allow-empty="true"` on the element.'
    },
    CONF_LIVE_FINDING: {
      description: "Catch-all forwarding code emitted by `installLiveAuditing()` for every structural conformance finding it surfaces. The forwarded `STR_*` rule code is embedded at the start of the message (e.g. `STR_CHILD_UNDERFILLS_PARENT_HEIGHT: <div>\u2026`), followed by a `[geom:\u2026]` state-signature marker that varies per observable state so consumers can see live transitions in DevTools without grouping. Edge-triggered: same state dedupes, every transition emits.",
      severity: "warning",
      docsAnchor: "web-conformance/live-finding",
      exampleFix: "Look at the embedded STR_* code at the start of the message for the actual structural finding \u2014 that is what to fix. The CONF_LIVE_FINDING wrapper just lets the auditor see live transitions; it does not represent a fix target itself."
    },
    STR_CONTAINER_RENDERS_EMPTY: {
      description: 'A layout container (<aside>, <section>, <nav>, <main>, <header>, <footer>, <article>, <form>, or a <div> \u2265 80\xD780 px) occupies visible space yet renders no text, no interactive descendants, no replaced elements (img/svg/canvas/video/iframe) with measurable rect, and no background-image. Catches the "panel is paint-empty" failure mode: a child component crashed silently, a data fetch resolved to an empty list, a conditional render always evaluated falsy, or the lib that should populate the slot never mounted. Complements STR_INTERACTIVE_ELEMENT_VISUALLY_EMPTY (which only catches interactive elements). 2026-05-18 \u2014 SVG descendants are now only counted when they have painted geometry (closes the diorama sparkline blind spot where an empty 120\xD732 <svg> silenced the ancestor warning). Opt-out: `data-allow-empty-container="true"`.',
      severity: "warning",
      docsAnchor: "web-conformance/container-renders-empty",
      exampleFix: 'Inspect the React tree at this DOM node: is the child component throwing without an error boundary? Is the data fetch resolving to an empty list? Is the conditional predicate always false? If intentionally blank (layout spacer / future slot), set `data-allow-empty-container="true"`.'
    },
    STR_SVG_RENDERS_PAINT_EMPTY: {
      description: 'A visible `<svg>` occupies non-trivial layout space yet contains NO descendant that paints pixels \u2014 no `<path>` with non-empty `d`, no `<rect>`/`<circle>`/`<ellipse>` with positive size, no `<line>` with distinct endpoints, no `<text>` with non-whitespace content, no `<image>`/`<use>`/`<foreignObject>`. Catches the diorama 2026-05-17 sparkline failure mode: a 120\xD732 SVG shipped with only a `<desc>` child (chart data never bound). Distinct from STR_CONTAINER_RENDERS_EMPTY: this rule fires on the SVG itself, regardless of ancestor content. Opt-out: `data-allow-empty-svg="true"`.',
      severity: "warning",
      docsAnchor: "web-conformance/svg-renders-paint-empty",
      exampleFix: 'Verify the SVG body is being populated \u2014 is the chart data resolving to []? Is the React effect that appends path/rect children never running? If the empty wrapper is intentional (decorative placeholder, hidden chart slot), set `data-allow-empty-svg="true"` on the <svg> or any ancestor.'
    },
    STR_ELEMENT_CLIPPED_BY_ANCESTOR: {
      description: 'A `data-keep-clear`, `data-norkron-lib`-stamped, or natively-interactive (button / role=button / role=tab / role=menuitem) element has its bounding rect extending past the content-box of the NEAREST clipping ancestor (overflow: hidden|clip|auto|scroll or clip-path set). Distinct from STR_CHILD_OVERFLOWS_PARENT (which fires when the parent is unclipped); this rule fires when the ancestor IS clipping, so the element is being visually shaved. Reports per-side clip amounts AND whether the element is FULLY HIDDEN vs partially clipped \u2014 so toolbar buttons that fall off a shrinking viewport are each flagged individually with their own pixel depth. Opt-out: `data-allow-clip="true"`.',
      severity: "warning",
      docsAnchor: "web-conformance/element-clipped-by-ancestor",
      exampleFix: "Grow the clipping ancestor to fit the element, shrink/relocate the element, change ancestor `overflow` to `visible`, OR implement a responsive collapse (hide low-priority chrome below a breakpoint). For chrome that may overflow on small viewports, the right pattern is usually overflow:auto + a scroll affordance, not silent clipping."
    },
    STR_THEME_VARIANT_MISSING: {
      description: 'Meta-rule: an element renders IDENTICALLY in both `data-theme="dark"` and `data-theme="light"`. The element is themed for ONE variant only \u2014 switch the document theme and the element will look broken. Detected by snapshot-toggle: read computed bg/fg/borders, flip `<html data-theme>` to the opposite value, force a reflow, re-read, restore. If neither paint property changed, the element is single-theme. Almost always inline-style hex literals OR a CSS file that only defines one of light/dark. Opt out via `data-allow-single-theme="true"` on the element or any ancestor (logos / branded splash chrome / photographs).',
      severity: "warning",
      docsAnchor: "web-conformance/theme-variant-missing",
      exampleFix: 'Replace inline hex literals with CSS custom properties that have light + dark fallbacks (define defaults at `:root` and override at `[data-theme="dark"]` \u2014 without `:where()` which zeroes specificity). For lib stylesheets, ensure BOTH theme blocks exist with matching keys. Opt out via `data-allow-single-theme="true"` for intentionally theme-invariant chrome.'
    },
    STR_THEME_TOKEN_NOT_THEMED: {
      description: 'An element stamped `data-norkron-lib` renders with a background luminance OPPOSITE to the active theme \u2014 a light-luminance bg in `data-theme="dark"`, or a dark-luminance bg in `data-theme="light"`. Almost always a stylesheet whose dark override has TOO LOW CSS specificity (wrapped in `:where()` which zeroes specificity \u2192 `:root` defaults win), a missing `[data-theme="dark"]` selector, or the lib\'s `styles.css` was not imported. Distinct from contrast rules (which only check fg-vs-bg ratio; they do not know which theme is active).',
      severity: "warning",
      docsAnchor: "web-conformance/theme-token-not-themed",
      exampleFix: 'Inspect the lib\'s tokens.css: the dark/light override must have HIGHER specificity than `:root`. Drop any `:where()` wrapper around the theme selector. Verify the lib\'s `styles.css` is imported in the consumer entrypoint. If the inversion IS intentional (callout / branded chrome), set `data-allow-theme-mismatch="true"`. For intentionally theme-invariant fixture surfaces (logos, photographs, an editor catalog stage that pins one theme by design), set `data-allow-single-theme="true"` \u2014 that attribute also satisfies STR_THEME_VARIANT_MISSING (iter-81).'
    },
    STR_I18N_KEY_LEAKED: {
      description: 'A visible text node renders what looks like an UNTRANSLATED i18n lookup key (e.g. `layout.height.label`, `nav.menu.profile`, `errors.network.timeout`) \u2014 three-or-more dot-separated lowercase segments, 6-80 chars total, EQUAL to the trimmed text. The raw key escaped because the locale catalog did not resolve it: missing key, mis-imported provider, Suspense fallback rendering keys, or `t(key)` called before the i18n instance hydrated. Opt-out via `data-allow-i18n-key="true"` on the element or ancestor. False-positive guards skip path-shapes (`/`, `./`, `../`), semver, domain suffixes, and filename extensions.',
      severity: "warning",
      docsAnchor: "web-conformance/i18n-key-leaked",
      exampleFix: "Verify the key is registered in the locale catalog. Check that the i18n provider/Suspense fallback isn't rendering before hydration. If the literal string IS intentional (developer docs), set data-allow-i18n-key on the element or ancestor."
    },
    STR_DEAD_INTERACTIVE: {
      description: 'Markup that LOOKS clickable but does nothing. Two sub-cases: (A) `<div>`/`<span>` with computed `cursor: pointer` but no `role`, no native interactive tag, no `tabIndex>=0`, no `onclick` attribute \u2014 sighted users get an affordance the element cannot fulfil. (B) `<a>` with `href=""` / `href="#"` / `href="javascript:void(0)"` / no href / `<button type="submit">` outside any `<form>` \u2014 looks like a link or submitter but navigates / submits nothing. Opt-out via `data-allow-passive` or `data-decorative-cursor`.',
      severity: "warning",
      docsAnchor: "web-conformance/dead-interactive",
      exampleFix: 'Case A: add `role="button"` + `tabIndex={0}` + a click handler, OR remove the cursor:pointer signal. Case B: provide a real `href` for the anchor, replace with `<button onClick>`, or move the submit button inside a `<form>`.'
    },
    STR_INTERACTIVE_NO_HOVER_FEEDBACK: {
      description: 'An interactive element (`<button>`, `<a href>`, `[role=button|link|tab|menuitem]`, `<summary>`, native form controls) has NO `:hover` stylesheet rule matching it that changes a perceptible property (background-color, color, border-color, box-shadow, outline, opacity, text-decoration, transform, filter). A control that gives zero hover feedback reads to pointer users as non-interactive. Mirrors STR_FOCUSABLE_NO_VISIBLE_FOCUS_INDICATOR but covers the `:hover` pseudo-state rather than focus. Distinct from STR_DEAD_INTERACTIVE (wired-to-nothing) and STR_INTERACTIVE_ELEMENT_VISUALLY_EMPTY (renders nothing at rest). Exclusions: `data-allow-no-hover="true"`, elements with a `[class*=hover]`/`[data-hover]` JS-driven hover convention, disabled/inert/aria-hidden. Degrades to STR_NEEDS_LAYOUT under headless DOM with no stylesheets.',
      severity: "warning",
      docsAnchor: "web-conformance/interactive-no-hover-feedback",
      exampleFix: 'Add a `:hover` rule that changes a visible property \u2014 e.g. `button:hover { background-color: var(--btn-hover-bg); }`. If hover is provided by a JS class swap, give the swapped class a `hover`-shaped name or set `data-hover`. Opt out via `data-allow-no-hover="true"`.'
    },
    STR_DISABLED_NOT_VISUALLY_DISTINCT: {
      description: 'An element with `[disabled]` or `[aria-disabled="true"]` is visually indistinguishable from its enabled state. Two triggers: (1) its computed paint fingerprint (background-color + color + border-color + opacity) is IDENTICAL to a non-disabled peer instance of the same component; (2) when no peer exists, its own `:disabled`/`[disabled]`/`[aria-disabled]` CSSOM rule changes nothing perceptible. Distinct from STR_DISABLED_CONTROL_LOW_CONTRAST: that rule is a CONTRAST FLOOR (disabled control \u22653:1 vs page bg so it is visible at all); THIS rule checks enabled-vs-disabled DIFFERENTIATION (disabled must look DIFFERENT from enabled). Orthogonal axes \u2014 both can fire. Opt-out: `data-allow-indistinct-disabled="true"`. Degrades to STR_NEEDS_LAYOUT under headless DOM.',
      severity: "warning",
      docsAnchor: "web-conformance/disabled-not-visually-distinct",
      exampleFix: 'Style `:disabled` / `[aria-disabled="true"]` with a distinct paint \u2014 desaturated background, dimmed text, lowered opacity, or `cursor: not-allowed`. Opt out via `data-allow-indistinct-disabled="true"` if the sameness is intentional.'
    },
    STR_DUPLICATE_ARIA_LABEL: {
      description: 'An element carries an `aria-label` whose normalised text EQUALS its own normalised visible text content. Screen readers either announce the name twice ("Save, Save") OR silently override the visible text with the label \u2014 both outcomes are worse than having no aria-label at all. Threshold: skip when aria-label is more than 2\xD7 the visible-text length (likely intentional fuller description). Skip when aria-hidden=true, when the element has zero visible text (icon-only is what aria-label is FOR), or when `data-allow-duplicate-label` is set on the element/ancestor.',
      severity: "warning",
      docsAnchor: "web-conformance/duplicate-aria-label",
      exampleFix: 'Drop the aria-label and let the text content be the accessible name. Use aria-label ONLY when there is no visible text (icon-only) OR when the visible text is shorter than the spoken intent (e.g. visible "Save" + aria-label "Save changes and continue").'
    },
    STR_TEXT_LEAKED_PROGRAMMING_VALUE: {
      description: 'A visible text node renders a stringified programming sentinel \u2014 `NaN`, `Infinity`, `-Infinity`, `undefined`, `null`, `[object Object]`, `Symbol(...)`, `function ...`, or a class declaration. Almost always a value rendered before it stabilised (e.g. `${zoom*100}%` while zoom is undefined), a divide-by-zero, an i18n key resolution failure (the raw key surfaces because the translation is missing), or an object-in-template-hole bug. The user sees junk math; the app does not crash. Opt-out via `data-allow-programming-text="true"` on the element or any ancestor.',
      severity: "warning",
      docsAnchor: "web-conformance/text-leaked-programming-value",
      exampleFix: "Guard the render against pre-stable values (default the state, render a placeholder, OR use `Number.isFinite()` before formatting). For object-as-string, format the field properly. For i18n key leakage, register the key in the locale catalog."
    },
    STR_CANVAS_ZERO_SIZED: {
      description: 'A visible `<canvas>` has intrinsic `.width === 0` or `.height === 0` (or NaN) while occupying measurable CSS area. The GPU backing texture cannot be allocated; the canvas paints nothing and WebGL/WebGPU logs hundreds of "Could not create swapchain texture of size 0" / "Extent3D width:N height:0" warnings every frame. Diorama 2026-05-17 case: Pixi `Application.init({ height: "fill" })` received the unresolved string prop, Number-coerced to NaN, allocated a 0-height texture forever. Opt-out via `data-allow-zero-sized="true"` for offscreen JS-only compositing buffers.',
      severity: "error",
      docsAnchor: "web-conformance/canvas-zero-sized",
      exampleFix: 'At canvas/renderer init, resolve every dimension to a finite pixel number. For lib props that accept `"fill" | number`, convert `"fill"`/`"100%"` to `containerRef.current.clientHeight` BEFORE passing to the renderer. After parent resize, call `renderer.resize(newW, newH)` with finite numbers.'
    },
    STR_REPLACED_ELEMENT_ZERO_RECT: {
      description: 'A replaced element (`<img>` / `<svg>` / `<video>` / `<iframe>` / `<picture>`) renders at zero on one axis (e.g. `1268x0`) while occupying layout space and being intentionally visible (not display:none / visibility:hidden / aria-hidden / lazy-loading). Almost always a replaced element inside a flex/grid track that collapsed on the cross axis, or an `<svg>` lacking an explicit `viewBox` / `width` / `height`. Opt-out via `data-allow-zero-rect="true"`.',
      severity: "warning",
      docsAnchor: "web-conformance/replaced-element-zero-rect",
      exampleFix: "Give the element explicit width AND height (or an aspect-ratio) so the renderer can reserve box space even when the parent collapses. For SVGs, set `viewBox` AND a CSS `width`/`height`. Or fix the upstream STR_CHILD_UNDERFILLS_PARENT_HEIGHT that collapsed the axis."
    },
    STR_CHILD_UNDERFILLS_PARENT_HEIGHT: {
      description: 'A direct child of a flex/grid parent that stretches children on the Y axis (default `align-items: stretch` with `flex-direction: row`) renders materially shorter than the parent\'s content-box height (default tolerance 8px). The visual symptom is an empty band of parent background below/above the child \u2014 typically followed by chrome overlays or fixed UI rendering "into the void" the child should have filled, then content from the child overflowing back into that chrome at certain zoom levels. The diorama 2026-05-17 wrapper-vs-viewer regression (wrapper 828px, viewer 200px) was the adversarial case that exposed this gap. Opt-out via `data-allow-shrink` on the child or any ancestor up to the parent, or set `align-self` to a non-stretch value. Degrades to STR_NEEDS_LAYOUT under headless DOM.',
      severity: "warning",
      docsAnchor: "web-conformance/child-underfills-parent-height",
      exampleFix: "Remove the explicit height/max-height on the child or an intermediate ancestor; OR set `height: 100%` (or `flex: 1; min-height: 0`) on the child; OR if the underfill is intentional, add `data-allow-shrink` to the child or change `align-self` to a non-stretch value."
    },
    STR_FULL_WIDTH_NOT_EDGE_TO_EDGE: {
      description: "An element declaring a full-width intent (width:100%, block/flex/grid with no explicit width, or a `data-full-width` / `w-full` / `block--full-width` hint) is inset from the common left/right edge its element siblings reach, by more than a 2px tolerance \u2014 an intermediate wrapper's padding/margin/border pushes ONLY this element in. Does NOT fire on intentional uniform inset (where all siblings share the inset edge); fires only when the element is anomalously inset relative to its siblings' common edge. Degrades to STR_NEEDS_LAYOUT under headless DOM.",
      severity: "warning",
      docsAnchor: "web-conformance/full-width-not-edge-to-edge",
      exampleFix: "Remove the padding/margin/border on the intermediate wrapper affecting only this element, apply the negative-margin full-bleed pattern consistently, or drop the full-width signal if the inset is intentional for this element."
    },
    STR_RESPONSIVE_CONTRACT_MISSING: {
      description: "A `data-norkron-lib`-stamped element's owning lib has not registered a ResponsiveContract via registerResponsiveContract() in the process-wide responsive registry. The audit therefore cannot reason about that lib's cross-viewport behavior. One finding per distinct un-migrated lib (deduped), with extras.lib + extras.elementCount. NOT layout-dependent \u2014 runs identically under happy-dom and a real browser. This is an intended ratchet: the Layer-1 lib migration (every @norkron/web-* lib calling registerResponsiveContract at import) ships in the same wave; the rule correctly fires \"missing\" until each lib migrates.",
      severity: "warning",
      docsAnchor: "web-conformance/responsive-contract-missing",
      exampleFix: "In the offending lib's entrypoint, call registerResponsiveContract(libName, { breakpoints, minSupportedWidth, strategy }) from @norkron/web-conformance at import time."
    },
    WEB_ERRORS_BARE_IMPORT_OUTSIDE_SCOPED_OUTPUT: {
      description: "A @norkron/web-* lib source file imports warn/panic/reportInvalidInput bare (or aliased) from @norkron/web-errors outside the canonical createScopedErrors(LIB, TOKEN) call site. The scoped output closes lib+libToken over the trio by construction; a bare import drops that identity wiring (cluster #102 failure mode). AST-based, alias-aware, comment-immune.",
      severity: "warning",
      docsAnchor: "web-conformance/web-errors-bare-import",
      exampleFix: "Replace the bare import with the destructured `{ warn, panic, reportInvalidInput } = createScopedErrors(YOUR_LIB, YOUR_TOKEN)` defined once in src/error-codes.ts, then re-export or import that scoped output from your other modules."
    },
    WEB_ERRORS_REPORTINVALIDINPUT_MISSING_LIB_TOKEN: {
      description: "A reportInvalidInput(...) call in a @norkron/web-* lib does not pass BOTH `lib:` AND `libToken:` keys in the object-literal payload. Without both, the resulting warning lands in @unregistered/<n> rather than the lib bucket \u2014 cluster #102 failure mode. AST-walked, alias-aware.",
      severity: "warning",
      docsAnchor: "web-conformance/reportinvalidinput-missing-lib-token",
      exampleFix: "Add both `lib` and `libToken` to the payload, OR (preferred) consume the scoped output of createScopedErrors(YOUR_LIB, YOUR_TOKEN) so identity is closed over automatically."
    },
    WEB_ERRORS_PANIC_MISSING_LIB_TOKEN: {
      description: "A panic(...) call in a @norkron/web-* lib does not pass BOTH `lib:` AND `libToken:` keys in the object-literal payload. Same shape as the reportInvalidInput rule, escalated because panic indicates a hard failure path. AST-walked, alias-aware. The resulting conformance finding carries severity `critical`.",
      severity: "critical-but-non-throwing",
      docsAnchor: "web-conformance/panic-missing-lib-token",
      exampleFix: "Add both `lib` and `libToken` to the panic payload, OR consume the scoped output of createScopedErrors(YOUR_LIB, YOUR_TOKEN)."
    },
    WEB_ERRORS_PARALLEL_SCOPED_AND_DIRECT_PATH: {
      description: "A @norkron/web-* lib has BOTH a createScopedErrors(LIB, TOKEN) call site AND a separate direct import of warn/panic/reportInvalidInput from @norkron/web-errors elsewhere in src/. Pattern-D structural smell: each direct call site must remember to pass lib+libToken manually; one missing key on a future edit silently re-opens cluster #102. Transitional allowlist for v0.8.5 covers web-a11y, web-tooltip, web-themer; v0.8.6 removes the allowlist.",
      severity: "warning",
      docsAnchor: "web-conformance/parallel-scoped-and-direct-path",
      exampleFix: "Migrate the direct-import file(s) to consume the destructured { warn, panic, reportInvalidInput } output of createScopedErrors(LIB, TOKEN). Delete the bare @norkron/web-errors import once the migration is complete."
    },
    CF_LIB_VERSION_DRIFT: {
      description: "Two instances of the same libId reported different versions via their data-norkron-lib attribute on the same page. Dual-realm load or duplicate bundle suspected.",
      severity: "warning",
      docsAnchor: "web-conformance/lib-version-drift",
      exampleFix: "Dedupe the lib in the consumer bundle so a single resolved version reaches the DOM."
    },
    WEB_LIB_VERSION_DRIFT: {
      description: "A @norkron/web-* lib has drift between package.json.version and src/version.ts LIB_VERSION literal. The runtime data-norkron-lib stamp reports the stale src/version.ts value, masking which build is loaded \u2014 cluster-#103 failure mode (35.3% pre-fix fleet base rate per the 2026-05-16 audit). Filesystem-based, comment-immune; reads both files from a libRoot and parses LIB_VERSION via a literal-export regex.",
      severity: "warning",
      docsAnchor: "web-conformance/web-lib-version-drift",
      exampleFix: "Update src/version.ts: change LIB_VERSION to match package.json.version. Add a prepublishOnly assertion (see web-scaffold templates) to block future drift mechanically."
    },
    WEB_LIB_HARDCODED_VERSION_STAMP_IN_TEST: {
      description: "A @norkron/web-* lib test file (`*.test.ts*`) hardcodes its OWN-lib `@norkron/web-X@SEMVER` stamp instead of interpolating from `LIB_VERSION`. Severity escalates to `critical` when the literal SEMVER differs from the current package.json.version (test is hard-broken on next run) and stays `minor` (warning) when the literal matches (latent \u2014 will silently re-open on the next version bump). Four AST-detected variants: plain string, template-with-zero-interpolation, CSS-selector-embedded, and `setAttribute` second-arg. Cross-lib fixture stamps (literal lib name \u2260 owning package) are acceptable; web-conformance internal fixtures are allowlisted; per-line `// @norkron-conformance-allow: hardcoded-stamp` comment suppresses individual hits. Cluster #4 (audit 2026-05-16, 24 libs).",
      severity: "warning",
      docsAnchor: "web-conformance/web-lib-hardcoded-version-stamp-in-test",
      exampleFix: "Import LIB_VERSION at the top of the test file (`import { LIB_VERSION } from '../version'`) and rewrite the literal as `` `@norkron/web-X@${LIB_VERSION}` `` so the stamp tracks package.json.version automatically. See web-wizard/src/__tests__/self-check.test.ts for the canonical pattern."
    },
    WEB_LIB_VERSION_TS_MISSING: {
      description: "A @norkron/web-* lib has no src/version.ts file at all (5/51 libs per the 2026-05-16 audit: web-conformance, web-flow-audit, web-flow-audit-registry, web-flow-contract, web-zig-autodoc-theme). Adoption-period warning that escalates to major after the sunset date (2026-06-15).",
      severity: "warning",
      docsAnchor: "web-conformance/web-lib-version-ts-missing",
      exampleFix: "Create src/version.ts exporting `export const LIB_VERSION = '<package.json.version>'` and re-export it from the lib entrypoint."
    },
    WEB_ERRORS_WRAPPER_FORWARDS_HARDCODED_SEVERITY: {
      description: "A @norkron/web-* lib has a wrapper around @norkron/web-errors warn/panic/reportInvalidInput/deprecate that passes a HARDCODED string-literal `severity:` for every code routed through it. Severity escalates to `critical` when the literal differs from the registered severity for the routed code (silent override at the wire \u2014 the cluster #5 bug shape, replicates web-admin-shell pre-v0.4.6 defect at web-data-grid + web-collection-view) and stays `minor` (warning) when the literal matches (redundant pass-through \u2014 registry is authoritative since v0.6.1; the explicit override masks the model and re-opens cluster #5 on next bump). Generic forwarders (severity literal applied to `code: input.code`) emit critical when the lib's registry contains ANY code at a different severity. Safe shapes (NOT defects): dynamic forward `severity: CODES[code].severity` (web-dataviz precedent), no severity literal at all (registry-driven), per-line `// @norkron-conformance-allow: severity-override` marker. Cluster #5 (audit 2026-05-16, 2 wrappers / 7 silent upgrades / 11 redundant pass-throughs across 52 web-* libs). 6th-of-6 SEXTUPLE mechanical-prevention rule for the web-errors invariant.",
      severity: "warning",
      docsAnchor: "web-conformance/web-errors-wrapper-forwards-hardcoded-severity",
      exampleFix: "Remove the hardcoded `severity:` property from the wrapper call site. The @norkron/web-errors registry is authoritative since v0.6.1 \u2014 let each registered code's severity flow through. If a specific surface genuinely needs an override, add the per-line marker `// @norkron-conformance-allow: severity-override` with rationale, OR fix the registry entry. See web-admin-shell v0.4.6 (commit 6ef691a) and the canonical safe-pattern in web-dataviz warnData (severity: CODES[code].severity)."
    },
    WEB_LIB_DIST_TREE_SHAKES_REGISTER_CONFORMANCE: {
      description: "A @norkron/web-* lib's src/index.ts contains a side-effect-only `import './register-conformance'` (or any `./register-*`) AND its package.json#sideEffects either `false` (critical \u2014 bundler explicitly authorised to strip; runtime selfCheck wiring becomes a silent no-op) or an array that does not enumerate the side-effect file's src/dist path (major \u2014 bundlers MAY still strip per explicit allow-list semantics). The canonical fix is Pattern B (top-level registerLazy(...) call in src/index.ts; see web-data-grid commit a2e7f210) which survives tree-shaking by construction. Source-side AST detection; distinguishes top-level statement vs nested-call vs call-inside-function-expression. Cluster #6 (audit 2026-05-16, 7-lib base rate).",
      severity: "warning",
      docsAnchor: "web-conformance/web-lib-dist-tree-shakes-register-conformance",
      exampleFix: "Refactor src/index.ts to the wizard pattern: (1) delete src/register-conformance.ts; (2) import `registerLazy` from `@norkron/web-conformance/register-lazy` and `selfCheck` from `./self-check`; (3) call `registerLazy(YOUR_LIB_ID, () => selfCheck)` at module top level; (4) drop the side-effect import line. See web-data-grid/src/index.ts:13-16 for the canonical shape."
    },
    WEB_LIB_TOKEN_NOT_DETERMINISTIC: {
      description: "A @norkron/web-* lib's createScopedErrors(LIB, TOKEN) call site is wired with a TOKEN built via `Symbol(...)` (non-deterministic) instead of `Symbol.for('norkron.lib.<name>')` (deterministic, global registry). Each `Symbol(...)` call returns a unique Symbol at module load; on substrate barrel re-import (the dual-bundle ESM+CJS shape Vite/Bun produces for interop) the lib evaluates twice and the second Symbol fails the downstream identity check with LIB_IDENTITY_SPOOFING_DETECTED. AST resolution: literal Symbol(...) inline \u2192 critical; identifier reference traced to a Symbol(...) declaration in same or sibling file \u2192 critical; unresolvable identifier (cross-file or external) \u2192 warning (manual review). Cluster #7 (audit 2026-05-16, 7-lib base rate confirmed at web-codeblock/web-csrf/web-i18n/web-segmented-control/web-survey/web-sync-queue/web-wizard; web-csrf runtime-confirmed via a617e9fd). 7th-of-7 SEPTUPLE mechanical prevention rule.",
      severity: "warning",
      docsAnchor: "web-conformance/web-lib-token-not-deterministic",
      exampleFix: "Change the *_LIB_TOKEN declaration to `Symbol.for('norkron.lib.<lib-name>')` (drop the `web-` prefix only if the lib's canonical key omits it \u2014 see web-themer/web-states/web-numeric-format for examples). The global symbol registry returns the same Symbol across every module load, every barrel re-export, every realm boundary. 40-of-47-fleet correct shape."
    },
    WEB_LIB_TOKEN_DEAD: {
      description: 'A @norkron/web-* lib declares `<NAME>_TOKEN = Symbol(.for)?(...)` somewhere in src/ but never passes it to createScopedErrors / registerLib / registerLazy, and the declaration is not exported for external consumption. Dead identity wiring (Pattern B per cluster #7 audit, web-flow-contract precedent). Invites future agents to "wire it up" in the wrong way; signals abandoned wiring attempt.',
      severity: "warning",
      docsAnchor: "web-conformance/web-lib-token-dead",
      exampleFix: "Either delete the dead token declaration, OR wire it via `const { warn, panic, reportInvalidInput } = createScopedErrors(YOUR_LIB, YOUR_TOKEN)` in src/error-codes.ts (canonical pattern). If the token is intentionally exported for downstream consumption, the rule auto-exempts it."
    },
    STR_LIB_TIER_INVERSION: {
      description: "Source rule. A @norkron/web-* lib statically imports (`import ... from` / `export ... from`) a STRICTLY HIGHER-tier lib. Tier model: Tier 0 = foundational (infra / headless / non-rendering primitives \u2014 web-errors, web-conformance, web-themer, web-a11y, web-floating, etc.); Tier 1 = UI primitives that render a visible widget (web-tooltip, web-dialog, web-menu, web-icons, etc.); Tier 2 = composites that compose Tier-1 primitives (web-command-palette, web-data-grid, web-wizard, etc.). A lower tier statically importing a higher tier risks a circular dependency and module-init crash \u2014 the 2026-05-21 incident where web-conformance (Tier 0) statically imported RichTooltip from web-tooltip (Tier 1), web-tooltip imported back, and every consumer's screen went blank (fixed in 20771eb). Lazy `import()` is EXEMPT (the sanctioned cycle-breaker). Same-tier and higher->lower imports are fine. Opt-out: a `// conformance-allow-tier-inversion: <reason>` comment on/above the import line. Unknown web-* libs default to Tier 1. Added 2026-05-21.",
      severity: "critical-but-non-throwing",
      docsAnchor: "web-conformance/lib-tier-inversion",
      exampleFix: "Replace the static import with a lazy `import('<spec>')` (see commit 20771eb \u2014 web-conformance moved its AuditReport React surface to a /react subpath consumed lazily), OR relocate the higher-tier-consuming code into the higher-tier lib, OR \u2014 if the dependency is deliberate and safe \u2014 add a `// conformance-allow-tier-inversion: <reason>` comment on the import line."
    },
    STR_LIB_NOT_IN_REGISTRY: {
      description: "Source rule (ecosystem-wide \u2014 audits the lib SET, not one lib's dist). A @norkron/web-* lib directory under frontend/web/ has a built dist/ but is absent from the editor lib registry (web-themer-editor/sample_lib/lib_registry.ts) \u2014 the master list the empiric-proof showcase + audit gate iterates over. The gate cannot flag what it does not know exists. Severity is tier-dependent: a UI-primitive (Tier 1) or composite (Tier 2) lib that renders a visible widget -> major (it MUST be showcased + audited); a foundational/headless lib (Tier 0) -> minor (lower-risk \u2014 audited via its own selfCheck + tests \u2014 but it should still be listed as `infra` for accountability). Non-consumer dirs (web-themer-editor the app, web-conformance itself, web-sample the fixture) are never flagged. An unbuilt lib (no dist/) is not flagged \u2014 it cannot be expected in the registry yet. Added 2026-05-21 after a measurement found 70 web-* dirs on disk but only 54 registry entries.",
      severity: "warning",
      docsAnchor: "web-conformance/lib-not-in-registry",
      exampleFix: "For a UI lib: add it to web-themer-editor/sample_lib/lib_registry.ts with status `wired` and an examples module so the empiric-proof gate showcases + audits it. For a headless lib: add it as `infra` (status `infra` \u2014 no visual preview by design) so every lib is accounted for."
    },
    STR_PILL_CONTAINER_ELLIPSE_AT_NARROW: {
      description: "A container's effective border-radius has collapsed the painted rect to an ellipse or super-ellipse. Two shapes: (1) PURE ELLIPSE \u2014 border-radius > min(width,height)/2; (2) PILL COLLAPSED TO VERTICAL COLUMN \u2014 raw radius >= height/2 AND rect.height > 2 * rect.width (the canonical diorama 2026-05-19 bug where 'border-radius: 999px' intended as a horizontal stadium clamped to half the WIDTH once wrap-balanced resolved the row to a tall narrow column). The chassis no longer reads as rectangular-with-rounded-corners. Opt-out: `data-allow-pill-ellipse=\"true\"`.",
      severity: "warning",
      docsAnchor: "web-conformance/pill-container-ellipse-at-narrow",
      exampleFix: "Bind the radius to a row dimension, not the wrap-resolved container: border-radius: calc(var(--row-height, 36px) / 2). For an actual circle/squircle, cap at min(width,height)/2."
    },
    STR_CONTROL_LABEL_MID_WORD_BREAK: {
      description: 'A control element (button / role=tab / role=option / role=menuitem / .btn / .ctl / .segmented-control__segment / control-family lib descendant) carries computed overflow-wrap:anywhere|break-word OR word-break:break-all AND contains a label whose longest word measures wider than the element\'s clientWidth. Any wrap CSS picks will split the label MID-WORD at an arbitrary character boundary, producing unreadable two-row segments \u2014 the canonical diorama 2026-05-19 bug shape. Measurement uses canvas 2D measureText where available, falls back to char-count * fontSize * 0.55. Opt-out: `data-allow-mid-word-break="true"`.',
      severity: "warning",
      docsAnchor: "web-conformance/control-label-mid-word-break",
      exampleFix: "Set white-space:nowrap + overflow-wrap:normal + word-break:keep-all on the control segment, AND add intrinsic min-content-width on flex children so the segment grows to fit its label. If truncation is the desired affordance, use text-overflow:ellipsis with an aria-label / tooltip for the full text."
    },
    WEB_LIB_SELFCHECK_BARE_DOM_GLOBAL_INSTANCEOF: {
      description: "A @norkron/web-* lib's src/self-check.ts (or src/**/*self-check*.ts) uses a bare `<value> instanceof <DOMGlobal>` (Element/HTMLElement/Node/Document/Window/HTMLDocument/Text/Comment/DocumentFragment/ShadowRoot/SVGElement/HTMLInputElement/HTMLButtonElement/SVGElement) without a `typeof <DOMGlobal> !== 'undefined'` guard. Under bare bun/linkedom SSR (the environment web-conformance audit() exercises server-side) the constructor is not on globalThis and the predicate throws ReferenceError, which audit() swallows into a *_SELFCHECK_INTERNAL_ERROR \u2014 silently disabling the lib's selfCheck server-side. The failure mode is invisible in browser test harnesses (happy-dom, jsdom, real Chrome all install constructors globally); only the SSR audit path surfaces it. Cluster #8 (audit 2026-05-16, 0 live defects + 1 historical instance fixed in web-i18n v0.3.5 commit 13a0970). P0 PREVENTIVE: live base rate 0 but failure shape is invisible enough that any reintroduction silently degrades the audit. 8th-of-8 OCTUPLE MECHANICAL PREVENTION COMPLETE for the cluster substrate.",
      severity: "warning",
      docsAnchor: "web-conformance/web-lib-selfcheck-bare-dom-global-instanceof",
      exampleFix: "Wrap the check in a typeof guard: `if (typeof Element !== 'undefined' && value instanceof Element) { ... }`. For full SSR parity prefer the duck-typed pattern from web-i18n v0.3.5 (`typeof value.getAttribute === 'function'` for Element-shaped checks), or import the canonical guards from `@norkron/web-conformance/src/internal/dom-guards.ts` (isElement, isHTMLElement, isHTMLInputElement, isNode). If a browser-only path is genuinely intentional, add the per-line marker `// @norkron-conformance-allow: bare-dom-instanceof` with rationale."
    },
    WEB_LIB_TOKEN_MULTIPLE_DECLARATIONS: {
      description: "A @norkron/web-* lib declares the SAME `<NAME>_TOKEN = Symbol.for('<key>')` in 3+ separate files within src/, all with the same registry key. Runtime-equivalent (the global registry returns the same Symbol) \u2014 NOT a determinism defect \u2014 but a rename-drift smell: changing the registry key string in one file silently splits identity. The web-themer THEMER_TOKEN observation (3 files: dtf-import.ts, branding-config.schema.ts, css-vars.ts).",
      severity: "warning",
      docsAnchor: "web-conformance/web-lib-token-multiple-declarations",
      exampleFix: "Pick ONE file (canonically src/error-codes.ts or src/index.ts) to declare `export const <NAME>_TOKEN = Symbol.for('<key>')` and import the token into the other files. Removes rename-drift risk."
    },
    STR_VIEWER_TOOLBAR_MISSING: {
      description: 'A `[data-diagram-renderer]` host renders with no descendant `[data-region="toolbar"]` (or it is display:none / visibility:hidden). Per FR 2026-05-19-3d-renderer-chrome-and-billboard-transparency (addendum): the unified Diagram shell from `@norkron/diorama` must wrap BOTH renderers (Pixi 2D + Three 3D) with the same chrome. The toolbar (zoom, fit, recenter, screenshot) is one of six canonical regions that must mount renderer-agnostically. Opt-out: `data-allow-missing-toolbar` on the host.',
      severity: "warning",
      docsAnchor: "web-conformance/viewer-toolbar-missing",
      exampleFix: "Mount <ViewerToolbar /> inside ViewerContainer.body so it wraps both renderers. The toolbar reads from the renderer-agnostic Diagram store via the RendererAdapter interface \u2014 never conditionally per-renderer."
    },
    STR_VIEWER_MINIMAP_MISSING: {
      description: 'A `[data-diagram-renderer]` host renders with no descendant `[data-region="minimap"]` (or it is display:none / visibility:hidden). Per FR addendum: the minimap reads viewport bounds via RendererAdapter.viewport() and must mount for both Pixi 2D and Three 3D renderers. Opt-out: `data-allow-missing-minimap` on the host.',
      severity: "warning",
      docsAnchor: "web-conformance/viewer-minimap-missing",
      exampleFix: "Mount <ViewerMinimap /> inside ViewerContainer.body. The minimap reads bounds via RendererAdapter \u2014 renderer-agnostic."
    },
    STR_VIEWER_LEGEND_MISSING: {
      description: 'A `[data-diagram-renderer]` host renders with no descendant `[data-region="legend"]` (or it is display:none / visibility:hidden). Per FR addendum: the legend reads from the renderer-agnostic Diagram store. Opt-out: `data-allow-missing-legend` on the host.',
      severity: "warning",
      docsAnchor: "web-conformance/viewer-legend-missing",
      exampleFix: "Mount <ViewerLegend /> inside ViewerContainer.body. The legend reads from the same renderer-agnostic store regardless of mode."
    },
    STR_VIEWER_SIDEBAR_MISSING: {
      description: 'A `[data-diagram-renderer]` host renders with no descendant `[data-region="sidebar"]` (or it is display:none / visibility:hidden). Per FR addendum: the sidebar (details panel, shape picker, settings) must mount renderer-agnostically. Opt-out: `data-allow-missing-sidebar` on the host.',
      severity: "warning",
      docsAnchor: "web-conformance/viewer-sidebar-missing",
      exampleFix: "Mount <ViewerSidebar /> inside ViewerContainer.body. The sidebar reads renderer-agnostic state from the Diagram store."
    },
    STR_VIEWER_BACKGROUND_PATTERN_MISSING: {
      description: 'A `[data-region="canvas-slot"]` descendant of a `[data-diagram-renderer]` host has empty `background-image` computed style (no checkerboard / grid / pattern). Per FR addendum: the CSS-themed surface is required for visual parity between 2D and 3D modes; without it the 3D mode looks like an unbounded void. Opt-out: `data-allow-missing-background-pattern` on the host.',
      severity: "warning",
      docsAnchor: "web-conformance/viewer-background-pattern-missing",
      exampleFix: 'Apply the canonical checker/grid pattern via `background-image` on the canvas-slot, referencing the `--viewer-bg` theme tokens. Mount <ViewerBackground pattern="checker" /> inside ViewerContainer.body.'
    },
    STR_VIEWER_BORDER_MISSING: {
      description: 'A `[data-region="canvas-slot"]` descendant of a `[data-diagram-renderer]` host has `border-width: 0px` on all sides OR `border-color: transparent` on all sides. Per FR addendum: the themed border / focus ring on the viewer container is one of six canonical chrome regions. Opt-out: `data-allow-missing-border` on the host.',
      severity: "warning",
      docsAnchor: "web-conformance/viewer-border-missing",
      exampleFix: "Apply `border: var(--viewer-border-width, 1px) solid var(--viewer-border)` on the canvas-slot. Themed via `--viewer-*` CSS variables so dark/light themes inherit."
    },
    STR_BILLBOARD_OPAQUE_OCCLUDES_3D_EDGES: {
      description: "A 2D canvas-texture billboard (THREE.Sprite) inside a `<canvas data-engine^=\"three\">` renderer is opaque where it should be alpha-transparent: either `material.transparent === false` OR the source CanvasTexture's (0,0) pixel reads alpha=255 (canvas wasn't `clearRect`-cleared before paint). Edges and shapes drawn behind the billboard are visually clipped. Requires the diorama-exposed `window.__diorama_three_scene` debug handle to access the scene; if absent the rule skips (no false-positive on missing evidence). Per FR 2026-05-19-3d-renderer-chrome-and-billboard-transparency.",
      severity: "warning",
      docsAnchor: "web-conformance/billboard-opaque-occludes-3d-edges",
      exampleFix: "Clear the canvas with `ctx.clearRect(0, 0, w, h)` before painting; set `material.transparent = true`; set `material.alphaTest = 0.01`; keep `material.depthWrite = true` so the sprite still occludes correctly where it has actual paint."
    },
    STR_BILLBOARD_TEXTURE_BLURRY_AT_ZOOM: {
      description: "A THREE.Sprite billboard's texture will look blurry when the camera zooms in: ANY of `material.map.image.width < devicePixelRatio * sprite.userData.cssWidth` (texture not supersampled), `material.map.anisotropy === 1` (default \u2014 no anisotropic filtering), `material.map.minFilter === THREE.LinearFilter` (value 1006 \u2014 no mipmaps). Requires the diorama-exposed `window.__diorama_three_scene` handle; otherwise the rule skips. Per FR 2026-05-19-3d-renderer-chrome-and-billboard-transparency.",
      severity: "warning",
      docsAnchor: "web-conformance/billboard-texture-blurry-at-zoom",
      exampleFix: "Supersample the canvas-texture at creation (canvas size = dpr \u2265 2 \xD7 intended cssWidth); set `texture.anisotropy = renderer.capabilities.getMaxAnisotropy()`; set `texture.minFilter = THREE.LinearMipmapLinearFilter` + `texture.generateMipmaps = true`."
    },
    STR_ICON_LOW_CONTRAST: {
      description: "An SVG <path>/<circle>/<rect>/<line>/<polyline>/<polygon>/<ellipse> has computed stroke or fill contrast below 3:1 (WCAG SC 1.4.11 graphical-object minimum) vs the nearest opaque ancestor background. Distinct from STR_CONTRAST_INSUFFICIENT which only walks text nodes \u2014 SVG icon paths have no text descendants and were invisible to that rule.",
      severity: "critical-but-non-throwing",
      docsAnchor: "web-conformance/icon-low-contrast",
      exampleFix: "Darken the icon stroke/fill OR lighten the surrounding background to \u22653:1. For inherited `currentColor`, bump the ancestor `color` to a body-text token."
    },
    STR_ELEMENT_WIDTH_EXCEEDS_PARENT: {
      description: "An element's BCR width exceeds its parent's clientWidth by \u22651.5x. Catches 'child blown up to viewBox-pixel size inside a narrow flex cell' \u2014 STR_VIEWPORT_OVERFLOW misses this because the child sits inside the body (no viewport-edge overflow) yet is visually enormous inside its container. Opt-out via data-allow-overflow.",
      severity: "critical-but-non-throwing",
      docsAnchor: "web-conformance/element-width-exceeds-parent",
      exampleFix: 'Constrain the element with explicit width/height attributes (SVG: width="16" height="16") OR CSS (max-width:100%, width: var(--space-4)).'
    },
    STR_TEXT_CROWDS_CONTAINER_EDGE: {
      description: `A leaf text node's rendered rect sits within <4px of its nearest bounded ancestor's content-box edge (positive gap; negative is overflow, owned by STR_TEXT_CLIPPED_BY_CONTAINER). "Bounded" = ancestor draws a border, distinct background-color, or box-shadow. Closes the insufficient-spacing gap: the overflow/overlap rules fire only on wrong geometry, this fires on right-but-tight geometry. Distinct from STR_TEXT_ASYMMETRIC_PADDING (off-center) \u2014 this fires on absolute tightness. Degrades to STR_NEEDS_LAYOUT under headless DOM.`,
      severity: "critical-but-non-throwing",
      docsAnchor: "web-conformance/text-crowds-container-edge",
      exampleFix: 'Add at least 4px padding on the crowded side(s) of the bounded container, or shrink the text run. Opt-out: data-allow-edge-text="true". Threshold override: data-min-edge-gap="<px>".'
    },
    STR_SIBLINGS_INSUFFICIENT_GAP: {
      description: "Two DOM-sibling elements, both visible with non-zero rects, whose nearest edges are >0px but <4px apart on a shared axis (not overlapping \u2014 overlap is owned by STR_OVERLAPPING_CLICK_TARGETS). They read as one smudged blob. major when both siblings are interactive (mis-tap hazard); minor otherwise. Skips table cells, segmented-control segments, list rows, and absolutely-positioned children. Degrades to STR_NEEDS_LAYOUT under headless DOM.",
      severity: "critical-but-non-throwing",
      docsAnchor: "web-conformance/siblings-insufficient-gap",
      exampleFix: 'Add at least 4px gap (flex/grid gap, margin) between the siblings. If they are meant to abut (joined buttons, toolbar group), set data-allow-flush-siblings="true" on the shared parent.'
    },
    STR_CONTAINER_TEXT_NO_PADDING: {
      description: 'An element that draws a visible boundary (border, distinct background-color, or box-shadow), directly owns a non-empty text node, and has computed padding <4px on one or more sides \u2014 the "text glued to the card border" defect. Computed-style complement of STR_TEXT_CROWDS_CONTAINER_EDGE; distinct from STR_TEXT_ASYMMETRIC_PADDING which fires on padding ASYMMETRY \u2014 this fires on absolute INSUFFICIENCY. Skips tiny chrome (<24x12px) and ellipsis clamps. Degrades to STR_NEEDS_LAYOUT under headless DOM.',
      severity: "critical-but-non-throwing",
      docsAnchor: "web-conformance/container-text-no-padding",
      exampleFix: 'Add at least 4px padding on the deficient side(s) of the bounded container. Opt-out: data-allow-no-padding="true".'
    },
    STR_SPACING_NOT_TOKENIZED: {
      description: 'A WIRING rule: the spacing separating a leaf text node from the nearest VISIBLE container (border / distinct background-color / box-shadow ancestor) is not wired to a design token. Inspects the CSSOM DECLARED value (NOT the resolved computed value, which collapses `var(--space-2)` and a literal `8px` into the same `8px`) across the text element, every invisible layout wrapper, and the visual container, looking for any padding/margin/gap declaration whose value contains `var(--token)` referencing a spacing namespace (--space-*, --gap-*, --padding-*, --spacing-*, --inset-*). Fires when a real spacing need exists (text NOT meant edge-to-edge) AND no node in the chain tokenizes the spacing. Sub-case (a) hardcoded literal present \u2192 "spacing present but not themeable"; sub-case (b) no spacing at all \u2192 "no spacing wired". Independent of the 3 visual spacing rules \u2014 the same DOM spot can carry both a visual and this wiring finding. Opt-out: data-allow-untokenized-spacing="true"; full-bleed via data-allow-edge-text="true". Degrades to STR_NEEDS_LAYOUT under sheetless DOM.',
      severity: "warning",
      docsAnchor: "web-conformance/spacing-not-tokenized",
      exampleFix: 'Replace the hardcoded padding/margin/gap literal with a web-themer spacing token, e.g. `padding: var(--space-2)`. Opt-out for genuinely fixed spacing: data-allow-untokenized-spacing="true".'
    },
    STR_EXCESSIVE_LINE_HEIGHT: {
      description: "The first UPPER-BOUND typographic rule (all prior spacing rules are lower-bound). A multi-line text block (an element directly owning text that renders \u22652 line boxes \u2014 getClientRects().length \u2265 2 OR scrollHeight \u2265 2\xD7 font-size) whose content-density ratio (computed line-height \xF7 computed font-size) exceeds a ceiling. PRIMARY deterministic baseline: flat ceiling 2.2 (1.4 comfortable, 2.0 airy, >2.2 visibly broken) \u2014 catches the codeblock-with-3.5-leading case. REFINEMENT: if the element OR its nearest data-norkron-lib ancestor carries data-text-role \u2208 {code,body,heading,display}, the role band applies instead \u2014 code 1.3\u20131.6, body 1.4\u20131.8, heading 1.05\u20131.4, display 1.05\u20132.0; ratio outside the band (too loose OR too tight) fires. Degrades to STR_NEEDS_LAYOUT under headless DOM.",
      severity: "warning",
      docsAnchor: "web-conformance/excessive-line-height",
      exampleFix: 'Set line-height in proportion to font-size (\u22642.2\xD7 for the flat ceiling; 1.4\u20131.6 typical body copy; apply the role band when data-text-role is declared). For deliberately loose leading, set data-allow-loose-leading="true".'
    },
    STR_GATE_LAYOUT_UNAVAILABLE: {
      description: "The audit ran in a runtime environment that does not compute layout (happy-dom / linkedom / SSR \u2014 `getBoundingClientRect` returns zero on every anchor or is missing entirely, OR `getComputedStyle` is unavailable). Every layout-dependent structural rule was degraded to info AND the per-rule STR_NEEDS_LAYOUT envelopes were collapsed into this single document-root finding (suppressed rule codes listed in `extras.suppressedForCodes`, count in `extras.suppressedCount`). Without this collapse the same env emits ~25-30 STR_NEEDS_LAYOUT per audit (~315 across the editor example corpus), drowning real findings. This is the `feedback_gate_must_prove_itself` doctrine in action \u2014 a matcher that cannot read its own input MUST NOT silently emit thousands of findings.",
      severity: "info",
      docsAnchor: "web-conformance/gate-layout-unavailable",
      exampleFix: "Run the audit in a real browser session (Chromium / Firefox / WebKit) where `getBoundingClientRect` returns non-zero rects. The layout-dependent rules then run normally. The `audit:examples` happy-dom pipeline intentionally cannot satisfy these rules \u2014 pair it with a chrome-devtools / narrate-driven follow-up audit for layout coverage."
    },
    STR_RULE_THREW: {
      description: "A structural rule threw an exception during the audit walk. The walker isolated it via per-rule try/catch \u2014 every OTHER rule still ran. The thrown rule is reported here so it can be fixed without blanking the entire report. Payload: { ruleCode, errorName, errorMessage, elementOuterHtml }. This is the post-mortem defence for the 2026-05-21 SVGAnimatedString className regression, where ONE buggy rule killed the whole walker in real Chromium while passing in happy-dom.",
      severity: "error",
      docsAnchor: "web-conformance/rule-threw",
      exampleFix: "Inspect extras.ruleCode + extras.errorMessage; fix the rule to handle the offending element type (often: SVG vs HTML className differences, missing getBoundingClientRect under headless, null computed style on detached elements). Add a happy-dom-vs-Chromium guard if the failure is browser-specific."
    },
    STR_SIBLINGS_EXCESSIVE_GAP: {
      description: "The UPPER-BOUND counterpart to STR_SIBLINGS_INSUFFICIENT_GAP. Two DOM-sibling elements whose nearest edges on the shared layout axis are separated by a gap that is disproportionate \u2014 fires when the inter-sibling gap exceeds 3\xD7 the larger sibling's own content extent on that axis AND the gap is >64px absolute (both conditions \u2014 avoids flagging tiny elements legitimately spread across a large container). Excludes flex/grid containers with justify-content space-between/space-around/space-evenly (intentional distribution), absolutely-positioned children, and first/last child against the container edge. Degrades to STR_NEEDS_LAYOUT under headless DOM.",
      severity: "info",
      docsAnchor: "web-conformance/siblings-excessive-gap",
      exampleFix: `Tighten the gap to \u22643\xD7 the elements' own extent, OR use a distributing layout (flex/grid justify-content: space-between) if the spread is intentional. For a deliberate wide gap, set data-allow-wide-gap="true" on the shared parent.`
    },
    STR_HREF_FRAGMENT_BROKEN: {
      description: 'An `<a href="#X">` references a fragment whose target id does not exist in the document (nor does a legacy `<a name="X">`). The link visually paints normal, cursor flips to pointer on hover, but clicking does nothing \u2014 silent in-page navigation defect. The `#` and `#top` placeholders are skipped (browser-magic). Emitted once per dead fragment with the count of links and sample anchor text.',
      severity: "warning",
      docsAnchor: "web-conformance/href-fragment-broken",
      exampleFix: "Add an element with the referenced id at the intended target location, OR rename the link's href to match an existing id, OR remove the broken link."
    },
    STR_FORM_INPUT_NO_ACCESSIBLE_NAME: {
      description: 'An `<input>` (except hidden/submit-with-value/reset-with-value/button-with-value/image-with-alt), `<select>`, or `<textarea>` has no source of accessible name: no resolvable aria-labelledby, no non-empty aria-label, no wrapping `<label>`, no `<label for>` matching the input id, no placeholder, no title. Screen readers announce just "edit text" / "combobox" / "edit multi-line text" with no name. Sighted devs assume the nearby text labels the input by visual proximity \u2014 visual proximity is NOT a name source per WAI. Placeholder-only and title-only sources emit separate `info`-severity findings.',
      severity: "warning",
      docsAnchor: "web-conformance/form-input-no-accessible-name",
      exampleFix: 'Pair the input with `<label for="id">Field name</label>` matched to `<input id="id">`, OR wrap the input in `<label>Field name <input /></label>`, OR set `aria-label="Field name"` if no visible label is acceptable. Placeholder is NOT a substitute \u2014 it vanishes on focus.'
    },
    STR_BUTTON_NO_ACCESSIBLE_NAME: {
      description: 'A `<button>` or `[role="button"]` has no source of accessible name: no resolvable aria-labelledby, no non-empty aria-label, no non-empty textContent, no submit-button `value`, no `title`. Screen readers announce "button" with no name \u2014 the icon-button trap. Sighted devs see the icon and assume it works; blind users are stranded. A `title`-only name source emits a separate `info`-severity finding (title is the lowest-priority name source and unreliable on mobile / touch).',
      severity: "warning",
      docsAnchor: "web-conformance/button-no-accessible-name",
      exampleFix: 'Add `aria-label="\u2026"` describing the action (e.g. `aria-label="Close dialog"`), OR put visible textContent inside (decorate the icon with `aria-hidden="true"`: `<button><Icon aria-hidden="true" /> Save</button>`), OR set `aria-labelledby` to a labelling element\'s id.'
    },
    STR_DUPLICATE_ID: {
      description: 'An `id` value appears on more than one element under the audit root. HTML requires ids to be document-unique. `getElementById` returns only the FIRST match \u2014 every aria-labelledby/describedby/controls/owns/flowto, `<label for>`, and `href="#id"` referencing the duplicated id silently routes to the wrong element. This breaks the IDREF guarantees that STR_ARIA_REFERENCE_BROKEN and STR_LABEL_FOR_BROKEN depend on (they observe resolution succeed and report nothing wrong). Emitted once per duplicated id value with all colliding tags listed.',
      severity: "warning",
      docsAnchor: "web-conformance/duplicate-id",
      exampleFix: "Rename duplicate ids so each is document-unique. If the elements are intentionally a group, use a shared `data-*` attribute or `aria-controls`/`aria-labelledby` with a single canonical id elsewhere."
    },
    STR_LABEL_FOR_BROKEN: {
      description: 'A `<label for="X">` element references an id that either does not exist, resolves to a non-labelable element (only button/input/meter/output/progress/select/textarea are labelable per HTML), or points at a hidden `<input type="hidden">`. The label/input association is silently broken \u2014 sighted users see the label next to the control and assume it works; screen reader users get an unlabeled input. Emitted once per (forId, reason) pair.',
      severity: "warning",
      docsAnchor: "web-conformance/label-for-broken",
      exampleFix: "Either fix the input id to match the label's `for`, or wrap the label content around the input for implicit association, or remove the broken `for` attribute. If the target is non-form content, use `aria-labelledby` instead of `<label for>`."
    },
    STR_ARIA_REFERENCE_BROKEN: {
      description: 'An `aria-{labelledby,describedby,controls,owns,flowto,activedescendant,details,errormessage}` attribute references one or more IDs that do not resolve to any element in the document. Screen readers will announce nothing (or fall back to the element\'s own textContent \u2014 usually empty for an icon button). Visually the widget looks fine \u2014 silent a11y defect of the "I assumed the referent exists" class. Emitted once per (sourceTag, attr, brokenId) triple to keep the gate signal-rich.',
      severity: "warning",
      docsAnchor: "web-conformance/aria-reference-broken",
      exampleFix: "Either add an element with the referenced id providing the label/description/control target, or remove the broken id from the (space-separated) attribute value, or drop the attribute entirely if no valid referent exists."
    },
    STR_TOKEN_REFERENCED_BUT_UNDEFINED: {
      description: 'A `var(--norkron-XYZ)` reference appears in CSSOM or in an inline `style` attribute, but no stylesheet visible to the audit env defines `--norkron-XYZ`. The widget will paint with the property\'s CSS-level default (usually 0/auto/initial) \u2014 silent visual defect of the "I assumed this token exists" class. Emission-site severity is `major` when at least one reference supplies no fallback, demoted to `info` when every reference has a fallback (lib was defensive, but the wiring is still wrong).',
      severity: "warning",
      docsAnchor: "web-conformance/token-referenced-but-undefined",
      exampleFix: 'Either define the token in @norkron/web-themer/src/base.css under `:root` with an intentional value, or rename the reference to match an existing defined token. Run `grep -n -- "--norkron-" frontend/web/web-themer/src/base.css` to see the catalog.'
    }
  });
} catch {
}

// src/types.ts
function normalizeSeverity(s) {
  if (s === "error") return "critical";
  if (s === "warning") return "minor";
  return s;
}

// src/census.ts
var NON_CONTENT_TAGS = /* @__PURE__ */ new Set([
  "script",
  "style",
  "template",
  "meta",
  "link",
  "title",
  "head",
  "base",
  "noscript"
]);
var CONTENT_TAGS = /* @__PURE__ */ new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "img",
  "a",
  "table",
  "td",
  "th",
  "canvas",
  "svg",
  "video",
  "audio",
  "iframe",
  "object",
  "embed",
  "picture",
  "figure",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "progress",
  "meter",
  "li"
]);
var LANDMARK_TAGS = /* @__PURE__ */ new Set([
  "main",
  "nav",
  "header",
  "footer",
  "aside",
  "section",
  "article",
  "form"
]);
var LANDMARK_ROLES = /* @__PURE__ */ new Set([
  "main",
  "navigation",
  "banner",
  "contentinfo",
  "complementary",
  "region",
  "article",
  "document",
  "form",
  "search"
]);
var INTERACTIVE_TAGS = /* @__PURE__ */ new Set([
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "details"
]);
function ownTextLength(el) {
  let total = 0;
  try {
    const kids = el.childNodes;
    for (let i = 0; i < kids.length; i++) {
      const n = kids.item(i);
      if (n != null && n.nodeType === 3) {
        total += (n.textContent ?? "").replace(/\s+/g, " ").trim().length;
      }
    }
  } catch {
    return 0;
  }
  return total;
}
function isInteractive(el, tag) {
  if (INTERACTIVE_TAGS.has(tag)) return true;
  if (tag === "a" && el.hasAttribute("href")) return true;
  const ti = el.getAttribute("tabindex");
  if (ti != null && Number.parseInt(ti, 10) >= 0) return true;
  const role = el.getAttribute("role");
  return role === "button" || role === "link" || role === "checkbox" || role === "radio" || role === "menuitem" || role === "tab" || role === "switch" || role === "option";
}
function censusSurface(root, mirror) {
  const scopeEl = mirror ?? root;
  const scope = mirror != null ? "mirror" : "root";
  const zero = {
    scope,
    elements: 0,
    headings: 0,
    images: 0,
    links: 0,
    tables: 0,
    dataCells: 0,
    landmarks: 0,
    interactive: 0,
    libStamps: 0,
    textBearingElements: 0,
    textLength: 0,
    auditableNodes: 0
  };
  let all;
  try {
    const list = scopeEl.querySelectorAll("*");
    all = [scopeEl, ...Array.from(list)];
  } catch {
    return zero;
  }
  const c = { ...zero };
  let textLength = 0;
  for (const el of all) {
    let tag;
    try {
      tag = el.tagName.toLowerCase();
    } catch {
      continue;
    }
    if (NON_CONTENT_TAGS.has(tag)) continue;
    c.elements += 1;
    const h = tag.length === 2 && tag[0] === "h" ? tag[1] ?? "" : "";
    if (h >= "1" && h <= "6" && h.length === 1) c.headings += 1;
    if (tag === "img") c.images += 1;
    if (tag === "a") c.links += 1;
    if (tag === "table") c.tables += 1;
    if (tag === "td") c.dataCells += 1;
    const role = el.getAttribute("role");
    if (LANDMARK_TAGS.has(tag) || role != null && LANDMARK_ROLES.has(role)) c.landmarks += 1;
    if (isInteractive(el, tag)) c.interactive += 1;
    if (el.hasAttribute("data-norkron-lib")) c.libStamps += 1;
    const ownLen = ownTextLength(el);
    const own = ownLen > 0;
    if (own) c.textBearingElements += 1;
    textLength += ownLen;
    const isScopeRoot = el === scopeEl;
    if (own || !isScopeRoot && (CONTENT_TAGS.has(tag) || LANDMARK_TAGS.has(tag) || role != null && role.length > 0 || el.hasAttribute("data-norkron-lib") || el.hasAttribute("aria-label"))) {
      c.auditableNodes += 1;
    }
  }
  c.textLength = textLength;
  return c;
}
function censusRefusal(census, thresholds = {}) {
  const minNodes = thresholds.minAuditableNodes ?? 1;
  const minText = thresholds.minTextLength ?? 0;
  if (census.auditableNodes < minNodes) {
    return `the audited surface carries ${census.auditableNodes} auditable node(s) (floor ${minNodes}); a finding-free report over a surface with nothing in it is not a pass \u2014 it is an audit that never examined anything. Census: ${summariseCensus(census)}.`;
  }
  if (minText > 0 && census.textLength < minText) {
    return `the audited surface carries ${census.textLength} characters of text (floor ${minText}); the surface did not render its document content. Census: ${summariseCensus(census)}.`;
  }
  return null;
}
function summariseCensus(c) {
  return `scope=${c.scope} elements=${c.elements} headings=${c.headings} images=${c.images} links=${c.links} tables=${c.tables} dataCells=${c.dataCells} landmarks=${c.landmarks} interactive=${c.interactive} libStamps=${c.libStamps} text=${c.textLength}`;
}
var SURFACE_REFUSAL_KINDS = ["EMPTY_SURFACE", "RULE_NOT_RUN"];

// src/register-lazy.ts
var PENDING_KEY = "__norkron_conformance_pending__";
function registerLazy(libId, thunk) {
  const g = globalThis;
  let p = g[PENDING_KEY];
  if (!p) {
    p = [];
    g[PENDING_KEY] = p;
  }
  p.push({ libId, thunk });
}

// src/registry.ts
var GLOBAL_KEY = "__norkron_conformance_registry__";
var PENDING_KEY2 = "__norkron_conformance_pending__";
function getRegistry() {
  const g = globalThis;
  let r = g[GLOBAL_KEY];
  if (!r) {
    r = /* @__PURE__ */ new Map();
    g[GLOBAL_KEY] = r;
  }
  return r;
}
function getPending() {
  const g = globalThis;
  let p = g[PENDING_KEY2];
  if (!p) {
    p = [];
    g[PENDING_KEY2] = p;
  }
  return p;
}
function flushPendingRegistrations() {
  const pending = getPending();
  if (pending.length === 0) return;
  const registry = getRegistry();
  const batch = pending.splice(0, pending.length);
  for (const { libId, thunk } of batch) {
    try {
      const fn = thunk();
      if (typeof fn === "function") registry.set(libId, fn);
    } catch {
    }
  }
}
function register(libId, fn) {
  getRegistry().set(libId, fn);
}
function listRegistered() {
  flushPendingRegistrations();
  return getRegistry();
}
function __resetRegistry() {
  const g = globalThis;
  g[GLOBAL_KEY] = /* @__PURE__ */ new Map();
  g[PENDING_KEY2] = [];
}

// src/structural/duplicate-text.ts
var ICON_CHAR_RE = /^[←-⇿⌀-⏿─-◿☀-➿\u{1F300}-\u{1FAFF}]$/u;
function ancestorSignature(el) {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role") ?? "";
  const lib = el.getAttribute("data-norkron-lib") ?? "";
  const cls = el.getAttribute("class") ?? "";
  const firstToken = cls.split(/\s+/).find((t) => t.length > 0) ?? "";
  const blockPrefix2 = firstToken.split(/__|--/)[0] ?? "";
  return `${tag}|${role}|${lib}|${blockPrefix2}`;
}
function hasAllowDuplicateOptOut(el, stop) {
  let cur = el;
  while (cur && cur !== stop) {
    if (cur.getAttribute && cur.getAttribute("data-norkron-allow-duplicate") !== null) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function ancestorChain(parent, scope) {
  const chain = [];
  let cur = parent;
  while (cur) {
    chain.push(cur);
    if (cur === scope) break;
    cur = cur.parentElement;
  }
  return chain;
}
function allUnderParallelCards(parents, scope) {
  if (parents.length < 2) return false;
  const chains = parents.map((p) => ancestorChain(p, scope).reverse());
  const minLen = Math.min(...chains.map((c) => c.length));
  if (minLen < 3) return false;
  for (let depth = 1; depth <= minLen - 2; depth += 1) {
    const ancestors = chains.map((c) => c[depth]);
    const uniq = new Set(ancestors);
    if (uniq.size !== ancestors.length) continue;
    const parentEl = ancestors[0]?.parentElement ?? null;
    if (!parentEl) continue;
    if (!ancestors.every((a) => a.parentElement === parentEl)) continue;
    const sig0 = ancestorSignature(ancestors[0]);
    if (sig0 === "|||") continue;
    if (!ancestors.every((a) => ancestorSignature(a) === sig0)) continue;
    return true;
  }
  return false;
}
function checkDuplicateVisibleText(root) {
  const findings = [];
  const mainEls = root.querySelectorAll("main");
  const scopes = mainEls.length > 0 ? Array.from(mainEls) : [root];
  for (const scope of scopes) {
    const occurrences = /* @__PURE__ */ new Map();
    const walker = (scope.ownerDocument ?? scope).createTreeWalker(
      scope,
      4
      // NodeFilter.SHOW_TEXT
    );
    const isUnderIgnore = (el) => {
      let cur = el;
      while (cur && cur !== scope.parentElement) {
        if (cur.getAttribute && cur.getAttribute("data-conformance-ignore") !== null) return true;
        cur = cur.parentElement;
      }
      return false;
    };
    let node = walker.nextNode();
    while (node) {
      const raw = (node.nodeValue ?? "").trim();
      if (raw.length >= 3 && raw.split(/\s+/).length >= 2 && !ICON_CHAR_RE.test(raw)) {
        const parent = node.parentElement;
        if (parent && !isUnderIgnore(parent) && !hasAllowDuplicateOptOut(parent, null)) {
          const list = occurrences.get(raw) ?? [];
          list.push(parent);
          occurrences.set(raw, list);
        }
      }
      node = walker.nextNode();
    }
    for (const [text, parents] of occurrences) {
      if (parents.length < 3) continue;
      if (allUnderParallelCards(parents, scope)) continue;
      findings.push({
        libId: "@norkron/web-conformance",
        code: "STR_DUPLICATE_VISIBLE_TEXT",
        category: "layout",
        severity: "major",
        message: `Text "${text.slice(0, 60)}" appears ${parents.length} times in <main>`,
        selector: "main",
        suggestedFix: "Likely a progress indicator + step rail + breadcrumb echoing the same label. Consolidate. If this is an intentional parallel-comparison grid, wrap each instance in a sibling card container with the same role/class/data-norkron-lib signature, or set `data-norkron-allow-duplicate` on the surrounding scope.",
        extras: { text, count: parents.length }
      });
    }
  }
  return findings;
}

// src/structural/_document-surface.ts
var DOC_SURFACE_LIB_ID = "@norkron/web-conformance";
var DOCUMENT_SURFACE_ATTR = "data-norkron-document-surface";
function selectorFor(el) {
  const tag = el.tagName.toLowerCase();
  const id = el.getAttribute("id");
  if (id != null && id.length > 0) return `${tag}#${id}`;
  const ord = el.getAttribute("data-block-ord");
  if (ord != null) return `${tag}[data-block-ord="${ord}"]`;
  const cls = (el.getAttribute("class") ?? "").split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls.length > 0 ? `${tag}.${cls}` : tag;
}
function textPreview(el) {
  const t = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  return t.length <= 80 ? t : `${t.slice(0, 77)}\u2026`;
}
function normText(el) {
  return (el.textContent ?? "").replace(/\s+/g, " ").trim();
}
function isAriaHidden(el) {
  let cur = el;
  while (cur != null) {
    if (cur.getAttribute("aria-hidden") === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function isHiddenFromAt(el) {
  let cur = el;
  while (cur != null) {
    if (cur.getAttribute("aria-hidden") === "true") return true;
    if (cur.hasAttribute("hidden")) return true;
    cur = cur.parentElement;
  }
  return false;
}
function isDecorative(el) {
  const role = el.getAttribute("role");
  return role === "presentation" || role === "none";
}
function isRemovedFromAtTree(el) {
  if (el.hasAttribute("hidden")) return "hidden attribute";
  const inline = (el.getAttribute("style") ?? "").replace(/\s+/g, "").toLowerCase();
  if (inline.includes("display:none")) return "display:none";
  if (inline.includes("visibility:hidden")) return "visibility:hidden";
  const view = el.ownerDocument?.defaultView;
  if (view?.getComputedStyle != null) {
    try {
      const cs = view.getComputedStyle(el);
      if (cs.display === "none") return "display:none";
      if (cs.visibility === "hidden") return "visibility:hidden";
    } catch {
    }
  }
  return null;
}
function isTabStop(el) {
  const ti = el.getAttribute("tabindex");
  if (ti != null) return Number.parseInt(ti, 10) >= 0;
  const tag = el.tagName.toLowerCase();
  if (tag === "a" || tag === "area") return el.hasAttribute("href");
  return ["button", "input", "select", "textarea", "summary", "iframe"].includes(tag);
}
function docFinding(f) {
  return {
    libId: DOC_SURFACE_LIB_ID,
    code: f.code,
    category: "a11y",
    severity: f.severity,
    wcag: f.wcag,
    message: f.message,
    selector: f.selector,
    suggestedFix: f.suggestedFix,
    extras: f.extras
  };
}
function declaredDocumentSurface(root, opts) {
  const explicit = opts?.documentSurface;
  if (explicit != null) return explicit;
  if (typeof root.matches === "function" && root.matches(`[${DOCUMENT_SURFACE_ATTR}]`)) {
    return { mirror: root };
  }
  const marked = root.querySelector?.(`[${DOCUMENT_SURFACE_ATTR}]`) ?? null;
  if (marked != null) return { mirror: marked };
  return null;
}
function documentSurfaceGated(rule) {
  return (root, opts) => {
    const surface = declaredDocumentSurface(root, opts);
    if (surface == null) return [];
    return rule(root, surface);
  };
}

// src/structural/heading-hierarchy.ts
var HEADING_SELECTOR = "h1, h2, h3, h4, h5, h6";
function levelOf(h) {
  return Number.parseInt(h.tagName.slice(1), 10);
}
function checkHeadingHierarchy(root) {
  const findings = [];
  const headings = Array.from(
    root.querySelectorAll(HEADING_SELECTOR)
  );
  let previousLevel = 0;
  for (const h of headings) {
    const level = levelOf(h);
    if (previousLevel > 0 && level > previousLevel + 1) {
      findings.push({
        libId: "@norkron/web-conformance",
        code: "STR_HEADING_HIERARCHY_BROKEN",
        category: "a11y",
        severity: "major",
        wcag: "1.3.1",
        message: `Heading level skipped from h${previousLevel} to h${level} at "${textPreview(h)}"; the intermediate level a user navigates through does not exist.`,
        selector: selectorFor(h),
        suggestedFix: `Insert an h${previousLevel + 1} before this h${level} or demote it.`,
        extras: { from: previousLevel, to: level, text: textPreview(h) }
      });
    }
    previousLevel = level;
  }
  return findings;
}
function checkHeadingOutline(root, _opts) {
  const findings = [];
  const headings = Array.from(root.querySelectorAll(HEADING_SELECTOR));
  const h1s = headings.filter((h) => levelOf(h) === 1);
  if (h1s.length === 0) {
    findings.push(
      docFinding({
        code: "STR_H1_MISSING",
        wcag: "1.3.1",
        severity: "major",
        message: "The document surface exposes no h1, so a screen-reader user has no top-level entry point and cannot tell where the document begins.",
        suggestedFix: "Project the document title as an <h1> at the top of the accessible surface (a landmark aria-label names the region; it does NOT substitute for a heading a user can jump to).",
        extras: { headingCount: headings.length }
      })
    );
  } else if (h1s.length > 1) {
    for (const extra of h1s.slice(1)) {
      findings.push(
        docFinding({
          code: "STR_H1_MULTIPLE",
          wcag: "1.3.1",
          severity: "major",
          message: `The document surface exposes ${h1s.length} h1 elements; "${textPreview(extra)}" is a second document root, so browse-mode reports two documents where there is one.`,
          selector: selectorFor(extra),
          suggestedFix: "Demote every h1 after the first to h2 (or deeper) so the outline has exactly one root.",
          extras: { h1Count: h1s.length, text: textPreview(extra) }
        })
      );
    }
  }
  const first = headings[0];
  if (first != null) {
    const level = levelOf(first);
    if (level > 1) {
      findings.push(
        docFinding({
          code: "STR_HEADING_OUTLINE_STARTS_BELOW_H1",
          wcag: "1.3.1",
          severity: "minor",
          message: `The first heading in the document is an h${level} ("${textPreview(first)}"), so the outline opens mid-tree.`,
          selector: selectorFor(first),
          suggestedFix: `Emit the document title as an h1 before this h${level}.`,
          extras: { firstLevel: level }
        })
      );
    }
  }
  return findings;
}

// src/structural/focusable-inside-heading.ts
var FOCUSABLE_SELECTOR = 'button, a, [role="button"]';
function checkFocusableInsideHeading(root) {
  const findings = [];
  const headings = root.querySelectorAll("h1, h2, h3, h4, h5, h6");
  headings.forEach((h) => {
    h.querySelectorAll(FOCUSABLE_SELECTOR).forEach((focusable) => {
      findings.push({
        libId: "@norkron/web-conformance",
        code: "STR_FOCUSABLE_INSIDE_HEADING",
        category: "a11y",
        severity: "major",
        message: `Focusable element ${focusable.tagName.toLowerCase()} inside ${h.tagName.toLowerCase()} pollutes the heading's accessible name`,
        selector: `${h.tagName.toLowerCase()} ${focusable.tagName.toLowerCase()}`,
        suggestedFix: "Move the button/link to a sibling of the heading, or use aria-labelledby to scope the accname."
      });
    });
  });
  return findings;
}

// src/structural/viewport-overflow.ts
var ALLOW_ATTR = "data-allow-overflow";
function hasAllowOptOut(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR) !== null) return true;
    cur = cur.parentElement;
  }
  return false;
}
function isUnderScrollableAncestor(el, root, win) {
  let cur = el.parentElement;
  while (cur && cur !== root.parentElement) {
    try {
      const cs = win.getComputedStyle(cur);
      const ox = cs.overflowX;
      const oy = cs.overflowY;
      if (ox === "auto" || ox === "scroll" || oy === "auto" || oy === "scroll") {
        return true;
      }
    } catch {
    }
    cur = cur.parentElement;
  }
  return false;
}
function isOffscreenAssistive(rect) {
  if (rect.right < -1e3) return true;
  if (rect.bottom < -1e3) return true;
  return false;
}
function isVisuallyHidden(el, root, win) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    try {
      const cs = win.getComputedStyle(cur);
      if (cs.visibility === "hidden" || cs.visibility === "collapse") return true;
      if (cs.display === "none") return true;
    } catch {
    }
    cur = cur.parentElement;
  }
  return false;
}
function checkViewportOverflow(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const body = doc.body;
  if (!body) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win) return findings;
  const bodyWidth = body.clientWidth;
  if (bodyWidth <= 0) return findings;
  const viewportW = win.innerWidth || bodyWidth;
  const viewportH = win.innerHeight || body.clientHeight;
  const all = root.querySelectorAll("*");
  const reported = /* @__PURE__ */ new Set();
  all.forEach((el) => {
    if (reported.has(el)) return;
    if (el === body || el === doc.documentElement) return;
    const w = el.clientWidth ?? 0;
    const widerThanBody = w > bodyWidth + 1;
    let rect = null;
    try {
      rect = el.getBoundingClientRect();
    } catch {
    }
    if (!rect) {
      if (widerThanBody) {
        reported.add(el);
        findings.push({
          libId: "@norkron/web-conformance",
          code: "STR_VIEWPORT_OVERFLOW",
          category: "layout",
          severity: "major",
          message: `<${el.tagName.toLowerCase()}> is ${w}px wide vs body ${bodyWidth}px`,
          selector: el.tagName.toLowerCase(),
          suggestedFix: "Add max-width: 100% or remove the fixed width; check parent flex/grid container.",
          extras: { width: w, bodyWidth, mode: "width" }
        });
      }
      return;
    }
    if (rect.width <= 0 || rect.height <= 0) return;
    if (isOffscreenAssistive(rect)) return;
    if (isVisuallyHidden(el, root, win)) return;
    const overflowsRight = rect.right > viewportW + 1;
    const overflowsLeft = rect.left < -1;
    if (!widerThanBody && !overflowsRight && !overflowsLeft) return;
    if (hasAllowOptOut(el, root)) return;
    if (widerThanBody && !overflowsRight && !overflowsLeft) {
      if (isUnderScrollableAncestor(el, root, win)) return;
    }
    reported.add(el);
    const reasons = [];
    if (widerThanBody) reasons.push(`width ${w}px > body ${bodyWidth}px`);
    if (overflowsRight) reasons.push(`right=${Math.round(rect.right)}px > viewport ${viewportW}px`);
    if (overflowsLeft) reasons.push(`left=${Math.round(rect.left)}px < 0`);
    const cls = (el.getAttribute("class") ?? "").slice(0, 60);
    const sel = el.tagName.toLowerCase() + (cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "");
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_VIEWPORT_OVERFLOW",
      category: "layout",
      severity: "major",
      message: `<${sel}> overflows the viewport: ${reasons.join(", ")}`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Cap width with max-width:100% / min-width:0 (flex children need both), wrap the parent in a horizontal scroll container, OR opt-out via data-allow-overflow="true" if the overflow is intentional (e.g., a marquee).',
      extras: {
        width: w,
        bodyWidth,
        viewportWidth: viewportW,
        viewportHeight: viewportH,
        rect: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
        mode: widerThanBody ? "width-and-position" : "position"
      }
    });
  });
  return findings;
}

// src/structural/progressbar-redundancy.ts
var LIB_ID = "@norkron/web-conformance";
function getRectSafely(el) {
  if (typeof el.getBoundingClientRect !== "function") return null;
  try {
    const r = el.getBoundingClientRect();
    if (r === null || r === void 0) return null;
    return { top: r.top, bottom: r.bottom };
  } catch {
    return null;
  }
}
function checkProgressbarRedundancy(root) {
  const findings = [];
  const bars = Array.from(
    root.querySelectorAll('[role="progressbar"], progress')
  );
  if (bars.length < 3) return findings;
  const infos = [];
  let allZero = true;
  for (const el of bars) {
    const r = getRectSafely(el);
    if (r === null) {
      infos.push({ el, top: 0, bottom: 0 });
    } else {
      infos.push({ el, top: r.top, bottom: r.bottom });
      if (r.top !== 0 || r.bottom !== 0) allZero = false;
    }
  }
  if (allZero) {
    const reportedAncestors = /* @__PURE__ */ new Set();
    const ancestor = findCommonAncestor(bars);
    if (ancestor && !reportedAncestors.has(ancestor)) {
      reportedAncestors.add(ancestor);
      findings.push(emitFinding(ancestor, bars.length, 0));
    }
    return findings;
  }
  const sorted = [...infos].sort((a, b) => a.top - b.top);
  let clusterStart = 0;
  for (let i = 0; i < sorted.length; i++) {
    const first = sorted[clusterStart];
    if (!first) continue;
    const cur = sorted[i];
    if (!cur) continue;
    if (cur.top - first.top > 800) {
      const clusterMembers = sorted.slice(clusterStart, i).map((b) => b.el);
      if (clusterMembers.length >= 3) {
        const anc = findCommonAncestor(clusterMembers);
        if (anc) {
          const span = (sorted[i - 1]?.bottom ?? 0) - (first.top ?? 0);
          findings.push(emitFinding(anc, clusterMembers.length, span));
        }
      }
      clusterStart = i;
    }
  }
  const tail = sorted.slice(clusterStart).map((b) => b.el);
  if (tail.length >= 3) {
    const anc = findCommonAncestor(tail);
    if (anc) {
      const first = sorted[clusterStart];
      const last = sorted[sorted.length - 1];
      const span = (last?.bottom ?? 0) - (first?.top ?? 0);
      findings.push(emitFinding(anc, tail.length, span));
    }
  }
  return findings;
}
function findCommonAncestor(els) {
  if (els.length === 0) return null;
  const first = els[0];
  if (!first) return null;
  let anc = first.parentElement;
  while (anc) {
    let containsAll = true;
    for (let i = 1; i < els.length; i++) {
      const e = els[i];
      if (e && !anc.contains(e)) {
        containsAll = false;
        break;
      }
    }
    if (containsAll) return anc;
    anc = anc.parentElement;
  }
  return null;
}
function emitFinding(container, count, span) {
  return {
    libId: LIB_ID,
    code: "STR_PROGRESSBAR_REDUNDANCY",
    category: "layout",
    severity: "major",
    message: `Detected ${count} progressbar elements (role=progressbar or <progress>) within an 800px viewport span \u2014 multiple competing progress indicators.`,
    selector: container.tagName.toLowerCase(),
    suggestedFix: "Render exactly one progress indicator per logical flow; collapse duplicates between wizard rail, header progress, and inline progress UI.",
    extras: { progressbars: count, viewportSpanPx: span }
  };
}

// src/structural/_visually-hidden.ts
function isVisuallyHiddenStyle(style) {
  const pos = style.position;
  const positioned = pos === "absolute" || pos === "fixed";
  if (!positioned) return false;
  const w = Number.parseFloat(style.width ?? "") || 0;
  const h = Number.parseFloat(style.height ?? "") || 0;
  const tiny = w <= 1 && h <= 1;
  const clip = (style.clip ?? "").replace(/\s+/g, "").replace(/0px/g, "0");
  const clipsToZero = clip === "rect(0,0,0,0)" || clip === "rect(0,0,0,0,)";
  const clipPath = (style.clipPath ?? "").replace(/\s+/g, "");
  const clipPathInset = clipPath === "inset(50%)" || clipPath.startsWith("inset(50%");
  const overflowHidden = style.overflow === "hidden" || style.overflowX === "hidden" || style.overflowY === "hidden";
  return tiny && overflowHidden && (clipsToZero || clipPathInset);
}
function isVisuallyHiddenElement(el) {
  const view = el.ownerDocument?.defaultView;
  if (view?.getComputedStyle != null) {
    try {
      if (isVisuallyHiddenStyle(view.getComputedStyle(el))) return true;
    } catch {
    }
  }
  const inline = (el.getAttribute("style") ?? "").replace(/\s+/g, "").toLowerCase();
  if (inline.length === 0) return false;
  const positioned = inline.includes("position:absolute") || inline.includes("position:fixed");
  const tiny = inline.includes("width:1px") && inline.includes("height:1px");
  const clipped = inline.includes("clip-path:inset(50%") || inline.includes("clip:rect(0");
  return positioned && tiny && clipped;
}

// src/structural/text-clipped.ts
var LIB_ID2 = "@norkron/web-conformance";
var OVERFLOW_THRESHOLD_PX_DOM = 4;
var OVERFLOW_THRESHOLD_PX_SVG = 1;
var ELLIPSIS_OVERFLOW_THRESHOLD_PX = 1;
function isInSvg(el) {
  let cur = el;
  while (cur) {
    const tag = cur.tagName?.toLowerCase?.() ?? "";
    if (tag === "svg") return true;
    cur = cur.parentElement;
  }
  return false;
}
var isVisuallyHidden2 = isVisuallyHiddenStyle;
function checkTextClipped(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  if (typeof root.getBoundingClientRect !== "function") {
    findings.push(needsLayoutFinding("STR_TEXT_CLIPPED_BY_CONTAINER"));
    return findings;
  }
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(needsLayoutFinding("STR_TEXT_CLIPPED_BY_CONTAINER"));
    return findings;
  }
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  let node = walker.nextNode();
  const seenParents = /* @__PURE__ */ new Set();
  while (node) {
    const text = (node.nodeValue ?? "").trim();
    if (text.length === 0) {
      node = walker.nextNode();
      continue;
    }
    const parent = node.parentElement;
    if (!parent || seenParents.has(parent)) {
      node = walker.nextNode();
      continue;
    }
    seenParents.add(parent);
    const ptag = parent.tagName?.toLowerCase?.() ?? "";
    if (ptag === "textarea" || ptag === "input" || ptag === "select") {
      node = walker.nextNode();
      continue;
    }
    const style = win.getComputedStyle(parent);
    if (isVisuallyHidden2(style)) {
      node = walker.nextNode();
      continue;
    }
    const overflowX = style.overflowX;
    const overflowY = style.overflowY;
    const clipsX = overflowX === "hidden" || overflowX === "clip" || overflowX === "auto";
    const clipsY = overflowY === "hidden" || overflowY === "clip" || overflowY === "auto";
    if (!clipsX && !clipsY) {
      node = walker.nextNode();
      continue;
    }
    if (style.textOverflow === "ellipsis" && style.whiteSpace === "nowrap") {
      const sw = parent.scrollWidth ?? 0;
      const cw = parent.clientWidth ?? 0;
      if (sw > cw + ELLIPSIS_OVERFLOW_THRESHOLD_PX && !hasAccessibleFullText(parent)) {
        findings.push({
          libId: LIB_ID2,
          code: "STR_TEXT_ELLIPSIS_TRUNCATED",
          category: "layout",
          severity: "info",
          message: `Text "${text.slice(0, 40)}" is being ellipsis-truncated (scrollWidth=${sw}, clientWidth=${cw}). Verify the truncation is intentional.`,
          selector: parent.tagName.toLowerCase(),
          suggestedFix: "If the full text is important (CTA / form field / data cell), increase parent width or remove the ellipsis clamp. If a tooltip already exposes the full text, ignore this info-level finding.",
          extras: {
            textPreview: text.slice(0, 80),
            scrollWidth: sw,
            clientWidth: cw,
            overflow: sw - cw
          }
        });
      }
      node = walker.nextNode();
      continue;
    }
    let textRect = null;
    try {
      const range = doc.createRange();
      range.selectNodeContents(node);
      if (typeof range.getBoundingClientRect === "function") {
        textRect = range.getBoundingClientRect();
      }
    } catch {
      textRect = null;
    }
    if (!textRect) {
      node = walker.nextNode();
      continue;
    }
    const parentRect = parent.getBoundingClientRect();
    const padRight = parseFloat(style.paddingRight ?? "0") || 0;
    const padBottom = parseFloat(style.paddingBottom ?? "0") || 0;
    const borderRight = parseFloat(style.borderRightWidth ?? "0") || 0;
    const borderBottom = parseFloat(style.borderBottomWidth ?? "0") || 0;
    const contentRight = parentRect.right - borderRight - padRight;
    const contentBottom = parentRect.bottom - borderBottom - padBottom;
    const overflowRight = textRect.right - contentRight;
    const overflowBottom = textRect.bottom - contentBottom;
    const threshold = isInSvg(parent) ? OVERFLOW_THRESHOLD_PX_SVG : OVERFLOW_THRESHOLD_PX_DOM;
    if (clipsX && overflowRight >= threshold || clipsY && overflowBottom >= threshold) {
      findings.push({
        libId: LIB_ID2,
        code: "STR_TEXT_CLIPPED_BY_CONTAINER",
        category: "layout",
        severity: "major",
        message: `Text "${text.slice(0, 40)}" overflows clipping parent by right=${Math.round(overflowRight)}px / bottom=${Math.round(overflowBottom)}px.`,
        selector: parent.tagName.toLowerCase(),
        suggestedFix: "Shrink font-size, rotate the label, increase parent padding/width, or apply text-overflow: ellipsis + white-space: nowrap if a clamp is intentional.",
        extras: {
          textPreview: text.slice(0, 80),
          overflowRight,
          overflowBottom,
          overflowX,
          overflowY,
          threshold,
          context: isInSvg(parent) ? "svg" : "dom"
        }
      });
    }
    node = walker.nextNode();
  }
  return findings;
}
function hasAccessibleFullText(el) {
  let cur = el;
  while (cur) {
    const title = cur.getAttribute?.("title");
    if (title != null && title.trim().length > 0) return true;
    const aria = cur.getAttribute?.("aria-label");
    if (aria != null && aria.trim().length > 0) return true;
    cur = cur.parentElement;
  }
  return false;
}
function needsLayoutFinding(forCode) {
  return {
    libId: LIB_ID2,
    code: "STR_NEEDS_LAYOUT",
    category: "perf",
    severity: "info",
    message: `Skipped ${forCode}: runtime environment lacks getBoundingClientRect / getComputedStyle (headless DOM). Re-run audit in a browser session for layout-dependent checks.`,
    extras: { forCode }
  };
}

// src/structural/contrast.ts
var LIB_ID3 = "@norkron/web-conformance";
var __colorCanvasCtx;
function tryGetColorCanvas() {
  if (__colorCanvasCtx !== void 0) return __colorCanvasCtx;
  try {
    if (typeof document === "undefined" || typeof document.createElement !== "function") {
      __colorCanvasCtx = null;
      return null;
    }
    const c = document.createElement("canvas");
    const anyC = c;
    if (typeof anyC.getContext !== "function") {
      __colorCanvasCtx = null;
      return null;
    }
    c.width = 1;
    c.height = 1;
    const ctx = anyC.getContext("2d", { willReadFrequently: true });
    __colorCanvasCtx = ctx ?? null;
    return __colorCanvasCtx;
  } catch {
    __colorCanvasCtx = null;
    return null;
  }
}
function srgbToLinear(channel) {
  const v = channel / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}
function srgbLuminance(r, g, b) {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}
function contrastRatio(fg, bg) {
  const l1 = srgbLuminance(fg[0], fg[1], fg[2]);
  const l2 = srgbLuminance(bg[0], bg[1], bg[2]);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}
function parseMixStop(raw) {
  const t = raw.trim().toLowerCase();
  if (t === "currentcolor") return [0, 0, 0, 1];
  return parseCssColor(t);
}
function parseColorMixSrgb(s) {
  const m = s.match(/^color-mix\(\s*in\s+srgb\s*,\s*(.+)\)$/);
  if (!m) return null;
  const inner = m[1];
  const parts = [];
  let depth = 0;
  let cur = "";
  for (const ch of inner) {
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
    if (ch === "," && depth === 0) {
      parts.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  parts.push(cur);
  if (parts.length !== 2) return null;
  const parseStop = (part) => {
    const tokens = part.trim().split(/\s+/);
    let pct = null;
    const colorTokens = [];
    for (const tk of tokens) {
      if (/^[\d.]+%$/.test(tk)) pct = parseFloat(tk);
      else colorTokens.push(tk);
    }
    const color = parseMixStop(colorTokens.join(" "));
    if (!color) return null;
    return { color, pct };
  };
  const a = parseStop(parts[0]);
  const b = parseStop(parts[1]);
  if (!a || !b) return null;
  let pa = a.pct;
  let pb = b.pct;
  if (pa === null && pb === null) {
    pa = 50;
    pb = 50;
  } else if (pa === null) {
    pa = 100 - pb;
  } else if (pb === null) {
    pb = 100 - pa;
  }
  const sum = pa + pb;
  if (sum <= 0) return null;
  const wa = pa / sum;
  const wb = pb / sum;
  const r = a.color[0] * wa + b.color[0] * wb;
  const g = a.color[1] * wa + b.color[1] * wb;
  const bl = a.color[2] * wa + b.color[2] * wb;
  const al = a.color[3] * wa + b.color[3] * wb;
  return [Math.round(r), Math.round(g), Math.round(bl), al];
}
function parseCssColor(input) {
  if (!input) return null;
  const s = input.trim().toLowerCase();
  if (s === "transparent") return [0, 0, 0, 0];
  if (s.startsWith("#")) {
    const hex = s.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      const a = hex.length === 4 ? parseInt(hex[3] + hex[3], 16) / 255 : 1;
      if ([r, g, b].some(Number.isNaN)) return null;
      return [r, g, b, a];
    }
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
      if ([r, g, b].some(Number.isNaN)) return null;
      return [r, g, b, a];
    }
    return null;
  }
  const m = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+))?\s*\)$/);
  if (m) {
    const r = parseFloat(m[1]);
    const g = parseFloat(m[2]);
    const b = parseFloat(m[3]);
    const a = m[4] !== void 0 ? parseFloat(m[4]) : 1;
    return [r, g, b, a];
  }
  const colorFn = s.match(
    /^color\(\s*(srgb|srgb-linear)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*(?:\/\s*([\d.]+%?))?\s*\)$/
  );
  if (colorFn) {
    const space = colorFn[1];
    let rf = parseFloat(colorFn[2]);
    let gf = parseFloat(colorFn[3]);
    let bf = parseFloat(colorFn[4]);
    if (space === "srgb-linear") {
      const linToSrgb = (u) => u <= 31308e-7 ? 12.92 * u : 1.055 * Math.pow(u, 1 / 2.4) - 0.055;
      rf = linToSrgb(rf);
      gf = linToSrgb(gf);
      bf = linToSrgb(bf);
    }
    const r = Math.round(rf * 255);
    const g = Math.round(gf * 255);
    const b = Math.round(bf * 255);
    let a = 1;
    if (colorFn[5] !== void 0) {
      const raw = colorFn[5];
      a = raw.endsWith("%") ? parseFloat(raw) / 100 : parseFloat(raw);
    }
    if ([r, g, b].some(Number.isNaN)) return null;
    return [r, g, b, a];
  }
  const named = {
    black: [0, 0, 0, 1],
    white: [255, 255, 255, 1],
    red: [255, 0, 0, 1],
    green: [0, 128, 0, 1],
    blue: [0, 0, 255, 1],
    gray: [128, 128, 128, 1],
    grey: [128, 128, 128, 1]
  };
  if (named[s]) return named[s];
  const mix = parseColorMixSrgb(s);
  if (mix) return mix;
  const ctx = tryGetColorCanvas();
  if (ctx) {
    try {
      ctx.clearRect(0, 0, 1, 1);
      ctx.fillStyle = "rgba(0, 0, 0, 0)";
      const sentinel = String(ctx.fillStyle);
      ctx.fillStyle = s;
      if (String(ctx.fillStyle) === sentinel && s !== sentinel) return null;
      ctx.fillRect(0, 0, 1, 1);
      const d = ctx.getImageData(0, 0, 1, 1).data;
      return [d[0], d[1], d[2], d[3] / 255];
    } catch {
      return null;
    }
  }
  return null;
}
function rgbToHex(rgb) {
  const h = (v) => {
    const c = Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
    return c;
  };
  return `#${h(rgb[0])}${h(rgb[1])}${h(rgb[2])}`;
}
function compositeOver(layer, background) {
  const a = Math.max(0, Math.min(1, layer[3]));
  return [
    a * layer[0] + (1 - a) * background[0],
    a * layer[1] + (1 - a) * background[1],
    a * layer[2] + (1 - a) * background[2]
  ];
}
function resolveBackgroundColor(el, win) {
  const layers = [];
  let cur = el;
  let opaqueBase = null;
  while (cur) {
    const style = win.getComputedStyle(cur);
    const bg = parseCssColor(style.backgroundColor ?? "");
    if (bg && bg[3] > 0) {
      if (bg[3] > 0.999) {
        opaqueBase = [bg[0], bg[1], bg[2]];
        break;
      }
      layers.push(bg);
    }
    cur = cur.parentElement;
  }
  let base = opaqueBase ?? [255, 255, 255];
  for (let i = layers.length - 1; i >= 0; i--) {
    base = compositeOver(layers[i], base);
  }
  return base;
}
function isExcluded(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute("aria-hidden") === "true") return true;
    if (cur.hasAttribute("data-decorative")) return true;
    cur = cur.parentElement;
  }
  return false;
}
function checkContrast(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(needsLayoutFinding2("STR_CONTRAST_INSUFFICIENT"));
    return findings;
  }
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  let node = walker.nextNode();
  const seenParents = /* @__PURE__ */ new Set();
  while (node) {
    const text = (node.nodeValue ?? "").trim();
    if (text.length === 0) {
      node = walker.nextNode();
      continue;
    }
    const parent = node.parentElement;
    if (!parent || seenParents.has(parent)) {
      node = walker.nextNode();
      continue;
    }
    seenParents.add(parent);
    if (isExcluded(parent)) {
      node = walker.nextNode();
      continue;
    }
    const style = win.getComputedStyle(parent);
    const fg = parseCssColor(style.color ?? "");
    if (!fg || fg[3] < 0.01) {
      node = walker.nextNode();
      continue;
    }
    const bg = resolveBackgroundColor(parent, win);
    const fgComposed = fg[3] < 0.999 ? compositeOver([fg[0], fg[1], fg[2], fg[3]], bg) : [fg[0], fg[1], fg[2]];
    const ratio = contrastRatio(fgComposed, bg);
    const fontSize = parseFloat(style.fontSize ?? "16") || 16;
    const fontWeightRaw = style.fontWeight ?? "400";
    const fontWeight = parseFloat(fontWeightRaw) || (/bold/i.test(fontWeightRaw) ? 700 : 400);
    const isLarge = fontSize >= 18 || fontSize >= 14 && fontWeight >= 700;
    const threshold = isLarge ? 3 : 4.5;
    if (ratio + 1e-6 < threshold) {
      findings.push({
        libId: LIB_ID3,
        code: "STR_CONTRAST_INSUFFICIENT",
        category: "a11y",
        severity: isLarge ? "major" : "critical",
        message: `Text "${text.slice(0, 40)}" has contrast ratio ${ratio.toFixed(2)}:1 vs WCAG ${isLarge ? "large/UI" : "body"} minimum ${threshold}:1.`,
        selector: parent.tagName.toLowerCase(),
        suggestedFix: "Darken the foreground, lighten the background, or increase font-size/weight so the text qualifies as large.",
        extras: {
          fg: rgbToHex(fgComposed),
          fgAlpha: fg[3],
          bg: rgbToHex(bg),
          ratio: Math.round(ratio * 100) / 100,
          threshold,
          fontSize,
          fontWeight,
          isLarge,
          textPreview: text.slice(0, 80)
        }
      });
    }
    node = walker.nextNode();
  }
  return findings;
}
function needsLayoutFinding2(forCode) {
  return {
    libId: LIB_ID3,
    code: "STR_NEEDS_LAYOUT",
    category: "perf",
    severity: "info",
    message: `Skipped ${forCode}: runtime environment lacks getComputedStyle (headless DOM). Re-run audit in a browser session for layout-dependent checks.`,
    extras: { forCode }
  };
}

// src/structural/palette-drift.ts
var LIB_ID4 = "@norkron/web-conformance";
var DISTANCE_THRESHOLD = 16;
function checkPaletteDrift(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(needsLayoutFinding3("STR_VISIBLE_TEXT_VS_LIB_PALETTE_DRIFT"));
    return findings;
  }
  const palette = collectActivePalette(doc, win);
  if (palette.length === 0) {
    return findings;
  }
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  let node = walker.nextNode();
  const seenParents = /* @__PURE__ */ new Set();
  while (node) {
    const text = (node.nodeValue ?? "").trim();
    if (text.length === 0) {
      node = walker.nextNode();
      continue;
    }
    const parent = node.parentElement;
    if (!parent || seenParents.has(parent)) {
      node = walker.nextNode();
      continue;
    }
    seenParents.add(parent);
    if (parent.getAttribute("aria-hidden") === "true" || parent.hasAttribute("data-decorative")) {
      node = walker.nextNode();
      continue;
    }
    const style = win.getComputedStyle(parent);
    const fg = parseCssColor(style.color ?? "");
    if (!fg || fg[3] < 0.01) {
      node = walker.nextNode();
      continue;
    }
    const fgRgb = [fg[0], fg[1], fg[2]];
    let nearest = Infinity;
    let nearestToken = "";
    for (const tok of palette) {
      const d = euclidean(fgRgb, tok.rgb);
      if (d < nearest) {
        nearest = d;
        nearestToken = tok.name;
      }
    }
    if (nearest > DISTANCE_THRESHOLD) {
      findings.push({
        libId: LIB_ID4,
        code: "STR_VISIBLE_TEXT_VS_LIB_PALETTE_DRIFT",
        category: "theme",
        severity: "major",
        message: `Text "${text.slice(0, 40)}" rendered in rgb(${fgRgb.join(",")}) which is not in the active web-themer palette (nearest token "${nearestToken}" is \u0394${Math.round(nearest)} away).`,
        selector: parent.tagName.toLowerCase(),
        suggestedFix: "Use a web-themer palette token via getCssVar(...) or a CSS variable; do not hard-code colors in lib internals.",
        extras: {
          actualRgb: fgRgb,
          nearestToken,
          nearestDistance: Math.round(nearest),
          threshold: DISTANCE_THRESHOLD
        }
      });
    }
    node = walker.nextNode();
  }
  return findings;
}
function collectActivePalette(doc, win) {
  const out = [];
  const root = doc.documentElement;
  if (!root) return out;
  const cs = win.getComputedStyle(root);
  const len = cs.length ?? 0;
  for (let i = 0; i < len; i++) {
    const name = cs.item(i);
    if (!name || !name.startsWith("--")) continue;
    if (!/(color|bg|fg|text|surface|border|series|brand|accent|tone|palette)/i.test(name)) {
      continue;
    }
    const value = cs.getPropertyValue(name).trim().replaceAll(/"/g, "");
    const parsed = parseCssColor(value);
    if (!parsed || parsed[3] < 0.01) continue;
    out.push({ name, rgb: [parsed[0], parsed[1], parsed[2]] });
  }
  return out;
}
function euclidean(a, b) {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}
function needsLayoutFinding3(forCode) {
  return {
    libId: LIB_ID4,
    code: "STR_NEEDS_LAYOUT",
    category: "perf",
    severity: "info",
    message: `Skipped ${forCode}: runtime environment lacks getComputedStyle (headless DOM). Re-run audit in a browser session for layout-dependent checks.`,
    extras: { forCode }
  };
}

// src/structural/primitive-signatures.ts
var KNOWN_PRIMITIVE_SIGNATURES = [
  {
    primitive: "progress",
    // `@norkron/web-onboarding` ships `TourProgress` (stepped tour-progress
    // rail with dot markers + numeric label) as a canonical primitive —
    // added to canonicalOwners so the duplicate-detection rules recognise
    // stamped instances as lib-emitted rather than hand-rolled.
    canonicalOwners: [
      "@norkron/web-wizard",
      "@norkron/web-states",
      "@norkron/web-onboarding"
    ],
    selectors: [
      '[role="progressbar"]',
      "progress",
      "[aria-valuenow][aria-valuemax]"
    ],
    // Patterns use lookarounds emulating "word boundary OR underscore" so
    // BEM-style classNames like `survey-runner__progress` match while
    // substrings like `process` / `egress` / `aggressor` do not. The
    // `(?:^|[^a-z0-9])` prefix + `(?:$|[^a-z0-9])` suffix treat `_` and
    // `-` as boundaries, which the default `\b` does not (since regex
    // `\w` includes underscore).
    classNamePatterns: [
      /(?:^|[^a-z0-9])progress(?:bar)?(?:$|[^a-z0-9])/i,
      /(?:^|[^a-z0-9])stepper(?:$|[^a-z0-9])/i,
      /(?:^|[^a-z0-9])(?:step-)?rail(?:$|[^a-z0-9])/i,
      /(?:^|[^a-z0-9])step-?bar(?:$|[^a-z0-9])/i
    ],
    ariaRoles: ["progressbar"]
  },
  {
    // Grounded in web-segmented-control `src/segmented-control.tsx`:
    //   <div role="radiogroup" className="segmented-control ...">
    //     <span className="segmented-control__indicator" aria-hidden>
    //     <button role="radio" className="segmented-control__segment ...">
    //       <span className="segmented-control__icon" aria-hidden>
    //       <span className="segmented-control__label">
    // The root carries role="radiogroup"; this signature targets the
    // segmented-control-specific shape (block className + the
    // `__segment` element) so it does NOT collide with a plain
    // radiogroup. canonicalOwners is the package name (lib stamps via
    // `data-norkron-lib="@norkron/web-segmented-control@x.y.z"`).
    primitive: "segmented-control",
    canonicalOwners: ["@norkron/web-segmented-control"],
    selectors: [
      '[role="radiogroup"][class*="segmented-control"]',
      '[class*="segmented-control__segment"]'
    ],
    classNamePatterns: [
      /(?:^|[^a-z0-9])segmented-control(?:__[a-z-]+)?(?:--[a-z0-9-]+)?(?:$|[^a-z0-9])/i,
      /(?:^|[^a-z0-9])segctrl(?:__[a-z-]+)?(?:$|[^a-z0-9])/i
    ]
    // No ariaRoles: `radiogroup` alone is too broad (a plain radio group
    // is NOT a segmented control). Selector pins the block className.
  },
  {
    // Generic ARIA radiogroup (NOT the styled segmented-control variant).
    // Grounded in web-collection-view
    //   `src/react/controls/ViewSwitcher.tsx`:
    //     <div role="radiogroup" className="...">  ... role="radio" ...
    // and web-survey `src/question-renderer.tsx` (role="radiogroup").
    // Detection is role-driven (`role="radiogroup"`) plus the canonical
    // `*-radiogroup` / `*__radiogroup` BEM/kebab className. The
    // segmented-control signature above is intentionally more specific;
    // both can match a segmented control (it IS a radiogroup) — the
    // duplicate rules dedupe per-primitive so that is acceptable and the
    // per-primitive `extras.primitive` disambiguates findings.
    primitive: "radiogroup",
    canonicalOwners: [
      "@norkron/web-collection-view",
      "@norkron/web-segmented-control"
    ],
    selectors: ['[role="radiogroup"]'],
    classNamePatterns: [
      /(?:^|[^a-z0-9])radio-?group(?:__[a-z-]+)?(?:--[a-z0-9-]+)?(?:$|[^a-z0-9])/i
    ],
    ariaRoles: ["radiogroup"]
  },
  {
    // Grounded in web-tabs `src/tabs.tsx` + `src/self-check.ts`:
    //   <div className="tabs tabs--{orientation} ...">
    //     <div role="tablist" className="tabs__list ...">
    //       <button role="tab" className="tabs__tab ...">
    //         <span className="tabs__icon" aria-hidden>
    //         <span className="tabs__label">
    // Detection: `role="tablist"` / `role="tab"` (APG-canonical) plus the
    // BEM `tabs` block / `tabs__list` element classNames. The `\btabs?\b`
    // shape with boundary lookarounds avoids matching unrelated tokens
    // like `data-tabs` substrings in larger words.
    primitive: "tabs",
    canonicalOwners: ["@norkron/web-tabs"],
    selectors: [
      '[role="tablist"]',
      '[class*="tabs__list"]'
    ],
    classNamePatterns: [
      /(?:^|[^a-z0-9])tabs(?:__[a-z-]+)?(?:--[a-z0-9-]+)?(?:$|[^a-z0-9])/i,
      /(?:^|[^a-z0-9])tablist(?:$|[^a-z0-9])/i
    ],
    ariaRoles: ["tablist"]
  }
];
function matchesClassNamePatterns(el, patterns) {
  const cls = (el.getAttribute("class") ?? "").trim();
  if (cls.length === 0) return false;
  for (const p of patterns) {
    if (p.test(cls)) return true;
  }
  return false;
}
function matchesPrimitive(el, sig) {
  const role = el.getAttribute("role");
  if (sig.primitive === "progress" && role !== null) {
    if (role === "slider" || role === "scrollbar" || role === "spinbutton" || role === "separator") {
      return false;
    }
  }
  for (const s of sig.selectors) {
    try {
      if (el.matches(s)) return true;
    } catch {
    }
  }
  if (matchesClassNamePatterns(el, sig.classNamePatterns)) return true;
  if (role && sig.ariaRoles?.includes(role)) return true;
  return false;
}
function nearestLibStamp(el) {
  let cur = el;
  while (cur) {
    const raw = cur.getAttribute("data-norkron-lib");
    if (raw && raw.length > 0) {
      const at = raw.indexOf("@", 1);
      return at > 0 ? raw.slice(0, at) : raw;
    }
    cur = cur.parentElement;
  }
  return null;
}
function canonicalOwnerFor(libStamp, sig) {
  for (const owner of sig.canonicalOwners) {
    if (libStamp === owner) return owner;
  }
  return null;
}

// src/structural/handrolled-duplicates.ts
var LIB_ID5 = "@norkron/web-conformance";
var CLUSTER_VIEWPORT_PX = 800;
var SHARED_ANCESTOR_MAX_DEPTH = 4;
function getRectSafely2(el) {
  if (typeof el.getBoundingClientRect !== "function") return null;
  try {
    const r = el.getBoundingClientRect();
    if (r === null || r === void 0) return null;
    return { top: r.top, bottom: r.bottom };
  } catch {
    return null;
  }
}
function collectInstances(root, sig) {
  const out = [];
  const all = Array.from(root.querySelectorAll("*"));
  for (const el of all) {
    if (!matchesPrimitive(el, sig)) continue;
    const libStamp = nearestLibStamp(el);
    const canonicalOwner = libStamp ? canonicalOwnerFor(libStamp, sig) : null;
    const rect = getRectSafely2(el);
    out.push({
      el,
      libStamp,
      canonicalOwner,
      top: rect?.top ?? 0,
      bottom: rect?.bottom ?? 0
    });
  }
  return out;
}
function sharedAncestorWithinDepth(a, b, maxDepth) {
  const ancestors = [];
  let cur = a.parentElement;
  for (let i = 0; cur && i < maxDepth; i++) {
    ancestors.push(cur);
    cur = cur.parentElement;
  }
  for (const anc of ancestors) {
    if (!anc.contains(b)) continue;
    let curB = b;
    let depth = 0;
    while (curB && curB !== anc && depth <= maxDepth) {
      curB = curB.parentElement;
      depth += 1;
    }
    if (curB === anc && depth <= maxDepth) return true;
  }
  return false;
}
function clusters(instances) {
  if (instances.length === 0) return [];
  const anyRect = instances.some((i) => i.top !== 0 || i.bottom !== 0);
  if (anyRect) {
    const sorted = [...instances].sort((a, b) => a.top - b.top);
    const buckets2 = [];
    let cur = [];
    let anchorTop = null;
    for (const inst of sorted) {
      if (anchorTop === null || inst.top - anchorTop <= CLUSTER_VIEWPORT_PX) {
        cur.push(inst);
        if (anchorTop === null) anchorTop = inst.top;
      } else {
        if (cur.length > 0) buckets2.push(cur);
        cur = [inst];
        anchorTop = inst.top;
      }
    }
    if (cur.length > 0) buckets2.push(cur);
    return buckets2;
  }
  const buckets = [];
  const consumed = /* @__PURE__ */ new Set();
  for (const inst of instances) {
    if (consumed.has(inst)) continue;
    const bucket = [inst];
    consumed.add(inst);
    for (const other of instances) {
      if (consumed.has(other)) continue;
      if (sharedAncestorWithinDepth(
        inst.el,
        other.el,
        SHARED_ANCESTOR_MAX_DEPTH
      )) {
        bucket.push(other);
        consumed.add(other);
      }
    }
    buckets.push(bucket);
  }
  return buckets;
}
function selectorFor2(el) {
  const cls = (el.getAttribute("class") ?? "").trim();
  const id = el.getAttribute("id");
  const role = el.getAttribute("role");
  const tag = el.tagName.toLowerCase();
  if (id) return `${tag}#${id}`;
  if (cls.length > 0) {
    const first = cls.split(/\s+/)[0];
    return `${tag}.${first}`;
  }
  if (role) return `${tag}[role="${role}"]`;
  return tag;
}
function checkHandrolledDuplicatesLibPrimitive(root) {
  const findings = [];
  for (const sig of KNOWN_PRIMITIVE_SIGNATURES) {
    const instances = collectInstances(root, sig);
    if (instances.length < 2) continue;
    const groups = clusters(instances);
    for (const group of groups) {
      const libs = group.filter((i) => i.canonicalOwner !== null);
      const handRolled = group.filter(
        (i) => i.canonicalOwner === null && i.libStamp === null
      );
      if (libs.length === 0 || handRolled.length === 0) continue;
      const libAlt = libs[0].canonicalOwner;
      for (const hr of handRolled) {
        const sel = selectorFor2(hr.el);
        findings.push({
          libId: LIB_ID5,
          code: "STR_HANDROLLED_DUPLICATES_LIB_PRIMITIVE",
          category: "layout",
          severity: "major",
          message: `Hand-rolled ${sig.primitive} widget (\`${sel}\`) duplicates the canonical \`${libAlt}\` primitive emitted on the same page. Rule Zero: products are composed from libs, never reimplemented.`,
          selector: sel,
          suggestedFix: `Replace \`${sel}\` with the existing \`${libAlt}\` primitive (or another lib-owned ${sig.primitive} indicator); delete the hand-rolled duplicate.`,
          extras: {
            primitive: sig.primitive,
            lib_alternative: libAlt,
            hand_rolled_selector: sel,
            cluster_count: group.length
          }
        });
      }
    }
  }
  return findings;
}
function checkLibPrimitiveDuplicateEmitters(root) {
  const findings = [];
  for (const sig of KNOWN_PRIMITIVE_SIGNATURES) {
    const instances = collectInstances(root, sig);
    if (instances.length < 2) continue;
    const groups = clusters(instances);
    for (const group of groups) {
      const stampedByOwner = /* @__PURE__ */ new Map();
      for (const inst of group) {
        if (inst.canonicalOwner === null) continue;
        const arr = stampedByOwner.get(inst.canonicalOwner) ?? [];
        arr.push(inst);
        stampedByOwner.set(inst.canonicalOwner, arr);
      }
      if (stampedByOwner.size < 2) continue;
      const owners = Array.from(stampedByOwner.keys()).sort();
      const firstOwnerInstances = stampedByOwner.get(owners[0]) ?? [];
      const anchor = firstOwnerInstances[0]?.el ?? group[0].el;
      findings.push({
        libId: LIB_ID5,
        code: "STR_LIB_PRIMITIVE_DUPLICATE_EMITTERS",
        category: "layout",
        severity: "major",
        message: `${owners.length} distinct stamped libs (${owners.join(", ")}) emit the same \`${sig.primitive}\` primitive on the same page. Pick one canonical owner.`,
        selector: selectorFor2(anchor),
        suggestedFix: `Designate ONE canonical lib for the \`${sig.primitive}\` UX role; migrate every consumer to it and remove the other emitter.`,
        extras: {
          primitive: sig.primitive,
          duplicate_emitters: owners,
          cluster_count: group.length
        }
      });
    }
  }
  return findings;
}

// src/structural/similarity.ts
function bucketDimension(px2) {
  if (px2 <= 120) return "small";
  if (px2 <= 320) return "medium";
  if (px2 <= 640) return "large";
  return "xlarge";
}
function bucketStroke(px2) {
  if (px2 <= 4) return "thin";
  if (px2 <= 10) return "medium";
  return "thick";
}
function safeRect(el) {
  const anyEl = el;
  if (typeof anyEl.getBoundingClientRect !== "function") return null;
  try {
    const r = anyEl.getBoundingClientRect();
    if (!r) return null;
    return { width: r.width, height: r.height };
  } catch {
    return null;
  }
}
function detectForm(el) {
  const stepLike = Array.from(
    el.querySelectorAll(
      '[data-step], .step, [aria-current], [aria-label*="step" i], [aria-label*="Step" i], li'
    )
  );
  const stepCount = stepLike.length;
  const tag = el.tagName.toLowerCase();
  let hasCircle = false;
  if (tag === "svg" || tag === "circle") hasCircle = true;
  else if (el.querySelector && el.querySelector("circle")) hasCircle = true;
  const role = el.getAttribute("role");
  const hasAriaValues = el.hasAttribute("aria-valuenow") && el.hasAttribute("aria-valuemax");
  const isLinearBar = tag === "progress" || role === "progressbar" || hasAriaValues;
  const textRaw = (el.textContent ?? "").trim();
  const text = textRaw.length <= 64 ? textRaw : "";
  const isNumericText = text.length > 0 && /^(?:step\s+)?\d+\s*(?:\/|of)\s*\d+$/i.test(text);
  if (stepCount >= 2) return { form: "stepped", count: stepCount };
  if (hasCircle && !isLinearBar) return { form: "circular", count: null };
  if (isLinearBar) return { form: "linear-bar", count: null };
  if (isNumericText) return { form: "numeric", count: null };
  return { form: "unknown", count: null };
}
function getComputedStyleSafely(el) {
  try {
    const win = el.ownerDocument?.defaultView ?? (typeof globalThis !== "undefined" ? globalThis.window : void 0);
    if (!win || typeof win.getComputedStyle !== "function") return null;
    return win.getComputedStyle(el);
  } catch {
    return null;
  }
}
function parsePx(s) {
  if (!s) return null;
  const m = s.match(/^(-?\d+(?:\.\d+)?)px$/);
  if (!m || !m[1]) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}
function computeFingerprint(el) {
  const { form, count } = detectForm(el);
  const rect = safeRect(el);
  let primaryDimensionPx;
  if (rect && (rect.width > 0 || rect.height > 0)) {
    primaryDimensionPx = form === "circular" ? Math.max(rect.width, rect.height) : rect.width;
  } else {
    primaryDimensionPx = 200;
  }
  let strokePx = 6;
  if (form === "linear-bar" && rect && rect.height > 0) {
    strokePx = rect.height;
  } else if (form === "circular") {
    const circle = el.tagName.toLowerCase() === "circle" ? el : el.querySelector("circle");
    if (circle) {
      const sw = circle.getAttribute("stroke-width");
      const swN = sw ? Number(sw) : NaN;
      if (Number.isFinite(swN) && swN > 0) strokePx = swN;
    }
  } else {
    const cs = getComputedStyleSafely(el);
    const h = cs ? parsePx(cs.height) : null;
    if (h !== null && h > 0) strokePx = h;
  }
  const primaryDimension = bucketDimension(primaryDimensionPx);
  const strokeStyle = bucketStroke(strokePx);
  const countToken = form === "stepped" && count !== null ? String(count) : "_";
  const raw = `${form}:${primaryDimension}:${strokeStyle}:${countToken}`;
  return { form, primaryDimension, strokeStyle, count, raw };
}
function levenshtein(a, b) {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const m = a.length;
  const n = b.length;
  let prev = new Array(n + 1);
  let cur = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    cur[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      const del = (prev[j] ?? 0) + 1;
      const ins = (cur[j - 1] ?? 0) + 1;
      const sub = (prev[j - 1] ?? 0) + cost;
      let best = del;
      if (ins < best) best = ins;
      if (sub < best) best = sub;
      cur[j] = best;
    }
    const tmp = prev;
    prev = cur;
    cur = tmp;
  }
  return prev[n] ?? 0;
}
function similarity(fpA, fpB) {
  const dist = levenshtein(fpA, fpB);
  const maxLen = Math.max(fpA.length, fpB.length);
  if (maxLen === 0) return 1;
  return 1 - dist / maxLen;
}
function severityForSimilarity(score) {
  if (score >= 0.9) return "critical";
  if (score >= 0.7) return "major";
  if (score >= 0.4) return "minor";
  return null;
}

// src/structural/primitive-similarity.ts
var LIB_ID6 = "@norkron/web-conformance";
var CLUSTER_VIEWPORT_PX2 = 800;
var SHARED_ANCESTOR_MAX_DEPTH2 = 4;
var MAX_FINDINGS_PER_CLUSTER = 5;
function getRectSafely3(el) {
  if (typeof el.getBoundingClientRect !== "function")
    return null;
  try {
    const r = el.getBoundingClientRect();
    if (!r) return null;
    return { top: r.top, bottom: r.bottom };
  } catch {
    return null;
  }
}
function collectInstances2(root, sig) {
  const out = [];
  const all = Array.from(root.querySelectorAll("*"));
  for (const el of all) {
    if (!matchesPrimitive(el, sig)) continue;
    const libStamp = nearestLibStamp(el);
    const canonicalOwner = libStamp ? canonicalOwnerFor(libStamp, sig) : null;
    const rect = getRectSafely3(el);
    const fingerprint2 = computeFingerprint(el);
    out.push({
      el,
      libStamp,
      canonicalOwner,
      fingerprint: fingerprint2,
      top: rect?.top ?? 0,
      bottom: rect?.bottom ?? 0
    });
  }
  return out;
}
function sharedAncestorWithinDepth2(a, b, maxDepth) {
  const ancestors = [];
  let cur = a.parentElement;
  for (let i = 0; cur && i < maxDepth; i++) {
    ancestors.push(cur);
    cur = cur.parentElement;
  }
  for (const anc of ancestors) {
    if (!anc.contains(b)) continue;
    let curB = b;
    let depth = 0;
    while (curB && curB !== anc && depth <= maxDepth) {
      curB = curB.parentElement;
      depth += 1;
    }
    if (curB === anc && depth <= maxDepth) return true;
  }
  return false;
}
function clusters2(instances) {
  if (instances.length === 0) return [];
  const anyRect = instances.some((i) => i.top !== 0 || i.bottom !== 0);
  if (anyRect) {
    const sorted = [...instances].sort((a, b) => a.top - b.top);
    const buckets2 = [];
    let cur = [];
    let anchorTop = null;
    for (const inst of sorted) {
      if (anchorTop === null || inst.top - anchorTop <= CLUSTER_VIEWPORT_PX2) {
        cur.push(inst);
        if (anchorTop === null) anchorTop = inst.top;
      } else {
        if (cur.length > 0) buckets2.push(cur);
        cur = [inst];
        anchorTop = inst.top;
      }
    }
    if (cur.length > 0) buckets2.push(cur);
    return buckets2;
  }
  const buckets = [];
  const consumed = /* @__PURE__ */ new Set();
  for (const inst of instances) {
    if (consumed.has(inst)) continue;
    const bucket = [inst];
    consumed.add(inst);
    for (const other of instances) {
      if (consumed.has(other)) continue;
      if (sharedAncestorWithinDepth2(
        inst.el,
        other.el,
        SHARED_ANCESTOR_MAX_DEPTH2
      )) {
        bucket.push(other);
        consumed.add(other);
      }
    }
    buckets.push(bucket);
  }
  return buckets;
}
function selectorFor3(el) {
  const cls = (el.getAttribute("class") ?? "").trim();
  const id = el.getAttribute("id");
  const role = el.getAttribute("role");
  const tag = el.tagName.toLowerCase();
  if (id) return `${tag}#${id}`;
  if (cls.length > 0) {
    const first = cls.split(/\s+/)[0];
    return `${tag}.${first}`;
  }
  if (role) return `${tag}[role="${role}"]`;
  return tag;
}
function emitForCluster(cluster, sig) {
  if (cluster.length < 2) return [];
  const pairs = [];
  for (let i = 0; i < cluster.length; i++) {
    for (let j = i + 1; j < cluster.length; j++) {
      const a = cluster[i];
      const b = cluster[j];
      let score;
      if (a.el.contains(b.el) || b.el.contains(a.el)) {
        score = 0;
      } else if (
        // Suppression gate 2 — same canonical-lib emitter: when both
        // elements resolve to the SAME canonical owner via
        // `data-norkron-lib`, they are intentional repeated lib usage
        // (e.g. a coverage-matrix rendering N color-pickers, or a
        // product showing two `<TourProgress />` widgets across
        // distinct flows). The lib already owns the primitive; pairing
        // its own instances against itself is doctrinally wrong.
        a.canonicalOwner !== null && b.canonicalOwner !== null && a.canonicalOwner === b.canonicalOwner
      ) {
        score = 0;
      } else if (a.fingerprint.form !== b.fingerprint.form) {
        score = 0;
      } else if (a.fingerprint.form === "unknown" || b.fingerprint.form === "unknown") {
        score = 0;
      } else {
        const slotMatches = (a.fingerprint.primaryDimension === b.fingerprint.primaryDimension ? 1 : 0) + (a.fingerprint.strokeStyle === b.fingerprint.strokeStyle ? 1 : 0) + (a.fingerprint.count === b.fingerprint.count ? 1 : 0);
        const slotScore = slotMatches / 3;
        const lev = similarity(a.fingerprint.raw, b.fingerprint.raw);
        score = (slotScore + lev) / 2;
      }
      pairs.push({ a, b, score });
    }
  }
  pairs.sort((x, y) => y.score - x.score);
  const findings = [];
  for (const pair of pairs) {
    if (findings.length >= MAX_FINDINGS_PER_CLUSTER) break;
    const sev = severityForSimilarity(pair.score);
    if (sev === null) continue;
    const aStamp = pair.a.canonicalOwner;
    const bStamp = pair.b.canonicalOwner;
    const aHandRolled = pair.a.libStamp === null;
    const bHandRolled = pair.b.libStamp === null;
    let primary;
    let counterpart;
    let suggestedFix;
    let messageSuffix;
    if (aStamp !== null && bHandRolled) {
      primary = pair.b;
      counterpart = pair.a;
      suggestedFix = `Replace \`${selectorFor3(primary.el)}\` with the existing \`${aStamp}\` primitive; per Rule Zero products are composed, never reimplemented.`;
      messageSuffix = ` hand-rolled \`${selectorFor3(primary.el)}\` duplicates \`${aStamp}\``;
    } else if (bStamp !== null && aHandRolled) {
      primary = pair.a;
      counterpart = pair.b;
      suggestedFix = `Replace \`${selectorFor3(primary.el)}\` with the existing \`${bStamp}\` primitive; per Rule Zero products are composed, never reimplemented.`;
      messageSuffix = ` hand-rolled \`${selectorFor3(primary.el)}\` duplicates \`${bStamp}\``;
    } else if (aStamp !== null && bStamp !== null && aStamp !== bStamp) {
      primary = pair.a;
      counterpart = pair.b;
      suggestedFix = `Pick one canonical lib for the \`${sig.primitive}\` UX role across the product; \`${aStamp}\` and \`${bStamp}\` both ship it.`;
      messageSuffix = ` two stamped libs ship the same form: \`${aStamp}\` + \`${bStamp}\``;
    } else {
      primary = pair.a;
      counterpart = pair.b;
      suggestedFix = `Collapse the duplicate \`${sig.primitive}\` widgets; if you need two distinct processes, give them visually different forms (e.g. stepper-dots vs linear-bar) so they communicate distinct meaning.`;
      messageSuffix = ` matched \`${selectorFor3(primary.el)}\` vs \`${selectorFor3(counterpart.el)}\``;
    }
    const scoreRounded = Math.round(pair.score * 100) / 100;
    const message = `Similar \`${sig.primitive}\` widgets detected (similarity ${scoreRounded.toFixed(2)}):${messageSuffix}. Different visual forms communicate distinct processes; identical forms suggest a missed Rule Zero composition.`;
    findings.push({
      libId: LIB_ID6,
      code: "STR_DUPLICATE_PRIMITIVE_BY_SIMILARITY",
      category: "layout",
      severity: sev,
      message,
      selector: selectorFor3(primary.el),
      suggestedFix,
      extras: {
        primitive: sig.primitive,
        severityComputed: pair.score,
        pairs: [
          {
            selectorA: selectorFor3(pair.a.el),
            fingerprintA: pair.a.fingerprint.raw,
            selectorB: selectorFor3(pair.b.el),
            fingerprintB: pair.b.fingerprint.raw,
            similarity: pair.score
          }
        ]
      }
    });
  }
  return findings;
}
function checkDuplicatePrimitiveBySimilarity(root) {
  const findings = [];
  for (const sig of KNOWN_PRIMITIVE_SIGNATURES) {
    const instances = collectInstances2(root, sig);
    if (instances.length < 2) continue;
    const groups = clusters2(instances);
    for (const group of groups) {
      findings.push(...emitForCluster(group, sig));
    }
  }
  return findings;
}

// src/structural/inline-style-on-lib.ts
var LIB_ID7 = "@norkron/web-conformance";
var LAYOUT_PROPERTIES = /* @__PURE__ */ new Set([
  "display",
  "position",
  "top",
  "right",
  "bottom",
  "left",
  "inset",
  "inset-block",
  "inset-block-start",
  "inset-block-end",
  "inset-inline",
  "inset-inline-start",
  "inset-inline-end",
  "width",
  "height",
  "min-width",
  "min-height",
  "max-width",
  "max-height",
  "flex",
  "flex-basis",
  "flex-grow",
  "flex-shrink",
  "flex-direction",
  "flex-wrap",
  // Flex / grid alignment — structural box positioning, never themed
  // paint. 2026-05-23 ITER-85: `@norkron/web-toast` viewport containers
  // set `align-items: flex-end|flex-start|center` inline to position the
  // toast stack at the configured corner (the placement is dynamic and
  // data-driven, not a static theme value). 14 live false positives on
  // `[data-norkron-lib="@norkron/web-toast@0.3.5"]` because `align-items`
  // was treated as a paint override. Flex/grid alignment belongs with
  // `display` / `flex-direction` / `gap` above — structural, not themed.
  "align-items",
  "align-content",
  "align-self",
  "justify-items",
  "justify-content",
  "justify-self",
  "place-items",
  "place-content",
  "place-self",
  "order",
  "grid-template-columns",
  "grid-template-rows",
  "grid-column",
  "grid-row",
  "gap",
  "row-gap",
  "column-gap",
  "overflow",
  "overflow-x",
  "overflow-y",
  "visibility",
  "z-index",
  "pointer-events",
  "touch-action",
  "contain",
  "isolation",
  "aspect-ratio",
  // Box-model sizing semantics — structural, not themed paint. A lib
  // setting `box-sizing: border-box` on its own root is normalising the
  // box model, not overriding the theme; it belongs with `display` /
  // `overflow` / `position` above, never with paint props.
  "box-sizing"
]);
function isLayoutProperty(property) {
  return LAYOUT_PROPERTIES.has(property);
}
var ANIMATION_PROPERTIES = /* @__PURE__ */ new Set([
  "transition",
  "transition-property",
  "transition-duration",
  "transition-timing-function",
  "transition-delay",
  "transform",
  "transform-origin",
  "opacity",
  "animation",
  "animation-name",
  "animation-duration",
  "animation-timing-function",
  "animation-delay",
  "animation-iteration-count",
  "animation-direction",
  "animation-fill-mode",
  "animation-play-state",
  "will-change"
]);
function parseInlineStyle(raw) {
  const out = [];
  if (!raw) return out;
  let depth = 0;
  let quote = null;
  let buf = "";
  const decls = [];
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (quote) {
      buf += ch;
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === "(") depth++;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    if (ch === ";" && depth === 0) {
      decls.push(buf);
      buf = "";
    } else {
      buf += ch;
    }
  }
  if (buf.trim().length > 0) decls.push(buf);
  for (const decl of decls) {
    const trimmed = decl.trim();
    if (trimmed.length === 0) continue;
    const colon = trimmed.indexOf(":");
    if (colon <= 0) continue;
    const property = trimmed.slice(0, colon).trim().toLowerCase();
    const value = trimmed.slice(colon + 1).trim();
    if (property.length === 0 || value.length === 0) continue;
    out.push({ property, value });
  }
  return out;
}
function isCustomProperty(property) {
  return property.startsWith("--");
}
function isAnimationProperty(property) {
  return ANIMATION_PROPERTIES.has(property);
}
function isPlacementOnlyTransform(value) {
  const v = value.trim().toLowerCase();
  if (v === "" || v === "none") return false;
  const NON_PLACEMENT = /\b(scale|scalex|scaley|scalez|scale3d|rotate|rotatex|rotatey|rotatez|rotate3d|skew|skewx|skewy|matrix|matrix3d|perspective)\s*\(/;
  if (NON_PLACEMENT.test(v)) return false;
  return /\btranslate(?:x|y|z|3d)?\s*\(/.test(v);
}
function checkInlineStyleOnLibStamp(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const stamped = root.querySelectorAll("[data-norkron-lib]");
  for (let i = 0; i < stamped.length; i++) {
    const el = stamped.item(i);
    if (!el) continue;
    const styleAttr = el.getAttribute("style");
    if (!styleAttr || styleAttr.trim().length === 0) continue;
    const libIdRaw = el.getAttribute("data-norkron-lib") ?? "";
    const decls = parseInlineStyle(styleAttr);
    if (decls.length === 0) continue;
    const overridingProperties = [];
    const animationProperties = [];
    for (const { property, value } of decls) {
      if (isCustomProperty(property)) continue;
      if (isLayoutProperty(property)) continue;
      if (property === "transform" && isPlacementOnlyTransform(value)) continue;
      if (isAnimationProperty(property)) {
        animationProperties.push(property);
        continue;
      }
      overridingProperties.push(property);
    }
    if (overridingProperties.length === 0 && animationProperties.length === 0) continue;
    const selector = `[data-norkron-lib="${libIdRaw}"]`;
    if (overridingProperties.length > 0) {
      findings.push({
        libId: LIB_ID7,
        code: "STR_INLINE_STYLE_ON_LIB_STAMP",
        category: "layout",
        severity: "major",
        message: `Stamped lib root \`${libIdRaw}\` has inline style overriding ${overridingProperties.length} themed propert${overridingProperties.length === 1 ? "y" : "ies"}: ${overridingProperties.slice(0, 5).join(", ")}.`,
        selector,
        suggestedFix: "Move these declarations into the lib's theme tokens via web-themer, or remove the consumer override.",
        extras: {
          libId: libIdRaw,
          overridingProperties,
          animationProperties,
          selector
        }
      });
    } else if (animationProperties.length > 0) {
      findings.push({
        libId: LIB_ID7,
        code: "STR_INLINE_STYLE_ON_LIB_STAMP",
        category: "layout",
        severity: "info",
        message: `Stamped lib root \`${libIdRaw}\` has inline animation-only style (transition/transform/opacity). Likely a web-motion-driven transition; verify intentional.`,
        selector,
        suggestedFix: "If driven by @norkron/web-motion this is canonical. If hand-rolled, route through web-motion so the animation respects prefers-reduced-motion.",
        extras: {
          libId: libIdRaw,
          overridingProperties: [],
          animationProperties,
          selector
        }
      });
    }
  }
  return findings;
}

// src/structural/utility-class-on-lib.ts
var LIB_ID8 = "@norkron/web-conformance";
var UTILITY_PATTERNS = [
  // Spacing: p|m|px|py|pt|pb|pl|pr|mx|my|mt|mb|ml|mr|gap-(N|auto|px)
  /^(p|m|px|py|pt|pb|pl|pr|mx|my|mt|mb|ml|mr|gap)-(\d+|auto|px)$/,
  // Sizing: w|h|min-w|max-w|min-h|max-h-(N|full|screen|auto|px|fit|min|max)
  /^(w|h|min-w|max-w|min-h|max-h)-(\d+|full|screen|auto|px|fit|min|max)$/,
  // Colors: bg|text|border|ring|fill|stroke-(palette)-(N?)
  /^(bg|text|border|ring|fill|stroke)-(slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose|white|black|transparent)(-\d+)?$/,
  // Layout
  /^(flex|grid|block|inline-block|hidden|absolute|relative|fixed|sticky|static)$/,
  // Items / justify / content / self / place
  /^(items|justify|content|self|place)-(start|end|center|between|around|evenly|stretch|baseline)$/,
  // Borders / rounded / ring / shadow
  /^(border|rounded|ring|shadow)(-(\d+|none|sm|md|lg|xl|full|inner))?$/,
  // Typography: text-(size), font-(weight), leading/tracking-(named)
  /^(text)-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)$/,
  /^(font)-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/,
  /^(leading)-(none|tight|snug|normal|relaxed|loose|\d+)$/,
  /^(tracking)-(tighter|tight|normal|wide|wider|widest)$/
];
var MODIFIER_PREFIXES = /* @__PURE__ */ new Set([
  "dark",
  "hover",
  "focus",
  "active",
  "disabled",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl",
  "first",
  "last",
  "odd",
  "even",
  "visited",
  "checked",
  "group-hover",
  "group-focus",
  "focus-within",
  "focus-visible",
  "motion-safe",
  "motion-reduce"
]);
function splitModifier(token) {
  let core = token;
  let hadModifier = false;
  while (true) {
    const colon = core.indexOf(":");
    if (colon <= 0) break;
    const prefix = core.slice(0, colon);
    if (!MODIFIER_PREFIXES.has(prefix)) break;
    core = core.slice(colon + 1);
    hadModifier = true;
    if (core.length === 0) break;
  }
  return { core, hadModifier };
}
function isUtilityClass(token) {
  const { core, hadModifier } = splitModifier(token);
  if (hadModifier && core.length > 0) {
    for (const re of UTILITY_PATTERNS) {
      if (re.test(core)) return true;
    }
    return false;
  }
  for (const re of UTILITY_PATTERNS) {
    if (re.test(core)) return true;
  }
  return false;
}
var UTILITY_THRESHOLD = 3;
function checkUtilityClassOnLibStamp(root) {
  const findings = [];
  const stamped = root.querySelectorAll("[data-norkron-lib]");
  for (let i = 0; i < stamped.length; i++) {
    const el = stamped.item(i);
    if (!el) continue;
    const className = (el.getAttribute("class") ?? "").trim();
    if (className.length === 0) continue;
    const tokens = className.split(/\s+/).filter((t) => t.length > 0);
    if (tokens.length === 0) continue;
    const utilityClasses = [];
    for (const tok of tokens) {
      if (isUtilityClass(tok)) utilityClasses.push(tok);
    }
    if (utilityClasses.length < UTILITY_THRESHOLD) continue;
    const libIdRaw = el.getAttribute("data-norkron-lib") ?? "";
    const selector = `[data-norkron-lib="${libIdRaw}"]`;
    findings.push({
      libId: LIB_ID8,
      code: "STR_UTILITY_CLASS_ON_LIB_STAMP",
      category: "layout",
      severity: "major",
      message: `Stamped lib root \`${libIdRaw}\` carries ${utilityClasses.length} utility/atomic class${utilityClasses.length === 1 ? "" : "es"} (Tailwind-style): ${utilityClasses.slice(0, 5).join(", ")}.`,
      selector,
      suggestedFix: "BEM-only mandate (see ~/.claude/CLAUDE.md). Replace utility classes with BEM lib-scoped classes or move them off the lib stamped root.",
      extras: {
        libId: libIdRaw,
        utilityClasses,
        totalClasses: tokens.length,
        selector
      }
    });
  }
  return findings;
}

// src/structural/svg-text-vs-sibling-fill.ts
var LIB_ID9 = "@norkron/web-conformance";
function isExcluded2(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute("aria-hidden") === "true") return true;
    if (cur.hasAttribute("data-decorative")) return true;
    cur = cur.parentElement;
  }
  return false;
}
function nearestFilledSibling(el, win) {
  let svgRoot = el;
  while (svgRoot && svgRoot.tagName.toLowerCase() !== "svg") {
    svgRoot = svgRoot.parentElement;
  }
  if (!svgRoot) return null;
  const candidates = Array.from(svgRoot.querySelectorAll("path,circle,rect,ellipse,polygon"));
  if (candidates.length === 0) return null;
  const textIdx = (() => {
    const all = Array.from(svgRoot.querySelectorAll("*"));
    return all.indexOf(el);
  })();
  const ranked = candidates.map((c) => {
    const cIdx = Array.from(svgRoot.querySelectorAll("*")).indexOf(c);
    return { c, dist: Math.abs(cIdx - textIdx) };
  }).sort((a, b) => a.dist - b.dist).map(({ c }) => c);
  for (const cand of ranked) {
    const style = win.getComputedStyle(cand);
    const fillStr = style.fill ?? "";
    const parsed = parseCssColor(fillStr);
    if (parsed && parsed[3] > 0.5) return cand;
  }
  return null;
}
function checkSvgTextVsSiblingFill(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const texts = Array.from(root.querySelectorAll("svg text"));
  for (const text of texts) {
    if (isExcluded2(text)) continue;
    const content = (text.textContent ?? "").trim();
    if (content.length === 0) continue;
    const style = win.getComputedStyle(text);
    const fgStr = style.fill ?? style.color ?? "";
    const fg = parseCssColor(fgStr);
    if (!fg || fg[3] < 0.5) continue;
    const sibling = nearestFilledSibling(text, win);
    if (!sibling) continue;
    const sibStyle = win.getComputedStyle(sibling);
    const bg = parseCssColor(sibStyle.fill ?? "");
    if (!bg || bg[3] < 0.5) continue;
    const ratio = contrastRatio([fg[0], fg[1], fg[2]], [bg[0], bg[1], bg[2]]);
    const fontSize = parseFloat(style.fontSize ?? "16") || 16;
    const fontWeightRaw = style.fontWeight ?? "400";
    const fontWeight = parseFloat(fontWeightRaw) || (/bold/i.test(fontWeightRaw) ? 700 : 400);
    const isLarge = fontSize >= 18 || fontSize >= 14 && fontWeight >= 700;
    const threshold = isLarge ? 3 : 4.5;
    if (ratio + 1e-6 < threshold) {
      findings.push({
        libId: LIB_ID9,
        code: "STR_SVG_TEXT_LOW_CONTRAST_VS_SIBLING_FILL",
        category: "a11y",
        severity: isLarge ? "major" : "critical",
        message: `SVG text "${content.slice(0, 40)}" has contrast ratio ${ratio.toFixed(
          2
        )}:1 against its nearest filled sibling (${sibling.tagName}) \u2014 WCAG minimum is ${threshold}:1. Body-bg contrast check misses this because the user perceives the text against the adjacent chart element, not the SVG's transparent backdrop.`,
        selector: `${text.tagName.toLowerCase()}`,
        suggestedFix: "Either darken/lighten the SVG <text> fill, change the adjacent shape fill, or place a contrasting halo (stroke / drop-shadow) on the text. For chart gauges, consider rendering the % label OUTSIDE the arc.",
        extras: {
          fg: rgbToHex([fg[0], fg[1], fg[2]]),
          bg: rgbToHex([bg[0], bg[1], bg[2]]),
          ratio: ratio.toFixed(2),
          threshold,
          siblingTagName: sibling.tagName.toLowerCase()
        }
      });
    }
  }
  return findings;
}

// src/internal/dom-guards.ts
function nodeTypeIs(v, nodeType) {
  return typeof v === "object" && v !== null && v.nodeType === nodeType;
}
function isElement(v, win) {
  const Ctor = win?.Element;
  if (Ctor) return v instanceof Ctor;
  if (typeof Element !== "undefined") return v instanceof Element;
  return nodeTypeIs(v, 1);
}

// src/structural/layout-nested-twice.ts
var LIB_ID10 = "@norkron/web-conformance";
function depthFromRoot(el) {
  let d = 0;
  let cur = el.parentElement;
  while (cur) {
    d++;
    cur = cur.parentElement;
  }
  return d;
}
var SECTIONING_CONTENT = /* @__PURE__ */ new Set(["article", "aside", "main", "nav", "section"]);
function hasSectioningAncestor(el) {
  let cur = el.parentElement;
  while (cur) {
    if (SECTIONING_CONTENT.has(cur.tagName.toLowerCase())) return true;
    cur = cur.parentElement;
  }
  return false;
}
function isShellLandmark(el) {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role")?.toLowerCase() ?? "";
  if (role === "banner" || role === "contentinfo") return true;
  if (tag === "header" || tag === "footer") return !hasSectioningAncestor(el);
  return false;
}
function hasLibStampAncestor(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute && cur.getAttribute("data-norkron-lib")) return true;
    cur = cur.parentElement;
  }
  return false;
}
function landmarksStack(a, b) {
  if (a === b) return false;
  if (a.contains(b) || b.contains(a)) return false;
  const dA = depthFromRoot(a);
  const dB = depthFromRoot(b);
  return dA <= 7 && dB <= 7;
}
function checkLayoutNestedTwice(root) {
  const findings = [];
  const win = root.ownerDocument?.defaultView ?? (typeof window !== "undefined" ? window : void 0);
  const headers = Array.from(root.querySelectorAll("header, [role=banner]")).filter(
    (el) => isElement(el, win) && isShellLandmark(el) && !hasLibStampAncestor(el)
  );
  const footers = Array.from(root.querySelectorAll("footer, [role=contentinfo]")).filter(
    (el) => isElement(el, win) && isShellLandmark(el) && !hasLibStampAncestor(el)
  );
  const checkGroup = (group, landmarkName) => {
    if (group.length < 2) return;
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        if (landmarksStack(group[i], group[j])) {
          findings.push({
            libId: LIB_ID10,
            code: "STR_LAYOUT_NESTED_TWICE",
            category: "layout",
            severity: "critical",
            message: `Two ${landmarkName} landmarks render at the page level (neither is nested inside the other). This usually means a route component renders the app shell AND the route's own page also renders its own shell \u2014 common when a 404 / error page is mounted inside a layout that already wraps in <header>+<footer>.`,
            selector: landmarkName,
            suggestedFix: landmarkName === "header" ? "Either remove the page component's own <header>, OR route the page outside the shell-wrapping layout." : "Either remove the page component's own <footer>, OR route the page outside the shell-wrapping layout.",
            extras: {
              landmarkCount: group.length,
              depthA: depthFromRoot(group[i]),
              depthB: depthFromRoot(group[j])
            }
          });
          return;
        }
      }
    }
  };
  checkGroup(headers, "header");
  checkGroup(footers, "footer");
  return findings;
}

// src/structural/_requires-layout.ts
var LIB_ID11 = "@norkron/web-conformance";
function needsLayoutFinding4(forCode, reason) {
  const message = forCode === "STR_CHILD_OVERFLOWS_PARENT" ? "Skipped STR_CHILD_OVERFLOWS_PARENT: runtime environment lacks getBoundingClientRect / getComputedStyle (headless DOM). Re-run audit in a browser session for layout-dependent checks." : `Skipped ${forCode}: runtime environment lacks getComputedStyle (headless DOM). Re-run audit in a browser session for layout-dependent checks.`;
  const extras = { forCode };
  if (reason !== void 0) extras.reason = reason;
  return {
    libId: LIB_ID11,
    code: "STR_NEEDS_LAYOUT",
    category: "perf",
    severity: "info",
    message,
    extras
  };
}
function requiresLayout(rootEl) {
  const doc = rootEl.ownerDocument ?? rootEl;
  const win = doc?.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return { ready: false, reason: "no-getComputedStyle" };
  }
  if (typeof rootEl.getBoundingClientRect !== "function") {
    return { ready: false, reason: "zero-rect" };
  }
  const r = rootEl.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) {
    return { ready: false, reason: "zero-rect" };
  }
  return { ready: true };
}
function detectLayoutAvailable(rootEl) {
  const doc = rootEl.ownerDocument ?? rootEl;
  const win = doc?.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return { available: false, reason: "no-getComputedStyle" };
  }
  if (typeof rootEl.getBoundingClientRect !== "function") {
    return { available: false, reason: "no-getBoundingClientRect" };
  }
  const anchors = [
    rootEl,
    doc?.body ?? null,
    doc?.documentElement ?? null
  ];
  for (const a of anchors) {
    if (!a) continue;
    try {
      const r = a.getBoundingClientRect();
      if ((r.width ?? 0) > 0 || (r.height ?? 0) > 0) {
        return { available: true };
      }
    } catch {
    }
  }
  return { available: false, reason: "anchor-zero-rect" };
}
function gateLayoutUnavailableFinding(reason) {
  return {
    libId: LIB_ID11,
    code: "STR_GATE_LAYOUT_UNAVAILABLE",
    category: "perf",
    severity: "info",
    message: "Audit running without layout \u2014 all layout-dependent structural rules degraded to info. Run a real-browser audit (chromium / firefox) for accurate measurement.",
    selector: ":root",
    extras: { reason }
  };
}

// src/structural/disabled-control-contrast.ts
var LIB_ID12 = "@norkron/web-conformance";
function isButtonish(el) {
  const tag = el.tagName.toLowerCase();
  if (tag === "button") return true;
  if (el.getAttribute("role")?.toLowerCase() === "button") return true;
  if (tag === "input") {
    const type = el.type;
    return type === "button" || type === "submit" || type === "reset";
  }
  return false;
}
function isDisabled(el) {
  if (el.hasAttribute("disabled")) return true;
  if (el.getAttribute("aria-disabled") === "true") return true;
  return false;
}
function checkDisabledControlContrast(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(needsLayoutFinding4("STR_DISABLED_CONTROL_LOW_CONTRAST"));
    return findings;
  }
  const candidates = Array.from(
    root.querySelectorAll("button, [role=button], input[type=button], input[type=submit], input[type=reset]")
  );
  for (const el of candidates) {
    if (!isButtonish(el)) continue;
    if (!isDisabled(el)) continue;
    const style = win.getComputedStyle(el);
    const rawBg = style.backgroundColor ?? "";
    if (rawBg === "") {
      findings.push(
        needsLayoutFinding4("STR_DISABLED_CONTROL_LOW_CONTRAST", "no-getComputedStyle")
      );
      continue;
    }
    const buttonBg = parseCssColor(rawBg);
    if (!buttonBg || buttonBg[3] < 0.1) {
      findings.push({
        libId: LIB_ID12,
        code: "STR_DISABLED_CONTROL_LOW_CONTRAST",
        category: "a11y",
        severity: "major",
        message: `Disabled control "${(el.textContent ?? "").trim().slice(0, 40)}" has a transparent/missing background. Users cannot identify it as a disabled affordance \u2014 looks like the button doesn't exist.`,
        selector: el.tagName.toLowerCase(),
        suggestedFix: "Give disabled buttons a distinct (\u22653:1 against page) background \u2014 e.g. a desaturated brand colour at 40-60% opacity over the page bg.",
        extras: { ratio: "transparent-bg", threshold: 3 }
      });
      continue;
    }
    const parent = el.parentElement;
    if (!parent) continue;
    const pageBg = resolveBackgroundColor(parent, win);
    const ratio = contrastRatio([buttonBg[0], buttonBg[1], buttonBg[2]], pageBg);
    if (ratio + 1e-6 < 3) {
      findings.push({
        libId: LIB_ID12,
        code: "STR_DISABLED_CONTROL_LOW_CONTRAST",
        category: "a11y",
        severity: "major",
        message: `Disabled button "${(el.textContent ?? "").trim().slice(0, 40)}" has only ${ratio.toFixed(
          2
        )}:1 background contrast against the page \u2014 looks broken/loading rather than disabled. WCAG SC 1.4.11 minimum is 3:1 for graphical UI objects.`,
        selector: el.tagName.toLowerCase(),
        suggestedFix: "Bump the disabled-button background opacity, or use a distinct disabled colour token (e.g. --btn-disabled-bg) that maintains \u22653:1 vs page bg.",
        extras: {
          buttonBg: rgbToHex([buttonBg[0], buttonBg[1], buttonBg[2]]),
          pageBg: rgbToHex(pageBg),
          ratio: ratio.toFixed(2),
          threshold: 3
        }
      });
    }
  }
  return findings;
}

// src/structural/chart-categories-no-color-differentiation.ts
var LIB_ID13 = "@norkron/web-conformance";
function checkChartCategoriesNoColorDifferentiation(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const groups = Array.from(root.querySelectorAll("svg g"));
  for (const g of groups) {
    const shapes = Array.from(g.children).filter((c) => {
      const t = c.tagName.toLowerCase();
      return t === "rect" || t === "path";
    });
    if (shapes.length < 3) continue;
    const fills = [];
    for (const s of shapes) {
      const style = win.getComputedStyle(s);
      const parsed = parseCssColor(style.fill ?? "");
      if (!parsed || parsed[3] < 0.5) {
        fills.length = 0;
        break;
      }
      fills.push(rgbToHex([parsed[0], parsed[1], parsed[2]]));
    }
    if (fills.length < 3) continue;
    const unique = new Set(fills);
    if (unique.size === 1) {
      findings.push({
        libId: LIB_ID13,
        code: "STR_CHART_CATEGORIES_NO_COLOR_DIFFERENTIATION",
        category: "a11y",
        severity: "major",
        message: `Chart group has ${shapes.length} categorical shapes (${shapes[0].tagName.toLowerCase()}) all sharing fill ${[...unique][0]} \u2014 viewers cannot tell categories apart.`,
        selector: "svg g",
        suggestedFix: "Assign distinct fills per category from a themed --chart-series-* palette, or add patterns/textures so categories remain distinguishable to color-blind users.",
        extras: { shapeCount: shapes.length, fill: [...unique][0] }
      });
    }
  }
  return findings;
}

// src/structural/legend-required-for-multi-series.ts
var LIB_ID14 = "@norkron/web-conformance";
function checkLegendRequiredForMultiSeries(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const svgs = Array.from(root.querySelectorAll("svg"));
  for (const svg of svgs) {
    const shapes = Array.from(svg.querySelectorAll("rect,path,circle"));
    if (shapes.length < 3) continue;
    const fills = /* @__PURE__ */ new Set();
    for (const s of shapes) {
      const style = win.getComputedStyle(s);
      const parsed = parseCssColor(style.fill ?? "");
      if (parsed && parsed[3] >= 0.5) {
        fills.add(rgbToHex([parsed[0], parsed[1], parsed[2]]));
      }
    }
    if (fills.size < 2) continue;
    const hasAria = svg.getAttribute("role") === "img" && (svg.hasAttribute("aria-label") || svg.hasAttribute("aria-labelledby"));
    const hasLegendEl = root.querySelector('.legend, .chart-legend, [class*="legend"]') !== null || svg.querySelector('text[class*="legend"]') !== null;
    if (!hasAria && !hasLegendEl) {
      findings.push({
        libId: LIB_ID14,
        code: "STR_LEGEND_REQUIRED_FOR_MULTI_SERIES",
        category: "a11y",
        severity: "major",
        message: `Chart with ${shapes.length} shapes across ${fills.size} colors has no legend, no aria-label/aria-labelledby, and no .legend descendant. Users cannot map color \u2192 category.`,
        selector: "svg",
        suggestedFix: 'Add a visible `.legend` next to the chart, or set role="img" + aria-label="\u2026" / aria-labelledby on the <svg>.',
        extras: { shapeCount: shapes.length, seriesCount: fills.size }
      });
    }
  }
  return findings;
}

// src/structural/redundant-empty-state-copy.ts
var LIB_ID15 = "@norkron/web-conformance";
var EMPTY_STATE_CLASS_RE = /empty[-_]?state|no[-_]?data|no[-_]?results|placeholder[-_]?state/i;
function normalize(s) {
  return s.replace(/\s+/g, " ").trim().toLowerCase();
}
function ownText(el) {
  let acc = "";
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === 3) acc += node.textContent ?? "";
  }
  return acc;
}
function isGray(rgb) {
  const lum = srgbLuminance(rgb[0], rgb[1], rgb[2]);
  if (lum < 0.15 || lum > 0.7) return false;
  const max = Math.max(rgb[0], rgb[1], rgb[2]);
  const min = Math.min(rgb[0], rgb[1], rgb[2]);
  return max - min <= 24;
}
function isEmptyStateParent(parent) {
  let p = parent;
  for (let i = 0; i < 4 && p; i++) {
    const cls = typeof p.className === "string" ? p.className : p.getAttribute("class") || "";
    if (EMPTY_STATE_CLASS_RE.test(cls)) return true;
    p = p.parentElement;
  }
  return false;
}
function emit(findings, parent, a, b, ratio, source) {
  findings.push({
    libId: LIB_ID15,
    code: "STR_REDUNDANT_EMPTY_STATE_COPY",
    category: "layout",
    severity: "minor",
    message: `Two adjacent empty-state captions in the same parent are near-duplicates: "${a.text.slice(0, 40)}" vs "${b.text.slice(0, 40)}" (Levenshtein ratio ${ratio.toFixed(2)}; matched via ${source}).`,
    selector: parent.tagName.toLowerCase(),
    suggestedFix: "Keep one empty-state caption per scope. If a title + description pair is needed, differentiate them (title = noun phrase, description = recovery action) instead of restating the same sentence twice.",
    extras: {
      ratio: ratio.toFixed(2),
      a: a.text.slice(0, 60),
      b: b.text.slice(0, 60),
      source
    }
  });
}
function checkRedundantEmptyStateCopy(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const parents = /* @__PURE__ */ new Set();
  for (const el of Array.from(root.querySelectorAll("*"))) {
    if (el.parentElement) parents.add(el.parentElement);
  }
  for (const parent of parents) {
    const styledCandidates = [];
    const bemCandidates = [];
    const inEmptyStateContext = isEmptyStateParent(parent);
    for (const child of Array.from(parent.children)) {
      const text = normalize(ownText(child) || child.textContent || "");
      if (text.length < 6) continue;
      const style = win.getComputedStyle(child);
      const color = parseCssColor(style.color ?? "");
      if (/italic/.test(style.fontStyle ?? "") && color && isGray([color[0], color[1], color[2]])) {
        styledCandidates.push({ el: child, text });
      }
      if (inEmptyStateContext) {
        bemCandidates.push({ el: child, text });
      }
    }
    const emitted = /* @__PURE__ */ new Set();
    const consider = (cands, source) => {
      for (let i = 0; i < cands.length; i++) {
        for (let j = i + 1; j < cands.length; j++) {
          const a = cands[i];
          const b = cands[j];
          const dist = levenshtein(a.text, b.text);
          const ratio = dist / Math.max(a.text.length, b.text.length);
          if (ratio < 0.4) {
            const key = `${a.text}::${b.text}`;
            if (emitted.has(key)) continue;
            emitted.add(key);
            emit(findings, parent, a, b, ratio, source);
          }
        }
      }
    };
    consider(styledCandidates, "italic-gray");
    consider(bemCandidates, "empty-state-class");
  }
  return findings;
}

// src/structural/cta-wrong-copy-after-flow-complete.ts
var LIB_ID16 = "@norkron/web-conformance";
var START_CTA_RE = /wype[łl]nij\s+ankiet[eę]|fill\s+survey|start\s+survey/i;
function pageIsResult(root) {
  if (root.querySelector('[data-flow-bind*="result"]')) return true;
  if (root.querySelector('[data-page="result"]')) return true;
  const win = root.ownerDocument?.defaultView ?? (typeof window !== "undefined" ? window : null);
  const path = win?.location?.pathname ?? "";
  if (/\/result/.test(path)) return true;
  return false;
}
function checkCtaWrongCopyAfterFlowComplete(root) {
  const findings = [];
  if (!pageIsResult(root)) return findings;
  const ctas = Array.from(root.querySelectorAll("button, a, [role=button]"));
  for (const cta of ctas) {
    const text = (cta.textContent ?? "").trim();
    if (text.length === 0) continue;
    if (!START_CTA_RE.test(text)) continue;
    findings.push({
      libId: LIB_ID16,
      code: "STR_CTA_WRONG_COPY_AFTER_FLOW_COMPLETE",
      category: "flow",
      severity: "major",
      message: `Result/summary surface still surfaces a "${text.slice(0, 40)}" CTA \u2014 invites the user to redo a flow they have just completed.`,
      selector: cta.tagName.toLowerCase(),
      suggestedFix: 'Replace the CTA copy with a result-appropriate next step ("Download report", "See details", "Return home"). Survey-restart copy belongs on the entry-point card, not the result page.',
      extras: { text: text.slice(0, 80) }
    });
  }
  return findings;
}

// src/structural/generic-error-copy-on-specific-state.ts
var LIB_ID17 = "@norkron/web-conformance";
var GENERIC_RE = /co[śs]\s+posz[lł]o\s+nie\s+tak|something\s+went\s+wrong|errors\.generic/i;
var F404_CLASS_RE = /not-?found|page-?404|\b404-/i;
function isErrorPage(root) {
  if (root.querySelector('[data-page="404"]')) return true;
  const headings = Array.from(root.querySelectorAll("h1"));
  if (headings.some((h) => /\b404\b/.test(h.textContent ?? ""))) return true;
  const doc = root.ownerDocument ?? root;
  const win = doc?.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (win && win.location && /\/404\b/.test(win.location.pathname ?? "")) return true;
  const candidates = Array.from(
    root.querySelectorAll('[class*="not-found"], [class*="404"], [class*="page-404"]')
  );
  for (const el of candidates) {
    const cls = el.className;
    const clsStr = typeof cls === "string" ? cls : el.getAttribute("class") || "";
    if (F404_CLASS_RE.test(clsStr)) return true;
  }
  return false;
}
function checkGenericErrorCopyOnSpecificState(root) {
  const findings = [];
  if (!isErrorPage(root)) return findings;
  const text = root.textContent ?? "";
  const match = text.match(GENERIC_RE);
  if (match) {
    findings.push({
      libId: LIB_ID17,
      code: "STR_GENERIC_ERROR_COPY_ON_SPECIFIC_STATE",
      category: "i18n",
      severity: "major",
      message: `404 page surfaces generic error copy ("${match[0]}"). The user already knows something went wrong \u2014 tell them WHAT: "this page does not exist" / "the route was renamed in v1.2".`,
      selector: "main",
      suggestedFix: 'Use a state-specific message for known error states (404 \u2192 "Page not found"; 403 \u2192 "You do not have access"). Reserve generic copy for truly unknown failures.',
      extras: { matched: match[0] }
    });
  }
  return findings;
}

// src/structural/input-placeholder-looks-like-value.ts
var LIB_ID18 = "@norkron/web-conformance";
var NUMERIC_INPUT_SELECTORS = [
  'input[type="number"]',
  'input[type="text"][inputmode="numeric"]',
  'input[type="text"][inputmode="decimal"]',
  'input[type="tel"][inputmode="numeric"]',
  'input[type="tel"][inputmode="decimal"]',
  "input[data-numeric-format]"
].join(",");
function describeInput(input) {
  const t = input.type || "text";
  const im = input.getAttribute("inputmode");
  if (input.hasAttribute("data-numeric-format")) return `<input data-numeric-format>`;
  if (im) return `<input type="${t}" inputmode="${im}">`;
  return `<input type="${t}">`;
}
function checkInputPlaceholderLooksLikeValue(root) {
  const findings = [];
  const seen = /* @__PURE__ */ new Set();
  const inputs = Array.from(root.querySelectorAll(NUMERIC_INPUT_SELECTORS));
  for (const el of inputs) {
    if (seen.has(el)) continue;
    seen.add(el);
    const input = el;
    const placeholder = input.placeholder ?? "";
    if (placeholder.trim() !== "0") continue;
    if ((input.value ?? "") !== "") continue;
    const desc = describeInput(input);
    findings.push({
      libId: LIB_ID18,
      code: "STR_INPUT_PLACEHOLDER_LOOKS_LIKE_VALUE",
      category: "a11y",
      severity: "minor",
      message: `${desc} with placeholder="0" is visually indistinguishable from a typed 0. Users may believe they have entered a value when the field is actually empty.`,
      selector: input.getAttribute("data-testid") ? `[data-testid="${input.getAttribute("data-testid")}"]` : NUMERIC_INPUT_SELECTORS,
      suggestedFix: 'Use a descriptive placeholder ("Enter amount", "kg/CO\u2082", units) \u2014 never a digit. Reserve "0" for actual defaults set via `value`.',
      extras: {
        name: input.name || null,
        type: input.type || null,
        inputmode: input.getAttribute("inputmode"),
        hasNumericFormat: input.hasAttribute("data-numeric-format")
      }
    });
  }
  return findings;
}

// src/structural/progress-indicator-count.ts
var LIB_ID19 = "@norkron/web-conformance";
var COUNT_RE = /\b\d+\s*[\/z]\s*\d+\b/;
function checkProgressIndicatorCount(root) {
  const findings = [];
  const parents = /* @__PURE__ */ new Map();
  const push = (parent, m) => {
    if (!parent) return;
    const arr = parents.get(parent) ?? [];
    arr.push(m);
    parents.set(parent, arr);
  };
  const insideProgressbar = (el) => el.parentElement?.closest("[role=progressbar]") != null;
  for (const el of Array.from(root.querySelectorAll("[role=progressbar]"))) {
    if (insideProgressbar(el)) continue;
    push(el.parentElement, { kind: "role", el });
  }
  for (const el of Array.from(root.querySelectorAll('[class*="progress"]'))) {
    if (el.getAttribute("role") === "progressbar") continue;
    if (insideProgressbar(el)) continue;
    push(el.parentElement, { kind: "class", el });
  }
  for (const el of Array.from(root.querySelectorAll("*"))) {
    if (el.children.length > 0) continue;
    if (insideProgressbar(el)) continue;
    const txt = (el.textContent ?? "").trim();
    if (txt.length === 0 || txt.length > 20) continue;
    if (COUNT_RE.test(txt)) push(el.parentElement, { kind: "text", el });
  }
  for (const [parent, markers] of parents) {
    const unique = new Set(markers.map((m) => m.el));
    if (unique.size > 1) {
      findings.push({
        libId: LIB_ID19,
        code: "STR_PROGRESS_INDICATOR_COUNT_GT_1_AT_SAME_LEVEL",
        category: "layout",
        severity: "major",
        message: `Parent <${parent.tagName.toLowerCase()}> hosts ${unique.size} progress affordances simultaneously \u2014 typically a stepper rail + N/M caption + role=progressbar. Pick one.`,
        selector: parent.tagName.toLowerCase(),
        suggestedFix: "Consolidate to ONE progress affordance per logical flow at any given DOM level. If different scopes need distinct indicators, separate them visually so the user does not double-count.",
        extras: { count: unique.size, kinds: markers.map((m) => m.kind) }
      });
    }
  }
  return findings;
}

// src/structural/duplicate-progress-disagrees.ts
var LIB_ID20 = "@norkron/web-conformance";
var COUNT_RE2 = /\b(\d+)\s*[\/z]\s*(\d+)\b/;
var CSS_VALUE_RE = /[()]|#[0-9a-fA-F]{3,8}\b|\b\d+(?:\.\d+)?(?:px|em|rem|vh|vw|deg|s|ms)\b|\b(?:rgb|rgba|hsl|hsla|oklch|oklab|lab|lch|hwb|color|var|calc|linear-gradient|radial-gradient)\b/;
function checkDuplicateProgressDisagrees(root) {
  const findings = [];
  const parsed = [];
  for (const el of Array.from(root.querySelectorAll("*"))) {
    if (el.children.length > 0) continue;
    const txt = (el.textContent ?? "").trim();
    if (txt.length === 0 || txt.length > 40) continue;
    if (CSS_VALUE_RE.test(txt)) continue;
    const m = txt.match(COUNT_RE2);
    if (!m) continue;
    const current = parseInt(m[1], 10);
    const total = parseInt(m[2], 10);
    if (Number.isNaN(current) || Number.isNaN(total)) continue;
    parsed.push({ el, current, total, text: txt });
  }
  if (parsed.length < 2) return findings;
  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      const a = parsed[i];
      const b = parsed[j];
      if (a.current !== b.current || a.total !== b.total) {
        findings.push({
          libId: LIB_ID20,
          code: "STR_DUPLICATE_PROGRESS_DISAGREES",
          category: "flow",
          severity: "critical",
          message: `Two progress captions disagree: "${a.text}" (=${a.current}/${a.total}) vs "${b.text}" (=${b.current}/${b.total}). One of them is lying to the user.`,
          selector: "main",
          suggestedFix: "Drive every progress caption from the same source-of-truth state. If two captions show different scopes (sub-step vs section), label them so the user can tell.",
          extras: { a: a.text, b: b.text }
        });
        return findings;
      }
    }
  }
  return findings;
}

// src/structural/tooltip-trigger-without-accessible-label.ts
var LIB_ID21 = "@norkron/web-conformance";
var ICON_GLYPHS = /* @__PURE__ */ new Set(["?", "!", "i", "\u2139", "?", "!", "\xD7", "x", "\u2715", "\u2716"]);
function isSingleCharIcon(text) {
  const t = text.trim();
  if (t.length === 0) return true;
  if (t.length === 1) return true;
  if (ICON_GLYPHS.has(t)) return true;
  return false;
}
function resolveLabelledBy(el, doc) {
  const ids = (el.getAttribute("aria-labelledby") ?? "").trim().split(/\s+/).filter(Boolean);
  let acc = "";
  for (const id of ids) {
    const target = doc.getElementById(id);
    if (target) acc += " " + (target.textContent ?? "");
  }
  return acc.trim();
}
function matchesTriggerSelector(el) {
  if (el.getAttribute("aria-haspopup") === "true") return true;
  const tag = el.tagName.toLowerCase();
  if (tag === "button" && el.hasAttribute("aria-expanded")) return true;
  const cls = el.className;
  if (typeof cls === "string") {
    if (/\bhelp-icon\b/.test(cls)) return true;
    if (/\btooltip-trigger\b/.test(cls)) return true;
  }
  return false;
}
function checkTooltipTriggerWithoutAccessibleLabel(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const candidates = Array.from(
    root.querySelectorAll(
      '[aria-haspopup="true"], button[aria-expanded], .help-icon, .tooltip-trigger'
    )
  );
  for (const el of candidates) {
    if (!matchesTriggerSelector(el)) continue;
    const ariaLabel = (el.getAttribute("aria-label") ?? "").trim();
    if (ariaLabel.length > 0) continue;
    const labelled = resolveLabelledBy(el, doc);
    if (labelled.length > 0) continue;
    const text = (el.textContent ?? "").trim();
    if (!isSingleCharIcon(text)) continue;
    findings.push({
      libId: LIB_ID21,
      code: "STR_TOOLTIP_TRIGGER_WITHOUT_ACCESSIBLE_LABEL",
      category: "a11y",
      severity: "major",
      message: `Tooltip/help trigger (${el.tagName.toLowerCase()}${el.className ? "." + String(el.className).split(/\s+/).join(".") : ""}) has no aria-label, no aria-labelledby, and only "${text}" as visible text \u2014 screen readers will announce "button" with no meaning.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Add `aria-label="<purpose>"` (e.g. "Show currency help") to the trigger, or wire `aria-labelledby` to a non-empty text node nearby.',
      extras: {
        visibleText: text,
        hasAriaHaspopup: el.getAttribute("aria-haspopup") === "true"
      }
    });
  }
  return findings;
}

// src/structural/segmented-control-no-visible-border.ts
var LIB_ID22 = "@norkron/web-conformance";
var SEGMENT_DESCENDANT_ATOMS = /__(?:label|indicator|track|chassis|slider|knob|background)(?:\s|--|$)/;
function isSegmentCandidate(el) {
  const role = el.getAttribute("role");
  if (role === "tab" || role === "tablist") return false;
  if (role === "radio") {
    const radiogroup = el.closest('[role="radiogroup"]');
    const rgCls = radiogroup && typeof radiogroup.className === "string" ? radiogroup.className : "";
    if (rgCls && /segmented/.test(rgCls)) return true;
  }
  const cls = typeof el.className === "string" ? el.className : "";
  if (!cls) return false;
  if (SEGMENT_DESCENDANT_ATOMS.test(cls)) return false;
  if (/segmented/.test(cls)) return true;
  if (/segment-control/.test(cls)) return true;
  return false;
}
function isSelected(el) {
  if (el.getAttribute("aria-selected") === "true") return true;
  const cls = el.className;
  if (typeof cls === "string") {
    if (/\bis-active\b/.test(cls)) return true;
    if (/\bis-selected\b/.test(cls)) return true;
  }
  return false;
}
function colorDistance(a, b) {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}
function borderWidthIsZero(style) {
  const bw = (style.borderWidth ?? "").trim();
  const bs = (style.borderStyle ?? "").trim();
  if (bs === "none" || bs === "") return true;
  if (bw === "" || bw === "0" || bw === "0px") return true;
  const parts = [style.borderTopWidth, style.borderRightWidth, style.borderBottomWidth, style.borderLeftWidth];
  const anyNonZero = parts.some((p) => {
    const v = (p ?? "").trim();
    return v.length > 0 && v !== "0" && v !== "0px";
  });
  return !anyNonZero;
}
function styleResolver(win) {
  if (win && typeof win.getComputedStyle === "function") {
    return (el) => win.getComputedStyle(el);
  }
  return (el) => el.style ?? {};
}
function resolveBackgroundColorInline(el) {
  let cur = el;
  while (cur) {
    const inline = cur.style;
    const bg = parseCssColor(inline?.backgroundColor ?? "");
    if (bg && bg[3] > 0.999) return [bg[0], bg[1], bg[2]];
    cur = cur.parentElement;
  }
  return [255, 255, 255];
}
function checkSegmentedControlNoVisibleBorder(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win) return findings;
  const getStyle = styleResolver(win);
  const hasComputed = typeof win.getComputedStyle === "function";
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) return findings;
  } catch {
  }
  const candidates = Array.from(
    root.querySelectorAll('[role="radio"], [class*="segmented"], [class*="segment-control"]')
  ).filter((el) => isElement(el, win) && isSegmentCandidate(el));
  for (const el of candidates) {
    if (isSelected(el)) continue;
    const style = getStyle(el);
    if (!borderWidthIsZero(style)) continue;
    const bg = parseCssColor(style.backgroundColor ?? "");
    if (!bg || bg[3] < 0.1) {
      findings.push({
        libId: LIB_ID22,
        code: "STR_SEGMENTED_CONTROL_NO_VISIBLE_BORDER",
        category: "a11y",
        severity: "major",
        message: `Unselected segment "${(el.textContent ?? "").trim().slice(0, 40)}" has no border and a transparent background \u2014 segment is invisible.`,
        selector: el.tagName.toLowerCase(),
        suggestedFix: "Give unselected segments a 1px border or a distinct (\u226516 sRGB units away from parent) background.",
        extras: { reason: "transparent-bg" }
      });
      continue;
    }
    const parent = el.parentElement;
    if (!parent) continue;
    const parentBg = hasComputed ? resolveBackgroundColor(parent, win) : resolveBackgroundColorInline(parent);
    const dist = colorDistance([bg[0], bg[1], bg[2]], parentBg);
    if (dist < 16) {
      findings.push({
        libId: LIB_ID22,
        code: "STR_SEGMENTED_CONTROL_NO_VISIBLE_BORDER",
        category: "a11y",
        severity: "major",
        message: `Unselected segment "${(el.textContent ?? "").trim().slice(0, 40)}" has no border and a background ${dist.toFixed(1)} sRGB units from parent \u2014 segment edge is invisible.`,
        selector: el.tagName.toLowerCase(),
        suggestedFix: "Add a 1px border on unselected segments, OR pick a background \u226516 sRGB units away from the parent.",
        extras: {
          segmentBg: rgbToHex([bg[0], bg[1], bg[2]]),
          parentBg: rgbToHex(parentBg),
          distance: dist.toFixed(1)
        }
      });
    }
  }
  return findings;
}

// src/structural/native-checkbox-in-dark-theme.ts
var LIB_ID23 = "@norkron/web-conformance";
function checkNativeCheckboxInDarkTheme(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const body = doc.body;
  if (!body) return findings;
  const bodyStyle = win.getComputedStyle(body);
  const bodyBg = parseCssColor(bodyStyle.backgroundColor ?? "");
  if (!bodyBg) return findings;
  const lum = srgbLuminance(bodyBg[0], bodyBg[1], bodyBg[2]);
  if (lum >= 0.5) return findings;
  const inputs = Array.from(root.querySelectorAll('input[type="checkbox"]'));
  for (const el of inputs) {
    const style = win.getComputedStyle(el);
    const appearance = (style.appearance ?? "").trim() || (style["-webkit-appearance"] ?? "").trim();
    if (appearance === "none") continue;
    findings.push({
      libId: LIB_ID23,
      code: "STR_NATIVE_CHECKBOX_IN_DARK_THEME",
      category: "a11y",
      severity: "major",
      message: `Native checkbox rendered on a dark page (body luminance ${lum.toFixed(
        3
      )}) without \`appearance: none\` \u2014 the UA-default thin grey square is nearly invisible against the dark bg.`,
      selector: 'input[type="checkbox"]',
      suggestedFix: "Apply `appearance: none` plus a lib-themed checkbox style (e.g. `@norkron/web-checkbox`) so the control has a deliberate dark-theme presentation.",
      extras: { bodyLuminance: lum.toFixed(3), appearance }
    });
  }
  return findings;
}

// src/structural/multi-select-visually-ambiguous-vs-enum.ts
var LIB_ID24 = "@norkron/web-conformance";
function fingerprint(el, win) {
  const s = win.getComputedStyle(el);
  const bg = (s.backgroundColor ?? "").trim();
  const bw = (s.borderWidth ?? "").trim();
  const bs = (s.borderStyle ?? "").trim();
  const bc = (s.borderColor ?? "").trim();
  const p = (s.padding ?? "").trim();
  return `bg=${bg}|border=${bw} ${bs} ${bc}|padding=${p}`;
}
function checkMultiSelectVisuallyAmbiguousVsEnum(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const allRadios = Array.from(root.querySelectorAll('[role="radio"]'));
  if (allRadios.length === 0) return findings;
  const radioFingerprints = new Set(allRadios.map((r) => fingerprint(r, win)));
  const groups = Array.from(root.querySelectorAll('[role="group"]'));
  for (const grp of groups) {
    const checkboxes = Array.from(grp.querySelectorAll('[role="checkbox"]'));
    if (checkboxes.length < 2) continue;
    const fps = checkboxes.map((c) => fingerprint(c, win));
    const allMatch = fps.every((fp) => radioFingerprints.has(fp));
    if (!allMatch) continue;
    findings.push({
      libId: LIB_ID24,
      code: "STR_MULTI_SELECT_VISUALLY_AMBIGUOUS_VS_ENUM",
      category: "a11y",
      severity: "minor",
      message: `Checkbox group of ${checkboxes.length} items has the same visual fingerprint (bg/border/padding) as a radio group elsewhere on the page. Users cannot tell "pick all that apply" from "pick one" from visuals alone.`,
      selector: '[role="group"]',
      suggestedFix: "Use distinct visual chrome for multi-select (e.g. square corner radius / checkmark glyph) vs single-select (round radio dot), or label the group with a programmatic affordance hint.",
      extras: {
        checkboxCount: checkboxes.length,
        fingerprint: fps[0]
      }
    });
  }
  return findings;
}

// src/structural/color-used-as-semantic-without-legend.ts
var LIB_ID25 = "@norkron/web-conformance";
function colorDistance2(a, b) {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}
function functionalKey(el) {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role") ?? "";
  const cls = typeof el.className === "string" ? el.className.trim() : "";
  return `${tag}|${role}|${cls}`;
}
function hasLegend(region) {
  if (region.querySelector(".legend")) return true;
  if (region.querySelector('[role="img"][aria-label]')) return true;
  return false;
}
function checkColorUsedAsSemanticWithoutLegend(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const regions = Array.from(
    root.querySelectorAll(
      '[data-flow-bind*="review"], .review-card, .review-region'
    )
  );
  for (const region of regions) {
    if (hasLegend(region)) continue;
    const groups = /* @__PURE__ */ new Map();
    const all = Array.from(region.querySelectorAll("*"));
    for (const el of all) {
      const text = (el.textContent ?? "").trim();
      if (text.length === 0) continue;
      const hasChildText = Array.from(el.children).some(
        (c2) => (c2.textContent ?? "").trim().length > 0
      );
      if (hasChildText) continue;
      const style = win.getComputedStyle(el);
      const c = parseCssColor(style.color ?? "");
      if (!c || c[3] < 0.5) continue;
      const key = functionalKey(el);
      const triple = [c[0], c[1], c[2]];
      const arr = groups.get(key);
      if (arr) arr.push({ el, color: triple });
      else groups.set(key, [{ el, color: triple }]);
    }
    let flagged = false;
    for (const [key, items] of groups) {
      if (items.length < 2) continue;
      let maxDist = 0;
      let pairA = null;
      let pairB = null;
      for (let i = 0; i < items.length && !flagged; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const d = colorDistance2(items[i].color, items[j].color);
          if (d > maxDist) {
            maxDist = d;
            pairA = items[i].color;
            pairB = items[j].color;
          }
        }
      }
      if (maxDist >= 30) {
        findings.push({
          libId: LIB_ID25,
          code: "STR_COLOR_USED_AS_SEMANTIC_WITHOUT_LEGEND",
          category: "a11y",
          severity: "major",
          message: `Review region uses \u22652 distinct text fills (sRGB distance ${maxDist.toFixed(
            1
          )}) on functionally similar elements (${key}) with no legend explaining the encoding. WCAG SC 1.4.1 \u2014 color must not be the only means of conveying information.`,
          selector: ".review-card, .review-region",
          suggestedFix: 'Add a `.legend` element (or `[role="img"][aria-label]`) inside the region explaining what each color means. Alternatively, supplement color with an icon or text label.',
          extras: {
            functionalKey: key,
            colorDistance: maxDist.toFixed(1),
            colorA: pairA,
            colorB: pairB
          }
        });
        flagged = true;
        break;
      }
    }
  }
  return findings;
}

// src/structural/brand-palette-drift-intra-product.ts
var LIB_ID26 = "@norkron/web-conformance";
var DISTANCE_THRESHOLD2 = 30;
function distance(a, b) {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}
function isAccent(rgb) {
  const max = Math.max(rgb[0], rgb[1], rgb[2]);
  const min = Math.min(rgb[0], rgb[1], rgb[2]);
  if (max < 32) return false;
  if (min > 224) return false;
  if (max - min < 24) return false;
  return true;
}
var SEMANTIC_VARIANTS_ATTR = "data-conformance-semantic-variants";
function hasSemanticVariantsOptOut(root) {
  const checkEl = (el) => {
    if (!el || typeof el.getAttribute !== "function") return false;
    if (!el.hasAttribute(SEMANTIC_VARIANTS_ATTR)) return false;
    const v = el.getAttribute(SEMANTIC_VARIANTS_ATTR);
    return v !== "false";
  };
  if (checkEl(root)) return true;
  if (typeof root.querySelector === "function") {
    const inside = root.querySelector(`[${SEMANTIC_VARIANTS_ATTR}]`);
    if (inside && checkEl(inside)) return true;
  }
  if (typeof root.closest === "function") {
    const ancestor = root.closest(`[${SEMANTIC_VARIANTS_ATTR}]`);
    if (ancestor && ancestor !== root && checkEl(ancestor)) return true;
  }
  return false;
}
function checkBrandPaletteDriftIntraProduct(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  if (hasSemanticVariantsOptOut(root)) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const candidates = Array.from(
    root.querySelectorAll(
      "button, [role=button], .badge, [class*=badge], [class*=chip], [class*=pill]"
    )
  );
  const hues = [];
  for (const el of candidates) {
    const style = win.getComputedStyle(el);
    const bg = parseCssColor(style.backgroundColor ?? "");
    if (bg && bg[3] >= 0.5 && isAccent([bg[0], bg[1], bg[2]])) {
      hues.push([bg[0], bg[1], bg[2]]);
    }
    const border = parseCssColor(style.borderTopColor ?? "");
    if (border && border[3] >= 0.5 && isAccent([border[0], border[1], border[2]])) {
      hues.push([border[0], border[1], border[2]]);
    }
  }
  const distinct = [];
  for (const h of hues) {
    let matched = false;
    for (const d of distinct) {
      if (distance(h, d) < DISTANCE_THRESHOLD2) {
        matched = true;
        break;
      }
    }
    if (!matched) distinct.push(h);
  }
  if (distinct.length >= 3) {
    findings.push({
      libId: LIB_ID26,
      code: "STR_BRAND_PALETTE_DRIFT_INTRA_PRODUCT",
      category: "theme",
      severity: "minor",
      message: `Page uses ${distinct.length} mutually distinct accent hues for button/badge/border slots \u2014 brand-palette drift. Pick one accent token (or one accent + one warning) and use it consistently across consumers.`,
      selector: "button, .badge",
      suggestedFix: "Consolidate to a single themed --accent token (and optionally one --warning/--success). Each consumer should request a palette slot, not hardcode its own hue.",
      extras: { distinctHueCount: distinct.length, hues: distinct.map((h) => rgbToHex(h)) }
    });
  }
  return findings;
}

// src/structural/text-size-below-min.ts
var LIB_ID27 = "@norkron/web-conformance";
var MIN_FONT_PX = 12;
function isAriaHidden2(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute("aria-hidden") === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function checkTextSizeBelowMin(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  for (const el of Array.from(root.querySelectorAll("*"))) {
    if (el.children.length > 0) continue;
    const text = (el.textContent ?? "").trim();
    if (text.length === 0) continue;
    if (isAriaHidden2(el)) continue;
    const style = win.getComputedStyle(el);
    const fontSize = parseFloat(style.fontSize ?? "16");
    if (Number.isNaN(fontSize) || fontSize === 0) continue;
    if (fontSize >= MIN_FONT_PX) continue;
    findings.push({
      libId: LIB_ID27,
      code: "STR_TEXT_SIZE_BELOW_MIN",
      category: "a11y",
      severity: "major",
      message: `Visible text "${text.slice(0, 40)}" is rendered at ${fontSize}px \u2014 below the ${MIN_FONT_PX}px minimum for readable body copy.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Bump font-size to \u226512px (use a token like --font-size-xs from web-themer). For purely decorative captions, set aria-hidden="true".',
      extras: { fontSize, minimum: MIN_FONT_PX }
    });
  }
  return findings;
}

// src/structural/html-lang-matches-locale.ts
var LIB_ID28 = "@norkron/web-conformance";
var KNOWN_LOCALE_PREFIXES = ["en", "pl", "de"];
function urlLocale(path) {
  const m = path.match(/^\/([a-z]{2})(?:\/|$)/);
  if (!m) return null;
  const code = m[1].toLowerCase();
  if (KNOWN_LOCALE_PREFIXES.includes(code)) return code;
  return null;
}
function checkHtmlLangMatchesLocale(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const html = doc.documentElement;
  if (!html) return findings;
  if (root.children.length === 0 && (root.textContent ?? "").trim().length === 0) {
    return findings;
  }
  const lang = (html.getAttribute("lang") ?? "").trim().toLowerCase();
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  const path = win?.location?.pathname ?? "";
  const urlLoc = urlLocale(path);
  if (lang.length === 0) {
    findings.push({
      libId: LIB_ID28,
      code: "STR_HTML_LANG_MATCHES_LOCALE",
      category: "i18n",
      severity: "major",
      message: `<html> has no lang attribute. Screen readers cannot pick a pronunciation profile. WCAG SC 3.1.1 requires a language declaration.`,
      selector: "html",
      suggestedFix: 'Set `<html lang="...">` to a BCP-47 tag (e.g. "en", "pl", "de"). If the app supports multiple locales, update the attribute on locale change.',
      extras: { lang: null, urlLocale: urlLoc }
    });
    return findings;
  }
  if (urlLoc && !lang.startsWith(urlLoc)) {
    findings.push({
      libId: LIB_ID28,
      code: "STR_HTML_LANG_MATCHES_LOCALE",
      category: "i18n",
      severity: "major",
      message: `URL prefix /${urlLoc}/ disagrees with <html lang="${lang}"> \u2014 visitors on the ${urlLoc} locale see content tagged as ${lang}. Mismatched lang attributes confuse screen readers and translation tools.`,
      selector: "html",
      suggestedFix: `Update <html lang> on locale change so it matches the URL prefix (e.g. "${urlLoc}" for /${urlLoc}/...).`,
      extras: { lang, urlLocale: urlLoc }
    });
  }
  return findings;
}

// src/structural/child-overflows-parent-bounds.ts
var LIB_ID29 = "@norkron/web-conformance";
var OVERFLOW_TOLERANCE_PX = 4;
function parentIntentionallyClips(style) {
  const ox = (style.overflowX ?? "").trim();
  const oy = (style.overflowY ?? "").trim();
  const clippy = (v) => v === "hidden" || v === "clip" || v === "scroll" || v === "auto";
  return clippy(ox) || clippy(oy);
}
function checkChildOverflowsParentBounds(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  if (typeof root.getBoundingClientRect !== "function") {
    findings.push(needsLayoutFinding5());
    return findings;
  }
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(needsLayoutFinding5());
    return findings;
  }
  const all = root.querySelectorAll("*");
  const reported = /* @__PURE__ */ new Set();
  const SVG_METADATA_TAGS = /* @__PURE__ */ new Set(["DESC", "TITLE", "METADATA"]);
  const isUnderIgnore = (el) => {
    let cur = el;
    while (cur && cur !== root.parentElement) {
      if (cur.getAttribute && cur.getAttribute("data-conformance-ignore") !== null) return true;
      cur = cur.parentElement;
    }
    return false;
  };
  all.forEach((el) => {
    if (reported.has(el)) return;
    if (SVG_METADATA_TAGS.has(el.tagName.toUpperCase())) return;
    if (el.namespaceURI && el.namespaceURI.endsWith("/svg")) return;
    if (isUnderIgnore(el)) return;
    if (el.tagName.toUpperCase() === "CANVAS") return;
    let elStyle;
    try {
      elStyle = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (elStyle.position === "absolute" || elStyle.position === "fixed") return;
    let collapsedAncestor = false;
    {
      let cur = el.parentElement;
      while (cur && cur !== root.parentElement) {
        try {
          const cs = win.getComputedStyle(cur);
          const ox = (cs.overflowX ?? "").trim();
          const oy = (cs.overflowY ?? "").trim();
          const clipsHard = ox === "hidden" || ox === "clip" || oy === "hidden" || oy === "clip";
          if (clipsHard) {
            const cr = cur.getBoundingClientRect();
            if (cr.width <= 1 || cr.height <= 1) {
              collapsedAncestor = true;
              break;
            }
          }
        } catch {
        }
        cur = cur.parentElement;
      }
    }
    if (collapsedAncestor) return;
    const parent = el.parentElement ?? el.offsetParent ?? null;
    if (!parent || parent === doc.documentElement || parent === doc.body) {
      return;
    }
    let parentStyle;
    try {
      parentStyle = win.getComputedStyle(parent);
    } catch {
      return;
    }
    if (parentIntentionallyClips(parentStyle)) {
      return;
    }
    {
      let cur = parent;
      let allowFromAncestor = false;
      while (cur && cur !== root.parentElement) {
        if (cur.getAttribute && cur.getAttribute("data-allow-child-overflow") !== null) {
          allowFromAncestor = true;
          break;
        }
        cur = cur.parentElement;
      }
      if (allowFromAncestor) return;
    }
    const stampedAncestor = el.closest("[data-norkron-lib]");
    if (stampedAncestor && stampedAncestor.contains(parent)) return;
    const childRect = el.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    if (childRect.width === 0 && childRect.height === 0) return;
    if (parentRect.width === 0 && parentRect.height === 0) return;
    const pxVal = (s) => {
      if (!s) return 0;
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : 0;
    };
    const padL = pxVal(parentStyle.paddingLeft);
    const padR = pxVal(parentStyle.paddingRight);
    const padT = pxVal(parentStyle.paddingTop);
    const padB = pxVal(parentStyle.paddingBottom);
    const borL = pxVal(parentStyle.borderLeftWidth);
    const borR = pxVal(parentStyle.borderRightWidth);
    const borT = pxVal(parentStyle.borderTopWidth);
    const borB = pxVal(parentStyle.borderBottomWidth);
    const contentLeft = parentRect.left + padL + borL;
    const contentRight = parentRect.right - padR - borR;
    const contentTop = parentRect.top + padT + borT;
    const contentBottom = parentRect.bottom - padB - borB;
    const overflowLeft = contentLeft - childRect.left;
    const overflowRight = childRect.right - contentRight;
    const overflowTop = contentTop - childRect.top;
    const overflowBottom = childRect.bottom - contentBottom;
    const escapes = overflowLeft > OVERFLOW_TOLERANCE_PX || overflowRight > OVERFLOW_TOLERANCE_PX || overflowTop > OVERFLOW_TOLERANCE_PX || overflowBottom > OVERFLOW_TOLERANCE_PX;
    if (!escapes) return;
    reported.add(el);
    findings.push({
      libId: LIB_ID29,
      code: "STR_CHILD_OVERFLOWS_PARENT",
      category: "layout",
      severity: "major",
      message: `<${el.tagName.toLowerCase()}> escapes its <${parent.tagName.toLowerCase()}> parent's content-box (left=${Math.round(overflowLeft)}px right=${Math.round(overflowRight)}px top=${Math.round(overflowTop)}px bottom=${Math.round(overflowBottom)}px) while parent overflow is visible.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: "Either constrain the child (max-width/100%, correct position offsets), or, if the visual overflow is intentional, set the parent overflow to hidden/clip/scroll/auto so the opt-in is explicit.",
      extras: {
        overflowLeft,
        overflowRight,
        overflowTop,
        overflowBottom,
        tolerance: OVERFLOW_TOLERANCE_PX,
        childRect: {
          left: childRect.left,
          right: childRect.right,
          top: childRect.top,
          bottom: childRect.bottom,
          width: childRect.width,
          height: childRect.height
        },
        parentRect: {
          left: parentRect.left,
          right: parentRect.right,
          top: parentRect.top,
          bottom: parentRect.bottom,
          width: parentRect.width,
          height: parentRect.height
        },
        parentContentBox: {
          left: contentLeft,
          right: contentRight,
          top: contentTop,
          bottom: contentBottom
        },
        parentPadding: { top: padT, right: padR, bottom: padB, left: padL },
        parentBorder: { top: borT, right: borR, bottom: borB, left: borL },
        parentOverflowX: parentStyle.overflowX,
        parentOverflowY: parentStyle.overflowY
      }
    });
  });
  return findings;
}
function needsLayoutFinding5() {
  return needsLayoutFinding4("STR_CHILD_OVERFLOWS_PARENT");
}

// src/structural/lib-not-instantiated.ts
var LIB_ID30 = "@norkron/web-conformance";
var STAMP_ANCESTOR_DEPTH = 3;
var LIB_DOM_SIGNATURES = [
  {
    primitive: "combobox",
    roles: ["combobox"],
    classPatterns: [],
    suggestedLib: "@norkron/web-combobox",
    alternativeLibs: []
  },
  {
    primitive: "dialog",
    roles: ["dialog", "alertdialog"],
    classPatterns: [/(?:^|[^a-z0-9])dialog__/i],
    suggestedLib: "@norkron/web-dialog",
    alternativeLibs: []
  },
  {
    primitive: "tablist",
    roles: ["tablist"],
    classPatterns: [/(?:^|[^a-z0-9])tabs__/i],
    suggestedLib: "@norkron/web-tabs",
    alternativeLibs: []
  },
  {
    primitive: "menu",
    roles: ["menu"],
    classPatterns: [/(?:^|[^a-z0-9])menu__/i],
    suggestedLib: "@norkron/web-menu",
    alternativeLibs: []
  },
  {
    primitive: "tree",
    roles: ["tree"],
    classPatterns: [],
    suggestedLib: "@norkron/web-tree",
    alternativeLibs: []
  },
  {
    primitive: "tooltip",
    roles: ["tooltip"],
    classPatterns: [/(?:^|[^a-z0-9])tooltip__/i],
    suggestedLib: "@norkron/web-tooltip",
    alternativeLibs: []
  },
  {
    primitive: "progressbar",
    roles: ["progressbar"],
    classPatterns: [],
    suggestedLib: "@norkron/web-wizard",
    alternativeLibs: ["@norkron/web-states"]
  },
  {
    primitive: "popover",
    roles: [],
    classPatterns: [/(?:^|[^a-z0-9])popover__/i],
    suggestedLib: "@norkron/web-popover",
    alternativeLibs: []
  }
];
function isDecorativeOverlayBackdrop(el) {
  const cls = el.getAttribute("class") ?? "";
  const isOverlayClass = /(?:^|[^a-z0-9])(?:dialog|popover|drawer)__overlay(?![a-z0-9_-])/i.test(cls);
  if (!isOverlayClass) return false;
  const role = el.getAttribute("role");
  if (role && role !== "presentation" && role !== "none") return false;
  const ariaHidden = el.getAttribute("aria-hidden");
  return ariaHidden === "true" || ariaHidden === "";
}
function matchesLibDom(el, sig) {
  const role = el.getAttribute("role");
  if (role && sig.roles.includes(role)) return true;
  const cls = (el.getAttribute("class") ?? "").trim();
  if (cls.length > 0) {
    for (const p of sig.classPatterns) {
      if (p.test(cls)) return true;
    }
  }
  return false;
}
function hasMatchingStamp(el, _owner, _alternatives, depth) {
  let cur = el;
  let walked = 0;
  while (cur && walked <= depth) {
    const raw = cur.getAttribute("data-norkron-lib");
    if (raw && raw.trim().length > 0) return true;
    cur = cur.parentElement;
    walked += 1;
  }
  return false;
}
function hasAllowUnwiredEscape(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute("data-allow-unwired") === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function selectorFor4(el) {
  const cls = (el.getAttribute("class") ?? "").trim();
  const id = el.getAttribute("id");
  const role = el.getAttribute("role");
  const tag = el.tagName.toLowerCase();
  if (id) return `${tag}#${id}`;
  if (cls.length > 0) {
    const first = cls.split(/\s+/)[0];
    return `${tag}.${first}`;
  }
  if (role) return `${tag}[role="${role}"]`;
  return tag;
}
function checkLibNotInstantiated(root) {
  const findings = [];
  const all = Array.from(root.querySelectorAll("*"));
  for (const el of all) {
    for (const sig of LIB_DOM_SIGNATURES) {
      if (!matchesLibDom(el, sig)) continue;
      if (isDecorativeOverlayBackdrop(el)) continue;
      if (hasMatchingStamp(
        el,
        sig.suggestedLib,
        sig.alternativeLibs,
        STAMP_ANCESTOR_DEPTH
      )) {
        continue;
      }
      if (hasAllowUnwiredEscape(el)) continue;
      const sel = selectorFor4(el);
      const altList = [sig.suggestedLib, ...sig.alternativeLibs];
      findings.push({
        libId: LIB_ID30,
        code: "STR_LIB_NOT_INSTANTIATED",
        category: "identity",
        severity: "major",
        message: `Element \`${sel}\` matches the \`${sig.primitive}\` lib signature but is missing \`data-norkron-lib="${sig.suggestedLib}"\` \u2014 the canonical lib appears to be uninstantiated.`,
        selector: sel,
        suggestedFix: `Instantiate \`${sig.suggestedLib}\` (or one of: ${altList.join(", ")}) and let it stamp this element; or set \`data-allow-unwired="true"\` if this is intentional standalone DOM.`,
        extras: {
          signature: sig.primitive,
          suggestedLib: sig.suggestedLib,
          alternativeLibs: sig.alternativeLibs
        }
      });
      break;
    }
  }
  return findings;
}

// src/structural/_displayed-background.ts
function getCentroid(el) {
  const hel = el;
  if (typeof hel.getBoundingClientRect !== "function") return null;
  const r = hel.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}
function sampleGradient(gradientCss) {
  const isLinear = /^linear-gradient\(/i.test(gradientCss);
  const isRadial = /^radial-gradient\(/i.test(gradientCss);
  if (!isLinear && !isRadial) return null;
  const open = gradientCss.indexOf("(");
  const close = gradientCss.lastIndexOf(")");
  if (open < 0 || close <= open) return null;
  const inner = gradientCss.slice(open + 1, close);
  const stops = [];
  let depth = 0;
  let cur = "";
  for (const ch of inner) {
    if (ch === "(") depth++;
    if (ch === ")") depth--;
    if (ch === "," && depth === 0) {
      stops.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  if (cur.trim()) stops.push(cur.trim());
  const colorStops = stops.filter(
    (s) => !/^(to\s|[\d.]+deg|[\d.]+turn|[\d.]+rad|circle|ellipse|at\s)/i.test(s) && s.length > 0
  );
  if (colorStops.length < 2) return null;
  const first = parseCssColor(colorStops[0].split(/\s+/)[0]);
  const last = parseCssColor(colorStops[colorStops.length - 1].split(/\s+/)[0]);
  if (!first || !last) return null;
  const rgb = [
    (first[0] + last[0]) / 2,
    (first[1] + last[1]) / 2,
    (first[2] + last[2]) / 2
  ];
  return { rgb, source: isLinear ? "linear-at-centroid" : "radial-at-centroid" };
}
function findZIndexIntruder(el, ctx) {
  if (!ctx.centroid) return null;
  const ef = ctx.doc.elementsFromPoint;
  if (typeof ef !== "function") return "unavailable";
  const stack = ctx.doc.elementsFromPoint(ctx.centroid.x, ctx.centroid.y);
  if (!Array.isArray(stack) || stack.length === 0) return null;
  const ancestorsOfEl = /* @__PURE__ */ new Set();
  let walk = el;
  while (walk) {
    ancestorsOfEl.add(walk);
    walk = walk.parentElement;
  }
  for (const candidate of stack) {
    if (candidate === el) return null;
    if (el.contains(candidate)) continue;
    if (ancestorsOfEl.has(candidate)) {
      return null;
    }
    let scopeIsDecorative = false;
    let scopeWalk = candidate;
    while (scopeWalk) {
      if (scopeWalk.getAttribute("aria-hidden") === "true") {
        scopeIsDecorative = true;
        break;
      }
      if (scopeWalk.hasAttribute("data-decorative")) {
        scopeIsDecorative = true;
        break;
      }
      scopeWalk = scopeWalk.parentElement;
    }
    if (scopeIsDecorative) continue;
    return candidate;
  }
  return null;
}
function resolveDisplayedBackground(el) {
  const doc = el.ownerDocument ?? el;
  const win = doc?.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return { kind: "uncertain", reason: "no-getComputedStyle" };
  }
  const ctx = {
    win,
    doc,
    centroid: getCentroid(el)
  };
  let inPortaledSubtree = false;
  let portalScan = el;
  while (portalScan) {
    const ps = win.getComputedStyle(portalScan);
    const psPos = ps.position;
    if (psPos === "fixed" || psPos === "absolute") {
      inPortaledSubtree = true;
      break;
    }
    portalScan = portalScan.parentElement;
  }
  if (inPortaledSubtree) {
    return resolveAncestorStack(el, ctx);
  }
  const intruder = findZIndexIntruder(el, ctx);
  if (intruder === "unavailable") ; else if (intruder) {
    return resolveAncestorStack(intruder, ctx);
  }
  return resolveAncestorStack(el, ctx);
}
function hasPositionedAncestor(el, ctx) {
  let p = el.parentElement;
  while (p && p !== ctx.doc.documentElement) {
    const pos = ctx.win.getComputedStyle(p).position;
    if (pos === "relative" || pos === "absolute" || pos === "fixed" || pos === "sticky") {
      return true;
    }
    p = p.parentElement;
  }
  return false;
}
function resolveAncestorStack(start, ctx) {
  const layers = [];
  let cur = start;
  let opaqueBase = null;
  let gradientSample = null;
  let imageBgEncountered = false;
  let backdropFilterEncountered = false;
  let crossedPortalBoundary = false;
  while (cur) {
    const style = ctx.win.getComputedStyle(cur);
    const bdf = style.backdropFilter;
    if (bdf && bdf !== "none" && bdf.trim() !== "") {
      backdropFilterEncountered = true;
    }
    const bgImage = (style.backgroundImage ?? "").trim();
    if (bgImage && bgImage !== "none") {
      const gs = sampleGradient(bgImage);
      if (gs) {
        gradientSample = gs;
        break;
      }
      if (/^url\(/i.test(bgImage)) {
        imageBgEncountered = true;
      }
    }
    const bg = parseCssColor(style.backgroundColor ?? "");
    if (bg && bg[3] > 0) {
      if (bg[3] > 0.999) {
        opaqueBase = [bg[0], bg[1], bg[2]];
        break;
      }
      layers.push(bg);
    }
    const pos = style.position;
    if (pos === "fixed") {
      crossedPortalBoundary = true;
      break;
    }
    if (pos === "absolute" && !hasPositionedAncestor(cur, ctx)) {
      crossedPortalBoundary = true;
      break;
    }
    cur = cur.parentElement;
  }
  if (crossedPortalBoundary && opaqueBase === null && !gradientSample) {
    const body = ctx.doc.body;
    if (body && typeof ctx.win.getComputedStyle === "function") {
      try {
        const bodyStyle = ctx.win.getComputedStyle(body);
        const bodyBg = parseCssColor(bodyStyle.backgroundColor ?? "");
        if (bodyBg && bodyBg[3] > 0.999) {
          opaqueBase = [bodyBg[0], bodyBg[1], bodyBg[2]];
        }
      } catch {
      }
    }
  }
  if (gradientSample) {
    let base2 = gradientSample.rgb;
    for (let i = layers.length - 1; i >= 0; i--) base2 = compositeOver(layers[i], base2);
    if (layers.length > 0) {
      return { kind: "composited-stack", layers, finalRgb: base2 };
    }
    return { kind: "gradient", sampledRgb: base2, sampleSource: gradientSample.source };
  }
  if (backdropFilterEncountered) {
    return { kind: "uncertain", reason: "backdrop-filter" };
  }
  if (imageBgEncountered && opaqueBase === null) {
    return { kind: "image-or-pattern", sampledRgb: null, reason: "unsamplable" };
  }
  const base = opaqueBase ?? [255, 255, 255];
  if (layers.length === 0) {
    return { kind: "opaque-solid", rgb: base };
  }
  let composed = base;
  for (let i = layers.length - 1; i >= 0; i--) composed = compositeOver(layers[i], composed);
  return { kind: "composited-stack", layers, finalRgb: composed };
}
function displayedBgRgb(bg) {
  switch (bg.kind) {
    case "opaque-solid":
      return bg.rgb;
    case "composited-stack":
      return bg.finalRgb;
    case "gradient":
      return bg.sampledRgb;
    case "image-or-pattern":
      return bg.sampledRgb;
    case "uncertain":
      return null;
  }
}

// src/structural/actual-displayed-background-contrast.ts
var LIB_ID31 = "@norkron/web-conformance";
function isExcluded3(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute("aria-hidden") === "true") return true;
    if (cur.hasAttribute("data-decorative")) return true;
    cur = cur.parentElement;
  }
  return false;
}
function buildSelector(el) {
  const tag = el.tagName.toLowerCase();
  const id = el.getAttribute("id");
  if (id) return `${tag}#${id}`;
  const testid = el.getAttribute("data-testid");
  if (testid) return `${tag}[data-testid="${testid}"]`;
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean)[0];
  if (cls) return `${tag}.${cls}`;
  return tag;
}
function extractForeground(el, style) {
  const tag = el.tagName.toLowerCase();
  const svgGraphical = /* @__PURE__ */ new Set(["path", "circle", "rect", "line", "polygon", "polyline", "ellipse"]);
  const isSvgGraphical = svgGraphical.has(tag);
  if (isSvgGraphical) {
    let semantic = false;
    let svgWalk = el;
    while (svgWalk) {
      if (svgWalk.getAttribute("role") === "img") {
        semantic = true;
        break;
      }
      const label = svgWalk.getAttribute("aria-label");
      if (label && label.trim().length > 0) {
        semantic = true;
        break;
      }
      if (svgWalk.getAttribute("aria-labelledby")) {
        semantic = true;
        break;
      }
      if (svgWalk.tagName.toLowerCase() === "svg") {
        for (const child of Array.from(svgWalk.children)) {
          if (child.tagName.toLowerCase() === "title" && (child.textContent ?? "").trim().length > 0) {
            semantic = true;
            break;
          }
        }
        break;
      }
      svgWalk = svgWalk.parentElement;
    }
    if (!semantic) return null;
    const fill = style.fill ?? "";
    const stroke = style.stroke ?? "";
    const pickRaw = fill && fill !== "none" ? fill : stroke && stroke !== "none" ? stroke : "";
    const fg2 = parseCssColor(pickRaw);
    if (!fg2 || fg2[3] < 0.01) return null;
    return {
      rgb: [fg2[0], fg2[1], fg2[2]],
      alpha: fg2[3],
      fontSize: 0,
      fontWeight: 0,
      isLarge: false,
      isSvgGraphical: true
    };
  }
  let hasDirectText = false;
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === 3 && (child.nodeValue ?? "").trim().length > 0) {
      hasDirectText = true;
      break;
    }
  }
  if (!hasDirectText) return null;
  const fg = parseCssColor(style.color ?? "");
  if (!fg || fg[3] < 0.01) return null;
  const fontSize = parseFloat(style.fontSize ?? "16") || 16;
  const fontWeightRaw = style.fontWeight ?? "400";
  const fontWeight = parseFloat(fontWeightRaw) || (/bold/i.test(fontWeightRaw) ? 700 : 400);
  const isLarge = fontSize >= 18 || fontSize >= 14 && fontWeight >= 700;
  return {
    rgb: [fg[0], fg[1], fg[2]],
    alpha: fg[3],
    fontSize,
    fontWeight,
    isLarge,
    isSvgGraphical: false
  };
}
function bgKindLabel(bg) {
  switch (bg.kind) {
    case "opaque-solid":
      return "opaque-solid";
    case "composited-stack":
      return `composited-stack:${bg.layers.length}-layer(s)`;
    case "gradient":
      return `gradient:${bg.sampleSource}`;
    case "image-or-pattern":
      return `image-or-pattern:${bg.reason}`;
    case "uncertain":
      return `uncertain:${bg.reason}`;
  }
}
function checkActualDisplayedBackgroundContrast(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4("STR_ACTUAL_BACKGROUND_CONTRAST_INSUFFICIENT", layout.reason)
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc?.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_ACTUAL_BACKGROUND_CONTRAST_INSUFFICIENT",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  let emittedNeedsLayoutForRun = false;
  const seen = /* @__PURE__ */ new Set();
  const walker = doc.createTreeWalker(
    root,
    1
    /* SHOW_ELEMENT */
  );
  let node = root.nodeType === 1 ? root : walker.nextNode();
  while (node) {
    const el = node;
    if (seen.has(el) || isExcluded3(el)) {
      node = walker.nextNode();
      continue;
    }
    seen.add(el);
    const style = win.getComputedStyle(el);
    const fg = extractForeground(el, style);
    if (!fg) {
      node = walker.nextNode();
      continue;
    }
    const bg = resolveDisplayedBackground(el);
    const bgRgb = displayedBgRgb(bg);
    if (bgRgb === null) {
      const envLacksLayout = bg.kind === "uncertain" && bg.reason === "no-getComputedStyle";
      if (envLacksLayout && !emittedNeedsLayoutForRun) {
        findings.push(
          needsLayoutFinding4("STR_ACTUAL_BACKGROUND_CONTRAST_INSUFFICIENT", "no-getComputedStyle")
        );
        emittedNeedsLayoutForRun = true;
      }
      node = walker.nextNode();
      continue;
    }
    const fgComposed = fg.alpha < 0.999 ? compositeOver([fg.rgb[0], fg.rgb[1], fg.rgb[2], fg.alpha], bgRgb) : fg.rgb;
    const ratio = contrastRatio(fgComposed, bgRgb);
    const threshold = fg.isSvgGraphical ? 3 : fg.isLarge ? 3 : 4.5;
    if (ratio + 1e-6 < threshold) {
      findings.push({
        libId: LIB_ID31,
        code: "STR_ACTUAL_BACKGROUND_CONTRAST_INSUFFICIENT",
        category: "a11y",
        severity: "major",
        message: `${fg.isSvgGraphical ? "SVG mark" : "Text"} on actual displayed background (kind=${bgKindLabel(bg)}) has contrast ${ratio.toFixed(2)}:1 vs WCAG minimum ${threshold}:1.`,
        selector: buildSelector(el),
        suggestedFix: fg.isSvgGraphical ? "Increase stroke/fill contrast against the actual visible background, or move the mark off the gradient/translucent overlay." : "Darken/lighten text, or make the underlying background opaque (drop alpha < 1 or remove the gradient/overlay) so contrast is computed against a known solid.",
        extras: {
          ratio: Math.round(ratio * 100) / 100,
          threshold,
          fg: rgbToHex(fgComposed),
          fgAlpha: fg.alpha,
          bg: rgbToHex(bgRgb),
          bgKind: bg.kind,
          bgKindDetail: bgKindLabel(bg),
          elTag: el.tagName.toLowerCase(),
          selector: buildSelector(el),
          fontSize: fg.fontSize,
          fontWeight: fg.fontWeight,
          isLarge: fg.isLarge,
          isSvgGraphical: fg.isSvgGraphical
        }
      });
    }
    node = walker.nextNode();
  }
  return findings;
}

// src/structural/responsive-collapse-missing.ts
var LIB_ID32 = "@norkron/web-conformance";
var OVERFLOW_THRESHOLD_PX = 2;
var LABEL_CLASS_RE = /(?:^|\s)[a-z0-9-]*(?:__|-|^)label(?:--[a-z0-9-]+)?(?=\s|$)/i;
var ICON_CLASS_RE = /(?:^|\s)[a-z0-9-]*(?:__|-|^)icon(?:--[a-z0-9-]+)?(?=\s|$)/i;
var LABEL_CLASS_PATTERNS = [LABEL_CLASS_RE];
var ICON_CLASS_PATTERNS = [ICON_CLASS_RE];
function matchesAny(classAttr, patterns) {
  return patterns.some((re) => re.test(classAttr));
}
function elementIsLabel(child) {
  if (child.getAttribute("data-collapse-label") !== null || child.getAttribute("data-responsive-collapse-label") !== null) {
    return true;
  }
  return matchesAny(child.getAttribute("class") ?? "", LABEL_CLASS_PATTERNS);
}
function elementIsIcon(child) {
  if (child.getAttribute("data-collapse-icon") !== null || child.getAttribute("data-responsive-collapse-icon") !== null) {
    return true;
  }
  if (child.getAttribute("role") === "img" && child.getAttribute("aria-hidden") === "true") {
    return true;
  }
  return matchesAny(child.getAttribute("class") ?? "", ICON_CLASS_PATTERNS);
}
function suggestedBreakpoint(currentViewport) {
  const stops = [320, 480, 768, 1024, 1280, 1920];
  for (let i = stops.length - 1; i >= 0; i--) {
    const stop = stops[i];
    if (stop !== void 0 && stop <= currentViewport) return stop;
  }
  return 480;
}
function isResponsiveCollapseCandidate(el) {
  if (el.getAttribute("data-responsive-collapse-target") === "true") return true;
  let hasIcon = false;
  let hasLabel = false;
  const children = el.querySelectorAll("*");
  children.forEach((child) => {
    if (elementIsLabel(child)) hasLabel = true;
    if (elementIsIcon(child)) hasIcon = true;
  });
  return hasIcon && hasLabel;
}
function labelStillVisible(el, win) {
  const labels = el.querySelectorAll("*");
  for (const child of Array.from(labels)) {
    if (!elementIsLabel(child)) continue;
    let style;
    try {
      style = win.getComputedStyle(child);
    } catch {
      return true;
    }
    if (style.display === "none") return false;
    if (style.visibility === "hidden") return false;
    return true;
  }
  return false;
}
function checkResponsiveCollapseMissing(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(needsLayoutFinding4("STR_RESPONSIVE_COLLAPSE_MISSING", layout.reason));
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win) {
    findings.push(needsLayoutFinding4("STR_RESPONSIVE_COLLAPSE_MISSING", "no-getComputedStyle"));
    return findings;
  }
  const viewport = win.innerWidth ?? 0;
  const candidates = root.querySelectorAll('button, [role="button"], a, nav *, [data-responsive-collapse-target="true"]');
  const seen = /* @__PURE__ */ new Set();
  candidates.forEach((el) => {
    if (seen.has(el)) return;
    if (!isResponsiveCollapseCandidate(el)) return;
    seen.add(el);
    if (!labelStillVisible(el, win)) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const availableWidth = rect.width;
    const intrinsicWidth = el.scrollWidth ?? 0;
    const overflowPx = intrinsicWidth - availableWidth;
    if (overflowPx <= OVERFLOW_THRESHOLD_PX) return;
    findings.push({
      libId: LIB_ID32,
      code: "STR_RESPONSIVE_COLLAPSE_MISSING",
      category: "layout",
      severity: "major",
      message: `<${el.tagName.toLowerCase()}> content overflows by ${Math.round(overflowPx)}px at viewport=${viewport}px \u2014 icon+label compound did not collapse for this size.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: "Hide the `__label` element below the suggested breakpoint via CSS (`@media (max-width: \u2026) { .btn__label { display:none } }`), shorten the label, or widen the parent. The label MUST be reachable via accessible name (aria-label / sr-only span) when visually hidden.",
      extras: {
        viewport,
        overflowPx,
        suggestedBreakpoint: suggestedBreakpoint(viewport),
        intrinsicWidth,
        availableWidth
      }
    });
  });
  return findings;
}

// src/structural/text-overflows-at-breakpoint.ts
var LIB_ID33 = "@norkron/web-conformance";
var TRUNCATION_TOLERANCE_PX = 1;
function hasAccessibleFullText2(el) {
  let cur = el;
  while (cur) {
    const title = cur.getAttribute?.("title");
    if (title != null && title.trim().length > 0) return true;
    const aria = cur.getAttribute?.("aria-label");
    if (aria != null && aria.trim().length > 0) return true;
    cur = cur.parentElement;
  }
  return false;
}
function checkTextOverflowsAtBreakpoint(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(needsLayoutFinding4("STR_TEXT_OVERFLOWS_AT_BREAKPOINT", layout.reason));
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win) {
    findings.push(needsLayoutFinding4("STR_TEXT_OVERFLOWS_AT_BREAKPOINT", "no-getComputedStyle"));
    return findings;
  }
  const viewport = win.innerWidth ?? 0;
  const seen = /* @__PURE__ */ new Set();
  const all = root.querySelectorAll("*");
  all.forEach((el) => {
    if (seen.has(el)) return;
    let style;
    try {
      style = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (style.textOverflow !== "ellipsis") return;
    const whiteSpace = (style.whiteSpace ?? "").trim();
    const isNowrap = whiteSpace === "nowrap" || whiteSpace === "pre";
    if (!isNowrap) return;
    seen.add(el);
    const scrollWidth = el.scrollWidth ?? 0;
    const clientWidth = el.clientWidth ?? 0;
    if (scrollWidth - clientWidth <= TRUNCATION_TOLERANCE_PX) {
      return;
    }
    if (hasAccessibleFullText2(el)) return;
    const text = (el.textContent ?? "").trim();
    const sample = text.slice(0, 80);
    findings.push({
      libId: LIB_ID33,
      code: "STR_TEXT_OVERFLOWS_AT_BREAKPOINT",
      category: "layout",
      severity: "major",
      message: `Text "${sample.slice(0, 40)}" is truncating (scrollWidth=${scrollWidth}, clientWidth=${clientWidth}) at viewport=${viewport}px. If the next viewport size up is wider AND does not truncate, the ellipsis is kicking in at the wrong breakpoint.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: "Either lower the breakpoint at which the layout reflows (so truncation never engages at the affected sizes), remove the ellipsis clamp on this element (let text wrap), or shorten the copy.",
      extras: {
        breakpoint: viewport,
        truncatedTextSample: sample,
        scrollWidth,
        clientWidth,
        overflow: scrollWidth - clientWidth
      }
    });
  });
  return findings;
}

// src/structural/child-overflows-visual-parent.ts
var LIB_ID34 = "@norkron/web-conformance";
var OVERFLOW_TOLERANCE_PX2 = 1;
function parentIntentionallyClips2(style) {
  const ox = (style.overflowX ?? "").trim();
  const oy = (style.overflowY ?? "").trim();
  const clippy = (v) => v === "hidden" || v === "clip" || v === "scroll" || v === "auto";
  return clippy(ox) || clippy(oy);
}
function px(v) {
  if (!v) return 0;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}
function hasVisibleBorder(style) {
  const sides = ["Top", "Right", "Bottom", "Left"];
  for (const s of sides) {
    const w = px(
      style[`border${s}Width`] ?? ""
    );
    if (w <= 0) continue;
    const st = (style[`border${s}Style`] ?? "").trim();
    if (st === "" || st === "none" || st === "hidden") continue;
    const col = (style[`border${s}Color`] ?? "").trim();
    if (col === "" || col === "transparent") continue;
    const rgbaMatch = col.match(
      /^(?:rgba|hsla)\(\s*[^,]+,\s*[^,]+,\s*[^,]+,\s*([\d.]+)\s*\)$/i
    );
    if (rgbaMatch && Number.parseFloat(rgbaMatch[1] ?? "1") === 0) continue;
    return true;
  }
  return false;
}
function backgroundsContrast(a, b) {
  const dr = Math.abs(a[0] - b[0]);
  const dg = Math.abs(a[1] - b[1]);
  const db = Math.abs(a[2] - b[2]);
  return Math.max(dr, dg, db) >= 16;
}
function findVisualParent(el, win, doc) {
  let cur = el.parentElement;
  while (cur && cur !== doc.documentElement && cur !== doc.body) {
    let style;
    try {
      style = win.getComputedStyle(cur);
    } catch {
      cur = cur.parentElement;
      continue;
    }
    if (hasVisibleBorder(style)) {
      return { parent: cur, style };
    }
    const here = displayedBgRgb(resolveDisplayedBackground(cur));
    const behindEl = cur.parentElement;
    if (here && behindEl) {
      const behind = displayedBgRgb(resolveDisplayedBackground(behindEl));
      if (behind && backgroundsContrast(here, behind)) {
        return { parent: cur, style };
      }
    }
    cur = cur.parentElement;
  }
  return null;
}
function hasAllowOverflowOptOut(el, stopAt) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute("data-allow-overflow") !== null) return true;
    if (cur === stopAt) return false;
    cur = cur.parentElement;
  }
  return false;
}
function checkChildOverflowsVisualParent(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4("STR_CHILD_OVERFLOWS_VISUAL_PARENT", layout.reason)
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_CHILD_OVERFLOWS_VISUAL_PARENT",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  const all = root.querySelectorAll("*");
  const reported = /* @__PURE__ */ new Set();
  const SVG_METADATA_TAGS = /* @__PURE__ */ new Set(["DESC", "TITLE", "METADATA"]);
  const isUnderIgnore = (el) => {
    let cur = el;
    while (cur && cur !== root.parentElement) {
      if (cur.getAttribute && cur.getAttribute("data-conformance-ignore") !== null) return true;
      cur = cur.parentElement;
    }
    return false;
  };
  const isInsideCollapsedClip = (el) => {
    let cur = el.parentElement;
    while (cur && cur !== root.parentElement) {
      try {
        const cs = win.getComputedStyle(cur);
        const ox = (cs.overflowX ?? "").trim();
        const oy = (cs.overflowY ?? "").trim();
        const clipsHard = ox === "hidden" || ox === "clip" || oy === "hidden" || oy === "clip";
        if (clipsHard) {
          const cr = cur.getBoundingClientRect();
          if (cr.width <= 1 || cr.height <= 1) return true;
        }
      } catch {
      }
      cur = cur.parentElement;
    }
    return false;
  };
  all.forEach((el) => {
    if (reported.has(el)) return;
    if (SVG_METADATA_TAGS.has(el.tagName.toUpperCase())) return;
    if (el.namespaceURI && el.namespaceURI.endsWith("/svg")) return;
    if (el.tagName.toUpperCase() === "CANVAS") return;
    if (isUnderIgnore(el)) return;
    if (isInsideCollapsedClip(el)) return;
    let elStyle = null;
    try {
      elStyle = win.getComputedStyle(el);
    } catch {
    }
    if (elStyle && (elStyle.position === "absolute" || elStyle.position === "fixed")) return;
    const vp = findVisualParent(el, win, doc);
    if (!vp) return;
    const { parent, style: parentStyle } = vp;
    if (parentIntentionallyClips2(parentStyle)) return;
    if (hasAllowOverflowOptOut(el, parent)) return;
    {
      let cur = el;
      let allowFromAncestor = false;
      while (cur && cur !== root.parentElement) {
        if (cur.getAttribute && cur.getAttribute("data-allow-child-overflow") !== null) {
          allowFromAncestor = true;
          break;
        }
        cur = cur.parentElement;
      }
      if (allowFromAncestor) return;
    }
    const childRect = el.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    if (childRect.width === 0 && childRect.height === 0) return;
    if (parentRect.width === 0 && parentRect.height === 0) return;
    const surfaceLeft = parentRect.left;
    const surfaceRight = parentRect.right;
    const surfaceTop = parentRect.top;
    const surfaceBottom = parentRect.bottom;
    const overflowLeft = surfaceLeft - childRect.left;
    const overflowRight = childRect.right - surfaceRight;
    const overflowTop = surfaceTop - childRect.top;
    const overflowBottom = childRect.bottom - surfaceBottom;
    const escapes = overflowLeft > OVERFLOW_TOLERANCE_PX2 || overflowRight > OVERFLOW_TOLERANCE_PX2 || overflowTop > OVERFLOW_TOLERANCE_PX2 || overflowBottom > OVERFLOW_TOLERANCE_PX2;
    if (!escapes) return;
    reported.add(el);
    findings.push({
      libId: LIB_ID34,
      code: "STR_CHILD_OVERFLOWS_VISUAL_PARENT",
      category: "layout",
      severity: "major",
      message: `<${el.tagName.toLowerCase()}> escapes the visible surface of its visual parent <${parent.tagName.toLowerCase()}> (a bordered/contrasting surface) by left=${Math.round(overflowLeft)}px right=${Math.round(overflowRight)}px top=${Math.round(overflowTop)}px bottom=${Math.round(overflowBottom)}px while that surface does not intentionally clip.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: "Constrain the child within the visible surface (max-width/100%, correct position offsets), OR move the surface boundary, OR \u2014 if the bleed is intentional (e.g. an avatar overlapping a card edge) \u2014 add `data-allow-overflow` to the child or set the visual parent overflow to hidden/clip so the opt-in is explicit.",
      extras: {
        overflowLeft,
        overflowRight,
        overflowTop,
        overflowBottom,
        tolerance: OVERFLOW_TOLERANCE_PX2,
        visualParentTag: parent.tagName.toLowerCase(),
        childRect: {
          left: childRect.left,
          right: childRect.right,
          top: childRect.top,
          bottom: childRect.bottom,
          width: childRect.width,
          height: childRect.height
        },
        visualParentBorderBox: {
          left: surfaceLeft,
          right: surfaceRight,
          top: surfaceTop,
          bottom: surfaceBottom
        }
      }
    });
  });
  return findings;
}

// src/structural/full-width-not-edge-to-edge.ts
var LIB_ID35 = "@norkron/web-conformance";
var EDGE_TOLERANCE_PX = 2;
var FULL_WIDTH_CLASS_HINT = /(^|\s)(w-full|block--full-width|full-width|full-bleed)(\s|$)/;
var SVG_NS = "http://www.w3.org/2000/svg";
function isSvgOrPositionedLayout(el) {
  let cur = el;
  while (cur) {
    const ns = cur.namespaceURI;
    if (ns === SVG_NS) return true;
    if (cur.tagName && cur.tagName.toLowerCase() === "svg") return true;
    if (cur.getAttribute && cur.getAttribute("role") === "img") return true;
    if (cur.getAttribute && cur.getAttribute("data-norkron-lib") === "@norkron/web-dataviz") {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function hasFullWidthSignal(el, style) {
  if (el.getAttribute("data-full-width") !== null) return true;
  const cls = el.getAttribute("class") ?? "";
  if (FULL_WIDTH_CLASS_HINT.test(cls)) return true;
  const w = (style.width ?? "").trim();
  if (w === "100%") return true;
  const display = (style.display ?? "").trim();
  const inlineWidth = el.style?.width ?? "";
  if ((display === "block" || display === "flex" || display === "grid") && (w === "" || w === "auto") && (inlineWidth === "" || inlineWidth === "auto")) {
    return true;
  }
  return false;
}
function commonEdge(values, pickMin) {
  if (values.length === 0) return null;
  const buckets = [];
  for (const v of values) {
    const b = buckets.find(
      (bk) => Math.abs(bk.ref - v) <= EDGE_TOLERANCE_PX
    );
    if (b) b.members.push(v);
    else buckets.push({ ref: v, members: [v] });
  }
  buckets.sort((a, b) => b.members.length - a.members.length);
  const top = buckets[0];
  if (!top) return null;
  const tied = buckets.filter((b) => b.members.length === top.members.length);
  const refs = tied.map((b) => b.ref);
  return pickMin ? Math.min(...refs) : Math.max(...refs);
}
function checkFullWidthNotEdgeToEdge(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4("STR_FULL_WIDTH_NOT_EDGE_TO_EDGE", layout.reason)
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_FULL_WIDTH_NOT_EDGE_TO_EDGE",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  const all = root.querySelectorAll("*");
  const reported = /* @__PURE__ */ new Set();
  all.forEach((el) => {
    if (reported.has(el)) return;
    if (el.getAttribute("data-allow-overflow") !== null) return;
    if (isSvgOrPositionedLayout(el)) return;
    let style;
    try {
      style = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (!hasFullWidthSignal(el, style)) return;
    const parent = el.parentElement;
    if (!parent || parent === doc.documentElement) return;
    const siblings = Array.from(parent.children).filter(
      (c) => c !== el && c.nodeType === 1
    );
    if (siblings.length === 0) return;
    const lefts = [];
    const rights = [];
    for (const sib of siblings) {
      const r = sib.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      lefts.push(r.left);
      rights.push(r.right);
    }
    if (lefts.length === 0) return;
    const siblingLeft = commonEdge(lefts, true);
    const siblingRight = commonEdge(rights, false);
    if (siblingLeft === null || siblingRight === null) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    const insetLeft = rect.left - siblingLeft;
    const insetRight = siblingRight - rect.right;
    const anomalouslyInset = insetLeft > EDGE_TOLERANCE_PX || insetRight > EDGE_TOLERANCE_PX;
    if (!anomalouslyInset) return;
    reported.add(el);
    findings.push({
      libId: LIB_ID35,
      code: "STR_FULL_WIDTH_NOT_EDGE_TO_EDGE",
      category: "layout",
      severity: "major",
      message: `<${el.tagName.toLowerCase()}> declares a full-width intent but is inset from the common edge its siblings reach (left inset=${Math.round(insetLeft)}px, right inset=${Math.round(insetRight)}px). An intermediate element's padding/margin/border is pushing only this element in.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: "Remove the padding/margin/border on the intermediate wrapper that affects only this element, OR apply the negative-margin full-bleed pattern consistently, OR drop the full-width signal if the inset is intentional for this element specifically.",
      extras: {
        insetLeft,
        insetRight,
        tolerance: EDGE_TOLERANCE_PX,
        siblingCommonLeft: siblingLeft,
        siblingCommonRight: siblingRight,
        elementRect: {
          left: rect.left,
          right: rect.right,
          width: rect.width
        },
        siblingCount: siblings.length
      }
    });
  });
  return findings;
}

// src/structural/child-underfills-parent-height.ts
var LIB_ID36 = "@norkron/web-conformance";
var HEIGHT_TOLERANCE_PX = 8;
function parentStretchesChildrenOnY(style) {
  const display = (style.display ?? "").trim();
  if (display !== "flex" && display !== "inline-flex") return false;
  const dir = (style.flexDirection ?? "").trim() || "row";
  if (dir !== "row" && dir !== "row-reverse") return false;
  const wrap = (style.flexWrap ?? "").trim();
  if (wrap === "wrap" || wrap === "wrap-reverse") return false;
  const align = (style.alignItems ?? "").trim() || "stretch";
  if (align === "stretch" || align === "normal") return true;
  return false;
}
function childOptsOutOfStretch(style) {
  const self = (style.alignSelf ?? "").trim();
  if (self === "start" || self === "flex-start" || self === "end" || self === "flex-end" || self === "center" || self === "baseline") {
    return true;
  }
  return false;
}
function hasOptOutAttr(el, stopAt) {
  let cur = el;
  while (cur && cur !== stopAt) {
    if (cur.getAttribute("data-allow-shrink") !== null) return true;
    cur = cur.parentElement;
  }
  if (stopAt.getAttribute("data-allow-shrink") !== null) return true;
  return false;
}
function parentContentBoxHeight(style, rect) {
  const px2 = (s) => {
    if (!s) return 0;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  };
  const pt = px2(style.paddingTop);
  const pb = px2(style.paddingBottom);
  const bt = px2(style.borderTopWidth);
  const bb = px2(style.borderBottomWidth);
  return Math.max(0, rect.height - pt - pb - bt - bb);
}
function checkChildUnderfillsParentHeight(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4(
        "STR_CHILD_UNDERFILLS_PARENT_HEIGHT",
        layout.reason
      )
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_CHILD_UNDERFILLS_PARENT_HEIGHT",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  const reported = /* @__PURE__ */ new Set();
  const candidates = [root, ...Array.from(root.querySelectorAll("*"))];
  candidates.forEach((parent) => {
    let parentStyle;
    try {
      parentStyle = win.getComputedStyle(parent);
    } catch {
      return;
    }
    const parentStretches = parentStretchesChildrenOnY(parentStyle);
    const parentStretchOptIn = parent.getAttribute && parent.getAttribute("data-stretch-children") === "true";
    if (!parentStretches && !parentStretchOptIn) return;
    const parentRect = parent.getBoundingClientRect();
    if (parentRect.height < HEIGHT_TOLERANCE_PX * 2) return;
    const contentHeight = parentContentBoxHeight(parentStyle, parentRect);
    if (contentHeight < HEIGHT_TOLERANCE_PX * 2) return;
    const children = Array.from(parent.children).filter(
      (c) => c.nodeType === 1
    );
    for (const child of children) {
      if (reported.has(child)) continue;
      let childStyle;
      try {
        childStyle = win.getComputedStyle(child);
      } catch {
        continue;
      }
      if (parentStretches && childOptsOutOfStretch(childStyle)) continue;
      const childDisplay = (childStyle.display ?? "").trim();
      if (childDisplay.startsWith("inline")) continue;
      const childRect = child.getBoundingClientRect();
      if (childRect.width === 0 || childRect.height === 0) continue;
      const pos = (childStyle.position ?? "").trim();
      if (pos === "absolute" || pos === "fixed") continue;
      if (hasOptOutAttr(child, parent)) continue;
      const shortBy = contentHeight - childRect.height;
      if (shortBy <= HEIGHT_TOLERANCE_PX) continue;
      if (!parentStretches) {
        const ch = (childStyle.height ?? "").trim();
        const explicitPx = /^\d+(\.\d+)?px$/.test(ch);
        if (!explicitPx) continue;
        if (shortBy < contentHeight * 0.25) continue;
      }
      reported.add(child);
      findings.push({
        libId: LIB_ID36,
        code: "STR_CHILD_UNDERFILLS_PARENT_HEIGHT",
        category: "layout",
        severity: "warning",
        // SVGElement.className is SVGAnimatedString (object with .baseVal), not a string —
        // `.split(...)` crashes the entire audit walker the first time we hit any SVG node.
        message: (() => {
          const cls = typeof child.className === "string" ? child.className : child.className?.baseVal ?? "";
          const classFrag = cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "";
          return `<${child.tagName.toLowerCase()}${classFrag}> is ${Math.round(shortBy)}px shorter than its <${parent.tagName.toLowerCase()}> parent's content-box (${Math.round(childRect.height)}px vs ${Math.round(contentHeight)}px) although the parent's flex stretch contract should have filled the parent's height. An explicit height on the child (or an intermediate ancestor), a max-height, or a missing min-height:0 on a flex chain has broken the stretch.`;
        })(),
        selector: child.tagName.toLowerCase(),
        suggestedFix: "Remove the explicit height/max-height on the child or the intermediate ancestor; OR set `height: 100%` (or `flex: 1; min-height: 0`) on the child; OR if the underfill is intentional, add `data-allow-shrink` to the child or set `align-self` to a non-stretch value.",
        extras: {
          shortBy,
          childHeight: childRect.height,
          parentContentHeight: contentHeight,
          parentRectHeight: parentRect.height,
          tolerance: HEIGHT_TOLERANCE_PX,
          parentDisplay: parentStyle.display,
          parentFlexDirection: parentStyle.flexDirection,
          parentAlignItems: parentStyle.alignItems,
          childPosition: pos,
          childAlignSelf: childStyle.alignSelf,
          childExplicitHeight: childStyle.height
        }
      });
    }
  });
  return findings;
}

// src/structural/keep-clear-region-overlapped.ts
var LIB_ID37 = "@norkron/web-conformance";
var OVERLAP_TOLERANCE_PX = 1;
function rectsIntersect(a, b) {
  return !(a.right <= b.left + OVERLAP_TOLERANCE_PX || a.left >= b.right - OVERLAP_TOLERANCE_PX || a.bottom <= b.top + OVERLAP_TOLERANCE_PX || a.top >= b.bottom - OVERLAP_TOLERANCE_PX);
}
function isAncestorOf(potentialAncestor, el) {
  let cur = el.parentElement;
  while (cur) {
    if (cur === potentialAncestor) return true;
    cur = cur.parentElement;
  }
  return false;
}
function isOutOfFlowOverlay(el) {
  const win = el.ownerDocument?.defaultView;
  if (!win || typeof win.getComputedStyle !== "function") return false;
  const cs = win.getComputedStyle(el);
  const pos = cs.position;
  return pos === "fixed" || pos === "sticky" || pos === "absolute";
}
function intruderCouldPaintAboveKeepClear(intruder, keepClear) {
  if (!isOutOfFlowOverlay(keepClear)) return true;
  const win = intruder.ownerDocument?.defaultView;
  if (!win || typeof win.getComputedStyle !== "function") return true;
  let cur = intruder;
  while (cur && cur !== document.documentElement && cur !== document.body) {
    const cs = win.getComputedStyle(cur);
    const pos = cs.position;
    const zRaw = cs.zIndex;
    const hasZ = zRaw !== "auto" && zRaw !== "" && zRaw !== "0";
    if (pos === "fixed" || pos === "sticky" || pos === "absolute" || hasZ) {
      if (!isAncestorOf(cur, keepClear) && cur !== keepClear) return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function sharesMultiOverlayCatalog(intruder, keepClear) {
  function collectCatalogs(start) {
    const out = /* @__PURE__ */ new Set();
    let c = start;
    while (c) {
      if (c.getAttribute && c.getAttribute("data-conformance-multi-overlay-catalog") === "true") {
        out.add(c);
      }
      if (c.id === "norkron-portal-root") {
        out.add(c);
      }
      c = c.parentElement;
    }
    return out;
  }
  const intruderCatalogs = collectCatalogs(intruder);
  const keepClearCatalogs = collectCatalogs(keepClear);
  if (intruderCatalogs.size === 0 && keepClearCatalogs.size === 0) {
    return false;
  }
  if (intruderCatalogs.size > 0 && keepClearCatalogs.size > 0) {
    return true;
  }
  let cur = keepClear;
  while (cur) {
    if (intruderCatalogs.has(cur)) return true;
    cur = cur.parentElement;
  }
  cur = intruder;
  while (cur) {
    if (keepClearCatalogs.has(cur)) return true;
    cur = cur.parentElement;
  }
  return false;
}
function hasAllowOverlapBetween(intruder, keepClear) {
  let cur = intruder;
  while (cur && cur !== document.documentElement && cur !== document.body) {
    if (cur.getAttribute("data-allow-overlap") !== null) return true;
    if (isAncestorOf(cur, keepClear) || cur === keepClear) break;
    cur = cur.parentElement;
  }
  return false;
}
function isLeafIntruder(intruder, keepClearRect) {
  const descendants = intruder.querySelectorAll("*");
  for (const d of Array.from(descendants)) {
    const r = d.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) continue;
    if (rectsIntersect(r, keepClearRect)) return false;
  }
  return true;
}
function checkKeepClearRegionOverlapped(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4(
        "STR_KEEP_CLEAR_REGION_OVERLAPPED",
        layout.reason
      )
    );
    return findings;
  }
  const keepClearEls = Array.from(
    root.querySelectorAll('[data-keep-clear="true"]')
  );
  if (root.getAttribute && root.getAttribute("data-keep-clear") === "true") {
    keepClearEls.unshift(root);
  }
  if (keepClearEls.length === 0) return findings;
  const allEls = Array.from(root.querySelectorAll("*"));
  const reported = /* @__PURE__ */ new Set();
  for (const keepClear of keepClearEls) {
    const kRect = keepClear.getBoundingClientRect();
    if (kRect.width === 0 || kRect.height === 0) continue;
    for (const candidate of allEls) {
      if (candidate === keepClear) continue;
      if (isAncestorOf(candidate, keepClear)) continue;
      if (isAncestorOf(keepClear, candidate)) continue;
      const cRect = candidate.getBoundingClientRect();
      if (cRect.width === 0 || cRect.height === 0) continue;
      if (!rectsIntersect(cRect, kRect)) continue;
      if (!isLeafIntruder(candidate, kRect)) continue;
      if (hasAllowOverlapBetween(candidate, keepClear)) continue;
      if (sharesMultiOverlayCatalog(candidate, keepClear)) continue;
      if (!intruderCouldPaintAboveKeepClear(candidate, keepClear)) continue;
      const cTag = candidate.tagName.toLowerCase();
      const kTag = keepClear.tagName.toLowerCase();
      const cKey = (candidate.className || cTag).toString().split(/\s+/).filter(Boolean).join(".");
      const kKey = (keepClear.className || kTag).toString().split(/\s+/).filter(Boolean).join(".");
      const dedup = `${kKey}::${cKey}`;
      if (reported.has(dedup)) continue;
      reported.add(dedup);
      findings.push({
        libId: LIB_ID37,
        code: "STR_KEEP_CLEAR_REGION_OVERLAPPED",
        category: "layout",
        severity: "warning",
        message: `<${cTag}.${cKey}> visually intersects keep-clear region <${kTag}.${kKey}> despite living in an unrelated subtree. A chrome / overlay / sticky-toolbar surface marked data-keep-clear was crossed by sibling-subtree content.`,
        selector: cTag,
        suggestedFix: "Either constrain the intruder so it stays out of the keep-clear rect, raise the keep-clear element's z-index above the intruder, or \u2014 if the overlap is intentional \u2014 set `data-allow-overlap` on the intruder (or any ancestor up to the common ancestor with the keep-clear element).",
        extras: {
          keepClearSelector: `${kTag}.${kKey}`,
          intruderSelector: `${cTag}.${cKey}`,
          keepClearRect: {
            left: kRect.left,
            right: kRect.right,
            top: kRect.top,
            bottom: kRect.bottom,
            width: kRect.width,
            height: kRect.height
          },
          intruderRect: {
            left: cRect.left,
            right: cRect.right,
            top: cRect.top,
            bottom: cRect.bottom,
            width: cRect.width,
            height: cRect.height
          },
          tolerance: OVERLAP_TOLERANCE_PX
        }
      });
    }
  }
  return findings;
}

// src/structural/interactive-element-visually-empty.ts
var LIB_ID38 = "@norkron/web-conformance";
var MIN_VISIBLE_SIZE_PX = 4;
var COLLAPSED_INTERACTIVE_PX = 14;
var MIN_VISIBLE_CHILD_PX = 4;
var INTERACTIVE_SELECTOR = [
  "button",
  "a[href]",
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="reset"]',
  '[role="button"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="switch"]',
  "summary"
].join(",");
var INLINE_VISUAL_SELECTOR = "img,svg,picture,canvas,video,iframe";
var ICON_FONT_NAME_HINTS = [
  "nerd",
  "icon",
  "fontawesome",
  "font awesome",
  "material",
  "symbols",
  "feather",
  "octicons",
  "phosphor",
  "remix",
  "tabler",
  "lucide",
  "bootstrap-icons",
  "glyphicon"
];
function isIconFontFamily(family) {
  const lower = family.toLowerCase();
  return ICON_FONT_NAME_HINTS.some((hint) => lower.includes(hint));
}
function pseudoContentLooksEmpty(content) {
  if (!content) return true;
  const c = content.trim();
  if (c === "none" || c === "normal" || c === '""' || c === "''" || c === '"\\200B"' || // zero-width space, often used as a placeholder
  c === "'\\200B'") {
    return true;
  }
  return false;
}
function pseudoFontIsAvailable(doc, style) {
  const fonts = doc.fonts;
  if (!fonts || typeof fonts.check !== "function") return true;
  const family = (style.fontFamily ?? "").trim();
  if (!family) return true;
  if (!isIconFontFamily(family)) return true;
  try {
    return fonts.check(`1em ${family}`);
  } catch {
    return true;
  }
}
function hasVisibleInlineImage(el) {
  const inlines = el.querySelectorAll(INLINE_VISUAL_SELECTOR);
  for (const v of Array.from(inlines)) {
    const r = v.getBoundingClientRect();
    if (r.width >= MIN_VISIBLE_CHILD_PX && r.height >= MIN_VISIBLE_CHILD_PX) {
      return true;
    }
  }
  return false;
}
function hasBackgroundOrMask(style) {
  const bg = (style.backgroundImage ?? "").trim();
  if (bg && bg !== "none") return true;
  const mask = style.maskImage;
  if (mask && mask.trim() && mask !== "none") return true;
  const wkMask = style.webkitMaskImage;
  if (wkMask && wkMask.trim() && wkMask !== "none") return true;
  return false;
}
function hasVisiblePseudoContent(doc, win, el) {
  for (const pseudo of ["::before", "::after"]) {
    let style = null;
    try {
      style = win.getComputedStyle(el, pseudo);
    } catch {
      style = null;
    }
    if (!style) continue;
    if (pseudoContentLooksEmpty(style.content)) continue;
    if (!pseudoFontIsAvailable(doc, style)) continue;
    return true;
  }
  return false;
}
function checkInteractiveElementVisuallyEmpty(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4(
        "STR_INTERACTIVE_ELEMENT_VISUALLY_EMPTY",
        layout.reason
      )
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_INTERACTIVE_ELEMENT_VISUALLY_EMPTY",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  const candidates = root.querySelectorAll(INTERACTIVE_SELECTOR);
  const reported = /* @__PURE__ */ new Set();
  candidates.forEach((el) => {
    if (reported.has(el)) return;
    if (el.getAttribute("data-allow-empty") === "true") return;
    if (el.hidden) return;
    if (el.hasAttribute("disabled")) return;
    if (el.hasAttribute("inert")) return;
    const rect = el.getBoundingClientRect();
    if (rect.width < MIN_VISIBLE_SIZE_PX || rect.height < MIN_VISIBLE_SIZE_PX) {
      return;
    }
    const collapsedAxis = rect.width < COLLAPSED_INTERACTIVE_PX || rect.height < COLLAPSED_INTERACTIVE_PX;
    const text = (el.textContent ?? "").replace(/\s+/g, "").trim();
    if (text.length > 0) return;
    if (hasVisibleInlineImage(el)) return;
    let style;
    try {
      style = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (hasBackgroundOrMask(style)) return;
    if (hasVisiblePseudoContent(doc, win, el)) return;
    reported.add(el);
    const ariaLabel = el.getAttribute("aria-label") ?? "";
    const ariaLabelledBy = el.getAttribute("aria-labelledby") ?? "";
    const cls = (el.getAttribute("class") ?? "").slice(0, 120);
    findings.push({
      libId: LIB_ID38,
      code: "STR_INTERACTIVE_ELEMENT_VISUALLY_EMPTY",
      category: "a11y",
      severity: "warning",
      message: `<${el.tagName.toLowerCase()}${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}> renders ${Math.round(rect.width)}x${Math.round(rect.height)}px${collapsedAxis ? " (COLLAPSED \u2014 sizing CSS likely missing)" : ""} of visible space but carries no visible content (no text, no inline image, no background-image, no rendered ::before/::after glyph). Almost certainly the icon-font CSS / glyph file did not load \u2014 sighted users see a blank clickable rectangle while screen readers happily announce "${ariaLabel || ariaLabelledBy || el.tagName}".`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Verify the icon CSS/font/SVG asset is imported and the font file actually loaded (network panel). For library buttons, ensure the consumer imported the lib\'s styles.css. If the empty render is intentional (placeholder, hit-target spacer), set `data-allow-empty="true"` on the element.',
      extras: {
        rect: {
          left: rect.left,
          right: rect.right,
          top: rect.top,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height
        },
        ariaLabel,
        ariaLabelledBy,
        className: cls,
        resolvedFontFamily: style.fontFamily,
        backgroundImage: style.backgroundImage
      }
    });
  });
  return findings;
}

// src/structural/text-leaked-programming-value.ts
var LIB_ID39 = "@norkron/web-conformance";
var SENTINEL_LITERALS = [
  "NaN",
  "Infinity",
  "-Infinity",
  "undefined",
  "null",
  "[object Object]",
  "[object Array]",
  "[object Map]",
  "[object Set]",
  "[object Promise]"
];
var SENTINEL_PREFIXES = [
  "function ",
  "Symbol(",
  "class "
  // class definitions stringified
];
var ALLOW_OPT_OUT_ATTR = "data-allow-programming-text";
function tokenContainsSentinel(text) {
  const trimmed = text.trim();
  if (!trimmed) return null;
  for (const lit of SENTINEL_LITERALS) {
    if (trimmed === lit) return lit;
  }
  for (const lit of SENTINEL_LITERALS) {
    const idx = trimmed.indexOf(lit);
    if (idx < 0) continue;
    const before = idx === 0 ? "" : trimmed[idx - 1] ?? "";
    const after = trimmed[idx + lit.length] ?? "";
    const beforeOk = before === "" || /[^A-Za-z0-9_]/.test(before);
    const afterOk = after === "" || /[^A-Za-z0-9_]/.test(after);
    if (beforeOk && afterOk) return lit;
  }
  for (const prefix of SENTINEL_PREFIXES) {
    if (trimmed.startsWith(prefix)) return prefix.trimEnd();
  }
  return null;
}
function hasOptOut(el, stopAt) {
  let cur = el;
  while (cur && cur !== stopAt.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_OPT_OUT_ATTR) !== null) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function checkTextLeakedProgrammingValue(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4(
        "STR_TEXT_LEAKED_PROGRAMMING_VALUE",
        layout.reason
      )
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  if (typeof doc.createTreeWalker !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_TEXT_LEAKED_PROGRAMMING_VALUE",
        "no-createTreeWalker"
      )
    );
    return findings;
  }
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  const reported = /* @__PURE__ */ new Set();
  const isUnderIgnore = (el) => {
    let cur = el;
    while (cur && cur !== root.parentElement) {
      if (cur.getAttribute && cur.getAttribute("data-conformance-ignore") !== null) return true;
      cur = cur.parentElement;
    }
    return false;
  };
  let node = walker.nextNode();
  while (node) {
    const text = node.nodeValue ?? "";
    if (text.includes("CONF_LIVE_FINDING") || text.includes("@norkron/web-conformance")) {
      node = walker.nextNode();
      continue;
    }
    const parentEl = node.parentElement ?? null;
    if (parentEl && isUnderIgnore(parentEl)) {
      node = walker.nextNode();
      continue;
    }
    const sentinel = tokenContainsSentinel(text);
    if (sentinel) {
      const parent = node.parentElement ?? null;
      if (parent && !reported.has(parent)) {
        if (!hasOptOut(parent, root)) {
          reported.add(parent);
          const rect = parent.getBoundingClientRect?.();
          const visible = rect && rect.width > 0 && rect.height > 0;
          if (visible) {
            const cls = (parent.getAttribute("class") ?? "").slice(0, 80);
            const sample = text.trim().slice(0, 80);
            findings.push({
              libId: LIB_ID39,
              code: "STR_TEXT_LEAKED_PROGRAMMING_VALUE",
              category: "content",
              severity: "warning",
              message: `<${parent.tagName.toLowerCase()}${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}> renders the string "${sample}" \u2014 a programming sentinel (${sentinel}) leaked into the user-visible DOM. Almost always a value rendered before it stabilised (e.g. \`\${zoom*100}\` while zoom is undefined), a divide-by-zero, an i18n key resolution failure, or an object-as-string template hole.`,
              selector: parent.tagName.toLowerCase(),
              suggestedFix: "Guard the render against the pre-stable value (default the state, render a placeholder until ready, OR use Number.isFinite() before formatting). For object-as-string, JSON.stringify() with intent or format the field properly. For i18n key leakage, verify the key is registered in the locale catalog. Mark with data-allow-programming-text only when the literal value is the intent (devtools / playground).",
              extras: {
                sentinel,
                textSample: sample,
                className: cls,
                rect: rect ? {
                  left: rect.left,
                  top: rect.top,
                  width: rect.width,
                  height: rect.height
                } : void 0
              }
            });
          }
        }
      }
    }
    node = walker.nextNode();
  }
  return findings;
}

// src/structural/canvas-zero-sized.ts
var LIB_ID40 = "@norkron/web-conformance";
function checkCanvasZeroSized(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4("STR_CANVAS_ZERO_SIZED", layout.reason)
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4("STR_CANVAS_ZERO_SIZED", "no-getComputedStyle")
    );
    return findings;
  }
  const canvases = root.querySelectorAll("canvas");
  const reported = /* @__PURE__ */ new Set();
  canvases.forEach((c) => {
    if (reported.has(c)) return;
    if (c.getAttribute("data-allow-zero-sized") === "true") return;
    let style;
    try {
      style = win.getComputedStyle(c);
    } catch {
      return;
    }
    const display = (style.display ?? "").trim();
    const visibility = (style.visibility ?? "").trim();
    if (display === "none") return;
    if (visibility === "hidden" || visibility === "collapse") return;
    const rect = c.getBoundingClientRect();
    const iw = c.width;
    const ih = c.height;
    const intrinsicOk = Number.isFinite(iw) && Number.isFinite(ih) && iw > 0 && ih > 0;
    if (intrinsicOk) return;
    const parent = c.parentElement;
    const parentRect = parent?.getBoundingClientRect();
    const canvasHasArea = rect.width > 0 && rect.height > 0;
    const parentHasArea = !!parentRect && parentRect.width > 0 && parentRect.height > 0;
    if (!canvasHasArea && !parentHasArea) return;
    reported.add(c);
    const cls = (c.getAttribute("class") ?? "").slice(0, 80);
    findings.push({
      libId: LIB_ID40,
      code: "STR_CANVAS_ZERO_SIZED",
      category: "rendering",
      severity: "error",
      message: `<canvas${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}> has intrinsic dimensions ${iw}x${ih} (one axis is zero or NaN) inside a parent that occupies ${Math.round(parentRect?.width ?? 0)}x${Math.round(parentRect?.height ?? 0)}px. The GPU backing texture cannot be allocated; the canvas paints nothing and WebGL/WebGPU emits a flood of "size 0" warnings every frame. Almost always a renderer initialised with a string or NaN dimension (e.g. \`app.init({ height: 'fill' })\` where 'fill' was the unresolved consumer prop, or a parent rect.height that was still 0 at init time).`,
      selector: "canvas",
      suggestedFix: 'At canvas/renderer init, resolve every dimension to a finite pixel number. For libraries that accept `height: "fill" | number`, convert "fill"/"100%" to `containerRef.current.clientHeight` before passing to the renderer. After parent resize, call `renderer.resize(newW, newH)` with finite numbers.',
      extras: {
        intrinsic: { width: iw, height: ih },
        rect: {
          width: rect.width,
          height: rect.height,
          left: rect.left,
          top: rect.top
        },
        className: cls
      }
    });
  });
  return findings;
}

// src/structural/replaced-element-zero-rect.ts
var LIB_ID41 = "@norkron/web-conformance";
var REPLACED_SELECTOR = "img,svg,video,iframe,picture";
function checkReplacedElementZeroRect(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4(
        "STR_REPLACED_ELEMENT_ZERO_RECT",
        layout.reason
      )
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_REPLACED_ELEMENT_ZERO_RECT",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  const replaced = root.querySelectorAll(REPLACED_SELECTOR);
  const reported = /* @__PURE__ */ new Set();
  replaced.forEach((el) => {
    if (reported.has(el)) return;
    if (el.getAttribute("data-allow-zero-rect") === "true") return;
    if (el.getAttribute("aria-hidden") === "true") return;
    let style;
    try {
      style = win.getComputedStyle(el);
    } catch {
      return;
    }
    const display = (style.display ?? "").trim();
    const visibility = (style.visibility ?? "").trim();
    if (display === "none") return;
    if (visibility === "hidden" || visibility === "collapse") return;
    if (el.tagName === "IMG") {
      const img = el;
      if (img.loading === "lazy" && !img.complete) return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return;
    if (rect.width === 0 && rect.height === 0) return;
    reported.add(el);
    const cls = (el.getAttribute("class") ?? "").slice(0, 80);
    findings.push({
      libId: LIB_ID41,
      code: "STR_REPLACED_ELEMENT_ZERO_RECT",
      category: "rendering",
      severity: "warning",
      message: `<${el.tagName.toLowerCase()}${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}> renders at ${Math.round(rect.width)}x${Math.round(rect.height)}px \u2014 one axis is zero. The element occupies layout space but paints nothing. Almost always a replaced element inside a flex/grid track that collapsed on the cross axis, OR an <svg> without an explicit viewBox/width/height defaulting to 0 in some content contexts.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: "Give the element an explicit width AND height (or an aspect-ratio) so the renderer can reserve box space even when the parent collapses. For SVGs, set `viewBox` AND a CSS `width`/`height`. Or fix the upstream layout that collapsed the axis (commonly STR_CHILD_UNDERFILLS_PARENT_HEIGHT).",
      extras: {
        rect: {
          width: rect.width,
          height: rect.height,
          left: rect.left,
          top: rect.top
        },
        className: cls
      }
    });
  });
  return findings;
}

// src/structural/i18n-key-leaked.ts
var LIB_ID42 = "@norkron/web-conformance";
var ALLOW_OPT_OUT_ATTR2 = "data-allow-i18n-key";
var I18N_KEY_PATTERN = /^[a-z][a-z0-9]*(\.[a-z0-9_-]+){1,}$/i;
var SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;
var DOMAIN_SUFFIXES = [".com", ".io", ".dev", ".org", ".net", ".app", ".co"];
var FILE_EXTENSIONS = [
  ".ts",
  ".tsx",
  ".js",
  ".jsx",
  ".css",
  ".md",
  ".json",
  ".png",
  ".svg",
  ".html"
];
function looksLikeI18nKey(text) {
  const trimmed = text.trim();
  if (trimmed.length < 6 || trimmed.length > 80) return null;
  if (trimmed.startsWith("/") || trimmed.startsWith("./") || trimmed.startsWith("../")) {
    return null;
  }
  if (SEMVER_PATTERN.test(trimmed)) return null;
  const lower = trimmed.toLowerCase();
  for (const suffix of DOMAIN_SUFFIXES) {
    if (lower.endsWith(suffix)) return null;
  }
  for (const ext of FILE_EXTENSIONS) {
    if (lower.endsWith(ext)) return null;
  }
  const dots = (trimmed.match(/\./g) ?? []).length;
  if (dots < 2) return null;
  if (!I18N_KEY_PATTERN.test(trimmed)) return null;
  return trimmed;
}
function hasOptOut2(el, stopAt) {
  let cur = el;
  while (cur && cur !== stopAt.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_OPT_OUT_ATTR2) !== null) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function checkI18nKeyLeaked(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(needsLayoutFinding4("STR_I18N_KEY_LEAKED", layout.reason));
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  if (typeof doc.createTreeWalker !== "function") {
    findings.push(
      needsLayoutFinding4("STR_I18N_KEY_LEAKED", "no-getComputedStyle")
    );
    return findings;
  }
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  const reported = /* @__PURE__ */ new Set();
  let node = walker.nextNode();
  while (node) {
    const text = node.nodeValue ?? "";
    const key = looksLikeI18nKey(text);
    if (key) {
      const parent = node.parentElement ?? null;
      if (parent && !reported.has(parent)) {
        if (!hasOptOut2(parent, root)) {
          reported.add(parent);
          const rect = parent.getBoundingClientRect?.();
          const visible = rect && rect.width > 0 && rect.height > 0;
          const win = doc.defaultView;
          let displayed = visible;
          if (displayed && win && typeof win.getComputedStyle === "function") {
            const cs = win.getComputedStyle(parent);
            if (cs.display === "none" || cs.visibility === "hidden") {
              displayed = false;
            }
          }
          if (displayed) {
            const cls = (parent.getAttribute("class") ?? "").slice(0, 80);
            const sample = key.slice(0, 80);
            findings.push({
              libId: LIB_ID42,
              code: "STR_I18N_KEY_LEAKED",
              category: "content",
              severity: "warning",
              message: `<${parent.tagName.toLowerCase()}${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}> renders the literal string "${sample}" \u2014 shape matches an i18n lookup key (pattern /^[a-z][a-z0-9]*(\\.[a-z0-9_-]+){1,}$/i, 3+ dot-separated segments). The locale catalog likely did not resolve this key, so the raw key surfaced to the user.`,
              selector: parent.tagName.toLowerCase(),
              suggestedFix: "Verify the key is registered in the locale catalog; check that the i18n provider/Suspense fallback isn't missing; if the literal string IS intentional (developer docs), set data-allow-i18n-key on the element or ancestor.",
              extras: {
                key: sample,
                className: cls,
                pattern: "/^[a-z][a-z0-9]*(\\.[a-z0-9_-]+){1,}$/i",
                rect: rect ? {
                  left: rect.left,
                  top: rect.top,
                  width: rect.width,
                  height: rect.height
                } : void 0
              }
            });
          }
        }
      }
    }
    node = walker.nextNode();
  }
  return findings;
}

// src/structural/dead-interactive.ts
var LIB_ID43 = "@norkron/web-conformance";
var CODE = "STR_DEAD_INTERACTIVE";
var INTERACTIVE_ROLES = /* @__PURE__ */ new Set([
  "button",
  "link",
  "menuitem",
  "tab",
  "option",
  "checkbox",
  "switch",
  "radio"
]);
var NATIVE_INTERACTIVE_TAGS = /* @__PURE__ */ new Set([
  "BUTTON",
  "A",
  "INPUT",
  "SELECT",
  "TEXTAREA",
  "SUMMARY"
]);
function hasPassiveOptOut(el) {
  let cur = el;
  while (cur) {
    if (cur.hasAttribute("data-allow-passive") || cur.hasAttribute("data-decorative-cursor")) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function isDisabled2(el) {
  const h = el;
  if (h.hasAttribute("disabled")) return true;
  if (h.getAttribute("aria-disabled") === "true") return true;
  if (h.hasAttribute("inert")) return true;
  return false;
}
function hasReachableHandler(el) {
  if (NATIVE_INTERACTIVE_TAGS.has(el.tagName)) return true;
  const role = el.getAttribute("role");
  if (role && INTERACTIVE_ROLES.has(role)) return true;
  const tabIndex = el.tabIndex;
  if (typeof tabIndex === "number" && tabIndex >= 0) return true;
  if (el.hasAttribute("onclick")) return true;
  return false;
}
function selectorFor5(el) {
  const tag = el.tagName.toLowerCase();
  const cls = (el.getAttribute("class") ?? "").trim().split(/\s+/).filter(Boolean);
  return cls.length ? `${tag}.${cls.slice(0, 2).join(".")}` : tag;
}
function isDeadAnchor(el) {
  if (el.tagName !== "A") return false;
  if (el.hasAttribute("onclick")) return false;
  if (!el.hasAttribute("href")) return true;
  const href = (el.getAttribute("href") ?? "").trim();
  if (href === "" || href === "#" || /^javascript:\s*void\s*\(\s*0\s*\)\s*;?$/i.test(href)) {
    return true;
  }
  return false;
}
function isOrphanSubmitButton(el) {
  if (el.tagName !== "BUTTON") return false;
  const type = (el.getAttribute("type") ?? "").toLowerCase();
  if (type !== "submit") return false;
  return el.closest("form") === null;
}
function checkDeadInteractive(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(needsLayoutFinding4(CODE, layout.reason));
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(needsLayoutFinding4(CODE, "no-getComputedStyle"));
    return findings;
  }
  const all = root.querySelectorAll("*");
  const reported = /* @__PURE__ */ new Set();
  all.forEach((el) => {
    if (reported.has(el)) return;
    if (hasPassiveOptOut(el)) return;
    if (isDisabled2(el)) return;
    let rect;
    try {
      rect = el.getBoundingClientRect();
    } catch {
      return;
    }
    if (rect.width === 0 && rect.height === 0) return;
    let style;
    try {
      style = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (style.display === "none") return;
    if (isDeadAnchor(el)) {
      reported.add(el);
      const href = el.getAttribute("href");
      findings.push({
        libId: LIB_ID43,
        code: CODE,
        category: "a11y",
        severity: "warning",
        message: `Dead interactive (case B): <a> with ${href === null ? "no href" : `href="${href}"`} and no inline onclick. Looks like a link but navigates nowhere.`,
        selector: selectorFor5(el),
        suggestedFix: "Provide a real `href` for the anchor, OR replace with `<button onClick>`, OR move the submit button inside a `<form>`.",
        extras: { subCase: "B", kind: "dead-anchor", href }
      });
      return;
    }
    if (isOrphanSubmitButton(el)) {
      reported.add(el);
      findings.push({
        libId: LIB_ID43,
        code: CODE,
        category: "a11y",
        severity: "warning",
        message: `Dead interactive (case B): <button type="submit"> with no ancestor <form>. Clicking submits nothing.`,
        selector: selectorFor5(el),
        suggestedFix: "Provide a real `href` for the anchor, OR replace with `<button onClick>`, OR move the submit button inside a `<form>`.",
        extras: { subCase: "B", kind: "orphan-submit" }
      });
      return;
    }
    if (style.cursor !== "pointer") return;
    if (hasReachableHandler(el)) return;
    if (el.namespaceURI === "http://www.w3.org/2000/svg" && el.tagName.toLowerCase() !== "svg") {
      return;
    }
    const INTERACTIVE_LIB_RE = /@norkron\/(web-controls|web-tooltip|web-menu|web-popover|web-dialog|web-tabs|web-segmented-control|web-combobox|web-command-palette|web-data-grid)/;
    let cur = el.parentElement;
    let hasInteractiveAncestor = false;
    while (cur && cur !== root && cur !== doc.body) {
      if (hasReachableHandler(cur)) {
        hasInteractiveAncestor = true;
        break;
      }
      const stamp = cur.getAttribute("data-norkron-lib");
      if (stamp && INTERACTIVE_LIB_RE.test(stamp)) {
        hasInteractiveAncestor = true;
        break;
      }
      cur = cur.parentElement;
    }
    if (hasInteractiveAncestor) return;
    reported.add(el);
    findings.push({
      libId: LIB_ID43,
      code: CODE,
      category: "a11y",
      severity: "warning",
      message: `Dead interactive (case A): <${el.tagName.toLowerCase()}> has cursor:pointer but no interactive role, no native interactive tag, no tabIndex>=0, and no onclick attribute. Sighted users get a click affordance the element cannot fulfil.`,
      selector: selectorFor5(el),
      suggestedFix: "Add `role='button'` + `tabIndex=0` + a click handler, OR remove the cursor:pointer signaling.",
      extras: {
        subCase: "A",
        kind: "cursor-without-handler",
        tag: el.tagName.toLowerCase(),
        role: el.getAttribute("role")
      }
    });
  });
  return findings;
}

// src/structural/duplicate-aria-label.ts
var LIB_ID44 = "@norkron/web-conformance";
function normalize2(s) {
  return s.replace(/\s+/g, " ").trim().toLowerCase();
}
function hasAllowDuplicateOptOut2(el) {
  let cur = el;
  while (cur) {
    if (cur.hasAttribute("data-allow-duplicate-label")) return true;
    cur = cur.parentElement;
  }
  return false;
}
function isRendered(el) {
  const doc = el.ownerDocument ?? el;
  const win = doc?.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (win && typeof win.getComputedStyle === "function") {
    const cs = win.getComputedStyle(el);
    if (cs.display === "none") return false;
    if (cs.visibility === "hidden") return false;
  }
  if (typeof el.getBoundingClientRect === "function") {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      return true;
    }
  }
  return true;
}
function checkDuplicateAriaLabel(root) {
  const findings = [];
  const candidates = Array.from(root.querySelectorAll("[aria-label]"));
  for (const el of candidates) {
    const rawLabel = el.getAttribute("aria-label") ?? "";
    const label = normalize2(rawLabel);
    if (label.length === 0) continue;
    if (el.getAttribute("aria-hidden") === "true") continue;
    if (hasAllowDuplicateOptOut2(el)) continue;
    const rawText = el.textContent ?? "";
    const text = normalize2(rawText);
    if (text.length === 0) continue;
    if (label.length > text.length * 2) continue;
    if (label !== text) continue;
    if (!isRendered(el)) continue;
    const tag = el.tagName.toLowerCase();
    const clsRaw = typeof el.className === "string" ? el.className : "";
    const clsSuffix = clsRaw.trim() ? "." + clsRaw.trim().split(/\s+/).join(".") : "";
    findings.push({
      libId: LIB_ID44,
      code: "STR_DUPLICATE_ARIA_LABEL",
      category: "a11y",
      severity: "warning",
      message: `<${tag}${clsSuffix}> has aria-label "${rawLabel.trim()}" that duplicates its visible text "${rawText.trim()}" \u2014 screen readers will either announce the name twice ("${rawLabel.trim()}, ${rawLabel.trim()}") or silently override the visible text with the aria-label, both worse than no aria-label at all.`,
      selector: tag + clsSuffix,
      suggestedFix: 'Drop the aria-label and let the text content be the accessible name. Use aria-label only when there is no visible text (icon-only) OR the visible text is shorter than the spoken intent (e.g. visible "Save" + aria-label "Save changes").',
      extras: {
        ariaLabel: rawLabel.trim(),
        visibleText: rawText.trim(),
        normalizedLabel: label,
        normalizedText: text
      }
    });
  }
  return findings;
}

// src/structural/theme-token-not-themed.ts
var LIB_ID45 = "@norkron/web-conformance";
var LIGHT_BG_LUMA = 0.7;
var DARK_BG_LUMA = 0.15;
function getActiveTheme(doc, win) {
  const root = doc.documentElement;
  const declared = root.getAttribute("data-theme") || doc.body?.getAttribute("data-theme") || null;
  if (declared === "dark") return "dark";
  if (declared === "light") return "light";
  if (typeof win.matchMedia === "function") {
    try {
      if (win.matchMedia("(prefers-color-scheme: dark)").matches)
        return "dark";
      if (win.matchMedia("(prefers-color-scheme: light)").matches)
        return "light";
    } catch {
    }
  }
  return "unknown";
}
function parseRgb(value) {
  const trimmed = (value ?? "").trim();
  if (!trimmed || trimmed === "transparent") return null;
  const m = trimmed.match(
    /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)\s*(?:,\s*([0-9.]+))?\s*\)$/
  );
  if (!m) return null;
  const r = parseFloat(m[1] ?? "0");
  const g = parseFloat(m[2] ?? "0");
  const b = parseFloat(m[3] ?? "0");
  const a = m[4] !== void 0 ? parseFloat(m[4]) : 1;
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b))
    return null;
  return { r, g, b, a };
}
function relativeLuminance(r, g, b) {
  const toLin = (n) => {
    const c = n / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}
function resolveOpaqueBg(el, win) {
  let cur = el;
  while (cur) {
    let style;
    try {
      style = win.getComputedStyle(cur);
    } catch {
      return null;
    }
    const rgb = parseRgb(style.backgroundColor ?? "");
    if (rgb && rgb.a > 0.99) return rgb;
    cur = cur.parentElement;
  }
  return null;
}
function hasOptOut3(el, stopAt) {
  let cur = el;
  while (cur && cur !== stopAt.parentElement) {
    if (cur.getAttribute) {
      if (cur.getAttribute("data-allow-theme-mismatch") !== null)
        return true;
      if (cur.getAttribute("data-allow-single-theme") !== null)
        return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function checkThemeTokenNotThemed(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4("STR_THEME_TOKEN_NOT_THEMED", layout.reason)
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_THEME_TOKEN_NOT_THEMED",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  const theme = getActiveTheme(doc, win);
  if (theme === "unknown") return findings;
  const stamped = Array.from(
    root.querySelectorAll("[data-norkron-lib]")
  );
  if (root.getAttribute && root.getAttribute("data-norkron-lib") !== null) {
    stamped.unshift(root);
  }
  const reported = /* @__PURE__ */ new Set();
  for (const el of stamped) {
    if (hasOptOut3(el, root)) continue;
    const rect = el.getBoundingClientRect?.();
    if (!rect || rect.width === 0 || rect.height === 0) continue;
    let style;
    try {
      style = win.getComputedStyle(el);
    } catch {
      continue;
    }
    if (style.display === "none" || style.visibility === "hidden") continue;
    const bg = resolveOpaqueBg(el, win);
    if (!bg) continue;
    const lum = relativeLuminance(bg.r, bg.g, bg.b);
    const tooLightForDark = theme === "dark" && lum > LIGHT_BG_LUMA;
    const tooDarkForLight = theme === "light" && lum < DARK_BG_LUMA;
    if (!tooLightForDark && !tooDarkForLight) continue;
    const stamp = el.getAttribute("data-norkron-lib") ?? "";
    const dedup = `${stamp}::${el.tagName}`;
    if (reported.has(dedup)) continue;
    reported.add(dedup);
    const cls = (el.getAttribute("class") ?? "").slice(0, 80);
    findings.push({
      libId: LIB_ID45,
      code: "STR_THEME_TOKEN_NOT_THEMED",
      category: "theme",
      severity: "warning",
      message: `<${el.tagName.toLowerCase()}${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}> (stamped ${stamp}) renders with background luminance ${lum.toFixed(2)} but the active theme is "${theme}". The element is painting in the OPPOSITE theme to the document \u2014 almost always a stylesheet whose dark override uses too-low CSS specificity (e.g. wrapped in \`:where()\`), or a missing \`[data-theme="dark"]\` selector, or the lib's tokens were not loaded.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Inspect the lib\'s tokens.css: the dark/light override must have HIGHER specificity than `:root`. Drop any `:where()` wrapper around the theme selector. Verify the lib\'s `styles.css` is imported. If the inversion IS intentional (a callout, branded chrome), set `data-allow-theme-mismatch="true"`. For intentionally theme-invariant fixture surfaces (logos, photographs, an editor catalog stage that pins one theme by design), set `data-allow-single-theme="true"` \u2014 that attribute also satisfies STR_THEME_VARIANT_MISSING.',
      extras: {
        stamp,
        theme,
        luminance: lum,
        backgroundColor: `rgb(${bg.r}, ${bg.g}, ${bg.b})`,
        className: cls,
        thresholds: { light: LIGHT_BG_LUMA, dark: DARK_BG_LUMA }
      }
    });
  }
  return findings;
}

// src/structural/theme-variant-missing.ts
var LIB_ID46 = "@norkron/web-conformance";
var ALLOW_OPT_OUT_ATTR3 = "data-allow-single-theme";
function rgbaIsTransparent(value) {
  const trimmed = (value ?? "").trim();
  if (!trimmed || trimmed === "transparent") return true;
  const m = trimmed.match(/^rgba?\([^)]*\)$/);
  if (!m) return false;
  const alphaMatch = trimmed.match(/,\s*([0-9.]+)\s*\)$/);
  if (alphaMatch) {
    const a = parseFloat(alphaMatch[1] ?? "1");
    if (Number.isFinite(a) && a < 0.01) return true;
  }
  return false;
}
function hasOptOut4(el, stopAt) {
  let cur = el;
  while (cur && cur !== stopAt.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_OPT_OUT_ATTR3) !== null) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function isThemableCandidate(el, style) {
  if (el.getAttribute && el.getAttribute("data-norkron-lib") !== null) {
    return true;
  }
  const inline = (el.getAttribute("style") ?? "").toLowerCase();
  if (inline.includes("background") || inline.includes("color:") && !inline.includes("color: inherit")) {
    return true;
  }
  if (!rgbaIsTransparent(style.backgroundColor ?? "")) {
    const rect = el.getBoundingClientRect?.();
    if (rect && rect.width > 0 && rect.height > 0) return true;
  }
  return false;
}
var PROPS = [
  { key: "backgroundColor", inline: ["background-color", "background:"] },
  { key: "color", inline: ["color:"] },
  { key: "borderTopColor", inline: ["border-top-color", "border:"] },
  { key: "borderRightColor", inline: ["border-right-color", "border:"] },
  { key: "borderBottomColor", inline: ["border-bottom-color", "border:"] },
  { key: "borderLeftColor", inline: ["border-left-color", "border:"] },
  { key: "outlineColor", inline: ["outline-color", "outline:"] },
  { key: "fill", inline: ["fill:"] },
  { key: "stroke", inline: ["stroke:"] },
  { key: "backgroundImage", inline: ["background-image", "background:"] }
];
function readSample(el, win) {
  try {
    const cs = win.getComputedStyle(el);
    const out = {};
    for (const p of PROPS) {
      out[p.key] = cs[p.key] ?? "";
    }
    return out;
  } catch {
    return null;
  }
}
function setProps(el, before) {
  const inline = (el.getAttribute("style") ?? "").toLowerCase();
  const setKeys = /* @__PURE__ */ new Set();
  for (const p of PROPS) {
    if (p.inline.some((frag) => inline.includes(frag))) {
      setKeys.add(p.key);
    }
  }
  if (el.getAttribute("data-norkron-lib") !== null) {
    if (!rgbaIsTransparent(before.backgroundColor)) {
      setKeys.add("backgroundColor");
    }
    if (before.color && before.color !== "rgb(0, 0, 0)") {
      setKeys.add("color");
    }
    if (before.backgroundImage && before.backgroundImage !== "none") {
      setKeys.add("backgroundImage");
    }
  }
  return [...setKeys];
}
function differingProps(before, after, keys) {
  return keys.filter((k) => before[k] !== after[k]);
}
var DEFAULT_NO_PAINT_VALUES = {
  backgroundImage: ["none"],
  backgroundColor: [
    "rgba(0, 0, 0, 0)",
    "rgba(0,0,0,0)",
    "transparent",
    ""
  ],
  borderTopColor: [],
  borderRightColor: [],
  borderBottomColor: [],
  borderLeftColor: [],
  outlineColor: [],
  fill: [],
  stroke: []
};
var BORDER_LIKE_KEYS = /* @__PURE__ */ new Set([
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "outlineColor"
]);
function isDefaultNoPaint(key, value, sample) {
  const defaults = DEFAULT_NO_PAINT_VALUES[key];
  if (defaults && defaults.includes(value.trim())) return true;
  if (BORDER_LIKE_KEYS.has(key) && value === sample.color) return true;
  return false;
}
function filterDefaultNoPaint(stuckKeys, sample) {
  return stuckKeys.filter((k) => !isDefaultNoPaint(k, sample[k], sample));
}
function checkThemeVariantMissing(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4("STR_THEME_VARIANT_MISSING", layout.reason)
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_THEME_VARIANT_MISSING",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  const html = doc.documentElement;
  const originalTheme = html.getAttribute("data-theme");
  const otherTheme = originalTheme === "dark" ? "light" : "dark";
  const all = root.querySelectorAll("*");
  const candidates = [];
  all.forEach((el) => {
    if (hasOptOut4(el, root)) return;
    let style;
    try {
      style = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (style.display === "none" || style.visibility === "hidden") return;
    if (!isThemableCandidate(el, style)) return;
    const before = readSample(el, win);
    if (!before) return;
    candidates.push({ el, before });
  });
  if (candidates.length === 0) return findings;
  html.setAttribute("data-theme", otherTheme);
  void root.offsetHeight;
  const reported = /* @__PURE__ */ new Set();
  for (const c of candidates) {
    const after = readSample(c.el, win);
    if (!after) continue;
    const ownedKeys = setProps(c.el, c.before);
    if (ownedKeys.length === 0) continue;
    const changedKeys = differingProps(c.before, after, ownedKeys);
    if (changedKeys.length === ownedKeys.length) continue;
    const rawStuckKeys = ownedKeys.filter((k) => !changedKeys.includes(k));
    const stuckKeys = filterDefaultNoPaint(rawStuckKeys, c.before);
    if (stuckKeys.length === 0) continue;
    const stamp = c.el.getAttribute("data-norkron-lib") ?? "";
    const tag = c.el.tagName.toLowerCase();
    const cls = (c.el.getAttribute("class") ?? "").slice(0, 80);
    const dedup = `${stamp}|${tag}|${cls}|${stuckKeys.join(",")}`;
    if (reported.has(dedup)) continue;
    reported.add(dedup);
    const stuckSample = stuckKeys.map((k) => `${k}=${c.before[k]}`).join(", ");
    findings.push({
      libId: LIB_ID46,
      code: "STR_THEME_VARIANT_MISSING",
      category: "theme",
      severity: "warning",
      message: `<${tag}${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}>${stamp ? ` (stamped ${stamp})` : ""} has consumer-set color properties that DO NOT change when the theme switches between "${originalTheme ?? "unset"}" and "${otherTheme}": ${stuckSample}. Almost always inline-style hex literals OR a stylesheet that only covers one of light/dark.`,
      selector: tag,
      suggestedFix: 'Replace inline hex literals with CSS custom properties that have light + dark fallbacks (define defaults at `:root` and override at `[data-theme="dark"]` WITHOUT `:where()` \u2014 that zeroes specificity). For lib stylesheets, ensure BOTH theme blocks exist with matching keys. Opt out via `data-allow-single-theme="true"` for intentionally theme-invariant chrome (logos, photographs, branded splashes).',
      extras: {
        stamp,
        className: cls,
        originalTheme,
        otherTheme,
        stuckKeys,
        stuckValues: stuckKeys.reduce(
          (acc, k) => ({ ...acc, [k]: c.before[k] }),
          {}
        ),
        changedKeys
      }
    });
  }
  if (originalTheme === null) html.removeAttribute("data-theme");
  else html.setAttribute("data-theme", originalTheme);
  void root.offsetHeight;
  return findings;
}

// src/structural/element-clipped-by-ancestor.ts
var LIB_ID47 = "@norkron/web-conformance";
var CLIP_TOLERANCE_PX = 1;
var ALLOW_OPT_OUT_ATTR4 = "data-allow-clip";
function isClippingStyle(style) {
  const isHardClip = (v) => {
    const t = (v ?? "").trim();
    return t === "hidden" || t === "clip";
  };
  if (isHardClip(style.overflowX) || isHardClip(style.overflowY)) return true;
  const cp = style.clipPath;
  if (cp && cp.trim() && cp !== "none") return true;
  return false;
}
function hasOptOut5(el, stopAt) {
  let cur = el;
  while (cur && cur !== (stopAt?.parentElement ?? null)) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_OPT_OUT_ATTR4) !== null) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function findAllClippingAncestors(el, win) {
  const out = [];
  let cur = el.parentElement;
  while (cur) {
    let style;
    try {
      style = win.getComputedStyle(cur);
    } catch {
      break;
    }
    const ox = (style.overflowX ?? "").trim();
    const oy = (style.overflowY ?? "").trim();
    if (ox === "auto" || ox === "scroll" || oy === "auto" || oy === "scroll") {
      break;
    }
    if (isClippingStyle(style)) {
      out.push({ ancestor: cur, style });
    }
    cur = cur.parentElement;
  }
  return out;
}
function pxToNum(s) {
  if (!s) return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}
function clipAmounts(candidate, ancestor, style) {
  const padL = pxToNum(style.paddingLeft);
  const padR = pxToNum(style.paddingRight);
  const padT = pxToNum(style.paddingTop);
  const padB = pxToNum(style.paddingBottom);
  const brdL = pxToNum(style.borderLeftWidth);
  const brdR = pxToNum(style.borderRightWidth);
  const brdT = pxToNum(style.borderTopWidth);
  const brdB = pxToNum(style.borderBottomWidth);
  const contentLeft = ancestor.left + brdL + padL;
  const contentRight = ancestor.right - brdR - padR;
  const contentTop = ancestor.top + brdT + padT;
  const contentBottom = ancestor.bottom - brdB - padB;
  return {
    left: Math.max(0, contentLeft - candidate.left),
    right: Math.max(0, candidate.right - contentRight),
    top: Math.max(0, contentTop - candidate.top),
    bottom: Math.max(0, candidate.bottom - contentBottom)
  };
}
function checkElementClippedByAncestor(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4(
        "STR_ELEMENT_CLIPPED_BY_ANCESTOR",
        layout.reason
      )
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_ELEMENT_CLIPPED_BY_ANCESTOR",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  const sel = '[data-keep-clear="true"], [data-norkron-lib], button, [role="button"], [role="tab"], [role="menuitem"]';
  const candidates = root.querySelectorAll(sel);
  const reported = /* @__PURE__ */ new Set();
  candidates.forEach((el) => {
    if (reported.has(el)) return;
    if (hasOptOut5(el, root)) return;
    try {
      const elStyle = win.getComputedStyle(el);
      if (elStyle && (elStyle.position === "fixed" || elStyle.position === "absolute")) {
        return;
      }
    } catch {
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const clippers = findAllClippingAncestors(el, win);
    if (clippers.length === 0) return;
    let clipper = null;
    let ancestorRect = null;
    let amounts = null;
    for (const c of clippers) {
      const r = c.ancestor.getBoundingClientRect();
      const a = clipAmounts(rect, r, c.style);
      const total = a.left + a.right + a.top + a.bottom;
      if (total > CLIP_TOLERANCE_PX) {
        clipper = c;
        ancestorRect = r;
        amounts = a;
        break;
      }
    }
    if (!clipper || !ancestorRect || !amounts) return;
    const fullyClipped = rect.left >= ancestorRect.right || rect.right <= ancestorRect.left || rect.top >= ancestorRect.bottom || rect.bottom <= ancestorRect.top;
    reported.add(el);
    const tag = el.tagName.toLowerCase();
    const cls = (el.getAttribute("class") ?? "").slice(0, 80);
    const stamp = el.getAttribute("data-norkron-lib") ?? "";
    const ariaLabel = el.getAttribute("aria-label") ?? "";
    const ancestorTag = clipper.ancestor.tagName.toLowerCase();
    const ancestorCls = (clipper.ancestor.getAttribute("class") ?? "").slice(0, 80);
    const kind = fullyClipped ? "FULLY HIDDEN" : "partially clipped";
    findings.push({
      libId: LIB_ID47,
      code: "STR_ELEMENT_CLIPPED_BY_ANCESTOR",
      category: "layout",
      severity: "warning",
      message: `<${tag}${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}>${ariaLabel ? ` (aria-label="${ariaLabel}")` : ""}${stamp ? ` stamped ${stamp}` : ""} is ${kind} by clipping ancestor <${ancestorTag}.${ancestorCls.split(/\s+/).filter(Boolean).join(".")}>: left=${Math.round(amounts.left)}px right=${Math.round(amounts.right)}px top=${Math.round(amounts.top)}px bottom=${Math.round(amounts.bottom)}px.`,
      selector: tag,
      suggestedFix: 'Either grow the clipping ancestor so it can hold the candidate, or shrink/relocate the candidate, OR change the ancestor\'s `overflow` to `visible` (and accept the bleed), OR opt out via `data-allow-clip="true"` on the candidate. For toolbar chrome that overflows on small viewports, consider a responsive collapse rule (hide low-priority buttons below a breakpoint).',
      extras: {
        stamp,
        ariaLabel,
        className: cls,
        clipAmounts: amounts,
        fullyClipped,
        clippingAncestor: {
          tag: ancestorTag,
          className: ancestorCls,
          rect: {
            left: ancestorRect.left,
            right: ancestorRect.right,
            top: ancestorRect.top,
            bottom: ancestorRect.bottom
          },
          overflowX: clipper.style.overflowX,
          overflowY: clipper.style.overflowY
        },
        candidateRect: {
          left: rect.left,
          right: rect.right,
          top: rect.top,
          bottom: rect.bottom
        }
      }
    });
  });
  return findings;
}

// src/structural/_svg-paint.ts
var TEXT_TAGS = /* @__PURE__ */ new Set(["text", "tspan", "textPath"]);
function parseNum(v) {
  if (v === null || v === "") return 0;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}
function pathDIsNonEmpty(el) {
  return (el.getAttribute("d") ?? "").trim().length > 0;
}
function rectPaints(el) {
  const w = parseNum(el.getAttribute("width"));
  const h = parseNum(el.getAttribute("height"));
  return w > 0 && h > 0;
}
function circlePaints(el) {
  return parseNum(el.getAttribute("r")) > 0;
}
function ellipsePaints(el) {
  return parseNum(el.getAttribute("rx")) > 0 && parseNum(el.getAttribute("ry")) > 0;
}
function linePaints(el) {
  const x1 = parseNum(el.getAttribute("x1"));
  const y1 = parseNum(el.getAttribute("y1"));
  const x2 = parseNum(el.getAttribute("x2"));
  const y2 = parseNum(el.getAttribute("y2"));
  return x1 !== x2 || y1 !== y2;
}
function polyPaints(el) {
  const pts = (el.getAttribute("points") ?? "").trim();
  if (pts.length === 0) return false;
  const nums = pts.split(/[\s,]+/).filter((s) => s.length > 0);
  return nums.length >= 4;
}
function textPaints(el) {
  const t = (el.textContent ?? "").replace(/\s+/g, "");
  return t.length > 0;
}
function svgHasPaintedContent(svg) {
  const all = svg.querySelectorAll("*");
  for (const node of Array.from(all)) {
    const tag = node.tagName.toLowerCase();
    switch (tag) {
      case "path":
        if (pathDIsNonEmpty(node)) return true;
        break;
      case "rect":
        if (rectPaints(node)) return true;
        break;
      case "circle":
        if (circlePaints(node)) return true;
        break;
      case "ellipse":
        if (ellipsePaints(node)) return true;
        break;
      case "line":
        if (linePaints(node)) return true;
        break;
      case "polygon":
      case "polyline":
        if (polyPaints(node)) return true;
        break;
      case "image":
      case "use":
      case "foreignobject":
        return true;
      default:
        if (TEXT_TAGS.has(tag)) {
          if (textPaints(node)) return true;
        }
    }
  }
  return false;
}

// src/structural/container-renders-empty.ts
var LIB_ID48 = "@norkron/web-conformance";
var MIN_CONTAINER_AREA = 80 * 80;
var MIN_TEXT_CHARS = 2;
var REPLACED_CHILDREN = "img,svg,canvas,video,iframe,picture";
var INTERACTIVE_CHILDREN = 'button,a[href],input,select,textarea,summary,[role="button"],[role="link"],[role="tab"],[role="menuitem"],[role="option"],[role="checkbox"],[role="switch"],[role="radio"]';
var CONTAINER_TAGS = /* @__PURE__ */ new Set([
  "ASIDE",
  "SECTION",
  "NAV",
  "MAIN",
  "HEADER",
  "FOOTER",
  "ARTICLE",
  "FORM"
]);
var ALLOW_ATTR2 = "data-allow-empty-container";
var PORTAL_TRIGGER_SELECTOR = '[aria-controls],[aria-haspopup],[popovertarget],[aria-expanded="false"],[aria-expanded="true"]';
function isPortalTriggerHost(el) {
  const triggers = el.querySelectorAll(PORTAL_TRIGGER_SELECTOR);
  if (triggers.length === 0) return false;
  const interactive = el.querySelectorAll(INTERACTIVE_CHILDREN);
  for (const c of Array.from(interactive)) {
    const isTrigger = c.matches(PORTAL_TRIGGER_SELECTOR) || c.closest(PORTAL_TRIGGER_SELECTOR) !== null;
    if (!isTrigger) return false;
  }
  return true;
}
function isLoadingSkeletonHost(el) {
  const busy = el.querySelectorAll('[aria-busy="true"]');
  if (busy.length === 0) return false;
  const walker = (el.ownerDocument ?? document).createTreeWalker(
    el,
    NodeFilter.SHOW_TEXT
  );
  let node = walker.nextNode();
  while (node) {
    const t = (node.textContent ?? "").replace(/\s+/g, "");
    if (t.length > 0) {
      const parent = node.parentElement;
      if (!parent || !parent.closest('[aria-busy="true"]')) return false;
    }
    node = walker.nextNode();
  }
  const painted = el.querySelectorAll(
    `${INTERACTIVE_CHILDREN},${REPLACED_CHILDREN}`
  );
  for (const p of Array.from(painted)) {
    if (!p.closest('[aria-busy="true"]')) return false;
  }
  return true;
}
function isOpaqueBgColor(bg) {
  const v = bg.trim().toLowerCase();
  if (!v || v === "transparent" || v === "none") return false;
  const rgbMatch = v.match(/^rgb\(\s*[\d.]+[\s,]+[\d.]+[\s,]+[\d.]+\s*\)$/);
  if (rgbMatch) return true;
  const rgbaMatch = v.match(/^rgba\(\s*[\d.]+[\s,]+[\d.]+[\s,]+[\d.]+[\s,/]+([\d.]+%?)\s*\)$/);
  if (rgbaMatch) {
    const raw = rgbaMatch[1] ?? "1";
    const alpha = raw.endsWith("%") ? parseFloat(raw) / 100 : parseFloat(raw);
    return Number.isFinite(alpha) && alpha > 0.999;
  }
  return false;
}
function hasOptOut6(el, stopAt) {
  let cur = el;
  while (cur && cur !== stopAt.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR2) !== null) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function maxAndSumContentArea(el, win) {
  let maxArea = 0;
  let sumArea = 0;
  const note = (r) => {
    const a = r.width * r.height;
    if (a > maxArea) maxArea = a;
    sumArea += a;
  };
  const interactive = el.querySelectorAll(INTERACTIVE_CHILDREN);
  for (const c of Array.from(interactive)) {
    const r = c.getBoundingClientRect();
    if (r.width >= 2 && r.height >= 2) note(r);
  }
  const replaced = el.querySelectorAll(REPLACED_CHILDREN);
  for (const c of Array.from(replaced)) {
    if (c.tagName.toLowerCase() === "svg" && !svgHasPaintedContent(c)) {
      continue;
    }
    const r = c.getBoundingClientRect();
    if (r.width >= 4 && r.height >= 4) note(r);
  }
  const all = el.querySelectorAll("*");
  for (const c of Array.from(all)) {
    try {
      const cs = win.getComputedStyle(c);
      const bg = (cs.backgroundImage ?? "").trim();
      if (bg && bg !== "none") {
        const r = c.getBoundingClientRect();
        if (r.width >= 8 && r.height >= 8) note(r);
      }
    } catch {
    }
  }
  return { maxArea, sumArea };
}
function checkContainerRendersEmpty(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4("STR_CONTAINER_RENDERS_EMPTY", layout.reason)
    );
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    findings.push(
      needsLayoutFinding4(
        "STR_CONTAINER_RENDERS_EMPTY",
        "no-getComputedStyle"
      )
    );
    return findings;
  }
  const reported = /* @__PURE__ */ new Set();
  const candidates = root.querySelectorAll(
    "aside,section,nav,main,header,footer,article,form,div"
  );
  candidates.forEach((el) => {
    if (reported.has(el)) return;
    const tagOk = CONTAINER_TAGS.has(el.tagName);
    const rect = el.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (!tagOk && area < MIN_CONTAINER_AREA) return;
    if (rect.width === 0 || rect.height === 0) return;
    if (hasOptOut6(el, root)) return;
    if (isPortalTriggerHost(el)) return;
    let style;
    try {
      style = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (style.display === "none" || style.visibility === "hidden") return;
    if (el.getAttribute("aria-hidden") === "true") return;
    if (el.closest && el.closest('[aria-busy="true"]')) return;
    if (isLoadingSkeletonHost(el)) return;
    const role = el.getAttribute("role");
    if (role === "slider" || role === "progressbar" || role === "separator") return;
    if (isOpaqueBgColor(style.backgroundColor ?? "")) return;
    const ownBg = (style.backgroundImage ?? "").trim();
    if (ownBg && ownBg !== "none") return;
    const text = (el.textContent ?? "").replace(/\s+/g, "").trim();
    const hasText = text.length >= MIN_TEXT_CHARS;
    const { maxArea, sumArea } = maxAndSumContentArea(el, win);
    const FILL_THRESHOLD = 0.05;
    const fillRatio = area > 0 ? sumArea / area : 0;
    const maxRatio = area > 0 ? maxArea / area : 0;
    const fullyEmpty = !hasText && maxArea === 0;
    const underfilled = !hasText && area >= MIN_CONTAINER_AREA && fillRatio < FILL_THRESHOLD && maxRatio < FILL_THRESHOLD;
    if (!fullyEmpty && !underfilled) return;
    reported.add(el);
    const cls = (el.getAttribute("class") ?? "").slice(0, 80);
    const kind = fullyEmpty ? "is paint-empty" : `renders ${Math.round(fillRatio * 100)}% filled (largest visible descendant covers ${Math.round(maxRatio * 100)}% of its area)`;
    findings.push({
      libId: LIB_ID48,
      code: "STR_CONTAINER_RENDERS_EMPTY",
      category: "rendering",
      severity: "warning",
      message: `<${el.tagName.toLowerCase()}${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}> occupies ${Math.round(rect.width)}x${Math.round(rect.height)}px of visible space but ${kind} \u2014 the container is visually empty to a sighted user. Almost always: a child component crashed silently, a data fetch resolved to an empty list, a conditional render always evaluated falsy, or the lib that should populate the slot never mounted (or mounted only its tiny trigger without the expanded content).`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Check the React tree at this DOM node \u2014 is the child component throwing without an error boundary? Is the data fetch resolving to an empty list? Is there a conditional render whose predicate is always false? Is a Combobox/Picker mounting only its trigger input without the expanded options list? If the container is intentionally blank (a layout spacer / future slot), set `data-allow-empty-container="true"` on it.',
      extras: {
        className: cls,
        rect: {
          width: rect.width,
          height: rect.height,
          left: rect.left,
          top: rect.top
        },
        area,
        fillRatio,
        maxDescendantRatio: maxRatio,
        kind: fullyEmpty ? "paint-empty" : "underfilled"
      }
    });
  });
  return findings;
}

// src/responsive-registry.ts
var REGISTRY;
function getRegistry2() {
  return REGISTRY ??= /* @__PURE__ */ new Map();
}
function registerResponsiveContract(lib, contract) {
  if (typeof lib !== "string" || lib.length === 0) return;
  if (contract == null || typeof contract !== "object") return;
  getRegistry2().set(lib, contract);
}
function getResponsiveContract(lib) {
  return getRegistry2().get(lib);
}
function hasResponsiveContract(lib) {
  return getRegistry2().has(lib);
}
function __resetResponsiveRegistry() {
  getRegistry2().clear();
}

// src/structural/responsive-contract-missing.ts
var LIB_ID49 = "@norkron/web-conformance";
function libNameOf(raw) {
  const at = raw.lastIndexOf("@");
  if (at > 0) return raw.slice(0, at);
  return raw;
}
function checkResponsiveContractMissing(root) {
  const findings = [];
  const stamped = root.querySelectorAll("[data-norkron-lib]");
  const missing = /* @__PURE__ */ new Map();
  stamped.forEach((el) => {
    const raw = el.getAttribute("data-norkron-lib");
    if (!raw) return;
    const lib = libNameOf(raw.trim());
    if (lib.length === 0) return;
    if (hasResponsiveContract(lib)) return;
    missing.set(lib, (missing.get(lib) ?? 0) + 1);
  });
  for (const [lib, count] of missing) {
    findings.push({
      libId: LIB_ID49,
      code: "STR_RESPONSIVE_CONTRACT_MISSING",
      category: "layout",
      severity: "major",
      message: `Lib "${lib}" stamps ${count} element(s) but has not registered a ResponsiveContract via registerResponsiveContract(). The audit cannot verify its cross-viewport behavior.`,
      selector: `[data-norkron-lib^="${lib}"]`,
      suggestedFix: `In ${lib}'s entrypoint, call registerResponsiveContract('${lib}', { breakpoints, minSupportedWidth, strategy }) from '@norkron/web-conformance' at import time so the audit can reason about its responsive behavior.`,
      extras: {
        lib,
        elementCount: count
      }
    });
  }
  return findings;
}

// src/structural/pill-container-wraps.ts
var ALLOW_ATTR3 = "data-allow-pill-wrap";
function maxCornerRadiusPx(cs, height) {
  const corners = [
    cs.borderTopLeftRadius,
    cs.borderTopRightRadius,
    cs.borderBottomLeftRadius,
    cs.borderBottomRightRadius
  ];
  let max = 0;
  for (const c of corners) {
    const parsed = parseFloat(c);
    if (!Number.isFinite(parsed)) continue;
    const px2 = c.endsWith("%") ? parsed / 100 * height : parsed;
    if (px2 > max) max = px2;
  }
  return max;
}
function hasOptOut7(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR3) !== null) return true;
    cur = cur.parentElement;
  }
  return false;
}
function checkPillContainerWraps(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const all = root.querySelectorAll("*");
  all.forEach((el) => {
    const children = Array.from(el.children);
    if (children.length < 2) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height < 20) return;
    if (el.getAttribute("aria-hidden") === "true") return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden") return;
    const maxR = maxCornerRadiusPx(cs, rect.height);
    if (maxR / rect.height < 0.4) return;
    const boxes = [];
    for (const c of children) {
      const cr = c.getBoundingClientRect();
      if (cr.width <= 0 || cr.height <= 0) continue;
      boxes.push({ top: cr.top, bottom: cr.bottom });
    }
    if (boxes.length < 2) return;
    boxes.sort((a, b) => a.top - b.top);
    let rowCount = 1;
    let rowMaxBottom = boxes[0].bottom;
    for (let i = 1; i < boxes.length; i++) {
      if (boxes[i].top > rowMaxBottom + 2) {
        rowCount += 1;
        rowMaxBottom = boxes[i].bottom;
      } else if (boxes[i].bottom > rowMaxBottom) {
        rowMaxBottom = boxes[i].bottom;
      }
      if (rowCount >= 2) break;
    }
    if (rowCount < 2) return;
    if (hasOptOut7(el, root)) return;
    const cls = (el.getAttribute("class") ?? "").slice(0, 60);
    const sel = el.tagName.toLowerCase() + (cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "");
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_PILL_CONTAINER_WRAPS",
      category: "rendering",
      severity: "major",
      message: `<${sel}> is a pill-shaped container (border-radius=${Math.round(maxR)}px on ${Math.round(rect.height)}px height) that wraps its content to multiple rows. The huge corner arc no longer matches the inner content shape \u2014 children sit inside a giant pill chassis with visible empty curved space at the corners.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Drop the pill border-radius to a moderate value (8-12px) so the chassis still looks intentional when wrapped; OR constrain the content so it cannot wrap (label truncation, switch to dropdown above N options); OR opt-out with data-allow-pill-wrap="true" if the visual is intentional.',
      extras: {
        borderRadiusPx: maxR,
        heightPx: rect.height,
        radiusRatio: maxR / rect.height,
        rowCount,
        className: cls
      }
    });
  });
  return findings;
}

// src/structural/corners-not-concentric.ts
var ALLOW_ATTR4 = "data-allow-corner-mismatch";
var MIN_RADIUS_TO_CHECK_PX = 4;
var TOLERANCE_PX = 2;
function maxCornerRadiusPx2(cs, w, h) {
  const corners = [
    cs.borderTopLeftRadius,
    cs.borderTopRightRadius,
    cs.borderBottomLeftRadius,
    cs.borderBottomRightRadius
  ];
  const ref = Math.min(w, h);
  let max = 0;
  for (const c of corners) {
    const parsed = parseFloat(c);
    if (!Number.isFinite(parsed)) continue;
    const px2 = c.endsWith("%") ? parsed / 100 * ref : parsed;
    if (px2 > max) max = px2;
  }
  return Math.min(max, ref / 2);
}
function hasOptOut8(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR4) !== null) return true;
    cur = cur.parentElement;
  }
  return false;
}
function checkCornersNotConcentric(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const all = root.querySelectorAll("*");
  const reported = /* @__PURE__ */ new Set();
  all.forEach((el) => {
    if (reported.has(el)) return;
    const children = Array.from(el.children);
    if (children.length === 0) return;
    const pRect = el.getBoundingClientRect();
    if (pRect.width < 24 || pRect.height < 24) return;
    if (el.getAttribute("aria-hidden") === "true") return;
    let pCs;
    try {
      pCs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (pCs.display === "none" || pCs.visibility === "hidden") return;
    const pRadius = maxCornerRadiusPx2(pCs, pRect.width, pRect.height);
    if (pRadius < MIN_RADIUS_TO_CHECK_PX) return;
    const padL = parseFloat(pCs.paddingLeft) || 0;
    const padT = parseFloat(pCs.paddingTop) || 0;
    const bdL = parseFloat(pCs.borderLeftWidth) || 0;
    const bdT = parseFloat(pCs.borderTopWidth) || 0;
    let worst = null;
    for (const c of children) {
      let cCs;
      try {
        cCs = win.getComputedStyle(c);
      } catch {
        continue;
      }
      if (cCs.display === "none" || cCs.visibility === "hidden") continue;
      const cRect = c.getBoundingClientRect();
      if (cRect.width <= 0 || cRect.height <= 0) continue;
      const cRadius = maxCornerRadiusPx2(cCs, cRect.width, cRect.height);
      if (cRadius < MIN_RADIUS_TO_CHECK_PX) continue;
      const offsetL = Math.max(0, cRect.left - (pRect.left + padL + bdL));
      const offsetT = Math.max(0, cRect.top - (pRect.top + padT + bdT));
      const cornerOffset = Math.max(padL + bdL + offsetL, padT + bdT + offsetT, 0);
      const expected = cRadius + cornerOffset;
      if (!worst || expected - pRadius > expected - worst.expected) {
        worst = { child: c, cRadius, offset: cornerOffset, expected };
      }
    }
    if (!worst) return;
    const delta = worst.expected - pRadius;
    if (delta <= TOLERANCE_PX) return;
    if (hasOptOut8(el, root)) return;
    reported.add(el);
    const cls = (el.getAttribute("class") ?? "").slice(0, 60);
    const sel = el.tagName.toLowerCase() + (cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "");
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_CORNERS_NOT_CONCENTRIC",
      category: "rendering",
      severity: "minor",
      message: `<${sel}> has border-radius ${Math.round(pRadius)}px, but its children's corners + offset expect ~${Math.round(worst.expected)}px (child radius ${Math.round(worst.cRadius)}px + ${Math.round(worst.offset)}px offset). The parent arc is tighter than the inner arc \u2014 corners diverge instead of nesting. Bump parent radius to ${Math.round(worst.expected)}px for concentric corners.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Concentric-corner rule: parent border-radius = child border-radius + (parent padding + border). Either bump parent radius to match, drop child radius to match (parent - offset), or remove rounding from one of the two. Opt out via data-allow-corner-mismatch="true" if the visual mismatch is intentional.',
      extras: {
        parentRadius: pRadius,
        childRadius: worst.cRadius,
        offset: worst.offset,
        expectedParentRadius: worst.expected,
        deltaPx: delta,
        className: cls
      }
    });
  });
  return findings;
}

// src/structural/wrapped-flex-orphan.ts
var ALLOW_ATTR5 = "data-allow-orphan-row";
function hasOptOut9(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR5) !== null) return true;
    cur = cur.parentElement;
  }
  return false;
}
function checkWrappedFlexOrphan(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const all = root.querySelectorAll("*");
  all.forEach((el) => {
    const children = Array.from(el.children);
    if (children.length < 3) return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden") return;
    const isFlexWrap = (cs.display === "flex" || cs.display === "inline-flex") && (cs.flexWrap === "wrap" || cs.flexWrap === "wrap-reverse");
    const isGridWrap = (cs.display === "grid" || cs.display === "inline-grid") && /auto/.test(cs.gridTemplateColumns);
    if (!isFlexWrap && !isGridWrap) return;
    const rows = /* @__PURE__ */ new Map();
    for (const c of children) {
      const cr = c.getBoundingClientRect();
      if (cr.width <= 1 || cr.height <= 1) continue;
      let cCs;
      try {
        cCs = win.getComputedStyle(c);
      } catch {
        continue;
      }
      if (cCs.display === "none" || cCs.visibility === "hidden") continue;
      if (cCs.position === "absolute" || cCs.position === "fixed") continue;
      const bucket = Math.round(cr.top / 4) * 4;
      if (!rows.has(bucket)) rows.set(bucket, []);
      rows.get(bucket).push(c);
    }
    if (rows.size < 2) return;
    const sortedRows = Array.from(rows.entries()).sort((a, b) => a[0] - b[0]);
    const lastRow = sortedRows[sortedRows.length - 1];
    if (!lastRow) return;
    const earlierRows = sortedRows.slice(0, -1);
    if (lastRow[1].length !== 1) return;
    const someEarlierRowHasMultiple = earlierRows.some(([, items]) => items.length >= 2);
    if (!someEarlierRowHasMultiple) return;
    if (hasOptOut9(el, root)) return;
    const cls = (el.getAttribute("class") ?? "").slice(0, 60);
    const sel = el.tagName.toLowerCase() + (cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "");
    const orphanText = (lastRow[1][0]?.textContent ?? "").trim().slice(0, 40);
    const totalChildren = children.length;
    const rowSizes = sortedRows.map(([, items]) => items.length).join("+");
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_WRAPPED_FLEX_ORPHAN",
      category: "rendering",
      severity: "minor",
      message: `<${sel}> has a wrapped layout where the last row contains only "${orphanText}" alone (row pattern ${rowSizes} for ${totalChildren} items). Same as typography widow/orphan \u2014 a lone item dangling on the last row reads as broken.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Use grid with `grid-template-columns: repeat(auto-fit, minmax(W, 1fr))` so the column count is consistent (5 items become 3+2 instead of 4+1). OR set `flex: 1 1 33%` on children so 3 per row is enforced. OR drop/merge an option to balance the count. OR opt out via data-allow-orphan-row="true" if intentional.',
      extras: {
        totalChildren,
        rowSizes,
        orphanText,
        className: cls
      }
    });
  });
  return findings;
}

// src/structural/text-overflows-content-box.ts
var ALLOW_ATTR6 = "data-allow-text-overflow";
var EDGE_ALLOW_ATTR = "data-allow-edge-text";
var TOLERANCE_PX2 = 3;
function hasOptOut10(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute) {
      if (cur.getAttribute(ALLOW_ATTR6) !== null) return true;
      if (cur.getAttribute(EDGE_ALLOW_ATTR) !== null) return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function checkTextOverflowsContentBox(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  let node = walker.nextNode();
  const seenParents = /* @__PURE__ */ new Set();
  while (node) {
    const text = (node.nodeValue ?? "").trim();
    if (text.length === 0) {
      node = walker.nextNode();
      continue;
    }
    const parent = node.parentElement;
    if (!parent || seenParents.has(parent)) {
      node = walker.nextNode();
      continue;
    }
    seenParents.add(parent);
    if (text.includes("CONF_LIVE_FINDING") || text.includes("@norkron/web-conformance")) {
      node = walker.nextNode();
      continue;
    }
    const tag = parent.tagName.toUpperCase();
    if (tag === "BODY" || tag === "HTML" || tag === "MAIN") {
      node = walker.nextNode();
      continue;
    }
    const pRect = parent.getBoundingClientRect();
    if (pRect.width < 24 || pRect.height < 16) {
      node = walker.nextNode();
      continue;
    }
    let pCs;
    try {
      pCs = win.getComputedStyle(parent);
    } catch {
      node = walker.nextNode();
      continue;
    }
    if (pCs.display === "none" || pCs.visibility === "hidden") {
      node = walker.nextNode();
      continue;
    }
    const pOverflow = `${pCs.overflowX || ""} ${pCs.overflow || ""}`;
    const clips = /\b(hidden|clip)\b/.test(pOverflow);
    const ellipsis = (pCs.textOverflow || "").includes("ellipsis");
    const noWrap = (pCs.whiteSpace || "").includes("nowrap");
    if (clips && ellipsis && noWrap) {
      node = walker.nextNode();
      continue;
    }
    let tRect;
    try {
      const range = doc.createRange();
      range.selectNodeContents(node);
      const rects = range.getClientRects();
      if (!rects || rects.length === 0) {
        node = walker.nextNode();
        continue;
      }
      let left = Infinity, right = -Infinity, top = Infinity, bottom = -Infinity;
      for (const r of Array.from(rects)) {
        if (r.width <= 0) continue;
        if (r.left < left) left = r.left;
        if (r.right > right) right = r.right;
        if (r.top < top) top = r.top;
        if (r.bottom > bottom) bottom = r.bottom;
      }
      if (!Number.isFinite(left)) {
        node = walker.nextNode();
        continue;
      }
      tRect = new DOMRect(left, top, right - left, bottom - top);
    } catch {
      node = walker.nextNode();
      continue;
    }
    const padL = parseFloat(pCs.paddingLeft) || 0;
    const padR = parseFloat(pCs.paddingRight) || 0;
    const padT = parseFloat(pCs.paddingTop) || 0;
    const padB = parseFloat(pCs.paddingBottom) || 0;
    const bdL = parseFloat(pCs.borderLeftWidth) || 0;
    const bdR = parseFloat(pCs.borderRightWidth) || 0;
    const bdT = parseFloat(pCs.borderTopWidth) || 0;
    const bdB = parseFloat(pCs.borderBottomWidth) || 0;
    const contentLeft = pRect.left + bdL + padL;
    const contentRight = pRect.right - bdR - padR;
    const contentTop = pRect.top + bdT + padT;
    const contentBottom = pRect.bottom - bdB - padB;
    const overL = contentLeft - tRect.left;
    const overR = tRect.right - contentRight;
    const overT = contentTop - tRect.top;
    const overB = tRect.bottom - contentBottom;
    const wsRaw = (pCs.whiteSpace || "").trim();
    const wraps = wsRaw !== "nowrap" && wsRaw !== "pre";
    if (wraps) {
      const el = parent;
      const sw = el.scrollWidth ?? 0;
      const cw = el.clientWidth ?? 0;
      const sh = el.scrollHeight ?? 0;
      const ch = el.clientHeight ?? 0;
      const overflowsX = sw - cw > TOLERANCE_PX2;
      const overflowsY = sh - ch > TOLERANCE_PX2;
      if (!overflowsX && !overflowsY) {
        node = walker.nextNode();
        continue;
      }
    }
    const reasons = [];
    if (overL > TOLERANCE_PX2) reasons.push(`left by ${Math.round(overL)}px`);
    if (overR > TOLERANCE_PX2) reasons.push(`right by ${Math.round(overR)}px`);
    if (overT > TOLERANCE_PX2) reasons.push(`top by ${Math.round(overT)}px`);
    if (overB > TOLERANCE_PX2) reasons.push(`bottom by ${Math.round(overB)}px`);
    if (reasons.length === 0) {
      node = walker.nextNode();
      continue;
    }
    if (hasOptOut10(parent, root)) {
      node = walker.nextNode();
      continue;
    }
    const cls = (parent.getAttribute("class") ?? "").slice(0, 60);
    const sel = parent.tagName.toLowerCase() + (cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "");
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_TEXT_OVERFLOWS_CONTENT_BOX",
      category: "rendering",
      severity: "major",
      message: `Text "${text.slice(0, 40)}" overflows its parent <${sel}>'s content-box: ${reasons.join(", ")}. Text is spilling into the parent's padding / border band even though overflow is visible \u2014 the label is crowding the edge.`,
      selector: parent.tagName.toLowerCase(),
      suggestedFix: 'Widen the parent (raise flex-basis / min-width), shorten the label, allow wrapping (white-space: normal), or truncate with overflow:hidden + text-overflow:ellipsis. Opt-out via data-allow-text-overflow="true" if the bleed is intentional.',
      extras: {
        text: text.slice(0, 80),
        className: cls,
        parentRect: { width: pRect.width, height: pRect.height },
        textRect: { width: tRect.width, height: tRect.height },
        overflowPx: { left: overL, right: overR, top: overT, bottom: overB },
        padding: { left: padL, right: padR, top: padT, bottom: padB }
      }
    });
    node = walker.nextNode();
  }
  return findings;
}

// src/structural/text-asymmetric-padding.ts
var ALLOW_ATTR7 = "data-allow-asymmetric-padding";
var TOLERANCE_PX3 = 4;
function hasOptOut11(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR7) !== null) return true;
    cur = cur.parentElement;
  }
  return false;
}
function checkTextAsymmetricPadding(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  let node = walker.nextNode();
  const seenParents = /* @__PURE__ */ new Set();
  while (node) {
    const text = (node.nodeValue ?? "").trim();
    if (text.length === 0) {
      node = walker.nextNode();
      continue;
    }
    const parent = node.parentElement;
    if (!parent || seenParents.has(parent)) {
      node = walker.nextNode();
      continue;
    }
    seenParents.add(parent);
    if (text.includes("CONF_LIVE_FINDING") || text.includes("@norkron/web-conformance")) {
      node = walker.nextNode();
      continue;
    }
    const tag = parent.tagName.toUpperCase();
    if (tag === "BODY" || tag === "HTML" || tag === "MAIN") {
      node = walker.nextNode();
      continue;
    }
    const pRect = parent.getBoundingClientRect();
    if (pRect.width < 40 || pRect.height < 16) {
      node = walker.nextNode();
      continue;
    }
    let pCs;
    try {
      pCs = win.getComputedStyle(parent);
    } catch {
      node = walker.nextNode();
      continue;
    }
    if (pCs.display === "none" || pCs.visibility === "hidden") {
      node = walker.nextNode();
      continue;
    }
    const isCenterAligned = pCs.textAlign === "center";
    const display = pCs.display;
    const isFlexCentered = (display.startsWith("flex") || display.startsWith("inline-flex")) && pCs.justifyContent === "center";
    if (!isCenterAligned && !isFlexCentered) {
      node = walker.nextNode();
      continue;
    }
    const padL = parseFloat(pCs.paddingLeft) || 0;
    const padR = parseFloat(pCs.paddingRight) || 0;
    if (Math.abs(padL - padR) > 1) {
      node = walker.nextNode();
      continue;
    }
    const bdL = parseFloat(pCs.borderLeftWidth) || 0;
    const bdR = parseFloat(pCs.borderRightWidth) || 0;
    const contentLeft = pRect.left + bdL + padL;
    const contentRight = pRect.right - bdR - padR;
    let tRect;
    try {
      const range = doc.createRange();
      range.selectNodeContents(node);
      const rects = range.getClientRects();
      if (!rects || rects.length === 0) {
        node = walker.nextNode();
        continue;
      }
      let left = Infinity, right = -Infinity, top = Infinity, bottom = -Infinity;
      for (const r of Array.from(rects)) {
        if (r.width <= 0) continue;
        if (r.left < left) left = r.left;
        if (r.right > right) right = r.right;
        if (r.top < top) top = r.top;
        if (r.bottom > bottom) bottom = r.bottom;
      }
      if (!Number.isFinite(left)) {
        node = walker.nextNode();
        continue;
      }
      tRect = new DOMRect(left, top, right - left, bottom - top);
    } catch {
      node = walker.nextNode();
      continue;
    }
    if (tRect.width >= contentRight - contentLeft) {
      node = walker.nextNode();
      continue;
    }
    const gapLeft = tRect.left - contentLeft;
    const gapRight = contentRight - tRect.right;
    const delta = Math.abs(gapLeft - gapRight);
    if (delta <= TOLERANCE_PX3) {
      node = walker.nextNode();
      continue;
    }
    if (hasOptOut11(parent, root)) {
      node = walker.nextNode();
      continue;
    }
    const cls = (parent.getAttribute("class") ?? "").slice(0, 60);
    const sel = parent.tagName.toLowerCase() + (cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "");
    const direction = gapLeft < gapRight ? "left" : "right";
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_TEXT_ASYMMETRIC_PADDING",
      category: "rendering",
      severity: "minor",
      message: `Text "${text.slice(0, 40)}" inside <${sel}> sits ${Math.round(delta)}px closer to the ${direction} edge than the other (gap left=${Math.round(gapLeft)}px, right=${Math.round(gapRight)}px). The container has symmetric padding declared (${Math.round(padL)}px each side) but the text appears off-center \u2014 looks visually unbalanced.`,
      selector: parent.tagName.toLowerCase(),
      suggestedFix: 'Either align the text explicitly (text-align: center / justify-content: center on the flex container), widen the parent so the natural text rect fits with symmetric gaps, or accept the asymmetry with data-allow-asymmetric-padding="true".',
      extras: {
        text: text.slice(0, 80),
        className: cls,
        gapLeft,
        gapRight,
        deltaPx: delta,
        paddingLeft: padL,
        paddingRight: padR,
        direction
      }
    });
    node = walker.nextNode();
  }
  return findings;
}

// src/structural/icu-template-leaked.ts
var ICU_PATTERN = /\{[\s\S]*?,\s*(plural|select|selectordinal|number|date|time)\b/i;
var IGNORE_TAGS = /* @__PURE__ */ new Set(["CODE", "PRE", "TEXTAREA", "SCRIPT", "STYLE"]);
function isUnderIgnoredAncestor(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute("data-conformance-ignore") !== null) return true;
    if (IGNORE_TAGS.has(cur.tagName.toUpperCase())) return true;
    cur = cur.parentElement;
  }
  return false;
}
function checkIcuTemplateLeaked(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  let node = walker.nextNode();
  const seenParents = /* @__PURE__ */ new Set();
  while (node) {
    const text = (node.nodeValue ?? "").trim();
    if (text.length === 0) {
      node = walker.nextNode();
      continue;
    }
    if (text.includes("CONF_LIVE_FINDING") || text.includes("@norkron/web-conformance")) {
      node = walker.nextNode();
      continue;
    }
    if (!ICU_PATTERN.test(text)) {
      node = walker.nextNode();
      continue;
    }
    const parent = node.parentElement;
    if (!parent || seenParents.has(parent)) {
      node = walker.nextNode();
      continue;
    }
    seenParents.add(parent);
    if (isUnderIgnoredAncestor(parent, root)) {
      node = walker.nextNode();
      continue;
    }
    const cls = (parent.getAttribute("class") ?? "").slice(0, 60);
    const sel = parent.tagName.toLowerCase() + (cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "");
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_ICU_TEMPLATE_LEAKED",
      category: "rendering",
      severity: "major",
      message: `<${sel}> renders a raw ICU MessageFormat template: "${text.slice(0, 100)}". The i18n stack passed it through unformatted \u2014 almost always means the ICU plugin (e.g. i18next-icu, @formatjs/intl) isn't loaded so plural/select/number/date templates fall through as literal strings.`,
      selector: parent.tagName.toLowerCase(),
      suggestedFix: "Wire the ICU plugin into the i18n stack (e.g. `i18next.use(ICU).init(...)`), OR rewrite the key using the i18n lib's native plural syntax (i18next: `key_one`/`key_other` + `count` param). See @norkron/web-i18n configuration.",
      extras: { text: text.slice(0, 200), className: cls }
    });
    node = walker.nextNode();
  }
  return findings;
}

// src/structural/minimap-viewport-not-inverted.ts
var ALLOW_ATTR8 = "data-allow-opaque-viewport-indicator";
function hasOptOut12(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR8) !== null) return true;
    cur = cur.parentElement;
  }
  return false;
}
function classOrRoleIncludes(el, needle, role) {
  const cls = (el.getAttribute("class") ?? "").toLowerCase();
  if (cls.includes(needle)) return true;
  if (role) {
    const r = (el.getAttribute("data-role") ?? "").toLowerCase();
    if (r === role) return true;
  }
  return false;
}
function alphaOf(color) {
  const s = (color ?? "").trim();
  const m = s.match(/^rgba?\(([^)]+)\)$/i);
  if (m) {
    const parts = (m[1] ?? "").split(",").map((p) => p.trim());
    if (parts.length === 4) {
      const a = parseFloat(parts[3] ?? "");
      return Number.isFinite(a) ? a : 1;
    }
    return 1;
  }
  if (s === "transparent") return 0;
  if (s.startsWith("#")) {
    if (s.length === 9) {
      const a = parseInt(s.slice(7, 9), 16);
      return Number.isFinite(a) ? a / 255 : 1;
    }
    return 1;
  }
  return 1;
}
function checkMinimapViewportNotInverted(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const candidates = root.querySelectorAll(
    '[class*="viewport"], [data-role="viewport-indicator"]'
  );
  for (const el of Array.from(candidates)) {
    if (!classOrRoleIncludes(el, "viewport", "viewport-indicator")) continue;
    let inMinimap = false;
    let cur = el.parentElement;
    while (cur && cur !== root.parentElement) {
      if (classOrRoleIncludes(cur, "minimap", "minimap")) {
        inMinimap = true;
        break;
      }
      cur = cur.parentElement;
    }
    if (!inMinimap) continue;
    if (hasOptOut12(el, root)) continue;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      continue;
    }
    if (cs.display === "none" || cs.visibility === "hidden") continue;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 1 || rect.height <= 1) continue;
    const bgAlpha = alphaOf(cs.backgroundColor);
    if (bgAlpha <= 0.3) continue;
    const cls = (el.getAttribute("class") ?? "").slice(0, 80);
    const sel = el.tagName.toLowerCase() + (cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "");
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_MINIMAP_VIEWPORT_NOT_INVERTED",
      category: "rendering",
      severity: "major",
      message: `<${sel}> is a minimap viewport indicator painted as an OPAQUE rectangle (background-color alpha=${bgAlpha.toFixed(2)}). This obscures the diagram preview the indicator is supposed to be marking. Canonical convention: viewport rect is TRANSPARENT, with a translucent dark veil covering the AREA OUTSIDE it (inverted spotlight pattern).`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: 'Set `background: transparent` on the viewport rect and add a giant outset box-shadow: `box-shadow: 0 0 0 9999px rgba(0,0,0,0.6)` to dim the rest of the minimap. Set `overflow: hidden` on the minimap container to clip the spread. OR opt out via `data-allow-opaque-viewport-indicator="true"` if the design intentionally uses an opaque marker.',
      extras: {
        className: cls,
        backgroundColor: cs.backgroundColor,
        backgroundAlpha: bgAlpha
      }
    });
  }
  return findings;
}

// src/structural/element-painted-off-viewport.ts
var ALLOW_ATTR9 = "data-allow-viewport-overflow";
var SEVERE_THRESHOLD_PX = 32;
function hasOptOut13(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR9) === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function nearestLibStamp2(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    const v = cur.getAttribute?.("data-norkron-lib");
    if (v) {
      const at = v.lastIndexOf("@");
      const lib = at > 0 ? v.slice(0, at) : v;
      return { lib, full: v };
    }
    cur = cur.parentElement;
  }
  return null;
}
function describe(el) {
  const cls = (el.getAttribute("class") ?? "").slice(0, 60);
  const base = el.tagName.toLowerCase();
  if (!cls) return base;
  return base + "." + cls.split(/\s+/).filter(Boolean).join(".");
}
function checkElementPaintedOffViewport(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const body = doc.body;
  const docEl = doc.documentElement;
  if (!body || !docEl) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const viewportW = docEl.clientWidth > 0 ? docEl.clientWidth : win.innerWidth || 0;
  const viewportH = docEl.clientHeight > 0 ? docEl.clientHeight : win.innerHeight || 0;
  if (viewportW <= 0) return findings;
  const reported = /* @__PURE__ */ new Set();
  root.querySelectorAll("*").forEach((el) => {
    if (reported.has(el)) return;
    if (el === body || el === docEl) return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none") return;
    if (cs.visibility === "hidden") return;
    if (cs.opacity === "0") return;
    if (cs.position === "fixed" || cs.position === "sticky") return;
    let rect;
    try {
      rect = el.getBoundingClientRect();
    } catch {
      return;
    }
    if (rect.width <= 0 || rect.height <= 0) return;
    if (rect.right < -1e3) return;
    const overflowRight = Math.max(0, rect.right - viewportW);
    if (overflowRight < 1) return;
    if (hasOptOut13(el, root)) return;
    const severity = overflowRight > SEVERE_THRESHOLD_PX ? "critical" : "major";
    reported.add(el);
    const sel = describe(el);
    const stamp = nearestLibStamp2(el, root);
    const attribution = stamp ? ` (lib: ${stamp.full})` : "";
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_ELEMENT_PAINTED_OFF_VIEWPORT",
      category: "layout",
      severity,
      message: `<${sel}> paints past the viewport's right edge${attribution}: right=+${Math.round(overflowRight)}px (rect right=${Math.round(rect.right)}, viewport width=${viewportW}). Children of this region are horizontally clipped / unreachable.`,
      selector: sel,
      suggestedFix: 'Consumer-side: cap the parent flex container with `flex-wrap: wrap` (or a horizontal-scroll container if intentional). Lib-side: register a ResponsiveContract with a narrow-tier behavior (`stacked` / `compact` / `icon-only`) so the lib publishes how it shrinks. Opt-out (intentional bleed): set `data-allow-viewport-overflow="true"` on self or an ancestor.',
      extras: {
        viewportWidth: viewportW,
        viewportHeight: viewportH,
        rect: {
          left: rect.left,
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height
        },
        overflowRight,
        lib: stamp?.lib,
        libStamp: stamp?.full
      }
    });
  });
  return findings;
}

// src/structural/flex-nowrap-overflows-parent.ts
var ALLOW_ATTR10 = "data-allow-flex-nowrap-overflow";
function hasOptOut14(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR10) === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function describe2(el) {
  const cls = (el.getAttribute("class") ?? "").slice(0, 60);
  const base = el.tagName.toLowerCase();
  if (!cls) return base;
  return base + "." + cls.split(/\s+/).filter(Boolean).join(".");
}
function nearestLibStamp3(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    const v = cur.getAttribute?.("data-norkron-lib");
    if (v) return v;
    cur = cur.parentElement;
  }
  return null;
}
function checkFlexNowrapOverflowsParent(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  root.querySelectorAll("*").forEach((el) => {
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display !== "flex" && cs.display !== "inline-flex") return;
    const wrap = cs.flexWrap;
    if (wrap !== "nowrap" && wrap !== "" && wrap !== void 0) return;
    if (cs.overflowX === "auto" || cs.overflowX === "scroll") return;
    if (cs.visibility === "hidden") return;
    const grandparent = el.parentElement?.parentElement ?? null;
    if (!grandparent) return;
    const gpClientWidth = grandparent.clientWidth ?? 0;
    if (gpClientWidth <= 0) return;
    const sw = el.scrollWidth ?? 0;
    if (sw <= 0) return;
    if (sw <= gpClientWidth + 1) return;
    const explicitWidth = parseFloat(cs.width || "");
    if (Number.isFinite(explicitWidth) && Math.abs(explicitWidth - sw) <= 1) {
      let cur = grandparent;
      while (cur && cur !== root.parentElement) {
        try {
          const ccs = win.getComputedStyle(cur);
          if (ccs.overflowX === "auto" || ccs.overflowX === "scroll") return;
        } catch {
        }
        cur = cur.parentElement;
      }
    }
    if (hasOptOut14(el, root)) return;
    const overflow = sw - gpClientWidth;
    const fraction = overflow / gpClientWidth;
    const severity = fraction > 1 ? "critical" : fraction > 0.5 ? "major" : "major";
    if (fraction <= 0.5) {
      return;
    }
    const sel = describe2(el);
    const stamp = nearestLibStamp3(el, root);
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_FLEX_NOWRAP_OVERFLOWS_PARENT",
      category: "layout",
      severity,
      message: `<${sel}> is flex+nowrap with scrollWidth=${sw}px vs grandparent clientWidth=${gpClientWidth}px (+${overflow}px, ${Math.round(fraction * 100)}% over). The flex parent bled past its natural slot; its children paint past the grandparent's edge.`,
      selector: sel,
      suggestedFix: 'Add `flex-wrap: wrap` (or grid `auto-fit` columns) so children reflow into multiple rows. Alternatives: `overflow-x: auto` on the flex parent (intentional horizontal scroll), or set `min-width: 0` on flex children so they shrink. Opt-out (intentional bleed): `data-allow-flex-nowrap-overflow="true"`.',
      extras: {
        scrollWidth: sw,
        grandparentClientWidth: gpClientWidth,
        overflowPx: overflow,
        overflowFraction: fraction,
        libStamp: stamp
      }
    });
  });
  return findings;
}

// src/structural/lib-formally-no-responsive.ts
var ALLOW_ATTRS = [
  "data-allow-viewport-overflow",
  "data-allow-flex-nowrap-overflow",
  "data-allow-overflow"
];
function hasOptOut15(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    for (const attr of ALLOW_ATTRS) {
      if (cur.getAttribute?.(attr) === "true") return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function libNameOf2(raw) {
  const at = raw.lastIndexOf("@");
  if (at > 0) return raw.slice(0, at);
  return raw;
}
function isPortalHost(parent) {
  const id = parent.id ?? "";
  if (id.includes("portal-root") || id.includes("portal-host")) return true;
  const cw = parent.clientWidth ?? 0;
  return cw <= 1;
}
function isFormallyFullOnly(c) {
  if (c.containerQueries && c.containerQueries.length > 0) return false;
  const bps = c.supportedBreakpoints;
  if (!bps || bps.length === 0) return false;
  for (const b of bps) {
    const behavior = c.breakpointBehavior?.[b];
    if (behavior !== "full") return false;
  }
  return true;
}
function describe3(el) {
  const cls = (el.getAttribute("class") ?? "").slice(0, 60);
  const base = el.tagName.toLowerCase();
  if (!cls) return base;
  return base + "." + cls.split(/\s+/).filter(Boolean).join(".");
}
function checkLibFormallyNoResponsive(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const reportedKeys = /* @__PURE__ */ new Set();
  root.querySelectorAll("[data-norkron-lib]").forEach((el) => {
    const raw = el.getAttribute("data-norkron-lib");
    if (!raw) return;
    const lib = libNameOf2(raw.trim());
    const contract = getResponsiveContract(lib);
    if (!contract) return;
    if (!isFormallyFullOnly(contract)) return;
    const parent = el.parentElement;
    if (!parent) return;
    if (isPortalHost(parent)) return;
    const parentWidth = parent.clientWidth ?? 0;
    if (parentWidth <= 0) return;
    let rect;
    try {
      rect = el.getBoundingClientRect();
    } catch {
      return;
    }
    if (rect.width <= 0) return;
    const slotOverflow = rect.width - parentWidth;
    const docEl = doc.documentElement;
    const viewportW = (docEl?.clientWidth ?? 0) > 0 ? docEl.clientWidth : win.innerWidth || 0;
    const viewportOverflow = viewportW > 0 ? rect.right - viewportW : 0;
    const overflowPx = Math.max(slotOverflow, viewportOverflow);
    if (overflowPx <= 1) return;
    if (hasOptOut15(el, root)) return;
    const sel = describe3(el);
    const parentSel = describe3(parent);
    const key = `${lib}::${parentSel}`;
    if (reportedKeys.has(key)) return;
    reportedKeys.add(key);
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_LIB_FORMALLY_NO_RESPONSIVE",
      category: "layout",
      severity: "major",
      message: `Lib "${lib}" declared a ResponsiveContract with breakpointBehavior:'full' at every tier (no narrow-tier collapse / stack / icon-only mode) AND a stamped instance is overflowing its parent slot (<${sel}> rect.width=${Math.round(rect.width)}px vs parent.clientWidth=${parentWidth}px, +${Math.round(overflowPx)}px). The lib's published contract is honest but mismatched to the consumer slot.`,
      selector: `[data-norkron-lib^="${lib}"]`,
      suggestedFix: `Two paths \u2014 pick one: (a) LIB-SIDE \u2014 add a narrow-tier variant to ${lib} (e.g., 'stacked' at xs/sm in breakpointBehavior, OR a --wrap-balanced size variant, OR icon-only chrome at narrow widths) and re-register the contract. (b) CONSUMER-SIDE \u2014 wrap the slot in a container that pre-sizes the lib (e.g., flex-wrap: wrap on the parent rail, a horizontal-scroll container, or a smart-pill-layout algorithm). Opt-out: data-allow-viewport-overflow / data-allow-flex-nowrap-overflow on the ancestor path.`,
      extras: {
        lib,
        libStamp: raw,
        breakpointBehavior: contract.breakpointBehavior,
        supportedBreakpoints: contract.supportedBreakpoints,
        rectWidth: rect.width,
        parentClientWidth: parentWidth,
        overflowPx,
        parentSelector: parentSel
      }
    });
  });
  return findings;
}

// src/structural/svg-renders-paint-empty.ts
var LIB_ID50 = "@norkron/web-conformance";
var ALLOW_ATTR11 = "data-allow-empty-svg";
function hasOptOut16(el, stopAt) {
  let cur = el;
  while (cur && cur !== stopAt.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR11) !== null) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function ancestorAriaHidden(el, stopAt) {
  let cur = el;
  while (cur && cur !== stopAt.parentElement) {
    if (cur.getAttribute && cur.getAttribute("aria-hidden") === "true") {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function checkSvgRendersPaintEmpty(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const svgs = root.querySelectorAll("svg");
  svgs.forEach((svg) => {
    if (hasOptOut16(svg, root)) return;
    if (ancestorAriaHidden(svg, root)) return;
    let style;
    try {
      style = win.getComputedStyle(svg);
    } catch {
      return;
    }
    if (style.display === "none") return;
    if (style.visibility === "hidden") return;
    const opacity = Number.parseFloat(style.opacity ?? "1");
    if (Number.isFinite(opacity) && opacity === 0) return;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    if (svgHasPaintedContent(svg)) return;
    const cls = (svg.getAttribute("class") ?? "").slice(0, 80);
    findings.push({
      libId: LIB_ID50,
      code: "STR_SVG_RENDERS_PAINT_EMPTY",
      category: "rendering",
      severity: "warning",
      message: `<svg${cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : ""}> occupies ${Math.round(rect.width)}x${Math.round(rect.height)}px but contains no paintable descendant \u2014 no <path>/<rect>/<circle>/<ellipse>/<line>/<polygon>/<text>/<image>/<use> with non-empty geometry. Sighted users see a blank cell. Almost always: the chart data fetched empty, the sparkline binding never ran, or the component shipped a placeholder <svg> wrapper without the geometry-producing child.`,
      selector: "svg",
      suggestedFix: 'Verify the SVG body is being populated \u2014 is the chart data resolving to []? Is the React effect that appends path/rect children never running? If the empty wrapper is intentional (decorative placeholder, hidden chart slot), set `data-allow-empty-svg="true"` on the <svg> or any ancestor.',
      extras: {
        className: cls,
        rect: {
          width: rect.width,
          height: rect.height,
          left: rect.left,
          top: rect.top
        }
      }
    });
  });
  return findings;
}

// src/structural/pill-container-ellipse-at-narrow.ts
var ALLOW_ATTR12 = "data-allow-pill-ellipse";
var MIN_DIMENSION_PX = 16;
function maxRawRadiusPx(cs, w, h) {
  const corners = [cs.borderTopLeftRadius, cs.borderTopRightRadius, cs.borderBottomLeftRadius, cs.borderBottomRightRadius];
  const ref = Math.min(w, h);
  let max = 0;
  for (const c of corners) {
    const parsed = parseFloat(c);
    if (!Number.isFinite(parsed)) continue;
    const px2 = c.endsWith("%") ? parsed / 100 * ref : parsed;
    if (px2 > max) max = px2;
  }
  return max;
}
function hasOptOut17(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR12) === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function nearestLibStamp4(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    const v = cur.getAttribute?.("data-norkron-lib");
    if (v) {
      const at = v.lastIndexOf("@");
      return at > 0 ? v.slice(0, at) : v;
    }
    cur = cur.parentElement;
  }
  return null;
}
function describeEl(el) {
  const cls = (el.getAttribute("class") ?? "").slice(0, 60);
  const base = el.tagName.toLowerCase();
  if (!cls) return base;
  return base + "." + cls.split(/\s+/).filter(Boolean).join(".");
}
function checkPillContainerEllipseAtNarrow(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const all = root.querySelectorAll("*");
  all.forEach((el) => {
    if (el === doc.documentElement || el === doc.body) return;
    const rect = el.getBoundingClientRect();
    if (rect.width < MIN_DIMENSION_PX || rect.height < MIN_DIMENSION_PX) return;
    if (el.getAttribute("aria-hidden") === "true") return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden") return;
    const rawR = maxRawRadiusPx(cs, rect.width, rect.height);
    if (rawR < 4) return;
    const halfMin = Math.min(rect.width, rect.height) / 2;
    const halfHeight = rect.height / 2;
    if (rect.width >= 2 * rect.height) return;
    const isPillVerticalCollapse = rawR >= halfHeight - 0.5 && rect.height >= 2 * rect.width;
    const isPureEllipse = !isPillVerticalCollapse && rawR > halfMin + 0.5;
    if (!isPureEllipse && !isPillVerticalCollapse) return;
    if (hasOptOut17(el, root)) return;
    const lib = nearestLibStamp4(el, root);
    const sel = describeEl(el);
    const shape = isPillVerticalCollapse ? "pill-collapsed-to-vertical-column" : "pure-ellipse";
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_PILL_CONTAINER_ELLIPSE_AT_NARROW",
      category: "layout",
      severity: "minor",
      message: "<" + sel + "> paints as an ellipse: border-radius " + Math.round(rawR) + "px on a " + Math.round(rect.width) + "x" + Math.round(rect.height) + "px rect (" + shape + "). The chassis no longer reads as a rectangle-with-rounded-corners.",
      selector: el.tagName.toLowerCase(),
      suggestedFix: "Bind the radius to a row dimension, not the wrap-resolved container size: border-radius: calc(var(--row-height, 36px) / 2). For an actual circle/squircle, cap the radius at min(width,height)/2.",
      extras: {
        widthPx: rect.width,
        heightPx: rect.height,
        borderRadiusPx: rawR,
        shape,
        ...lib ? { lib } : {}
      }
    });
  });
  return findings;
}

// src/structural/control-label-mid-word-break.ts
var ALLOW_ATTR13 = "data-allow-mid-word-break";
var CONTROL_SELECTORS = [
  "button",
  '[role="button"]',
  '[role="tab"]',
  '[role="option"]',
  '[role="menuitem"]',
  '[role="radio"]',
  ".btn",
  ".ctl",
  ".segmented-control__segment",
  ".segmented-control__option",
  '[data-norkron-lib*="segmented-control"] [role="radio"]',
  '[data-norkron-lib*="segmented-control"] button',
  '[data-norkron-lib*="tabs"] [role="tab"]',
  '[data-norkron-lib*="radio-group"] [role="radio"]'
].join(",");
function hasOptOut18(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR13) === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function nearestLibStamp5(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    const v = cur.getAttribute?.("data-norkron-lib");
    if (v) {
      const at = v.lastIndexOf("@");
      return at > 0 ? v.slice(0, at) : v;
    }
    cur = cur.parentElement;
  }
  return null;
}
function visibleText(el) {
  const raw = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  return raw;
}
function policyFromStyle(cs) {
  const ow = (cs.overflowWrap || "").trim();
  const wb = (cs.wordBreak || "").trim();
  if (ow === "anywhere") return "overflow-wrap:anywhere";
  if (ow === "break-word") return "overflow-wrap:break-word";
  if (wb === "break-all") return "word-break:break-all";
  return null;
}
function measureWidth(doc, text, fontSize, fontFamily) {
  if (!text) return 0;
  try {
    const canvas = doc.createElement("canvas");
    const ctx = canvas.getContext?.("2d");
    if (ctx) {
      ctx.font = fontSize + "px " + (fontFamily || "sans-serif");
      const m = ctx.measureText(text);
      if (Number.isFinite(m.width) && m.width > 0) return m.width;
    }
  } catch {
  }
  return text.length * fontSize * 0.55;
}
function describeEl2(el) {
  const cls = (el.getAttribute("class") ?? "").slice(0, 60);
  const base = el.tagName.toLowerCase();
  if (!cls) return base;
  return base + "." + cls.split(/\s+/).filter(Boolean).join(".");
}
function checkControlLabelMidWordBreak(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const candidates = root.querySelectorAll(CONTROL_SELECTORS);
  candidates.forEach((el) => {
    if (el.getAttribute("aria-hidden") === "true") return;
    const clientWidth = el.clientWidth;
    if (!clientWidth || clientWidth <= 0) return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden") return;
    const policy = policyFromStyle(cs);
    if (!policy) return;
    const text = visibleText(el);
    if (!text) return;
    const fontSize = parseFloat(cs.fontSize) || 16;
    const fontFamily = cs.fontFamily || "sans-serif";
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length === 0) return;
    let longestWord = words[0];
    for (const w of words) if (w.length > longestWord.length) longestWord = w;
    const wordWidth = measureWidth(doc, longestWord, fontSize, fontFamily);
    const fullWidth = measureWidth(doc, text, fontSize, fontFamily);
    if (wordWidth <= clientWidth + 0.5) return;
    if (hasOptOut18(el, root)) return;
    const lib = nearestLibStamp5(el, root);
    const sel = describeEl2(el);
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_CONTROL_LABEL_MID_WORD_BREAK",
      category: "layout",
      severity: "major",
      message: "<" + sel + "> control has " + policy + ' AND the longest word "' + longestWord + '" measures ' + Math.round(wordWidth) + "px (clientWidth " + Math.round(clientWidth) + "px). Any wrap will split the label mid-word.",
      selector: el.tagName.toLowerCase(),
      suggestedFix: "Set white-space:nowrap + overflow-wrap:normal + word-break:keep-all on the control segment, AND add min-width / min-content-width on flex children so the segment grows to fit its label instead of breaking it. If truncation is the desired affordance, use text-overflow:ellipsis with an aria-label / tooltip for the full text.",
      extras: {
        textWidthPx: fullWidth,
        longestWordPx: wordWidth,
        clientWidthPx: clientWidth,
        wordBreakPolicy: policy,
        text: text.slice(0, 80),
        longestWord,
        ...lib ? { lib } : {}
      }
    });
  });
  return findings;
}

// src/structural/_viewer-chrome-region.ts
function nearestLibStamp6(el) {
  let cur = el;
  while (cur) {
    const v = cur.getAttribute?.("data-norkron-lib");
    if (v) {
      const at = v.lastIndexOf("@");
      return at > 0 ? v.slice(0, at) : v;
    }
    cur = cur.parentElement;
  }
  return null;
}
function isVisible(el, win) {
  let cs;
  try {
    cs = win.getComputedStyle(el);
  } catch {
    return true;
  }
  if (cs.display === "none" || cs.visibility === "hidden") return false;
  return true;
}
function checkViewerRegionMissing(root, spec) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const optOutAttr = `data-allow-missing-${spec.region}`;
  const hosts = root.querySelectorAll("[data-diagram-renderer]");
  for (const host of Array.from(hosts)) {
    if (host.getAttribute(optOutAttr) !== null) continue;
    const region = host.querySelector(`[data-region="${spec.region}"]`);
    const present = region !== null && isVisible(region, win);
    if (present) continue;
    const lib = nearestLibStamp6(host);
    findings.push({
      libId: "@norkron/web-conformance",
      code: spec.code,
      category: "layout",
      severity: "major",
      message: `<${host.tagName.toLowerCase()}[data-diagram-renderer]> is missing its '${spec.region}' chrome region (no descendant [data-region="${spec.region}"] OR it is display:none/visibility:hidden). The ViewerContainer shell must wrap both renderers; the chrome must not be conditionally rendered per-renderer.`,
      selector: "[data-diagram-renderer]",
      suggestedFix: spec.suggestedFix,
      extras: {
        region: spec.region,
        mode: host.getAttribute("data-diagram-mode") ?? null,
        ...lib ? { lib } : {}
      }
    });
  }
  return findings;
}

// src/structural/viewer-toolbar-missing.ts
function checkViewerToolbarMissing(root) {
  return checkViewerRegionMissing(root, {
    region: "toolbar",
    code: "STR_VIEWER_TOOLBAR_MISSING",
    suggestedFix: "Mount <ViewerToolbar /> inside the unified ViewerContainer shell so it wraps both renderers. The toolbar (zoom, fit, recenter, screenshot) must read from the renderer-agnostic Diagram store via the RendererAdapter interface \u2014 never conditionally per-renderer. Opt out via `data-allow-missing-toolbar` on the host if the surface intentionally has no toolbar."
  });
}

// src/structural/viewer-minimap-missing.ts
function checkViewerMinimapMissing(root) {
  return checkViewerRegionMissing(root, {
    region: "minimap",
    code: "STR_VIEWER_MINIMAP_MISSING",
    suggestedFix: "Mount <ViewerMinimap /> inside the unified ViewerContainer shell so it wraps both renderers. The minimap reads viewport bounds via RendererAdapter.viewport() \u2014 renderer-agnostic. Opt out via `data-allow-missing-minimap` on the host if the surface intentionally has no minimap."
  });
}

// src/structural/viewer-legend-missing.ts
function checkViewerLegendMissing(root) {
  return checkViewerRegionMissing(root, {
    region: "legend",
    code: "STR_VIEWER_LEGEND_MISSING",
    suggestedFix: "Mount <ViewerLegend /> inside the unified ViewerContainer shell so it wraps both renderers. The legend reads from the same renderer-agnostic Diagram store. Opt out via `data-allow-missing-legend` on the host if no legend is appropriate (uncategorised graphs)."
  });
}

// src/structural/viewer-sidebar-missing.ts
function checkViewerSidebarMissing(root) {
  return checkViewerRegionMissing(root, {
    region: "sidebar",
    code: "STR_VIEWER_SIDEBAR_MISSING",
    suggestedFix: "Mount <ViewerSidebar /> inside the unified ViewerContainer shell so it wraps both renderers. The sidebar (details panel, shape picker, settings) reads renderer-agnostic state. Opt out via `data-allow-missing-sidebar` on the host if the surface is intentionally chrome-less (embed mode, thumbnail preview)."
  });
}

// src/structural/viewer-background-pattern-missing.ts
function nearestLibStamp7(el) {
  let cur = el;
  while (cur) {
    const v = cur.getAttribute?.("data-norkron-lib");
    if (v) {
      const at = v.lastIndexOf("@");
      return at > 0 ? v.slice(0, at) : v;
    }
    cur = cur.parentElement;
  }
  return null;
}
function checkViewerBackgroundPatternMissing(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const hosts = root.querySelectorAll("[data-diagram-renderer]");
  for (const host of Array.from(hosts)) {
    if (host.getAttribute("data-allow-missing-background-pattern") !== null) continue;
    const slot = host.querySelector('[data-region="canvas-slot"]');
    if (!slot) continue;
    let cs;
    try {
      cs = win.getComputedStyle(slot);
    } catch {
      continue;
    }
    const bgImage = (cs.backgroundImage ?? "").trim();
    const hasPattern = bgImage !== "" && bgImage !== "none";
    if (hasPattern) continue;
    const lib = nearestLibStamp7(host);
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_VIEWER_BACKGROUND_PATTERN_MISSING",
      category: "layout",
      severity: "major",
      message: `<${slot.tagName.toLowerCase()}[data-region="canvas-slot"]> has empty background-image (no checkerboard / grid / pattern). The CSS-themed surface must mount renderer-agnostically; without it the 3D mode looks like an unbounded void.`,
      selector: '[data-region="canvas-slot"]',
      suggestedFix: 'Apply the canonical checker/grid pattern via `background-image` on the ViewerBackground (or its canvas-slot equivalent), referencing the `--viewer-bg` theme tokens. Mount <ViewerBackground pattern="checker" /> inside ViewerContainer.body. Opt out via `data-allow-missing-background-pattern` on the host if the surface intentionally has no pattern.',
      extras: {
        backgroundImage: bgImage,
        mode: host.getAttribute("data-diagram-mode") ?? null,
        ...lib ? { lib } : {}
      }
    });
  }
  return findings;
}

// src/structural/viewer-border-missing.ts
function nearestLibStamp8(el) {
  let cur = el;
  while (cur) {
    const v = cur.getAttribute?.("data-norkron-lib");
    if (v) {
      const at = v.lastIndexOf("@");
      return at > 0 ? v.slice(0, at) : v;
    }
    cur = cur.parentElement;
  }
  return null;
}
function parseBorderWidthPx(value) {
  const n = parseFloat((value ?? "").trim());
  return Number.isFinite(n) ? n : 0;
}
function isTransparentColor(value) {
  const s = (value ?? "").trim().toLowerCase();
  if (s === "transparent") return true;
  const m = s.match(/^rgba?\(([^)]+)\)$/);
  if (m) {
    const parts = (m[1] ?? "").split(",").map((p) => p.trim());
    if (parts.length === 4) {
      const a = parseFloat(parts[3] ?? "");
      return Number.isFinite(a) && a <= 0.01;
    }
  }
  return false;
}
function checkViewerBorderMissing(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const hosts = root.querySelectorAll("[data-diagram-renderer]");
  for (const host of Array.from(hosts)) {
    if (host.getAttribute("data-allow-missing-border") !== null) continue;
    const slot = host.querySelector('[data-region="canvas-slot"]');
    if (!slot) continue;
    let cs;
    try {
      cs = win.getComputedStyle(slot);
    } catch {
      continue;
    }
    const widths = [
      parseBorderWidthPx(cs.borderTopWidth),
      parseBorderWidthPx(cs.borderRightWidth),
      parseBorderWidthPx(cs.borderBottomWidth),
      parseBorderWidthPx(cs.borderLeftWidth)
    ];
    const maxW = Math.max(...widths);
    const colors = [
      cs.borderTopColor,
      cs.borderRightColor,
      cs.borderBottomColor,
      cs.borderLeftColor
    ];
    const allTransparent = colors.every(isTransparentColor);
    const zeroWidth = maxW <= 0;
    if (!zeroWidth && !allTransparent) continue;
    const reason = zeroWidth ? "border-width: 0px on all sides" : "border-color is transparent on all sides";
    const lib = nearestLibStamp8(host);
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_VIEWER_BORDER_MISSING",
      category: "layout",
      severity: "major",
      message: `<${slot.tagName.toLowerCase()}[data-region="canvas-slot"]> has no painted border (${reason}). The themed border / focus ring is part of the canonical viewer chrome and must mount renderer-agnostically.`,
      selector: '[data-region="canvas-slot"]',
      suggestedFix: "Apply the canonical `--viewer-border` token as `border: var(--viewer-border-width, 1px) solid var(--viewer-border)` on the canvas-slot. Themed via `--viewer-*` CSS variables so dark/light themes inherit. Opt out via `data-allow-missing-border` on the host if the surface intentionally has no border.",
      extras: {
        borderWidths: widths,
        borderColors: colors,
        mode: host.getAttribute("data-diagram-mode") ?? null,
        ...lib ? { lib } : {}
      }
    });
  }
  return findings;
}

// src/structural/billboard-opaque-occludes-3d-edges.ts
function collectSprites(scene) {
  const out = [];
  if (typeof scene.traverse === "function") {
    scene.traverse((obj) => {
      if (obj && (obj.isSprite === true || obj.type === "Sprite")) out.push(obj);
    });
    return out;
  }
  const stack = [scene];
  while (stack.length > 0) {
    const cur = stack.pop();
    if (cur && (cur.isSprite === true || cur.type === "Sprite")) out.push(cur);
    const kids = cur.children;
    if (Array.isArray(kids)) for (const k of kids) stack.push(k);
  }
  return out;
}
function imageCornerAlphaOpaque(img) {
  if (!img || typeof img.getContext !== "function") return false;
  try {
    const ctx = img.getContext("2d");
    if (!ctx || typeof ctx.getImageData !== "function") return false;
    const px2 = ctx.getImageData(0, 0, 1, 1);
    return px2.data[3] === 255;
  } catch {
    return false;
  }
}
function nearestLibStamp9(el) {
  let cur = el;
  while (cur) {
    const v = cur.getAttribute?.("data-norkron-lib");
    if (v) {
      const at = v.lastIndexOf("@");
      return at > 0 ? v.slice(0, at) : v;
    }
    cur = cur.parentElement;
  }
  return null;
}
function checkBillboardOpaqueOccludes3dEdges(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win) return findings;
  const canvases = root.querySelectorAll('canvas[data-engine^="three"]');
  if (canvases.length === 0) return findings;
  const scene = win.__diorama_three_scene;
  if (!scene || typeof scene !== "object") return findings;
  const sprites = collectSprites(scene);
  if (sprites.length === 0) return findings;
  for (const sprite of sprites) {
    const mat = sprite.material;
    if (!mat) continue;
    const transparent = mat.transparent === true;
    const opaqueCorner = imageCornerAlphaOpaque(mat.map?.image);
    if (transparent && !opaqueCorner) continue;
    const canvas = canvases[0];
    const lib = nearestLibStamp9(canvas);
    const reason = !transparent ? "material.transparent === false (sprite renders as opaque rect, occluding edges behind it)" : "source CanvasTexture (0,0) pixel has alpha=255 (texture-canvas wasn't clearRect-cleared before paint)";
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_BILLBOARD_OPAQUE_OCCLUDES_3D_EDGES",
      category: "rendering",
      severity: "major",
      message: `THREE.Sprite billboard (uuid=${sprite.uuid ?? "?"}, name=${sprite.name ?? "?"}) is opaque where it should be alpha-transparent: ${reason}. Edges passing behind the billboard will be visually clipped.`,
      selector: 'canvas[data-engine^="three"]',
      suggestedFix: "Canvas-2D textures used as Sprite materials must: (1) call `ctx.clearRect(0,0,w,h)` before painting (alpha=0 background); (2) set `material.transparent = true`; (3) set `material.alphaTest = 0.01` so fully-transparent fragments drop out of the depth test; (4) keep `material.depthWrite = true` so the sprite still occludes correctly where it has actual paint.",
      extras: {
        spriteUuid: sprite.uuid ?? null,
        spriteName: sprite.name ?? null,
        transparent: mat.transparent ?? null,
        alphaTest: mat.alphaTest ?? null,
        opaqueCorner,
        ...lib ? { lib } : {}
      }
    });
  }
  return findings;
}

// src/structural/billboard-texture-blurry-at-zoom.ts
var THREE_LINEAR_FILTER = 1006;
function collectSprites2(scene) {
  const out = [];
  if (typeof scene.traverse === "function") {
    scene.traverse((obj) => {
      if (obj && (obj.isSprite === true || obj.type === "Sprite")) out.push(obj);
    });
    return out;
  }
  const stack = [scene];
  while (stack.length > 0) {
    const cur = stack.pop();
    if (cur && (cur.isSprite === true || cur.type === "Sprite")) out.push(cur);
    const kids = cur.children;
    if (Array.isArray(kids)) for (const k of kids) stack.push(k);
  }
  return out;
}
function nearestLibStamp10(el) {
  let cur = el;
  while (cur) {
    const v = cur.getAttribute?.("data-norkron-lib");
    if (v) {
      const at = v.lastIndexOf("@");
      return at > 0 ? v.slice(0, at) : v;
    }
    cur = cur.parentElement;
  }
  return null;
}
function isLinearFilter(v) {
  if (typeof v === "number") return v === THREE_LINEAR_FILTER;
  if (typeof v === "string") return v === "LinearFilter";
  return false;
}
function checkBillboardTextureBlurryAtZoom(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win) return findings;
  const canvases = root.querySelectorAll('canvas[data-engine^="three"]');
  if (canvases.length === 0) return findings;
  const scene = win.__diorama_three_scene;
  if (!scene || typeof scene !== "object") return findings;
  const dpr = typeof win.devicePixelRatio === "number" && win.devicePixelRatio > 0 ? win.devicePixelRatio : 1;
  const sprites = collectSprites2(scene);
  if (sprites.length === 0) return findings;
  for (const sprite of sprites) {
    const tex = sprite.material?.map;
    if (!tex) continue;
    const reasons = [];
    const cssWidth = sprite.userData?.cssWidth;
    if (typeof cssWidth === "number" && cssWidth > 0) {
      const imgW = tex.image?.width;
      if (typeof imgW === "number" && imgW < dpr * cssWidth) {
        reasons.push(`texture width ${imgW}px < devicePixelRatio (${dpr}) * cssWidth (${cssWidth}px) \u2014 not supersampled`);
      }
    }
    if (typeof tex.anisotropy === "number" && tex.anisotropy <= 1) {
      reasons.push("material.map.anisotropy === 1 (default \u2014 no anisotropic filtering)");
    }
    if (isLinearFilter(tex.minFilter)) {
      reasons.push("material.map.minFilter === THREE.LinearFilter (no mipmaps)");
    }
    if (reasons.length === 0) continue;
    const canvas = canvases[0];
    const lib = nearestLibStamp10(canvas);
    findings.push({
      libId: "@norkron/web-conformance",
      code: "STR_BILLBOARD_TEXTURE_BLURRY_AT_ZOOM",
      category: "rendering",
      severity: "minor",
      message: `THREE.Sprite billboard (uuid=${sprite.uuid ?? "?"}, name=${sprite.name ?? "?"}) texture will look blurry at zoom: ${reasons.join("; ")}.`,
      selector: 'canvas[data-engine^="three"]',
      suggestedFix: "Supersample the canvas-texture at creation (size canvas to dpr \u2265 2 \xD7 intended cssWidth); set `texture.anisotropy = renderer.capabilities.getMaxAnisotropy()`; set `texture.minFilter = THREE.LinearMipmapLinearFilter` + `texture.generateMipmaps = true` so minification has a mipmap chain.",
      extras: {
        spriteUuid: sprite.uuid ?? null,
        spriteName: sprite.name ?? null,
        devicePixelRatio: dpr,
        cssWidth: cssWidth ?? null,
        textureWidth: tex.image?.width ?? null,
        anisotropy: tex.anisotropy ?? null,
        minFilter: tex.minFilter ?? null,
        reasons,
        ...lib ? { lib } : {}
      }
    });
  }
  return findings;
}

// src/structural/intra-product-panel-overlaps-peer.ts
var LIB_ID51 = "@norkron/web-conformance";
var MIN_INTERSECTION_WIDTH = 8;
var MIN_INTERSECTION_HEIGHT = 32;
var PANEL_CLASS_RE = /\b(__sidebar|__panel|-sidekick|-drawer|-pane|-aside)(\s|$|--)/;
var PANEL_ROLES = /* @__PURE__ */ new Set([
  "region",
  "complementary",
  "navigation",
  "main",
  "form",
  "search"
]);
function isPanelCandidate(el) {
  const role = el.getAttribute("role");
  if (role && PANEL_ROLES.has(role)) return true;
  if (el.getAttribute("data-norkron-panel") === "true") return true;
  const cls = el.getAttribute("class") ?? "";
  if (cls && PANEL_CLASS_RE.test(cls)) return true;
  return false;
}
function isAncestorOf2(potentialAncestor, el) {
  let cur = el.parentElement;
  while (cur) {
    if (cur === potentialAncestor) return true;
    cur = cur.parentElement;
  }
  return false;
}
function rectIntersection(a, b) {
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.right, b.right);
  const top = Math.max(a.top, b.top);
  const bottom = Math.min(a.bottom, b.bottom);
  return {
    left,
    top,
    right,
    bottom,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top)
  };
}
function sharesPanelAncestor(a, b) {
  const ancestors = /* @__PURE__ */ new Set();
  let cur = a.parentElement;
  while (cur) {
    ancestors.add(cur);
    cur = cur.parentElement;
  }
  cur = b.parentElement;
  while (cur) {
    if (ancestors.has(cur) && isPanelCandidate(cur)) return true;
    cur = cur.parentElement;
  }
  return false;
}
function selectorFor6(el) {
  const tag = el.tagName.toLowerCase();
  const cls = (el.getAttribute("class") ?? "").split(/\s+/).filter(Boolean).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function checkIntraProductPanelOverlapsPeer(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(
      needsLayoutFinding4(
        "STR_INTRA_PRODUCT_PANEL_OVERLAPS_PEER",
        layout.reason
      )
    );
    return findings;
  }
  const all = Array.from(root.querySelectorAll("*"));
  if (isPanelCandidate(root)) all.unshift(root);
  const panels = all.filter(isPanelCandidate);
  if (panels.length < 2) return findings;
  const reported = /* @__PURE__ */ new Set();
  for (let i = 0; i < panels.length; i++) {
    const a = panels[i];
    if (a.closest("[data-allow-panel-overlap]")) continue;
    const aRect = a.getBoundingClientRect();
    if (aRect.width === 0 || aRect.height === 0) continue;
    for (let j = i + 1; j < panels.length; j++) {
      const b = panels[j];
      if (b.closest("[data-allow-panel-overlap]")) continue;
      if (isAncestorOf2(a, b) || isAncestorOf2(b, a)) continue;
      if (sharesPanelAncestor(a, b)) continue;
      const bRect = b.getBoundingClientRect();
      if (bRect.width === 0 || bRect.height === 0) continue;
      const inter = rectIntersection(aRect, bRect);
      if (inter.width < MIN_INTERSECTION_WIDTH || inter.height < MIN_INTERSECTION_HEIGHT)
        continue;
      const aSel = selectorFor6(a);
      const bSel = selectorFor6(b);
      const dedup = `${aSel}::${bSel}`;
      if (reported.has(dedup)) continue;
      reported.add(dedup);
      findings.push({
        libId: LIB_ID51,
        code: "STR_INTRA_PRODUCT_PANEL_OVERLAPS_PEER",
        category: "layout",
        severity: "major",
        message: `Panel <${aSel}> visually overlaps peer panel <${bSel}> by ${Math.round(
          inter.width
        )}x${Math.round(inter.height)}px. Two intra-product panels at the same z-layer occlude each other.`,
        selector: aSel,
        suggestedFix: "Constrain the panels so they do not share screen real estate at this breakpoint (collapse one, reflow, or split into tabs). If the overlap is intentional (e.g. a deliberately stacked drawer on top of a sidebar), opt out with `data-allow-panel-overlap` on either panel or any shared ancestor.",
        extras: {
          panelASelector: aSel,
          panelBSelector: bSel,
          intersection: {
            left: inter.left,
            top: inter.top,
            right: inter.right,
            bottom: inter.bottom,
            width: inter.width,
            height: inter.height
          },
          thresholds: {
            minWidth: MIN_INTERSECTION_WIDTH,
            minHeight: MIN_INTERSECTION_HEIGHT
          }
        }
      });
    }
  }
  return findings;
}

// src/structural/declared-chrome-not-visible.ts
var LIB_ID52 = "@norkron/web-conformance";
var DECLARED_REGION_RE = /^(legend|minimap|controls|panel-list|tab-list|nav|toolbar|sidebar|drawer|sidekick)$/;
function declarationFor(el) {
  const expect = el.getAttribute("data-conformance-expect-visible");
  if (expect !== null) {
    return {
      label: expect || el.tagName.toLowerCase(),
      source: "expect-visible"
    };
  }
  const lib = el.getAttribute("data-norkron-lib");
  const region = el.getAttribute("data-region");
  if (lib && region && DECLARED_REGION_RE.test(region)) {
    return { label: lib + " " + region, source: "lib-region", lib, region };
  }
  return null;
}
function ancestorCanScrollIntoView(el, win) {
  const isScrollable = (v) => {
    const t = (v ?? "").trim();
    return t === "auto" || t === "scroll";
  };
  let cur = el.parentElement;
  while (cur) {
    try {
      const cs = win.getComputedStyle(cur);
      if (isScrollable(cs.overflowX) || isScrollable(cs.overflowY)) return true;
      if (isScrollable(cs.overflow)) {
        return true;
      }
      const inlineEl = cur;
      if (isScrollable(inlineEl.style?.overflow) || isScrollable(inlineEl.style?.overflowX) || isScrollable(inlineEl.style?.overflowY)) {
        return true;
      }
    } catch {
    }
    cur = cur.parentElement;
  }
  return false;
}
function hasAnimationInFlight(el, cs) {
  const td = cs.transitionDuration || "";
  const ad = cs.animationDuration || "";
  const hasNonZero = (value) => value.split(",").map((s) => s.trim()).some((s) => s.length > 0 && s !== "0s" && s !== "0ms");
  if (hasNonZero(td) || hasNonZero(ad)) return true;
  const elAny = el;
  if (typeof elAny.getAnimations === "function") {
    try {
      const running = elAny.getAnimations().some((a) => a.playState === "running");
      if (running) return true;
    } catch {
    }
  }
  return false;
}
function selectorFor7(el) {
  const tag = el.tagName.toLowerCase();
  const cls = (el.getAttribute("class") ?? "").split(/\s+/).filter(Boolean).join(".");
  return cls ? tag + "." + cls : tag;
}
function checkDeclaredChromeNotVisible(root) {
  const findings = [];
  const layout = requiresLayout(root);
  if (!layout.ready) {
    findings.push(needsLayoutFinding4("STR_DECLARED_CHROME_NOT_VISIBLE", layout.reason));
    return findings;
  }
  const doc = root.ownerDocument ?? root;
  const win = doc?.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win) return findings;
  const docEl = doc.documentElement;
  const viewportW = (docEl?.clientWidth ?? 0) > 0 ? docEl.clientWidth : win.innerWidth || 0;
  const viewportH = (docEl?.clientHeight ?? 0) > 0 ? docEl.clientHeight : win.innerHeight || 0;
  const all = Array.from(root.querySelectorAll("*"));
  if (declarationFor(root)) all.unshift(root);
  for (const el of all) {
    const decl = declarationFor(el);
    if (!decl) continue;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      continue;
    }
    let rect;
    try {
      rect = el.getBoundingClientRect();
    } catch {
      continue;
    }
    const reasons = [];
    if (cs.display === "none") reasons.push("display:none");
    if (cs.visibility && cs.visibility !== "visible") {
      reasons.push("visibility:" + cs.visibility);
    }
    if (rect.width < 2 || rect.height < 2) {
      reasons.push("zero-rect (" + Math.round(rect.width) + "x" + Math.round(rect.height) + ")");
    }
    if (viewportW > 0 && viewportH > 0) {
      const fullyOutside = rect.right <= 0 || rect.bottom <= 0 || rect.left >= viewportW || rect.top >= viewportH;
      if (fullyOutside && !ancestorCanScrollIntoView(el, win)) {
        reasons.push(
          "off-viewport (rect=" + Math.round(rect.left) + "," + Math.round(rect.top) + " " + Math.round(rect.width) + "x" + Math.round(rect.height) + "; viewport=" + viewportW + "x" + viewportH + ")"
        );
      }
    }
    if (cs.opacity === "0" && !hasAnimationInFlight(el, cs)) {
      reasons.push("opacity:0 (no animation in flight)");
    }
    if (reasons.length === 0) continue;
    const sel = selectorFor7(el);
    const sourceLabel = decl.source === "expect-visible" ? "data-conformance-expect-visible" : "data-norkron-lib + data-region";
    findings.push({
      libId: LIB_ID52,
      code: "STR_DECLARED_CHROME_NOT_VISIBLE",
      category: "layout",
      severity: "major",
      message: "Declared chrome <" + sel + '> ("' + decl.label + '") is NOT visible to the user: ' + reasons.join("; ") + ". The element opted into a visibility contract via " + sourceLabel + " but its computed style or layout suppresses rendering.",
      selector: sel,
      suggestedFix: "Either (a) make the element render visibly (remove display:none, set visibility:visible, give it a non-zero rect, scroll it into view, or animate opacity > 0), or (b) drop the visibility declaration if the element is intentionally hidden in this state (remove data-conformance-expect-visible / data-region, or replace with conditional rendering so the node is not in the DOM at all).",
      extras: {
        label: decl.label,
        source: decl.source,
        lib: decl.lib,
        region: decl.region,
        reasons,
        rect: {
          left: rect.left,
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height
        },
        viewport: { width: viewportW, height: viewportH },
        computed: {
          display: cs.display,
          visibility: cs.visibility,
          opacity: cs.opacity
        }
      }
    });
  }
  return findings;
}

// src/structural/icon-low-contrast.ts
var LIB_ID53 = "@norkron/web-conformance";
var SHAPE_TAGS = /* @__PURE__ */ new Set(["path", "circle", "rect", "line", "polyline", "polygon", "ellipse"]);
var ICON_CONTRAST_MIN = 3;
function isExcluded4(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute("aria-hidden") === "true") return true;
    if (cur.hasAttribute("data-decorative")) return true;
    cur = cur.parentElement;
  }
  return false;
}
function resolvedPaint(el, prop, win) {
  const style = win.getComputedStyle(el);
  let raw = style[prop];
  if ((!raw || raw === "" || raw === "rgb(0, 0, 0)") && el.hasAttribute(prop)) {
    raw = el.getAttribute(prop) ?? "";
  }
  if (!raw || raw === "none" || raw === "transparent") return null;
  const parsed = parseCssColor(raw);
  if (!parsed) return null;
  if (parsed[3] < 0.01) return null;
  return parsed;
}
function checkIconLowContrast(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const svgs = root.querySelectorAll("svg");
  svgs.forEach((svg) => {
    if (isExcluded4(svg)) return;
    try {
      const rect = svg.getBoundingClientRect?.();
      if (rect && (rect.width <= 0 || rect.height <= 0)) return;
    } catch {
    }
    let worst = null;
    let shapeCount = 0;
    const shapes = svg.querySelectorAll("path,circle,rect,line,polyline,polygon,ellipse");
    shapes.forEach((shape) => {
      if (!SHAPE_TAGS.has(shape.tagName.toLowerCase())) return;
      if (isExcluded4(shape)) return;
      const bg = resolveBackgroundColor(svg, win);
      for (const prop of ["stroke", "fill"]) {
        const paint = resolvedPaint(shape, prop, win);
        if (!paint) continue;
        const fgComposed = paint[3] < 0.999 ? compositeOver(paint, bg) : [paint[0], paint[1], paint[2]];
        const ratio = contrastRatio(fgComposed, bg);
        if (ratio + 1e-6 < ICON_CONTRAST_MIN) {
          shapeCount++;
          if (!worst || ratio < worst.ratio) {
            worst = { ratio, fg: fgComposed, bg, prop, tag: shape.tagName.toLowerCase() };
          }
        }
      }
    });
    if (worst && shapeCount > 0) {
      const w = worst;
      const ariaLabel = svg.getAttribute("aria-label") ?? svg.getAttribute("data-icon-name") ?? "";
      findings.push({
        libId: LIB_ID53,
        code: "STR_ICON_LOW_CONTRAST",
        category: "a11y",
        severity: "major",
        message: `SVG icon ${ariaLabel ? `"${ariaLabel}" ` : ""}has <${w.tag}> ${w.prop} contrast ${w.ratio.toFixed(2)}:1 vs background \u2014 WCAG graphical-object minimum is ${ICON_CONTRAST_MIN}:1.`,
        selector: "svg",
        suggestedFix: "Darken the icon stroke/fill OR lighten the surrounding background so the icon meets the 3:1 graphical-object minimum (WCAG SC 1.4.11). For inherited `currentColor`, bump the ancestor `color` to a body-text token.",
        extras: {
          fg: rgbToHex(w.fg),
          bg: rgbToHex(w.bg),
          ratio: Math.round(w.ratio * 100) / 100,
          threshold: ICON_CONTRAST_MIN,
          paintProp: w.prop,
          shapeTag: w.tag,
          shapesBelow: shapeCount,
          ariaLabel
        }
      });
    }
  });
  return findings;
}

// src/structural/element-width-exceeds-parent.ts
var LIB_ID54 = "@norkron/web-conformance";
var RATIO_THRESHOLD = 1.5;
function hasAllowOverflow(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute("data-allow-overflow") !== null) return true;
    cur = cur.parentElement;
  }
  return false;
}
function checkElementWidthExceedsParent(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  const body = doc.body;
  if (!body) return findings;
  const all = root.querySelectorAll("*");
  const reported = /* @__PURE__ */ new Set();
  all.forEach((el) => {
    if (reported.has(el)) return;
    if (el === body || el === doc.documentElement) return;
    const parent = el.parentElement;
    if (!parent) return;
    if (parent === body || parent === doc.documentElement) return;
    let elStyle = null;
    try {
      elStyle = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (elStyle && (elStyle.position === "fixed" || elStyle.position === "absolute")) {
      return;
    }
    {
      let cur = parent;
      let allowFromAncestor = false;
      while (cur && cur !== root.parentElement) {
        if (cur.getAttribute && cur.getAttribute("data-allow-child-overflow") !== null) {
          allowFromAncestor = true;
          break;
        }
        cur = cur.parentElement;
      }
      if (allowFromAncestor) return;
    }
    let rect = null;
    try {
      rect = el.getBoundingClientRect();
    } catch {
      return;
    }
    if (!rect || rect.width <= 0) return;
    const parentClientW = parent.clientWidth ?? 0;
    if (parentClientW <= 0) return;
    const ratio = rect.width / parentClientW;
    if (ratio < RATIO_THRESHOLD) return;
    if (hasAllowOverflow(el, root)) return;
    reported.add(el);
    const tag = el.tagName.toLowerCase();
    const cls = (el.getAttribute("class") ?? "").slice(0, 60);
    const sel = tag + (cls ? "." + cls.split(/\s+/).filter(Boolean).join(".") : "");
    findings.push({
      libId: LIB_ID54,
      code: "STR_ELEMENT_WIDTH_EXCEEDS_PARENT",
      category: "layout",
      severity: "major",
      message: `<${sel}> renders ${Math.round(rect.width)}px wide inside a ${Math.round(parentClientW)}px parent (${ratio.toFixed(2)}x).`,
      selector: tag,
      suggestedFix: 'Constrain the element with explicit width/height attributes (for SVG: width="16" height="16") OR CSS (max-width: 100%, width: var(--space-4)). Common cause: an `<svg>` with viewBox but no width/height falls back to its viewBox-defined pixel size.',
      extras: {
        width: Math.round(rect.width),
        parentClientWidth: Math.round(parentClientW),
        ratio: Math.round(ratio * 100) / 100,
        threshold: RATIO_THRESHOLD,
        tag,
        cls
      }
    });
  });
  return findings;
}

// src/structural/text-invisible.ts
var LIB_ID55 = "@norkron/web-conformance";
function isExcluded5(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute?.("aria-hidden") === "true") return true;
    if (cur.hasAttribute?.("data-decorative")) return true;
    if (cur.tagName.toLowerCase() === "svg") return true;
    cur = cur.parentElement;
  }
  return false;
}
function hasOwnVisibleText(el) {
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === 3) {
      const t = (node.nodeValue ?? "").replace(/\s+/g, "");
      if (t.length >= 1) return true;
    }
  }
  return false;
}
function checkTextInvisible(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) return [needsLayoutFinding4("STR_TEXT_INVISIBLE", "no-getComputedStyle")];
  } catch {
  }
  const seen = /* @__PURE__ */ new Set();
  root.querySelectorAll("*").forEach((el) => {
    if (seen.has(el)) return;
    if (isExcluded5(el)) return;
    if (!hasOwnVisibleText(el)) return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") return;
    const colorRaw = cs.color;
    if (!colorRaw) return;
    const parsedFg = parseCssColor(colorRaw);
    if (!parsedFg || parsedFg[3] < 0.01) return;
    const bg = resolveBackgroundColor(el, win);
    const fgComposed = parsedFg[3] < 0.999 ? compositeOver(parsedFg, bg) : [parsedFg[0], parsedFg[1], parsedFg[2]];
    if (Math.abs(fgComposed[0] - bg[0]) > 1) return;
    if (Math.abs(fgComposed[1] - bg[1]) > 1) return;
    if (Math.abs(fgComposed[2] - bg[2]) > 1) return;
    const ratio = contrastRatio(fgComposed, bg);
    if (ratio > 1.01) return;
    seen.add(el);
    const sample = (el.textContent ?? "").trim().slice(0, 40);
    findings.push({
      libId: LIB_ID55,
      code: "STR_TEXT_INVISIBLE",
      category: "a11y",
      severity: "critical",
      message: `<${el.tagName.toLowerCase()}> text "${sample}" is painted in the same color as its background (${rgbToHex(fgComposed)} on ${rgbToHex(bg)}, contrast ratio ${ratio.toFixed(2)}:1) \u2014 invisible to sighted users.`,
      selector: el.tagName.toLowerCase(),
      suggestedFix: "Adjust the `color` or `background-color` token so foreground and background are not the same value.",
      extras: { fg: rgbToHex(fgComposed), bg: rgbToHex(bg), ratio: Math.round(ratio * 100) / 100, sample }
    });
  });
  return findings;
}

// src/structural/focusable-no-visible-focus-indicator.ts
var LIB_ID56 = "@norkron/web-conformance";
var FOCUSABLE_TAGS = /* @__PURE__ */ new Set(["button", "a", "input", "select", "textarea"]);
var OPT_OUT_ATTR = "data-allow-no-focus-indicator";
function isExcluded6(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute?.("aria-hidden") === "true") return true;
    if (cur.hasAttribute?.("inert")) return true;
    if (cur.getAttribute?.(OPT_OUT_ATTR) === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function isFocusable(el) {
  const tag = el.tagName.toLowerCase();
  if (el.hasAttribute?.("disabled")) return false;
  const ti = el.getAttribute("tabindex");
  if (ti != null) {
    const n = Number(ti);
    return Number.isFinite(n) && n >= 0;
  }
  if (FOCUSABLE_TAGS.has(tag)) {
    if (tag === "a" && !el.hasAttribute("href")) return false;
    return true;
  }
  return false;
}
function hasFocusRuleForElement(doc, el) {
  const sheets = Array.from(doc.styleSheets ?? []);
  for (const sheet of sheets) {
    let rules = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    if (!rules) continue;
    const flat = [];
    const pushFlat = (list) => {
      if (!list) return;
      for (let i = 0; i < list.length; i++) {
        const node = list[i];
        if (!node) continue;
        if (node.selectorText) flat.push(node);
        else if (node.cssRules) pushFlat(node.cssRules);
      }
    };
    pushFlat(rules);
    for (let i = 0; i < flat.length; i++) {
      const r = flat[i];
      if (!r || !r.selectorText) continue;
      if (!/:focus(-visible)?\b/.test(r.selectorText)) continue;
      const bare = r.selectorText.split(",").map((s) => s.trim().replace(/:focus(-visible)?/g, "").trim());
      for (const sel of bare) {
        try {
          if (sel === "" || sel === "*" || el.matches(sel)) return true;
        } catch {
        }
      }
    }
  }
  return false;
}
function hasRestingIndicator(cs) {
  const outlineW = parseFloat(cs.outlineWidth || "0") || 0;
  if (outlineW >= 1 && cs.outlineStyle && cs.outlineStyle !== "none") return true;
  const bs = cs.boxShadow || "none";
  if (bs !== "none") return true;
  const borderW = parseFloat(cs.borderTopWidth || "0") || 0;
  if (borderW >= 2) return true;
  return false;
}
function checkFocusableNoVisibleFocusIndicator(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) return [needsLayoutFinding4("STR_FOCUSABLE_NO_VISIBLE_FOCUS_INDICATOR", "no-getComputedStyle")];
  } catch {
  }
  const candidates = root.querySelectorAll("button, a, input, select, textarea, [tabindex]");
  candidates.forEach((el) => {
    if (!isFocusable(el)) return;
    if (isExcluded6(el)) return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden") return;
    if (hasRestingIndicator(cs)) return;
    if (hasFocusRuleForElement(doc, el)) return;
    const sel = el.tagName.toLowerCase();
    findings.push({
      libId: LIB_ID56,
      code: "STR_FOCUSABLE_NO_VISIBLE_FOCUS_INDICATOR",
      category: "a11y",
      severity: "major",
      message: `Focusable <${sel}> has no :focus / :focus-visible rule and no resting outline / box-shadow \u2014 WCAG 2.4.7 Focus Visible violation.`,
      selector: sel,
      suggestedFix: "Add a `:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 2px; }` rule scoped to the focusable element.",
      extras: { tag: sel, outlineWidth: cs.outlineWidth, outlineStyle: cs.outlineStyle, boxShadow: cs.boxShadow }
    });
  });
  return findings;
}

// src/structural/z-index-stacking-conflict.ts
var LIB_ID57 = "@norkron/web-conformance";
var OVERLAY_REGIONS = /* @__PURE__ */ new Set(["drawer", "popover", "dialog", "tooltip", "menu", "modal", "sheet"]);
var OPT_OUT_ATTR2 = "data-allow-stacking-overlap";
function rectsOverlap(a, b) {
  const x1 = Math.max(a.left, b.left);
  const x2 = Math.min(a.right, b.right);
  const y1 = Math.max(a.top, b.top);
  const y2 = Math.min(a.bottom, b.bottom);
  const w = x2 - x1;
  const h = y2 - y1;
  if (w < 4 || h < 4) return null;
  return { w, h };
}
function isOverlay(el) {
  const region = el.getAttribute("data-region");
  if (region && OVERLAY_REGIONS.has(region)) return true;
  const role = el.getAttribute("role");
  if (role === "dialog" || role === "tooltip" || role === "menu") return true;
  return false;
}
function checkZIndexStackingConflict(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) return [needsLayoutFinding4("STR_Z_INDEX_STACKING_CONFLICT", "no-getComputedStyle")];
  } catch {
  }
  const stamped = [];
  root.querySelectorAll("[data-norkron-lib]").forEach((el) => {
    if (el.getAttribute("aria-hidden") === "true") return;
    if (el.getAttribute(OPT_OUT_ATTR2) === "true") return;
    if (isOverlay(el)) return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden") return;
    if (cs.position !== "fixed" && cs.position !== "absolute") return;
    const zRaw = cs.zIndex;
    if (!zRaw || zRaw === "auto") return;
    const z3 = Number(zRaw);
    if (!Number.isFinite(z3)) return;
    let rect;
    try {
      rect = el.getBoundingClientRect();
    } catch {
      return;
    }
    if (rect.width <= 0 || rect.height <= 0) return;
    stamped.push({ el, lib: el.getAttribute("data-norkron-lib") ?? "", rect, z: z3, position: cs.position });
  });
  const reported = /* @__PURE__ */ new Set();
  for (let i = 0; i < stamped.length; i++) {
    for (let j = i + 1; j < stamped.length; j++) {
      const a = stamped[i], b = stamped[j];
      if (!a || !b) continue;
      if (a.lib === b.lib) continue;
      if (a.el.contains(b.el) || b.el.contains(a.el)) continue;
      const ov = rectsOverlap(a.rect, b.rect);
      if (!ov) continue;
      const key = a.lib < b.lib ? `${a.lib}|${b.lib}` : `${b.lib}|${a.lib}`;
      if (reported.has(key)) continue;
      reported.add(key);
      findings.push({
        libId: LIB_ID57,
        code: "STR_Z_INDEX_STACKING_CONFLICT",
        category: "layout",
        severity: "major",
        message: `Two non-overlay lib roots (${a.lib} z=${a.z}, ${b.lib} z=${b.z}) sit at ${a.position}/${b.position} with overlapping bounding rects (${Math.round(ov.w)}x${Math.round(ov.h)}px overlap). Likely a stacking-context layering bug.`,
        selector: `[data-norkron-lib="${a.lib}"], [data-norkron-lib="${b.lib}"]`,
        suggestedFix: "Move one of the lib roots out of the shared stacking context, OR declare one as an overlay region.",
        extras: { libA: a.lib, libB: b.lib, zA: a.z, zB: b.z, positionA: a.position, positionB: b.position, overlapWidth: Math.round(ov.w), overlapHeight: Math.round(ov.h) }
      });
    }
  }
  return findings;
}

// src/structural/click-target-below-min-size.ts
var LIB_ID58 = "@norkron/web-conformance";
var TARGET_AAA = 24;
var TARGET_AA = 44;
var OPT_OUT_ATTR3 = "data-allow-small-target";
var INLINE_PARENTS = /* @__PURE__ */ new Set(["p", "li", "span", "em", "strong", "small", "cite", "label"]);
function isInteractive2(el) {
  const tag = el.tagName.toLowerCase();
  if (tag === "button") return true;
  if (tag === "a" && el.hasAttribute("href")) return true;
  if (tag === "input") {
    const t = (el.getAttribute("type") ?? "text").toLowerCase();
    return t === "button" || t === "submit" || t === "reset" || t === "checkbox" || t === "radio";
  }
  const role = el.getAttribute("role");
  return role === "button" || role === "link" || role === "switch" || role === "checkbox" || role === "radio";
}
function isExcluded7(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute?.("aria-hidden") === "true") return true;
    if (cur.hasAttribute?.("inert")) return true;
    if (cur.getAttribute?.(OPT_OUT_ATTR3) === "true") return true;
    cur = cur.parentElement;
  }
  if (el.hasAttribute?.("disabled")) return true;
  return false;
}
function isInlineTextLink(el) {
  if (el.tagName.toLowerCase() !== "a") return false;
  const parent = el.parentElement;
  if (!parent) return false;
  return INLINE_PARENTS.has(parent.tagName.toLowerCase());
}
function checkClickTargetBelowMinSize(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) return [needsLayoutFinding4("STR_CLICK_TARGET_BELOW_MIN_SIZE", "zero-rect")];
  } catch {
  }
  root.querySelectorAll("*").forEach((el) => {
    if (!isInteractive2(el)) return;
    if (isExcluded7(el)) return;
    if (isInlineTextLink(el)) return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden") return;
    let rect;
    try {
      rect = el.getBoundingClientRect();
    } catch {
      return;
    }
    if (rect.width <= 0 || rect.height <= 0) return;
    const smaller = Math.min(rect.width, rect.height);
    if (smaller >= TARGET_AA) return;
    const severity = smaller < TARGET_AAA ? "major" : "minor";
    const threshold = severity === "major" ? TARGET_AAA : TARGET_AA;
    const sel = el.tagName.toLowerCase();
    findings.push({
      libId: LIB_ID58,
      code: "STR_CLICK_TARGET_BELOW_MIN_SIZE",
      category: "a11y",
      severity,
      message: `Interactive <${sel}> has tap-target ${Math.round(rect.width)}x${Math.round(rect.height)}px \u2014 below the WCAG 2.5.5 ${severity === "major" ? "AAA (24px)" : "AA (44px)"} target-size threshold.`,
      selector: sel,
      suggestedFix: "Grow the control to at least 24x24 CSS pixels (AAA) \u2014 44x44 preferred (AA).",
      extras: { width: Math.round(rect.width), height: Math.round(rect.height), smaller: Math.round(smaller), threshold, tag: sel }
    });
  });
  return findings;
}

// src/structural/lib-root-no-data-norkron-lib-stamp.ts
var LIB_ID59 = "@norkron/web-conformance";
var OPT_OUT_ATTR4 = "data-allow-unstamped-bem";
var BEM_RE = /^([a-z][a-z0-9-]*?)(?:__|--)[a-z0-9-]+/;
var PRODUCT_PREFIXES = ["app-", "page-", "route-", "view-", "shell-"];
var UTILITY_PREFIXES = ["u-", "utility-", "is-", "has-"];
function isInsideSvg(el) {
  let cur = el;
  while (cur) {
    if (cur.tagName.toLowerCase() === "svg") return true;
    cur = cur.parentElement;
  }
  return false;
}
function hasOptOut19(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute?.(OPT_OUT_ATTR4) === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function ancestorIsStamped(el) {
  let cur = el;
  while (cur) {
    if (cur.hasAttribute?.("data-norkron-lib")) return true;
    cur = cur.parentElement;
  }
  return false;
}
function blockPrefix(className) {
  const m = className.match(BEM_RE);
  if (!m || m[1] === void 0) return null;
  const prefix = m[1];
  for (const p of UTILITY_PREFIXES) if (className.startsWith(p)) return null;
  for (const p of PRODUCT_PREFIXES) if (prefix.startsWith(p)) return null;
  return prefix;
}
function checkLibRootNoDataNorkronLibStamp(root) {
  const findings = [];
  try {
    const doc = root.ownerDocument ?? root;
    const sheetCount = (doc?.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) return [needsLayoutFinding4("STR_LIB_ROOT_NO_DATA_NORKRON_LIB_STAMP", "no-getComputedStyle")];
  } catch {
  }
  const byPrefix = /* @__PURE__ */ new Map();
  root.querySelectorAll("[class]").forEach((el) => {
    if (isInsideSvg(el)) return;
    if (hasOptOut19(el)) return;
    if (ancestorIsStamped(el)) return;
    const cls = el.getAttribute("class") ?? "";
    const tokens = cls.split(/\s+/).filter(Boolean);
    for (const tok of tokens) {
      const prefix = blockPrefix(tok);
      if (!prefix) continue;
      const existing = byPrefix.get(prefix);
      if (existing) existing.count++;
      else byPrefix.set(prefix, { count: 1, example: el });
      break;
    }
  });
  for (const [prefix, info] of byPrefix.entries()) {
    const tag = info.example.tagName.toLowerCase();
    findings.push({
      libId: LIB_ID59,
      code: "STR_LIB_ROOT_NO_DATA_NORKRON_LIB_STAMP",
      category: "identity",
      severity: "major",
      message: `BEM block "${prefix}" rendered ${info.count}x without any ancestor carrying \`data-norkron-lib\` \u2014 the audit cannot attribute findings to the owning lib.`,
      selector: `.${prefix}__*, .${prefix}--*`,
      suggestedFix: `Add \`data-norkron-lib="@norkron/web-${prefix}@<version>"\` to the lib root, OR rename to a product prefix, OR set \`data-allow-unstamped-bem="true"\` on an ancestor.`,
      extras: { blockPrefix: prefix, count: info.count, exampleTag: tag }
    });
  }
  return findings;
}

// src/structural/overlapping-click-targets.ts
var LIB_ID60 = "@norkron/web-conformance";
var OVERLAP_THRESHOLD = 0.5;
var OPT_OUT_ATTR5 = "data-allow-overlap-targets";
function isInteractive3(el) {
  const tag = el.tagName.toLowerCase();
  if (tag === "button") return true;
  if (tag === "a" && el.hasAttribute("href")) return true;
  const role = el.getAttribute("role");
  return role === "button" || role === "link";
}
function isExcluded8(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute?.("aria-hidden") === "true") return true;
    if (cur.hasAttribute?.("inert")) return true;
    if (cur.getAttribute?.(OPT_OUT_ATTR5) === "true") return true;
    cur = cur.parentElement;
  }
  if (el.hasAttribute?.("disabled")) return true;
  return false;
}
function rectArea(r) {
  return Math.max(0, r.width) * Math.max(0, r.height);
}
function overlapArea(a, b) {
  const x = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const y = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return x * y;
}
function checkOverlappingClickTargets(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") return findings;
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) return [needsLayoutFinding4("STR_OVERLAPPING_CLICK_TARGETS", "zero-rect")];
  } catch {
  }
  const items = [];
  root.querySelectorAll("*").forEach((el) => {
    if (!isInteractive3(el)) return;
    if (isExcluded8(el)) return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden") return;
    let r;
    try {
      r = el.getBoundingClientRect();
    } catch {
      return;
    }
    if (r.width <= 0 || r.height <= 0) return;
    const area = rectArea(r);
    if (area <= 0) return;
    items.push({ el, rect: r, area });
  });
  const reported = /* @__PURE__ */ new Set();
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i], b = items[j];
      if (!a || !b) continue;
      if (a.el.contains(b.el) || b.el.contains(a.el)) continue;
      const ov = overlapArea(a.rect, b.rect);
      if (ov <= 0) continue;
      const smaller = Math.min(a.area, b.area);
      const fraction = ov / smaller;
      if (fraction < OVERLAP_THRESHOLD) continue;
      if (reported.has(a.el) && reported.has(b.el)) continue;
      reported.add(a.el);
      reported.add(b.el);
      const sela = a.el.tagName.toLowerCase();
      const selb = b.el.tagName.toLowerCase();
      findings.push({
        libId: LIB_ID60,
        code: "STR_OVERLAPPING_CLICK_TARGETS",
        category: "layout",
        severity: "major",
        message: `Two interactive controls (<${sela}> and <${selb}>) overlap by ${(fraction * 100).toFixed(0)}% of the smaller control \u2014 only the top one is hittable.`,
        selector: `${sela}, ${selb}`,
        suggestedFix: "Reposition one control out of the other, OR merge them into one composite control.",
        extras: { tagA: sela, tagB: selb, overlapFraction: Math.round(fraction * 1e3) / 1e3, overlapArea: Math.round(ov), smallerArea: Math.round(smaller) }
      });
    }
  }
  return findings;
}

// src/structural/text-crowds-container-edge.ts
var LIB_ID61 = "@norkron/web-conformance";
var DEFAULT_MIN_GAP_PX = 4;
var ALLOW_ATTR14 = "data-allow-edge-text";
var THRESHOLD_ATTR = "data-min-edge-gap";
function ancestorAttr(el, root, attr) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute) {
      const v = cur.getAttribute(attr);
      if (v !== null) return v;
    }
    cur = cur.parentElement;
  }
  return null;
}
function hasVisibleBoundary(cs) {
  const bw = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0) + (parseFloat(cs.borderLeftWidth) || 0);
  if (bw > 0) return true;
  const bg = (cs.backgroundColor || "").trim().toLowerCase();
  if (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)" && bg !== "rgba(0,0,0,0)") {
    return true;
  }
  const shadow = (cs.boxShadow || "").trim().toLowerCase();
  if (shadow && shadow !== "none") return true;
  return false;
}
function checkTextCrowdsContainerEdge(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return [needsLayoutFinding4("STR_TEXT_CROWDS_CONTAINER_EDGE", "no-getComputedStyle")];
  }
  if (typeof root.getBoundingClientRect !== "function") {
    return [needsLayoutFinding4("STR_TEXT_CROWDS_CONTAINER_EDGE", "zero-rect")];
  }
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) {
      return [needsLayoutFinding4("STR_TEXT_CROWDS_CONTAINER_EDGE", "zero-rect")];
    }
  } catch {
  }
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  let node = walker.nextNode();
  const seenParents = /* @__PURE__ */ new Set();
  while (node) {
    const text = (node.nodeValue ?? "").trim();
    if (text.length === 0) {
      node = walker.nextNode();
      continue;
    }
    const parent = node.parentElement;
    if (!parent || seenParents.has(parent)) {
      node = walker.nextNode();
      continue;
    }
    seenParents.add(parent);
    if (text.includes("CONF_LIVE_FINDING") || text.includes("@norkron/web-conformance")) {
      node = walker.nextNode();
      continue;
    }
    if (ancestorAttr(parent, root, ALLOW_ATTR14) === "true") {
      node = walker.nextNode();
      continue;
    }
    const thrAttr = ancestorAttr(parent, root, THRESHOLD_ATTR);
    const minGap = thrAttr !== null && Number.isFinite(parseFloat(thrAttr)) ? parseFloat(thrAttr) : DEFAULT_MIN_GAP_PX;
    let bounded = null;
    let bcs = null;
    let cur = parent;
    while (cur && cur !== root.parentElement) {
      let cs;
      try {
        cs = win.getComputedStyle(cur);
      } catch {
        break;
      }
      if (cs.display === "none" || cs.visibility === "hidden") break;
      if (cs.textOverflow === "ellipsis" && cs.whiteSpace === "nowrap") {
        bounded = null;
        break;
      }
      if (hasVisibleBoundary(cs)) {
        bounded = cur;
        bcs = cs;
        break;
      }
      cur = cur.parentElement;
    }
    if (!bounded || !bcs) {
      node = walker.nextNode();
      continue;
    }
    let pRect;
    try {
      pRect = bounded.getBoundingClientRect();
    } catch {
      node = walker.nextNode();
      continue;
    }
    if (pRect.width <= 0 || pRect.height <= 0) {
      node = walker.nextNode();
      continue;
    }
    const bdT = parseFloat(bcs.borderTopWidth) || 0;
    const bdR = parseFloat(bcs.borderRightWidth) || 0;
    const bdB = parseFloat(bcs.borderBottomWidth) || 0;
    const bdL = parseFloat(bcs.borderLeftWidth) || 0;
    const cTop = pRect.top + bdT;
    const cRight = pRect.right - bdR;
    const cBottom = pRect.bottom - bdB;
    const cLeft = pRect.left + bdL;
    let pcs = null;
    try {
      pcs = win.getComputedStyle(parent);
    } catch {
      pcs = null;
    }
    const fontSizePx = pcs ? parseFloat(pcs.fontSize) || 0 : 0;
    let lineHeightPx = 0;
    if (pcs) {
      const lhRaw = (pcs.lineHeight || "").trim();
      if (lhRaw && lhRaw !== "normal") {
        const lhNum = parseFloat(lhRaw);
        if (Number.isFinite(lhNum)) {
          lineHeightPx = lhRaw.endsWith("px") ? lhNum : lhNum * fontSizePx;
        }
      }
      if (lineHeightPx === 0 && fontSizePx > 0) lineHeightPx = fontSizePx * 1.2;
    }
    const halfLeading = Math.max(0, (lineHeightPx - fontSizePx) / 2);
    let tRect;
    try {
      const range = doc.createRange();
      range.selectNodeContents(node);
      const rects = range.getClientRects();
      if (!rects || rects.length === 0) {
        node = walker.nextNode();
        continue;
      }
      let l = Infinity, r = -Infinity, t = Infinity, b = -Infinity;
      for (const rc of Array.from(rects)) {
        if (rc.width <= 0 && rc.height <= 0) continue;
        const rcTop = rc.top + halfLeading;
        const rcBottom = rc.bottom - halfLeading;
        if (rc.left < l) l = rc.left;
        if (rc.right > r) r = rc.right;
        if (rcTop < t) t = rcTop;
        if (rcBottom > b) b = rcBottom;
      }
      if (!Number.isFinite(l)) {
        node = walker.nextNode();
        continue;
      }
      tRect = { x: l, y: t, left: l, top: t, width: r - l, height: b - t, right: r, bottom: b, toJSON() {
        return this;
      } };
    } catch {
      node = walker.nextNode();
      continue;
    }
    const gapTop = tRect.top - cTop;
    const gapRight = cRight - tRect.right;
    const gapBottom = cBottom - tRect.bottom;
    const gapLeft = tRect.left - cLeft;
    const crowded = [];
    if (gapTop >= 0 && gapTop < minGap) crowded.push({ side: "top", gap: gapTop });
    if (gapRight >= 0 && gapRight < minGap) crowded.push({ side: "right", gap: gapRight });
    if (gapBottom >= 0 && gapBottom < minGap) crowded.push({ side: "bottom", gap: gapBottom });
    if (gapLeft >= 0 && gapLeft < minGap) crowded.push({ side: "left", gap: gapLeft });
    if (crowded.length === 0) {
      node = walker.nextNode();
      continue;
    }
    const tightest = crowded.reduce((a, c) => c.gap < a.gap ? c : a);
    const sides = crowded.map((c) => c.side).join(", ");
    const sel = bounded.tagName.toLowerCase();
    findings.push({
      libId: LIB_ID61,
      code: "STR_TEXT_CROWDS_CONTAINER_EDGE",
      category: "layout",
      severity: "major",
      message: `Text "${text.slice(0, 40)}" sits ${Math.round(tightest.gap)}px from the ${sides} padding-box (visible-boundary) edge of its bounded <${sel}> ancestor (min ${minGap}px expected) \u2014 no breathing room, the text is jammed against the edge.`,
      selector: sel,
      suggestedFix: `Add at least ${minGap}px padding on the ${sides} side(s) of the bounded container, or shrink the text run. Mark intentional full-bleed text with data-allow-edge-text="true", or relax the threshold with data-min-edge-gap.`,
      extras: {
        textPreview: text.slice(0, 80),
        crowdedSides: crowded.map((c) => c.side),
        gapTop,
        gapRight,
        gapBottom,
        gapLeft,
        minGap,
        tightestSide: tightest.side,
        tightestGap: tightest.gap,
        fontSizePx,
        lineHeightPx,
        halfLeading
      }
    });
    node = walker.nextNode();
  }
  return findings;
}

// src/structural/siblings-insufficient-gap.ts
var LIB_ID62 = "@norkron/web-conformance";
var MIN_GAP_PX = 4;
var FLUSH_OPT_OUT = "data-allow-flush-siblings";
function isInteractive4(el) {
  const tag = el.tagName.toLowerCase();
  if (tag === "button" || tag === "select" || tag === "textarea") return true;
  if (tag === "input") {
    const t = (el.getAttribute("type") || "text").toLowerCase();
    return t !== "hidden";
  }
  if (tag === "a" && el.hasAttribute("href")) return true;
  const role = el.getAttribute("role");
  return role === "button" || role === "link" || role === "checkbox" || role === "radio" || role === "switch" || role === "menuitem" || role === "tab";
}
function isFlushByDesign(el) {
  const tag = el.tagName.toLowerCase();
  if (tag === "td" || tag === "th" || tag === "tr" || tag === "li" || tag === "col" || tag === "option") return true;
  const role = el.getAttribute("role");
  if (role === "tab" || role === "row" || role === "gridcell" || role === "cell" || role === "columnheader" || role === "rowheader" || role === "option" || role === "menuitem" || role === "listitem") return true;
  const cls = (el.getAttribute("class") || "").toLowerCase();
  if (cls.includes("segment") || cls.includes("segmented")) return true;
  return false;
}
function checkSiblingsInsufficientGap(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return [needsLayoutFinding4("STR_SIBLINGS_INSUFFICIENT_GAP", "no-getComputedStyle")];
  }
  if (typeof root.getBoundingClientRect !== "function") {
    return [needsLayoutFinding4("STR_SIBLINGS_INSUFFICIENT_GAP", "zero-rect")];
  }
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) {
      return [needsLayoutFinding4("STR_SIBLINGS_INSUFFICIENT_GAP", "zero-rect")];
    }
  } catch {
  }
  const reported = /* @__PURE__ */ new Set();
  const containers = [root];
  root.querySelectorAll("*").forEach((el) => containers.push(el));
  for (const container of containers) {
    if (container.getAttribute?.(FLUSH_OPT_OUT) === "true") continue;
    const kids = Array.from(container.children);
    if (kids.length < 2) continue;
    const boxes = [];
    for (const kid of kids) {
      if (isFlushByDesign(kid)) continue;
      let cs;
      try {
        cs = win.getComputedStyle(kid);
      } catch {
        continue;
      }
      if (cs.display === "none" || cs.visibility === "hidden") continue;
      if (parseFloat(cs.opacity || "1") === 0) continue;
      if (cs.position === "absolute" || cs.position === "fixed") continue;
      let r;
      try {
        r = kid.getBoundingClientRect();
      } catch {
        continue;
      }
      if (r.width <= 0 || r.height <= 0) continue;
      boxes.push({ el: kid, rect: r, interactive: isInteractive4(kid) });
    }
    if (boxes.length < 2) continue;
    const sorted = [...boxes];
    let minTop = Infinity, maxBottom = -Infinity;
    let minLeft = Infinity, maxRight = -Infinity;
    for (const bx of sorted) {
      minTop = Math.min(minTop, bx.rect.top);
      maxBottom = Math.max(maxBottom, bx.rect.bottom);
      minLeft = Math.min(minLeft, bx.rect.left);
      maxRight = Math.max(maxRight, bx.rect.right);
    }
    const verticalStack = maxBottom - minTop >= maxRight - minLeft;
    sorted.sort((p, q) => verticalStack ? p.rect.top - q.rect.top || p.rect.left - q.rect.left : p.rect.left - q.rect.left || p.rect.top - q.rect.top);
    for (let i = 0; i + 1 < sorted.length; i++) {
      const a = sorted[i], b = sorted[i + 1];
      if (!a || !b) continue;
      if (a.el.contains(b.el) || b.el.contains(a.el)) continue;
      const ra = a.rect, rb = b.rect;
      const vOverlap = Math.min(ra.bottom, rb.bottom) - Math.max(ra.top, rb.top);
      const hOverlap = Math.min(ra.right, rb.right) - Math.max(ra.left, rb.left);
      let gap = Infinity;
      let axis = "";
      if (vOverlap > 0) {
        const hg = ra.right <= rb.left ? rb.left - ra.right : rb.right <= ra.left ? ra.left - rb.right : -1;
        if (hg >= 0 && hg < gap) {
          gap = hg;
          axis = "horizontal";
        }
      }
      if (hOverlap > 0) {
        const vg = ra.bottom <= rb.top ? rb.top - ra.bottom : rb.bottom <= ra.top ? ra.top - rb.bottom : -1;
        if (vg >= 0 && vg < gap) {
          gap = vg;
          axis = "vertical";
        }
      }
      if (!Number.isFinite(gap) || axis === "") continue;
      if (gap <= 0) continue;
      if (gap >= MIN_GAP_PX) continue;
      if (reported.has(a.el) && reported.has(b.el)) continue;
      reported.add(a.el);
      reported.add(b.el);
      const bothInteractive = a.interactive && b.interactive;
      const sela = a.el.tagName.toLowerCase();
      const selb = b.el.tagName.toLowerCase();
      findings.push({
        libId: LIB_ID62,
        code: "STR_SIBLINGS_INSUFFICIENT_GAP",
        category: "layout",
        severity: bothInteractive ? "major" : "minor",
        message: `Sibling <${sela}> and <${selb}> are only ${gap.toFixed(1)}px apart (${axis}) \u2014 under the ${MIN_GAP_PX}px minimum. ${bothInteractive ? "Both are interactive: the cramped gap is a mis-tap hazard." : "They read as a single smudged element with no visual separation."}`,
        selector: `${sela}, ${selb}`,
        suggestedFix: `Add at least ${MIN_GAP_PX}px gap between the siblings (flex/grid gap, margin). If the siblings are MEANT to abut (toolbar group, joined buttons), set data-allow-flush-siblings="true" on the shared parent.`,
        extras: {
          tagA: sela,
          tagB: selb,
          axis,
          gapPx: Math.round(gap * 100) / 100,
          bothInteractive
        }
      });
    }
  }
  return findings;
}

// src/structural/container-text-no-padding.ts
var LIB_ID63 = "@norkron/web-conformance";
var MIN_PADDING_PX = 4;
var ALLOW_ATTR15 = "data-allow-no-padding";
function ancestorHasAttr(el, root, attr, val) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(attr) === val) return true;
    cur = cur.parentElement;
  }
  return false;
}
function hasVisibleBoundary2(cs) {
  const bw = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0) + (parseFloat(cs.borderLeftWidth) || 0);
  if (bw > 0) return true;
  const bg = (cs.backgroundColor || "").trim().toLowerCase();
  if (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)" && bg !== "rgba(0,0,0,0)") {
    return true;
  }
  const shadow = (cs.boxShadow || "").trim().toLowerCase();
  if (shadow && shadow !== "none") return true;
  return false;
}
function directTextContent(el) {
  let acc = "";
  const kids = el.childNodes;
  for (let i = 0; i < kids.length; i++) {
    const n = kids[i];
    if (n && n.nodeType === 3) {
      const t = (n.nodeValue ?? "").trim();
      if (t) acc += (acc ? " " : "") + t;
    }
  }
  return acc;
}
function checkContainerTextNoPadding(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return [needsLayoutFinding4("STR_CONTAINER_TEXT_NO_PADDING", "no-getComputedStyle")];
  }
  if (typeof root.getBoundingClientRect !== "function") {
    return [needsLayoutFinding4("STR_CONTAINER_TEXT_NO_PADDING", "zero-rect")];
  }
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) {
      return [needsLayoutFinding4("STR_CONTAINER_TEXT_NO_PADDING", "zero-rect")];
    }
  } catch {
  }
  const candidates = [root];
  root.querySelectorAll("*").forEach((el) => candidates.push(el));
  for (const el of candidates) {
    const tag = el.tagName.toUpperCase();
    if (tag === "BODY" || tag === "HTML" || tag === "HEAD" || tag === "STYLE" || tag === "SCRIPT" || tag === "SVG") continue;
    const text = directTextContent(el);
    if (!text) continue;
    if (text.includes("CONF_LIVE_FINDING") || text.includes("@norkron/web-conformance")) continue;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      continue;
    }
    if (cs.display === "none" || cs.visibility === "hidden") continue;
    if (cs.textOverflow === "ellipsis" && cs.whiteSpace === "nowrap") continue;
    if (!hasVisibleBoundary2(cs)) continue;
    let r;
    try {
      r = el.getBoundingClientRect();
    } catch {
      continue;
    }
    if (r.width < 24 || r.height < 12) continue;
    if (ancestorHasAttr(el, root, ALLOW_ATTR15, "true")) continue;
    const padT = parseFloat(cs.paddingTop) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const padB = parseFloat(cs.paddingBottom) || 0;
    const padL = parseFloat(cs.paddingLeft) || 0;
    const deficient = [];
    if (padT < MIN_PADDING_PX) deficient.push({ side: "top", pad: padT });
    if (padR < MIN_PADDING_PX) deficient.push({ side: "right", pad: padR });
    if (padB < MIN_PADDING_PX) deficient.push({ side: "bottom", pad: padB });
    if (padL < MIN_PADDING_PX) deficient.push({ side: "left", pad: padL });
    if (deficient.length === 0) continue;
    const sides = deficient.map((d) => d.side).join(", ");
    const sel = el.tagName.toLowerCase();
    findings.push({
      libId: LIB_ID63,
      code: "STR_CONTAINER_TEXT_NO_PADDING",
      category: "layout",
      severity: "minor",
      message: `Bounded <${sel}> directly contains text "${text.slice(0, 40)}" but has < ${MIN_PADDING_PX}px padding on the ${sides} side(s) (top=${Math.round(padT)} right=${Math.round(padR)} bottom=${Math.round(padB)} left=${Math.round(padL)}). The text is glued to the container border.`,
      selector: sel,
      suggestedFix: `Add at least ${MIN_PADDING_PX}px padding on the ${sides} side(s) of the bounded container. Mark intentional zero-padding surfaces with data-allow-no-padding="true".`,
      extras: {
        textPreview: text.slice(0, 80),
        deficientSides: deficient.map((d) => d.side),
        paddingTop: padT,
        paddingRight: padR,
        paddingBottom: padB,
        paddingLeft: padL,
        minPadding: MIN_PADDING_PX
      }
    });
  }
  return findings;
}

// src/structural/spacing-not-tokenized.ts
var LIB_ID64 = "@norkron/web-conformance";
var ALLOW_ATTR16 = "data-allow-untokenized-spacing";
var EDGE_ATTR = "data-allow-edge-text";
var SPACING_TOKEN_PREFIXES = [
  // Legacy un-namespaced prefixes (pre iter-1 migration).
  "--space",
  "--gap",
  "--padding",
  "--spacing",
  "--inset",
  "--pad",
  // Canonical Norkron spacing-related token families (web-themer base.css).
  "--norkron-space",
  "--norkron-gap",
  "--norkron-padding",
  "--norkron-inset",
  "--norkron-radius",
  "--norkron-touch-target",
  "--norkron-scrollbar",
  "--norkron-font-size",
  "--norkron-line-height"
];
var CANONICAL_TOKEN_NAMESPACE = "--norkron-";
var SPACING_PROPS = [
  "padding",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "padding-inline",
  "padding-block",
  "padding-inline-start",
  "padding-inline-end",
  "padding-block-start",
  "padding-block-end",
  "margin",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "margin-inline",
  "margin-block",
  "gap",
  "row-gap",
  "column-gap"
];
function ancestorHasAttr2(el, root, attr, val) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(attr) === val) return true;
    cur = cur.parentElement;
  }
  return false;
}
function valueReferencesSpacingToken(value) {
  const v = (value || "").toLowerCase();
  if (!v.includes("var(")) return false;
  const re = /var\(\s*(--[a-z0-9-]+)/g;
  let m;
  while ((m = re.exec(v)) !== null) {
    const name = m[1] ?? "";
    if (name.startsWith(CANONICAL_TOKEN_NAMESPACE)) return true;
    for (const pre of SPACING_TOKEN_PREFIXES) {
      if (name.startsWith(pre)) return true;
    }
  }
  return false;
}
function isNonZeroSpacingLiteral(value) {
  const v = (value || "").trim().toLowerCase();
  if (!v || v === "0" || v === "none" || v === "normal" || v === "auto") return false;
  if (v.includes("var(")) return false;
  const nums = v.match(/-?\d*\.?\d+/g);
  if (!nums) return false;
  return nums.some((n) => parseFloat(n) !== 0);
}
function cssomSpacingFor(el, doc) {
  const result = { tokenized: false, literal: false };
  const inspectStyle = (style) => {
    if (!style) return;
    for (const prop of SPACING_PROPS) {
      let val = "";
      try {
        val = style.getPropertyValue(prop);
      } catch {
        continue;
      }
      if (!val) continue;
      if (valueReferencesSpacingToken(val)) result.tokenized = true;
      else if (isNonZeroSpacingLiteral(val)) result.literal = true;
    }
  };
  inspectStyle(el.style ?? null);
  let sheets;
  try {
    sheets = doc.styleSheets;
  } catch {
    return result;
  }
  for (let i = 0; i < sheets.length; i++) {
    const sheet = sheets[i];
    if (!sheet) continue;
    let rules = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    if (!rules) continue;
    walkRules(rules, el, inspectStyle);
  }
  return result;
}
function walkRules(rules, el, inspectStyle) {
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    const styleRule = rule;
    if (typeof styleRule.selectorText === "string" && styleRule.style) {
      let matches = false;
      try {
        matches = el.matches(styleRule.selectorText);
      } catch {
        matches = false;
      }
      if (matches) inspectStyle(styleRule.style);
      continue;
    }
    const grouping = rule;
    if (grouping && grouping.cssRules) {
      walkRules(grouping.cssRules, el, inspectStyle);
    }
  }
}
function hasVisibleBoundary3(cs, parentBg) {
  const bw = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0) + (parseFloat(cs.borderLeftWidth) || 0);
  const borderStyle = (cs.borderStyle || "").trim().toLowerCase();
  if (bw > 0 && borderStyle !== "none") return true;
  const shadow = (cs.boxShadow || "").trim().toLowerCase();
  if (shadow && shadow !== "none") return true;
  const bg = (cs.backgroundColor || "").trim().toLowerCase();
  const transparent = !bg || bg === "transparent" || bg === "rgba(0, 0, 0, 0)" || bg === "rgba(0,0,0,0)";
  if (transparent) return false;
  if (parentBg !== null && bg === parentBg.trim().toLowerCase()) return false;
  return true;
}
function checkSpacingNotTokenized(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return [needsLayoutFinding4("STR_SPACING_NOT_TOKENIZED", "no-getComputedStyle")];
  }
  let sheetCount = 0;
  try {
    sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
  } catch {
    sheetCount = 0;
  }
  if (sheetCount === 0) {
    return [needsLayoutFinding4("STR_SPACING_NOT_TOKENIZED", "zero-rect")];
  }
  const walker = doc.createTreeWalker(
    root,
    4
    /* SHOW_TEXT */
  );
  let node = walker.nextNode();
  const seenContainers = /* @__PURE__ */ new Set();
  while (node) {
    const text = (node.nodeValue ?? "").trim();
    if (text.length === 0) {
      node = walker.nextNode();
      continue;
    }
    const parent = node.parentElement;
    if (!parent) {
      node = walker.nextNode();
      continue;
    }
    if (text.includes("CONF_LIVE_FINDING") || text.includes("@norkron/web-conformance")) {
      node = walker.nextNode();
      continue;
    }
    if (ancestorHasAttr2(parent, root, ALLOW_ATTR16, "true")) {
      node = walker.nextNode();
      continue;
    }
    if (ancestorHasAttr2(parent, root, EDGE_ATTR, "true")) {
      node = walker.nextNode();
      continue;
    }
    const chain = [];
    let container = null;
    let cur = parent;
    while (cur && cur !== root.parentElement) {
      let cs;
      try {
        cs = win.getComputedStyle(cur);
      } catch {
        break;
      }
      if (cs.display === "none" || cs.visibility === "hidden") break;
      const parentEl = cur.parentElement;
      let parentBg = null;
      if (parentEl) {
        try {
          parentBg = win.getComputedStyle(parentEl).backgroundColor;
        } catch {
          parentBg = null;
        }
      }
      if (hasVisibleBoundary3(cs, parentBg)) {
        container = cur;
        break;
      }
      chain.push(cur);
      cur = parentEl;
    }
    if (!container) {
      node = walker.nextNode();
      continue;
    }
    if (seenContainers.has(container)) {
      node = walker.nextNode();
      continue;
    }
    seenContainers.add(container);
    let tokenized = false;
    let anyLiteral = false;
    for (const el of [...chain, container]) {
      const sp = cssomSpacingFor(el, doc);
      if (sp.tokenized) tokenized = true;
      if (sp.literal) anyLiteral = true;
    }
    if (tokenized) {
      node = walker.nextNode();
      continue;
    }
    const sel = container.tagName.toLowerCase();
    if (anyLiteral) {
      findings.push({
        libId: LIB_ID64,
        code: "STR_SPACING_NOT_TOKENIZED",
        category: "theme",
        severity: "major",
        message: `Spacing separating text "${text.slice(0, 40)}" from its visible <${sel}> container is set by a hardcoded literal \u2014 no node in the chain wires it to a spacing token (var(--space-*) / var(--gap-*) / var(--padding-*)). A theme change cannot touch this spacing.`,
        selector: sel,
        suggestedFix: 'Replace the hardcoded padding/margin/gap literal with a web-themer spacing token, e.g. `padding: var(--space-2)`. Opt-out for genuinely fixed spacing: data-allow-untokenized-spacing="true".',
        extras: {
          textPreview: text.slice(0, 80),
          subCase: "hardcoded-literal",
          containerTag: sel,
          chainLength: chain.length
        }
      });
    } else {
      findings.push({
        libId: LIB_ID64,
        code: "STR_SPACING_NOT_TOKENIZED",
        category: "theme",
        severity: "major",
        message: `Text "${text.slice(0, 40)}" inside visible <${sel}> container has no spacing wired to a design token \u2014 neither the text element, its layout wrappers, nor the container set any tokenized padding/margin/gap. Spacing here cannot respond to a theme change.`,
        selector: sel,
        suggestedFix: 'Wire the container or wrapper spacing to a web-themer spacing token, e.g. `padding: var(--space-2)`. If the text is intentionally edge-to-edge, mark it data-allow-edge-text="true"; for genuinely fixed spacing use data-allow-untokenized-spacing="true".',
        extras: {
          textPreview: text.slice(0, 80),
          subCase: "no-spacing-wired",
          containerTag: sel,
          chainLength: chain.length
        }
      });
    }
    node = walker.nextNode();
  }
  return findings;
}

// src/structural/interactive-no-hover-feedback.ts
var LIB_ID65 = "@norkron/web-conformance";
var CODE2 = "STR_INTERACTIVE_NO_HOVER_FEEDBACK";
var OPT_OUT_ATTR6 = "data-allow-no-hover";
var INTERACTIVE_TAGS2 = /* @__PURE__ */ new Set(["button", "a", "summary", "input", "select", "textarea"]);
var INTERACTIVE_ROLES2 = /* @__PURE__ */ new Set(["button", "link", "tab", "menuitem"]);
var PERCEPTIBLE_PROPS = [
  "background-color",
  "background",
  "background-image",
  "color",
  "border-color",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "border",
  "box-shadow",
  "outline",
  "outline-color",
  "opacity",
  "text-decoration",
  "text-decoration-line",
  "text-decoration-color",
  "transform",
  "filter"
];
function isExcluded9(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute?.(OPT_OUT_ATTR6) === "true") return true;
    if (cur.getAttribute?.("aria-hidden") === "true") return true;
    if (cur.hasAttribute?.("inert")) return true;
    if (cur.hasAttribute?.("data-hover")) return true;
    const cls = cur.getAttribute?.("class") ?? "";
    if (/\bhover\b|hover[-_A-Z]|[-_]hover/.test(cls)) return true;
    cur = cur.parentElement;
  }
  return false;
}
function isDisabled3(el) {
  if (el.hasAttribute?.("disabled")) return true;
  if (el.getAttribute("aria-disabled") === "true") return true;
  if (el.hasAttribute?.("inert")) return true;
  return false;
}
function isInteractive5(el) {
  if (isDisabled3(el)) return false;
  const role = el.getAttribute("role")?.toLowerCase();
  if (role && INTERACTIVE_ROLES2.has(role)) return true;
  const tag = el.tagName.toLowerCase();
  if (tag === "a") return el.hasAttribute("href");
  return INTERACTIVE_TAGS2.has(tag);
}
function hasPerceptibleHoverRule(doc, el) {
  const sheets = Array.from(doc.styleSheets ?? []);
  for (const sheet of sheets) {
    let rules = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    if (!rules) continue;
    const flatRules = [];
    const pushFlat = (list) => {
      if (!list) return;
      for (let i = 0; i < list.length; i++) {
        const node = list[i];
        if (!node) continue;
        if (node.selectorText) {
          flatRules.push(node);
        } else if (node.cssRules) {
          pushFlat(node.cssRules);
        }
      }
    };
    pushFlat(rules);
    for (let i = 0; i < flatRules.length; i++) {
      const r = flatRules[i];
      if (!r || !r.selectorText || !r.style) continue;
      if (!/:hover\b/.test(r.selectorText)) continue;
      const segments = r.selectorText.split(",").map((s) => s.trim()).filter((s) => /:hover\b/.test(s)).map((s) => s.replace(/:hover\b/g, "").trim());
      let matches = false;
      for (const sel of segments) {
        try {
          if (sel === "" || sel === "*" || el.matches(sel)) {
            matches = true;
            break;
          }
        } catch {
        }
      }
      if (!matches) continue;
      for (const prop of PERCEPTIBLE_PROPS) {
        const v = r.style.getPropertyValue(prop);
        if (v && v.trim() !== "") return true;
      }
    }
  }
  return false;
}
function checkInteractiveNoHoverFeedback(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) return [needsLayoutFinding4(CODE2, "no-getComputedStyle")];
  } catch {
    return [needsLayoutFinding4(CODE2, "no-getComputedStyle")];
  }
  const candidates = root.querySelectorAll(
    "button, a, summary, input, select, textarea, [role=button], [role=link], [role=tab], [role=menuitem]"
  );
  candidates.forEach((el) => {
    if (!isInteractive5(el)) return;
    if (isExcluded9(el)) return;
    if (hasPerceptibleHoverRule(doc, el)) return;
    const sel = el.tagName.toLowerCase();
    findings.push({
      libId: LIB_ID65,
      code: CODE2,
      category: "a11y",
      severity: "major",
      message: `Interactive <${sel}> has no :hover rule that changes a perceptible property (background-color, color, border-color, box-shadow, outline, opacity, text-decoration, transform). A control that gives zero hover feedback reads to pointer users as non-interactive.`,
      selector: sel,
      suggestedFix: 'Add a `:hover` rule that changes a visible property \u2014 e.g. `button:hover { background-color: var(--btn-hover-bg); }`. If hover is provided by a JS class swap, ensure the swapped class name contains `hover` (or set `data-hover`). Opt out via `data-allow-no-hover="true"`.',
      extras: { tag: sel, role: el.getAttribute("role") }
    });
  });
  return findings;
}

// src/structural/disabled-not-visually-distinct.ts
var LIB_ID66 = "@norkron/web-conformance";
var CODE3 = "STR_DISABLED_NOT_VISUALLY_DISTINCT";
var OPT_OUT_ATTR7 = "data-allow-indistinct-disabled";
var PERCEPTIBLE_PROPS2 = [
  "background-color",
  "background",
  "background-image",
  "color",
  "border-color",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "opacity",
  "filter",
  "box-shadow",
  "text-decoration",
  "cursor"
];
function isExcluded10(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute?.(OPT_OUT_ATTR7) === "true") return true;
    if (cur.getAttribute?.("aria-hidden") === "true") return true;
    if (cur.hasAttribute?.("inert")) return true;
    cur = cur.parentElement;
  }
  return false;
}
function isDisabled4(el) {
  if (el.hasAttribute?.("disabled")) return true;
  if (el.getAttribute("aria-disabled") === "true") return true;
  return false;
}
function paintFingerprint(cs) {
  return [
    cs.backgroundColor ?? "",
    cs.color ?? "",
    cs.borderTopColor ?? "",
    cs.borderBottomColor ?? "",
    cs.opacity ?? "",
    cs.backgroundImage ?? "",
    cs.filter ?? ""
  ].join("|");
}
function componentKey(el) {
  const stamp = el.getAttribute("data-norkron-lib");
  const tag = el.tagName.toLowerCase();
  if (stamp) return `${stamp}#${tag}`;
  const cls = (el.getAttribute("class") ?? "").trim().split(/\s+/).filter(Boolean)[0];
  return cls ? `${tag}.${cls}` : tag;
}
function findEnabledPeer(root, el) {
  const key = componentKey(el);
  const all = Array.from(root.querySelectorAll("*"));
  for (const cand of all) {
    if (cand === el) continue;
    if (isDisabled4(cand)) continue;
    if (componentKey(cand) !== key) continue;
    return cand;
  }
  return null;
}
function hasPerceptibleDisabledRule(doc, el) {
  const sheets = Array.from(doc.styleSheets ?? []);
  for (const sheet of sheets) {
    let rules = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    if (!rules) continue;
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      if (!r || !r.selectorText || !r.style) continue;
      if (!/:disabled\b|\[disabled\b|\[aria-disabled\b/.test(r.selectorText)) continue;
      const segments = r.selectorText.split(",").map((s) => s.trim()).filter((s) => /:disabled\b|\[disabled\b|\[aria-disabled\b/.test(s)).map(
        (s) => s.replace(/:disabled\b/g, "").replace(/\[disabled[^\]]*\]/g, "").replace(/\[aria-disabled[^\]]*\]/g, "").trim()
      ).filter(Boolean);
      let matches = false;
      if (segments.length === 0) {
        matches = true;
      }
      for (const sel of segments) {
        try {
          if (sel === "" || sel === "*" || el.matches(sel)) {
            matches = true;
            break;
          }
        } catch {
        }
      }
      if (!matches) continue;
      for (const prop of PERCEPTIBLE_PROPS2) {
        const v = r.style.getPropertyValue(prop);
        if (v && v.trim() !== "") return true;
      }
    }
  }
  return false;
}
function checkDisabledNotVisuallyDistinct(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  if (!doc) return findings;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return [needsLayoutFinding4(CODE3, "no-getComputedStyle")];
  }
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) return [needsLayoutFinding4(CODE3, "no-getComputedStyle")];
  } catch {
    return [needsLayoutFinding4(CODE3, "no-getComputedStyle")];
  }
  const candidates = root.querySelectorAll('[disabled], [aria-disabled="true"]');
  candidates.forEach((el) => {
    if (!isDisabled4(el)) return;
    if (isExcluded10(el)) return;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      return;
    }
    if (cs.display === "none" || cs.visibility === "hidden") return;
    const peer = findEnabledPeer(root, el);
    const sel = el.tagName.toLowerCase();
    if (peer) {
      let peerCs;
      try {
        peerCs = win.getComputedStyle(peer);
      } catch {
        return;
      }
      const disabledFp = paintFingerprint(cs);
      const enabledFp = paintFingerprint(peerCs);
      if (disabledFp === enabledFp) {
        findings.push({
          libId: LIB_ID66,
          code: CODE3,
          category: "a11y",
          severity: "major",
          message: `Disabled <${sel}> paints identically to a non-disabled peer of the same component (background-color, color, border-color, opacity all equal). Disabled controls must LOOK disabled \u2014 a sighted user cannot tell this control is inert.`,
          selector: sel,
          suggestedFix: 'Style `:disabled` / `[aria-disabled="true"]` with a distinct paint \u2014 desaturated background, dimmed text, lowered opacity, or `cursor: not-allowed`. Opt out via `data-allow-indistinct-disabled="true"` if the visual sameness is intentional.',
          extras: { tag: sel, trigger: "peer-fingerprint", fingerprint: disabledFp }
        });
      }
      return;
    }
    if (!hasPerceptibleDisabledRule(doc, el)) {
      findings.push({
        libId: LIB_ID66,
        code: CODE3,
        category: "a11y",
        severity: "major",
        message: `Disabled <${sel}> has no :disabled / [disabled] / [aria-disabled] stylesheet rule that changes a perceptible property, and there is no enabled peer to compare against. The disabled state is visually indistinguishable from the enabled state.`,
        selector: sel,
        suggestedFix: 'Add a `:disabled` (or `[aria-disabled="true"]`) rule changing a visible property \u2014 opacity, background-color, color, or `cursor: not-allowed`. Opt out via `data-allow-indistinct-disabled="true"`.',
        extras: { tag: sel, trigger: "no-disabled-rule" }
      });
    }
  });
  return findings;
}

// src/structural/excessive-line-height.ts
var LIB_ID67 = "@norkron/web-conformance";
var FLAT_CEILING = 2.2;
var ALLOW_ATTR17 = "data-allow-loose-leading";
var ROLE_BANDS = {
  code: { min: 1.3, max: 1.6 },
  body: { min: 1.4, max: 1.8 },
  heading: { min: 1.05, max: 1.4 },
  display: { min: 1.05, max: 2 }
};
function hasOptOut20(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    if (cur.getAttribute && cur.getAttribute(ALLOW_ATTR17) === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function resolveRole(el, root) {
  let cur = el;
  while (cur && cur !== root.parentElement) {
    const r = cur.getAttribute && cur.getAttribute("data-text-role");
    if (r === "code" || r === "body" || r === "heading" || r === "display") {
      return r;
    }
    if (cur.getAttribute && cur.getAttribute("data-norkron-lib") !== null) {
      const lr = cur.getAttribute("data-text-role");
      if (lr === "code" || lr === "body" || lr === "heading" || lr === "display") {
        return lr;
      }
    }
    cur = cur.parentElement;
  }
  return null;
}
function isMultiLine(el, fontSize) {
  const he = el;
  try {
    if (typeof he.getClientRects === "function") {
      const rects = he.getClientRects();
      if (rects && rects.length >= 2) return true;
    }
  } catch {
  }
  const sh = he.scrollHeight;
  if (typeof sh === "number" && fontSize > 0 && sh >= 2 * fontSize) return true;
  return false;
}
function checkExcessiveLineHeight(root) {
  const layout = requiresLayout(root);
  if (!layout.ready) {
    return [needsLayoutFinding4("STR_EXCESSIVE_LINE_HEIGHT", layout.reason)];
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return [needsLayoutFinding4("STR_EXCESSIVE_LINE_HEIGHT", "no-getComputedStyle")];
  }
  const findings = [];
  const elements = [root];
  root.querySelectorAll("*").forEach((el) => elements.push(el));
  for (const el of elements) {
    let ownsText = false;
    for (const child of Array.from(el.childNodes)) {
      if (child.nodeType === 3 && (child.nodeValue ?? "").trim().length > 0) {
        ownsText = true;
        break;
      }
    }
    if (!ownsText) continue;
    let cs;
    try {
      cs = win.getComputedStyle(el);
    } catch {
      continue;
    }
    if (cs.display === "none" || cs.visibility === "hidden") continue;
    if (parseFloat(cs.opacity || "1") === 0) continue;
    const fontSize = parseFloat(cs.fontSize || "");
    if (!Number.isFinite(fontSize) || fontSize <= 0) continue;
    const lhRaw = cs.lineHeight;
    if (!lhRaw || lhRaw === "normal") continue;
    const lineHeight = parseFloat(lhRaw);
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) continue;
    if (!isMultiLine(el, fontSize)) continue;
    const ratio = lineHeight / fontSize;
    const role = resolveRole(el, root);
    let exceeded;
    let band;
    let violated = false;
    if (role) {
      const b = ROLE_BANDS[role];
      if (ratio > b.max || ratio < b.min) {
        violated = true;
        exceeded = ratio > b.max ? b.max : b.min;
        band = `${role} band ${b.min}\u2013${b.max}`;
      } else {
        exceeded = b.max;
        band = "";
      }
    } else {
      exceeded = FLAT_CEILING;
      band = `flat ceiling ${FLAT_CEILING}`;
      if (ratio > FLAT_CEILING) violated = true;
    }
    if (!violated) continue;
    if (hasOptOut20(el, root)) continue;
    const tag = el.tagName.toLowerCase();
    const tooLoose = ratio > exceeded;
    findings.push({
      libId: LIB_ID67,
      code: "STR_EXCESSIVE_LINE_HEIGHT",
      category: "layout",
      severity: "major",
      message: `<${tag}> multi-line text has a line-height ratio of ${ratio.toFixed(2)} (line-height ${lineHeight}px \xF7 font-size ${fontSize}px) \u2014 ${tooLoose ? "above" : "below"} the ${band}. The vertical rhythm is ${tooLoose ? "visibly broken: dead gaps between consecutive lines" : "too tight"}.`,
      selector: tag,
      suggestedFix: `Set a line-height in proportion to the font-size${role ? ` for a "${role}" role (${ROLE_BANDS[role].min}\u2013${ROLE_BANDS[role].max})` : ` (\u2264${FLAT_CEILING}\xD7 font-size; 1.4\u20131.6 is typical body copy)`}. For genuinely loose leading by design, set data-allow-loose-leading="true".`,
      extras: {
        tag,
        ratio: Math.round(ratio * 1e3) / 1e3,
        lineHeightPx: lineHeight,
        fontSizePx: fontSize,
        role: role ?? null,
        ceiling: exceeded
      }
    });
  }
  return findings;
}

// src/structural/siblings-excessive-gap.ts
var LIB_ID68 = "@norkron/web-conformance";
var GAP_RATIO = 3;
var MIN_ABS_GAP_PX = 64;
var WIDE_OPT_OUT = "data-allow-wide-gap";
var DISTRIBUTING_JUSTIFY = /* @__PURE__ */ new Set([
  "space-between",
  "space-around",
  "space-evenly"
]);
function checkSiblingsExcessiveGap(root) {
  const layout = requiresLayout(root);
  if (!layout.ready) {
    return [needsLayoutFinding4("STR_SIBLINGS_EXCESSIVE_GAP", layout.reason)];
  }
  const doc = root.ownerDocument ?? root;
  const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.getComputedStyle !== "function") {
    return [needsLayoutFinding4("STR_SIBLINGS_EXCESSIVE_GAP", "no-getComputedStyle")];
  }
  try {
    const sheetCount = (doc.styleSheets && doc.styleSheets.length) ?? 0;
    if (sheetCount === 0) {
      return [needsLayoutFinding4("STR_SIBLINGS_EXCESSIVE_GAP", "zero-rect")];
    }
  } catch {
  }
  const findings = [];
  const reported = /* @__PURE__ */ new Set();
  const containers = [root];
  root.querySelectorAll("*").forEach((el) => containers.push(el));
  for (const container of containers) {
    if (container.getAttribute?.(WIDE_OPT_OUT) === "true") continue;
    let ccs;
    try {
      ccs = win.getComputedStyle(container);
    } catch {
      continue;
    }
    const cDisplay = ccs.display || "";
    const isFlexOrGrid = cDisplay.includes("flex") || cDisplay.includes("grid");
    if (isFlexOrGrid && DISTRIBUTING_JUSTIFY.has(ccs.justifyContent || "")) {
      continue;
    }
    const kids = Array.from(container.children);
    if (kids.length < 2) continue;
    const boxes = [];
    for (const kid of kids) {
      let cs;
      try {
        cs = win.getComputedStyle(kid);
      } catch {
        continue;
      }
      if (cs.display === "none" || cs.visibility === "hidden") continue;
      if (parseFloat(cs.opacity || "1") === 0) continue;
      if (cs.position === "absolute" || cs.position === "fixed") continue;
      let r;
      try {
        r = kid.getBoundingClientRect();
      } catch {
        continue;
      }
      if (r.width <= 0 || r.height <= 0) continue;
      boxes.push({ el: kid, rect: r });
    }
    if (boxes.length < 2) continue;
    const sorted = [...boxes];
    let minTop = Infinity, maxBottom = -Infinity;
    let minLeft = Infinity, maxRight = -Infinity;
    for (const bx of sorted) {
      minTop = Math.min(minTop, bx.rect.top);
      maxBottom = Math.max(maxBottom, bx.rect.bottom);
      minLeft = Math.min(minLeft, bx.rect.left);
      maxRight = Math.max(maxRight, bx.rect.right);
    }
    const ySpread = maxBottom - minTop;
    const xSpread = maxRight - minLeft;
    const verticalStack = ySpread >= xSpread;
    sorted.sort((p, q) => verticalStack ? p.rect.top - q.rect.top || p.rect.left - q.rect.left : p.rect.left - q.rect.left || p.rect.top - q.rect.top);
    for (let i = 0; i + 1 < sorted.length; i++) {
      const a = sorted[i], b = sorted[i + 1];
      if (!a || !b) continue;
      if (a.el.contains(b.el) || b.el.contains(a.el)) continue;
      const ra = a.rect, rb = b.rect;
      const vOverlap = Math.min(ra.bottom, rb.bottom) - Math.max(ra.top, rb.top);
      const hOverlap = Math.min(ra.right, rb.right) - Math.max(ra.left, rb.left);
      let gap = -1;
      let axis = "";
      let extentA = 0, extentB = 0;
      if (vOverlap > 0) {
        const hg = ra.right <= rb.left ? rb.left - ra.right : rb.right <= ra.left ? ra.left - rb.right : -1;
        if (hg > gap) {
          gap = hg;
          axis = "horizontal";
          extentA = ra.width;
          extentB = rb.width;
        }
      }
      if (hOverlap > 0) {
        const vg = ra.bottom <= rb.top ? rb.top - ra.bottom : rb.bottom <= ra.top ? ra.top - rb.bottom : -1;
        if (vg > gap) {
          gap = vg;
          axis = "vertical";
          extentA = ra.height;
          extentB = rb.height;
        }
      }
      if (axis === "" || gap <= 0) continue;
      const largerExtent = Math.max(extentA, extentB);
      if (largerExtent <= 0) continue;
      if (gap <= MIN_ABS_GAP_PX) continue;
      if (gap <= GAP_RATIO * largerExtent) continue;
      if (reported.has(a.el) && reported.has(b.el)) continue;
      reported.add(a.el);
      reported.add(b.el);
      const sela = a.el.tagName.toLowerCase();
      const selb = b.el.tagName.toLowerCase();
      findings.push({
        libId: LIB_ID68,
        code: "STR_SIBLINGS_EXCESSIVE_GAP",
        category: "layout",
        severity: "minor",
        message: `Sibling <${sela}> and <${selb}> are ${gap.toFixed(0)}px apart (${axis}) \u2014 over ${GAP_RATIO}\xD7 the larger sibling's own ${axis === "horizontal" ? "width" : "height"} of ${largerExtent.toFixed(0)}px and above the ${MIN_ABS_GAP_PX}px absolute floor. The gap reads as a disconnected / unintentional layout.`,
        selector: `${sela}, ${selb}`,
        suggestedFix: `Tighten the gap to be proportional to the elements (\u2264${GAP_RATIO}\xD7 their own extent), OR if the spread IS intentional use a distributing layout (flex/grid with justify-content: space-between). For a deliberate wide gap, set data-allow-wide-gap="true" on the shared parent.`,
        extras: {
          tagA: sela,
          tagB: selb,
          axis,
          gapPx: Math.round(gap * 100) / 100,
          largerExtentPx: Math.round(largerExtent * 100) / 100,
          ratio: Math.round(gap / largerExtent * 100) / 100
        }
      });
    }
  }
  return findings;
}

// src/structural/token-referenced-but-undefined.ts
var LIB_ID69 = "@norkron/web-conformance";
var AUDITED_NAMESPACES = [
  "--norkron-",
  "--space-",
  "--gap-",
  "--padding-",
  "--pad-",
  "--spacing-",
  "--inset-",
  "--radius-",
  "--shadow-"
];
function valueReferencesAuditedNamespace(value) {
  for (const ns of AUDITED_NAMESPACES) {
    if (value.includes(`var(${ns}`)) return true;
  }
  return false;
}
function extractVarReferences(value) {
  const out = [];
  const namespaceAlternation = AUDITED_NAMESPACES.map(
    (p) => p.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")
  ).join("|");
  const re = new RegExp(
    `var\\(\\s*((?:${namespaceAlternation})[A-Za-z0-9_-]+)\\s*(?:,\\s*([^)]*))?\\)`,
    "g"
  );
  let m;
  while ((m = re.exec(value)) !== null) {
    const name = m[1];
    if (!name) continue;
    const fb = (m[2] ?? "").trim();
    out.push([name, fb === "" ? null : fb]);
  }
  return out;
}
function tokenIsDefined(doc, tokenName) {
  const win = doc.defaultView;
  if (!win || typeof win.getComputedStyle !== "function") return false;
  try {
    const value = win.getComputedStyle(doc.documentElement).getPropertyValue(tokenName);
    if (value && value.trim() !== "") return true;
  } catch {
  }
  const needle = `${tokenName}:`;
  const sheets = Array.from(doc.styleSheets ?? []);
  const visit = (rules) => {
    if (!rules) return false;
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      if (!r) continue;
      if (r.cssText && r.cssText.includes(needle)) return true;
      if (r.cssRules && visit(r.cssRules)) return true;
    }
    return false;
  };
  for (const sheet of sheets) {
    let rules = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    if (visit(rules)) return true;
  }
  return false;
}
function collectReferences(doc, root) {
  const refs = [];
  const sheets = Array.from(doc.styleSheets ?? []);
  const visit = (rules) => {
    if (!rules) return;
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      if (!r) continue;
      if (r.style && typeof r.style.length === "number") {
        for (let k = 0; k < r.style.length; k++) {
          const propName = r.style.item(k);
          const value = r.style.getPropertyValue(propName);
          if (!value || !valueReferencesAuditedNamespace(value)) continue;
          for (const [tok, fb] of extractVarReferences(value)) {
            refs.push({
              tokenName: tok,
              fallback: fb,
              origin: "cssom",
              selectorOrTag: r.selectorText ?? "(unknown selector)"
            });
          }
        }
      }
      if (r.cssRules) visit(r.cssRules);
    }
  };
  for (const sheet of sheets) {
    let rules = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    visit(rules);
  }
  const all = [root, ...Array.from(root.querySelectorAll("*"))];
  for (const el of all) {
    const inline = el.getAttribute("style");
    if (!inline || !valueReferencesAuditedNamespace(inline)) continue;
    for (const [tok, fb] of extractVarReferences(inline)) {
      refs.push({
        tokenName: tok,
        fallback: fb,
        origin: "inline",
        selectorOrTag: el.tagName.toLowerCase()
      });
    }
  }
  return refs;
}
function checkTokenReferencedButUndefined(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const refs = collectReferences(doc, root);
  const definedCache = /* @__PURE__ */ new Map();
  const isDefined = (name) => {
    const cached = definedCache.get(name);
    if (cached !== void 0) return cached;
    const r = tokenIsDefined(doc, name);
    definedCache.set(name, r);
    return r;
  };
  const groups = /* @__PURE__ */ new Map();
  for (const r of refs) {
    if (isDefined(r.tokenName)) continue;
    const key = r.tokenName;
    const arr = groups.get(key) ?? [];
    arr.push(r);
    groups.set(key, arr);
  }
  for (const [tokenName, sites] of groups) {
    const allHaveFallback = sites.every((s) => s.fallback !== null);
    const severity = allHaveFallback ? "info" : "major";
    findings.push({
      libId: LIB_ID69,
      code: "STR_TOKEN_REFERENCED_BUT_UNDEFINED",
      category: "theme",
      severity,
      message: allHaveFallback ? `Token \`${tokenName}\` is referenced by ${sites.length} site(s) but is NOT defined by any stylesheet visible to the audit env. Every reference supplies a fallback so the widget will still paint \u2014 but the intended token wiring is BROKEN. Either define the token in @norkron/web-themer or rename the reference to a defined token.` : `Token \`${tokenName}\` is referenced by ${sites.length} site(s) but is NOT defined by any stylesheet visible to the audit env, AND at least one reference supplies NO fallback. The widget will paint with the CSS-level default for the property (usually 0px / auto / initial) \u2014 silent visual defect. Define the token in @norkron/web-themer or fix the reference name.`,
      selector: sites[0]?.selectorOrTag,
      suggestedFix: 'Either add the token to @norkron/web-themer/src/base.css under `:root` with an intentional value, or rename the reference to match an existing defined token (run `grep -nh -- "--norkron-" frontend/web/web-themer/src/base.css` for the catalog).',
      extras: {
        tokenName,
        referenceCount: sites.length,
        referencedFrom: sites.map((s) => ({
          origin: s.origin,
          selectorOrTag: s.selectorOrTag,
          fallback: s.fallback
        }))
      }
    });
  }
  return findings;
}

// src/structural/aria-reference-broken.ts
var LIB_ID70 = "@norkron/web-conformance";
var ARIA_IDREF_ATTRS = [
  { attr: "aria-labelledby", isList: true },
  { attr: "aria-describedby", isList: true },
  { attr: "aria-controls", isList: true },
  { attr: "aria-owns", isList: true },
  { attr: "aria-flowto", isList: true },
  { attr: "aria-activedescendant", isList: false },
  { attr: "aria-details", isList: false },
  { attr: "aria-errormessage", isList: false }
];
function escapeIdForSelector(id) {
  const win = typeof globalThis !== "undefined" ? globalThis : null;
  if (win?.CSS && typeof win.CSS.escape === "function") {
    return win.CSS.escape(id);
  }
  return id.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
function elementExistsForId(doc, id) {
  try {
    if (doc.getElementById(id)) return true;
  } catch {
  }
  try {
    if (doc.querySelector(`#${escapeIdForSelector(id)}`)) return true;
  } catch {
  }
  return false;
}
function checkAriaReferenceBroken(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const all = [root, ...Array.from(root.querySelectorAll("*"))];
  const seen = /* @__PURE__ */ new Set();
  for (const el of all) {
    for (const { attr, isList } of ARIA_IDREF_ATTRS) {
      const raw = el.getAttribute(attr);
      if (raw === null) continue;
      const trimmed = raw.trim();
      if (trimmed === "") continue;
      const ids = isList ? trimmed.split(/\s+/).filter((s) => s !== "") : [trimmed];
      for (const id of ids) {
        if (elementExistsForId(doc, id)) continue;
        const tag = el.tagName.toLowerCase();
        const key = `${tag}|${attr}|${id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        findings.push({
          libId: LIB_ID70,
          code: "STR_ARIA_REFERENCE_BROKEN",
          category: "a11y",
          severity: "major",
          message: `<${tag}> declares \`${attr}="${id}"\` but no element with id="${id}" exists in the document. Screen readers will announce nothing (or fall back to the element's own textContent). The widget visually looks fine \u2014 this is a silent a11y defect of the "I assumed the referent exists" class.`,
          selector: tag,
          suggestedFix: isList ? `Either add an element with id="${id}" providing the label/description/control target, OR remove the broken id from the space-separated \`${attr}\` value, OR drop the attribute entirely if it has no valid referents.` : `Either add an element with id="${id}", OR remove the \`${attr}\` attribute entirely.`,
          extras: {
            attr,
            brokenId: id,
            sourceTag: tag,
            isList
          }
        });
      }
    }
  }
  return findings;
}

// src/structural/label-for-broken.ts
var LIB_ID71 = "@norkron/web-conformance";
var LABELABLE_TAGS = /* @__PURE__ */ new Set([
  "button",
  "input",
  "meter",
  "output",
  "progress",
  "select",
  "textarea"
]);
function classifyLabelFor(doc, forId) {
  let target = null;
  try {
    target = doc.getElementById(forId);
  } catch {
  }
  if (!target) {
    return { forId, reason: "no-such-id", targetTag: null };
  }
  const tag = target.tagName.toLowerCase();
  if (!LABELABLE_TAGS.has(tag)) {
    return { forId, reason: "target-not-labelable", targetTag: tag };
  }
  if (tag === "input") {
    const type = (target.getAttribute("type") ?? "text").toLowerCase();
    if (type === "hidden") {
      return { forId, reason: "hidden-input", targetTag: tag };
    }
  }
  return null;
}
function checkLabelForBroken(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const labels = [];
  if (root.tagName?.toLowerCase() === "label") labels.push(root);
  for (const el of Array.from(root.querySelectorAll("label"))) {
    labels.push(el);
  }
  const seen = /* @__PURE__ */ new Set();
  for (const label of labels) {
    const forId = label.getAttribute("for");
    if (forId === null) continue;
    const trimmed = forId.trim();
    if (trimmed === "") continue;
    const broken = classifyLabelFor(doc, trimmed);
    if (!broken) continue;
    const key = `${broken.forId}|${broken.reason}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const labelText = (label.textContent ?? "").trim().slice(0, 64);
    const message = (() => {
      if (broken.reason === "no-such-id") {
        return `<label for="${broken.forId}">${labelText}</label> references id="${broken.forId}" but no element with that id exists in the document. The label has no labelable target \u2014 screen readers see an unlabeled input. Silent form-association defect of the "I assumed the input id matches" class.`;
      }
      if (broken.reason === "target-not-labelable") {
        return `<label for="${broken.forId}">${labelText}</label> references id="${broken.forId}" which resolves to a <${broken.targetTag}>, but only button/input/meter/output/progress/select/textarea are labelable per HTML spec. The label/input association is INVALID and screen readers see an unlabeled control.`;
      }
      return `<label for="${broken.forId}">${labelText}</label> references a hidden <input type="hidden"> \u2014 labels on hidden inputs are almost certainly a bug (the visible control the user actually types into has no association).`;
    })();
    findings.push({
      libId: LIB_ID71,
      code: "STR_LABEL_FOR_BROKEN",
      category: "a11y",
      severity: "major",
      message,
      selector: "label",
      suggestedFix: broken.reason === "no-such-id" ? `Either add a labelable input with id="${broken.forId}", or wrap the label CONTENT around the input (implicit association), or remove the broken \`for\` attribute.` : broken.reason === "target-not-labelable" ? `Point the label at an actual <input>/<select>/<textarea> (or other labelable element), or use \`aria-labelledby="${broken.forId}"\` if you really meant to label a non-form element.` : `Remove the label, or point it at the visible input the user actually interacts with.`,
      extras: {
        forId: broken.forId,
        reason: broken.reason,
        targetTag: broken.targetTag,
        labelText
      }
    });
  }
  return findings;
}

// src/structural/duplicate-id.ts
var LIB_ID72 = "@norkron/web-conformance";
function checkDuplicateId(root) {
  const findings = [];
  const candidates = [root, ...Array.from(root.querySelectorAll("[id]"))];
  const byId = /* @__PURE__ */ new Map();
  for (const el of candidates) {
    const id = el.getAttribute("id");
    if (id === null) continue;
    const trimmed = id.trim();
    if (trimmed === "") continue;
    const list = byId.get(trimmed) ?? [];
    list.push(el);
    byId.set(trimmed, list);
  }
  for (const [id, els] of byId) {
    if (els.length < 2) continue;
    const tags = els.map((e) => e.tagName.toLowerCase());
    findings.push({
      libId: LIB_ID72,
      code: "STR_DUPLICATE_ID",
      category: "a11y",
      severity: "major",
      message: `id="${id}" appears on ${els.length} elements (${tags.join(", ")}). HTML requires ids to be document-unique. getElementById will return only the FIRST match in document order \u2014 every aria-labelledby/describedby/controls/owns/flowto, label[for], and href="#id" pointing at this id silently routes to the wrong element. This breaks the IDREF guarantees that STR_ARIA_REFERENCE_BROKEN and STR_LABEL_FOR_BROKEN depend on (they see resolution succeed and report nothing wrong).`,
      selector: tags[0] ?? "*",
      suggestedFix: "Rename duplicate ids so each is document-unique. If the elements are meant to be linked (e.g. groups), use a shared `data-*` attribute or `aria-controls`/`aria-labelledby` with a single canonical id elsewhere.",
      extras: {
        id,
        count: els.length,
        tags
      }
    });
  }
  return findings;
}

// src/structural/button-no-accessible-name.ts
var LIB_ID73 = "@norkron/web-conformance";
function elementHasNonEmptyText(el) {
  if (!el) return false;
  const text = (el.textContent ?? "").trim();
  return text.length > 0;
}
function resolveLabelledbyText(doc, refValue) {
  const ids = refValue.trim().split(/\s+/).filter((s) => s !== "");
  for (const id of ids) {
    let target = null;
    try {
      target = doc.getElementById(id);
    } catch {
    }
    if (elementHasNonEmptyText(target)) return true;
  }
  return false;
}
function isHiddenFromA11y(el) {
  if (el.hasAttribute("hidden")) return true;
  if (el.getAttribute("aria-hidden") === "true") return true;
  let cur = el.parentElement;
  while (cur) {
    if (cur.getAttribute("aria-hidden") === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function accessibleName(el, doc) {
  const labelledby = el.getAttribute("aria-labelledby");
  if (labelledby && labelledby.trim() !== "") {
    if (resolveLabelledbyText(doc, labelledby)) {
      return { hasName: true, source: "labelledby", fallbackToTitle: false };
    }
  }
  const ariaLabel = el.getAttribute("aria-label");
  if (ariaLabel && ariaLabel.trim() !== "") {
    return { hasName: true, source: "aria-label", fallbackToTitle: false };
  }
  if (el.tagName.toLowerCase() === "button") {
    const btnType = (el.getAttribute("type") ?? "submit").toLowerCase();
    if (btnType === "submit" || btnType === "reset") {
      const value = el.getAttribute("value");
      if (value && value.trim() !== "") {
        return { hasName: true, source: "value", fallbackToTitle: false };
      }
    }
  }
  if (elementHasNonEmptyText(el)) {
    return { hasName: true, source: "textContent", fallbackToTitle: false };
  }
  const title = el.getAttribute("title");
  if (title && title.trim() !== "") {
    return { hasName: true, source: "title", fallbackToTitle: true };
  }
  return { hasName: false, source: null, fallbackToTitle: false };
}
function isButtonLike(el) {
  if (el.tagName.toLowerCase() === "button") return true;
  if (el.getAttribute("role") === "button") return true;
  return false;
}
function checkButtonNoAccessibleName(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const candidates = [];
  if (isButtonLike(root)) candidates.push(root);
  for (const el of Array.from(
    root.querySelectorAll('button, [role="button"]')
  )) {
    candidates.push(el);
  }
  for (const el of candidates) {
    if (isHiddenFromA11y(el)) continue;
    const result = accessibleName(el, doc);
    if (!result.hasName) {
      const innerSummary = (() => {
        const inner = el.innerHTML.trim().slice(0, 80);
        return inner === "" ? "(empty)" : inner;
      })();
      findings.push({
        libId: LIB_ID73,
        code: "STR_BUTTON_NO_ACCESSIBLE_NAME",
        category: "a11y",
        severity: "major",
        message: `<${el.tagName.toLowerCase()}${el.getAttribute("role") === "button" ? ' role="button"' : ""}> has NO source of accessible name: no resolvable aria-labelledby, no non-empty aria-label, no non-empty textContent, no submit-value, no title. Screen readers announce "button" with no name. Inner: ${innerSummary}`,
        selector: el.tagName.toLowerCase(),
        suggestedFix: 'Add an `aria-label="\u2026"` describing the action (e.g. `aria-label="Close dialog"`), OR put visible textContent inside (e.g. wrap the icon: `<button><Icon aria-hidden="true" /> Save</button>`), OR set `aria-labelledby` to a label element\'s id.',
        extras: {
          tag: el.tagName.toLowerCase(),
          role: el.getAttribute("role"),
          innerHtmlSnippet: innerSummary
        }
      });
      continue;
    }
    if (result.fallbackToTitle) {
      findings.push({
        libId: LIB_ID73,
        code: "STR_BUTTON_NO_ACCESSIBLE_NAME",
        category: "a11y",
        severity: "info",
        message: `<${el.tagName.toLowerCase()}> uses ONLY the \`title\` attribute for its accessible name. Title is the lowest-priority name source \u2014 not announced reliably by mobile screen readers, requires hover on desktop (broken on touch). Prefer aria-label or visible textContent.`,
        selector: el.tagName.toLowerCase(),
        suggestedFix: "Promote the title content to `aria-label` for first-class name resolution, or add visible textContent. Keep `title` only for supplementary tooltip text.",
        extras: {
          tag: el.tagName.toLowerCase(),
          nameSource: "title"
        }
      });
    }
  }
  return findings;
}

// src/structural/form-input-no-accessible-name.ts
var LIB_ID74 = "@norkron/web-conformance";
function nonEmptyText(s) {
  return s !== null && s.trim() !== "";
}
function elementHasNonEmptyText2(el) {
  if (!el) return false;
  const t = (el.textContent ?? "").trim();
  return t.length > 0;
}
function resolveLabelledbyText2(doc, refValue) {
  const ids = refValue.trim().split(/\s+/).filter((s) => s !== "");
  for (const id of ids) {
    let target = null;
    try {
      target = doc.getElementById(id);
    } catch {
    }
    if (elementHasNonEmptyText2(target)) return true;
  }
  return false;
}
function isHiddenFromA11y2(el) {
  if (el.hasAttribute("hidden")) return true;
  if (el.getAttribute("aria-hidden") === "true") return true;
  let cur = el.parentElement;
  while (cur) {
    if (cur.getAttribute("aria-hidden") === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function classify(el) {
  const tag = el.tagName.toLowerCase();
  if (tag !== "input" && tag !== "select" && tag !== "textarea") return null;
  const inputType = tag === "input" ? (el.getAttribute("type") ?? "text").toLowerCase() : tag;
  if (tag === "input" && inputType === "hidden") {
    return { tag, inputType, skipReason: "hidden" };
  }
  if (tag === "input" && (inputType === "submit" || inputType === "reset" || inputType === "button")) {
    const v = el.getAttribute("value");
    if (nonEmptyText(v)) return { tag, inputType, skipReason: "has-value-name" };
    return { tag, inputType, skipReason: null };
  }
  if (tag === "input" && inputType === "image") {
    const alt = el.getAttribute("alt");
    if (nonEmptyText(alt)) return { tag, inputType, skipReason: "image-with-alt" };
    return { tag, inputType, skipReason: null };
  }
  return { tag, inputType, skipReason: null };
}
function hasWrappingLabel(el) {
  let cur = el.parentElement;
  while (cur) {
    if (cur.tagName.toLowerCase() === "label") {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
function hasExplicitLabel(doc, el) {
  const id = el.getAttribute("id");
  if (!id || id.trim() === "") return false;
  const sel = `label[for="${id.replace(/"/g, '\\"')}"]`;
  let label = null;
  try {
    label = doc.querySelector(sel);
  } catch {
    return false;
  }
  if (!label) return false;
  return elementHasNonEmptyText2(label);
}
function accessibleName2(doc, el) {
  const labelledby = el.getAttribute("aria-labelledby");
  if (labelledby && resolveLabelledbyText2(doc, labelledby)) {
    return { hasName: true, source: "labelledby", fallbackSource: null };
  }
  const ariaLabel = el.getAttribute("aria-label");
  if (nonEmptyText(ariaLabel)) {
    return { hasName: true, source: "aria-label", fallbackSource: null };
  }
  if (hasWrappingLabel(el)) {
    return { hasName: true, source: "wrapping-label", fallbackSource: null };
  }
  if (hasExplicitLabel(doc, el)) {
    return { hasName: true, source: "explicit-label", fallbackSource: null };
  }
  const placeholder = el.getAttribute("placeholder");
  if (nonEmptyText(placeholder)) {
    return { hasName: true, source: "placeholder", fallbackSource: "placeholder" };
  }
  const title = el.getAttribute("title");
  if (nonEmptyText(title)) {
    return { hasName: true, source: "title", fallbackSource: "title" };
  }
  return { hasName: false, source: null, fallbackSource: null };
}
function checkFormInputNoAccessibleName(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const candidates = [];
  const rootTag = root.tagName?.toLowerCase();
  if (rootTag === "input" || rootTag === "select" || rootTag === "textarea") {
    candidates.push(root);
  }
  for (const el of Array.from(
    root.querySelectorAll("input, select, textarea")
  )) {
    candidates.push(el);
  }
  for (const el of candidates) {
    const cls = classify(el);
    if (!cls || cls.skipReason !== null) continue;
    if (isHiddenFromA11y2(el)) continue;
    const result = accessibleName2(doc, el);
    if (!result.hasName) {
      findings.push({
        libId: LIB_ID74,
        code: "STR_FORM_INPUT_NO_ACCESSIBLE_NAME",
        category: "a11y",
        severity: "major",
        message: `<${cls.tag}${cls.tag === "input" ? ` type="${cls.inputType}"` : ""}> has NO source of accessible name: no resolvable aria-labelledby, no non-empty aria-label, no wrapping <label>, no <label for="\u2026"> pointing here, no placeholder, no title. Screen readers announce "${cls.tag === "input" ? `edit ${cls.inputType}` : cls.tag}" with no name. Visual proximity is NOT a name source.`,
        selector: cls.tag,
        suggestedFix: 'Add a `<label for="\u2026">\u2026</label>` paired with `id="\u2026"` on the input (preferred), OR wrap the input in a `<label>`, OR set `aria-label="\u2026"`. Placeholder is NOT a substitute (it disappears on focus).',
        extras: {
          tag: cls.tag,
          inputType: cls.inputType
        }
      });
      continue;
    }
    if (result.fallbackSource !== null) {
      findings.push({
        libId: LIB_ID74,
        code: "STR_FORM_INPUT_NO_ACCESSIBLE_NAME",
        category: "a11y",
        severity: "info",
        message: `<${cls.tag}${cls.tag === "input" ? ` type="${cls.inputType}"` : ""}> uses ONLY the \`${result.fallbackSource}\` attribute for its accessible name. ${result.fallbackSource === "placeholder" ? "Placeholder is NOT a name source per WAI; it disappears on focus, leaving screen readers AND sighted users without a visible label." : "Title is unreliable on mobile / touch and requires hover on desktop."} Add a real <label> or aria-label.`,
        selector: cls.tag,
        suggestedFix: `Promote the ${result.fallbackSource} content to a paired <label> or to aria-label. Keep ${result.fallbackSource} only for supplementary hint text.`,
        extras: {
          tag: cls.tag,
          inputType: cls.inputType,
          nameSource: result.fallbackSource
        }
      });
    }
  }
  return findings;
}

// src/structural/href-fragment-broken.ts
var LIB_ID75 = "@norkron/web-conformance";
function elementForFragment(doc, fragment) {
  try {
    const byId = doc.getElementById(fragment);
    if (byId) return byId;
  } catch {
  }
  try {
    const byName = doc.querySelector(`a[name="${fragment.replace(/"/g, '\\"')}"]`);
    if (byName) return byName;
  } catch {
  }
  return null;
}
function checkHrefFragmentBroken(root) {
  const findings = [];
  const doc = root.ownerDocument ?? root;
  const anchors = [];
  if (root.tagName?.toLowerCase() === "a") anchors.push(root);
  for (const a of Array.from(root.querySelectorAll("a[href]"))) {
    anchors.push(a);
  }
  const groups = /* @__PURE__ */ new Map();
  for (const a of anchors) {
    const href = a.getAttribute("href");
    if (!href) continue;
    if (!href.startsWith("#")) continue;
    const fragment = href.slice(1);
    if (fragment === "" || fragment === "top") continue;
    let decoded = fragment;
    try {
      decoded = decodeURIComponent(fragment);
    } catch {
    }
    if (elementForFragment(doc, decoded)) continue;
    const text = (a.textContent ?? "").trim().slice(0, 64);
    const existing = groups.get(decoded);
    if (existing) {
      existing.count++;
      if (existing.anchorTexts.length < 3) existing.anchorTexts.push(text);
    } else {
      groups.set(decoded, { count: 1, anchorTexts: [text] });
    }
  }
  for (const [fragment, info] of groups) {
    findings.push({
      libId: LIB_ID75,
      code: "STR_HREF_FRAGMENT_BROKEN",
      category: "a11y",
      severity: "major",
      message: `<a href="#${fragment}"> appears on ${info.count} link(s) (sample text: ${info.anchorTexts.map((t) => JSON.stringify(t)).join(", ")}) but no element with id="${fragment}" (or legacy <a name="${fragment}">) exists in the document. The link visually paints normal, cursor flips to pointer on hover, but clicking does nothing \u2014 silent in-page nav defect.`,
      selector: "a",
      suggestedFix: `Either add an element with id="${fragment}" at the intended target location, or rename the link's href to match an existing id, or remove the broken link.`,
      extras: {
        brokenFragment: fragment,
        linkCount: info.count,
        sampleTexts: info.anchorTexts
      }
    });
  }
  return findings;
}

// src/structural/table-header-association.ts
var SCOPES = /* @__PURE__ */ new Set(["col", "row", "colgroup", "rowgroup"]);
function hasHeaderAssociation(cell, table) {
  const headers = cell.getAttribute("headers");
  if (headers != null && headers.trim().length > 0) return true;
  const row = cell.closest("tr");
  if (row == null) return false;
  const rowHeader = row.querySelector('th[scope="row"], th[scope="rowgroup"]');
  if (rowHeader != null) return true;
  return table.querySelector('th[scope="col"], th[scope="colgroup"]') != null;
}
function checkTableHeaders(root, _opts) {
  const findings = [];
  for (const table of Array.from(root.querySelectorAll("table"))) {
    const role = table.getAttribute("role");
    if (role === "presentation" || role === "none") continue;
    if (table.getAttribute("aria-hidden") === "true") continue;
    const headerCells = Array.from(table.querySelectorAll("th"));
    const dataCells = Array.from(table.querySelectorAll("td"));
    if (dataCells.length > 0 && headerCells.length === 0) {
      findings.push(
        docFinding({
          code: "STR_TABLE_NO_HEADER_CELLS",
          wcag: "1.3.1",
          severity: "critical",
          message: `A ${dataCells.length}-cell data table has no <th> at all, so every cell is announced as a bare value with no idea what it measures.`,
          selector: selectorFor(table),
          suggestedFix: 'Emit the header row as <th scope="col"> cells (check the projection is not dropping <thead>), or mark the table role="presentation" if it genuinely carries no data.',
          extras: { dataCells: dataCells.length }
        })
      );
      continue;
    }
    for (const th of headerCells) {
      const scope = th.getAttribute("scope");
      if (scope != null && SCOPES.has(scope)) continue;
      if ((th.getAttribute("id") ?? "").length > 0) continue;
      findings.push(
        docFinding({
          code: "STR_TABLE_HEADER_NO_SCOPE",
          wcag: "1.3.1",
          severity: "major",
          message: `Header cell "${textPreview(th)}" declares no scope, so which cells it heads is left to engine-specific guessing.`,
          selector: selectorFor(th),
          suggestedFix: 'Add scope="col" (a column header) or scope="row" (a row header) to this <th>.',
          extras: { text: textPreview(th), scope: scope ?? null }
        })
      );
    }
    for (const td of dataCells) {
      if (hasHeaderAssociation(td, table)) continue;
      findings.push(
        docFinding({
          code: "STR_TABLE_CELL_UNASSOCIATED",
          wcag: "1.3.1",
          severity: "major",
          message: `Data cell "${textPreview(td)}" resolves to no header cell in either direction.`,
          selector: selectorFor(td),
          suggestedFix: 'Give the table a scoped header row/column, or set headers="<th id>" on this cell.',
          extras: { text: textPreview(td) }
        })
      );
      break;
    }
  }
  return findings;
}

// src/structural/document-landmark.ts
var DOCUMENT_ROLES = /* @__PURE__ */ new Set(["document", "article", "main", "region"]);
var DOCUMENT_TAGS = /* @__PURE__ */ new Set(["article", "main"]);
function hasDocumentRole(el) {
  const explicit = el.getAttribute("role");
  if (explicit != null) return DOCUMENT_ROLES.has(explicit.trim().toLowerCase());
  return DOCUMENT_TAGS.has(el.tagName.toLowerCase());
}
function regionName(root) {
  const doc = root.ownerDocument;
  const by = root.getAttribute("aria-labelledby");
  if (by != null && by.trim().length > 0 && doc != null) {
    const parts = [];
    for (const id of by.trim().split(/\s+/)) {
      const target = doc.getElementById(id);
      if (target != null) parts.push((target.textContent ?? "").trim());
    }
    const joined = parts.filter(Boolean).join(" ").trim();
    if (joined.length > 0) return joined;
  }
  const label = root.getAttribute("aria-label");
  if (label != null && label.trim().length > 0) return label.trim();
  const title = root.getAttribute("title");
  if (title != null && title.trim().length > 0) return title.trim();
  return "";
}
function checkDocumentLandmark(root, opts) {
  const findings = [];
  const surface = opts?.mirror ?? root;
  if (!hasDocumentRole(surface)) {
    findings.push(
      docFinding({
        code: "STR_DOCUMENT_ROLE_MISSING",
        wcag: "4.1.2",
        severity: "critical",
        message: `The document surface is a bare <${surface.tagName.toLowerCase()}> with no document role, so assistive technology has no landmark to reach it by and may never enter browse mode.`,
        selector: selectorFor(surface),
        suggestedFix: 'Set role="document" on the surface root (or emit it as an <article>/<main>).',
        extras: {
          tag: surface.tagName.toLowerCase(),
          role: surface.getAttribute("role")
        }
      })
    );
  }
  if (regionName(surface).length === 0) {
    findings.push(
      docFinding({
        code: "STR_DOCUMENT_NAME_MISSING",
        wcag: "2.4.6",
        severity: "critical",
        message: "The document region has no accessible name, so it announces as an unlabelled document \u2014 indistinguishable from a diff, a version snapshot or a comment body of the same document.",
        selector: selectorFor(surface),
        suggestedFix: "Set aria-label to the document title on the surface root (or aria-labelledby pointing at a visible title element)."
      })
    );
  }
  return findings;
}

// src/structural/document-language.ts
var LANG_RE = /^[A-Za-z]{1,8}(?:-[A-Za-z0-9]{1,8})*$/;
function inheritedLang(el) {
  let cur = el;
  while (cur != null) {
    const raw = cur.getAttribute("lang");
    if (raw != null && raw.trim().length > 0) return { value: raw.trim(), from: cur };
    cur = cur.parentElement;
  }
  return null;
}
function checkDocumentLanguage(root, opts) {
  const findings = [];
  const surface = opts?.mirror ?? root;
  const declared = inheritedLang(surface);
  if (declared == null) {
    findings.push(
      docFinding({
        code: "STR_DOCUMENT_LANG_MISSING",
        wcag: "3.1.1",
        severity: "critical",
        message: "No language is declared on the document surface or any ancestor, so the screen reader speaks the document in whatever voice the user last used \u2014 a German deliverable read in an English voice.",
        selector: selectorFor(surface),
        suggestedFix: "Set lang to the document's BCP-47 language on the surface root."
      })
    );
    return findings;
  }
  if (!LANG_RE.test(declared.value) || declared.value.length > 64) {
    findings.push(
      docFinding({
        code: "STR_DOCUMENT_LANG_MALFORMED",
        wcag: "3.1.1",
        severity: "critical",
        message: `The declared language "${declared.value}" is not a BCP-47-shaped tag, so it is either ignored or resolves to the wrong voice.`,
        selector: selectorFor(declared.from),
        suggestedFix: 'Emit a well-formed tag such as "en", "de" or "de-DE"; refuse to emit anything else rather than guessing.',
        extras: { declared: declared.value }
      })
    );
    return findings;
  }
  const expected = opts?.expectedLang;
  if (expected != null && expected.length > 0) {
    const got = declared.value.toLowerCase();
    const want = expected.toLowerCase();
    const gotPrimary = got.split("-")[0] ?? got;
    const wantPrimary = want.split("-")[0] ?? want;
    if (gotPrimary !== wantPrimary) {
      findings.push(
        docFinding({
          code: "STR_DOCUMENT_LANG_MISMATCH",
          wcag: "3.1.1",
          severity: "critical",
          message: `The surface declares lang="${declared.value}" but the document's content language is "${expected}"; the whole document will be pronounced in the wrong language.`,
          selector: selectorFor(declared.from),
          suggestedFix: `Set lang="${expected}" on the document surface root.`,
          extras: { declared: declared.value, expected }
        })
      );
    }
  }
  return findings;
}
function checkImageAlternatives(root, _opts) {
  const findings = [];
  for (const img of Array.from(root.querySelectorAll("img"))) {
    if (isHiddenFromAt(img) || isDecorative(img)) continue;
    const alt = img.getAttribute("alt");
    if (alt !== null) continue;
    const label = img.getAttribute("aria-label");
    if (label != null && label.trim().length > 0) continue;
    findings.push(
      docFinding({
        code: "STR_IMAGE_NO_ALT",
        wcag: "1.1.1",
        severity: "critical",
        message: "An image in the document has no alt attribute, so a screen reader announces its file name (or nothing) instead of what it shows.",
        selector: selectorFor(img),
        suggestedFix: 'Emit alt="<what the image conveys>", or alt="" if the image is genuinely decorative.',
        extras: { src: img.getAttribute("src")?.slice(0, 120) ?? null }
      })
    );
  }
  return findings;
}
var NON_DESCRIPTIVE = /* @__PURE__ */ new Set([
  "here",
  "click here",
  "read more",
  "more",
  "link",
  "this",
  "this link",
  "learn more"
]);
function nameOfLink(a) {
  const doc = a.ownerDocument;
  if (doc != null) {
    const name = accessibleName$1(
      doc,
      a
    ).replace(/\s+/g, " ").trim();
    if (name.length > 0) return name;
  }
  const img = a.querySelector("img[alt]");
  return img?.getAttribute("alt")?.trim() ?? "";
}
function checkLinkNames(root, _opts) {
  const findings = [];
  for (const a of Array.from(root.querySelectorAll("a"))) {
    if (isHiddenFromAt(a) || isDecorative(a)) continue;
    const name = nameOfLink(a);
    if (name.length === 0) {
      findings.push(
        docFinding({
          code: "STR_LINK_NO_ACCESSIBLE_NAME",
          wcag: "4.1.2",
          severity: "critical",
          message: "A link in the document has no accessible name, so it appears in the screen reader's link list as an empty entry that cannot be identified or acted on.",
          selector: selectorFor(a),
          suggestedFix: "Ensure the link's run text survives the projection (a link mark wrapping zero runs produces an empty <a>), or give the anchor an aria-label.",
          extras: { href: a.getAttribute("href")?.slice(0, 200) ?? null }
        })
      );
      continue;
    }
    if (NON_DESCRIPTIVE.has(name.toLowerCase())) {
      findings.push(
        docFinding({
          code: "STR_LINK_NAME_NOT_DESCRIPTIVE",
          wcag: "2.4.4",
          severity: "minor",
          message: `Link text "${name}" names the mechanism, not the destination; in a screen reader's link list it carries no information.`,
          selector: selectorFor(a),
          suggestedFix: "Rewrite the link text to name its destination, or add aria-label with the destination.",
          extras: { name, href: a.getAttribute("href")?.slice(0, 200) ?? null }
        })
      );
    }
  }
  return findings;
}
function checkBlockedHrefStillReadable(root, _opts) {
  const findings = [];
  for (const a of Array.from(root.querySelectorAll("a.docz-blocked-href"))) {
    if ((a.textContent ?? "").trim().length > 0) continue;
    findings.push(
      docFinding({
        code: "STR_BLOCKED_LINK_TEXT_LOST",
        wcag: "1.3.1",
        severity: "critical",
        message: `A link whose href was refused by the projection carries no text ("${textPreview(a)}"), so refusing the URL also deleted the sentence from the accessible document.`,
        selector: selectorFor(a),
        suggestedFix: "Keep the run text when dropping an unsafe href \u2014 refuse the destination, never the content."
      })
    );
  }
  return findings;
}

// src/structural/canvas-semantic-mirror.ts
var DEFAULT_PAINTED_SELECTOR = "canvas";
function checkPaintedSurfaceHidden(root, opts) {
  const findings = [];
  const selector = opts?.paintedSelector ?? DEFAULT_PAINTED_SELECTOR;
  const painted = Array.from(root.querySelectorAll(selector));
  if (painted.length === 0) return findings;
  const mirror = opts?.mirror ?? null;
  for (const el of painted) {
    if (isAriaHidden(el)) continue;
    findings.push(
      docFinding({
        code: "STR_PAINTED_SURFACE_EXPOSED",
        wcag: "4.1.2",
        severity: "critical",
        message: `A painted <${el.tagName.toLowerCase()}> is still in the accessibility tree, so assistive technology reaches an unlabelled graphic where the document should be${mirror != null ? " \u2014 and reads it in addition to the mirror" : ""}.`,
        selector: selectorFor(el),
        suggestedFix: `Set aria-hidden="true" on every painted surface under the document host (a mirror's hideFromAssistiveTech() returns how many it newly hid \u2014 assert that count, do not assume it).`,
        extras: { tag: el.tagName.toLowerCase(), selector }
      })
    );
  }
  if (mirror == null) {
    findings.push(
      docFinding({
        code: "STR_NO_ACCESSIBLE_REPRESENTATION",
        wcag: "1.3.1",
        severity: "critical",
        message: `${painted.length} painted surface(s) carry the document and no semantic mirror is mounted, so the document does not exist for assistive technology at all \u2014 while the pixels stay perfect and no sighted check can see the loss.`,
        selector: selectorFor(root),
        suggestedFix: "Mount a semantic mirror on the document host and pass it as documentSurface.mirror.",
        extras: { paintedCount: painted.length }
      })
    );
  }
  return findings;
}
function checkMirrorStillInAtTree(_root, opts) {
  const mirror = opts?.mirror;
  if (mirror == null) return [];
  const removedBy = isRemovedFromAtTree(mirror);
  if (removedBy == null) return [];
  return [
    docFinding({
      code: "STR_MIRROR_REMOVED_FROM_AT_TREE",
      wcag: "1.3.1",
      severity: "critical",
      message: `The accessible mirror is hidden with ${removedBy}, which removes it from the accessibility tree \u2014 assistive technology sees neither the canvas nor the mirror.`,
      selector: selectorFor(mirror),
      suggestedFix: "Hide the mirror with the clip technique (position:absolute; width:1px; height:1px; clip-path:inset(50%); overflow:hidden) \u2014 never display:none, visibility:hidden or [hidden].",
      extras: { removedBy }
    })
  ];
}
function checkSingleExposedRepresentation(root, opts) {
  const mirror = opts?.mirror;
  if (mirror == null) return [];
  if (isAriaHidden(mirror)) return [];
  const mirrorText = normText(mirror);
  if (mirrorText.length < 200) return [];
  const probe = mirrorText.slice(0, 160);
  const surfaceText = normText(root);
  const firstAt = surfaceText.indexOf(probe);
  if (firstAt === -1 || surfaceText.indexOf(probe, firstAt + 1) === -1) return [];
  const findings = [];
  for (const cand of Array.from(root.querySelectorAll("*"))) {
    if (cand === mirror) continue;
    if (mirror.contains(cand) || cand.contains(mirror)) continue;
    if (isAriaHidden(cand)) continue;
    if (isRemovedFromAtTree(cand) != null) continue;
    const text = normText(cand);
    if (text.length < 200) continue;
    if (!text.includes(probe)) continue;
    findings.push(
      docFinding({
        code: "STR_DOCUMENT_DOUBLE_EXPOSED",
        wcag: "1.3.1",
        severity: "critical",
        message: `The document's text is exposed to assistive technology twice \u2014 once through the mirror and once through <${cand.tagName.toLowerCase()}> \u2014 so a screen-reader user hears the entire document, then hears it again.`,
        selector: selectorFor(cand),
        suggestedFix: 'Run the mirror in its silent exposure mode (aria-hidden on the mirror) while a real accessible DOM surface is still mounted, or hide that surface with aria-hidden="true" when the mirror becomes the sole representation. Exactly one of the two must be exposed.',
        extras: { duplicateTag: cand.tagName.toLowerCase(), sharedChars: probe.length }
      })
    );
    break;
  }
  return findings;
}
function checkNoInvisibleTabStops(_root, opts) {
  const mirror = opts?.mirror;
  if (mirror == null) return [];
  if (!isVisuallyHiddenElement(mirror)) return [];
  const findings = [];
  for (const el of Array.from(mirror.querySelectorAll("*"))) {
    if (!isTabStop(el)) continue;
    findings.push(
      docFinding({
        code: "STR_INVISIBLE_TAB_STOP",
        wcag: "2.4.7",
        severity: "critical",
        message: `<${el.tagName.toLowerCase()}> inside the visually-hidden mirror is in the tab order, so Tab moves focus to something the user cannot see and cannot locate.`,
        selector: selectorFor(el),
        suggestedFix: 'Give it tabindex="-1" (programmatically focusable only), or keep it out of the mirror entirely \u2014 the mirror is read-only content, not a control surface.',
        extras: { tag: el.tagName.toLowerCase(), tabindex: el.getAttribute("tabindex") }
      })
    );
  }
  return findings;
}

// src/structural/index.ts
var STRUCTURAL_RULES = [
  { code: "STR_DUPLICATE_VISIBLE_TEXT", fn: checkDuplicateVisibleText },
  { code: "STR_HEADING_HIERARCHY_BROKEN", fn: checkHeadingHierarchy },
  { code: "STR_FOCUSABLE_INSIDE_HEADING", fn: checkFocusableInsideHeading },
  { code: "STR_VIEWPORT_OVERFLOW", fn: checkViewportOverflow },
  { code: "STR_PROGRESSBAR_REDUNDANCY", fn: checkProgressbarRedundancy },
  { code: "STR_TEXT_CLIPPED_BY_CONTAINER", fn: checkTextClipped },
  { code: "STR_CONTRAST_INSUFFICIENT", fn: checkContrast },
  { code: "STR_VISIBLE_TEXT_VS_LIB_PALETTE_DRIFT", fn: checkPaletteDrift },
  { code: "STR_HANDROLLED_DUPLICATES_LIB_PRIMITIVE", fn: checkHandrolledDuplicatesLibPrimitive },
  { code: "STR_LIB_PRIMITIVE_DUPLICATE_EMITTERS", fn: checkLibPrimitiveDuplicateEmitters },
  { code: "STR_DUPLICATE_PRIMITIVE_BY_SIMILARITY", fn: checkDuplicatePrimitiveBySimilarity },
  { code: "STR_INLINE_STYLE_ON_LIB_STAMP", fn: checkInlineStyleOnLibStamp },
  { code: "STR_UTILITY_CLASS_ON_LIB_STAMP", fn: checkUtilityClassOnLibStamp },
  { code: "STR_SVG_TEXT_LOW_CONTRAST_VS_SIBLING_FILL", fn: checkSvgTextVsSiblingFill },
  { code: "STR_LAYOUT_NESTED_TWICE", fn: checkLayoutNestedTwice },
  { code: "STR_DISABLED_CONTROL_LOW_CONTRAST", fn: checkDisabledControlContrast },
  { code: "STR_CHART_CATEGORIES_NO_COLOR_DIFFERENTIATION", fn: checkChartCategoriesNoColorDifferentiation },
  { code: "STR_LEGEND_REQUIRED_FOR_MULTI_SERIES", fn: checkLegendRequiredForMultiSeries },
  { code: "STR_REDUNDANT_EMPTY_STATE_COPY", fn: checkRedundantEmptyStateCopy },
  { code: "STR_CTA_WRONG_COPY_AFTER_FLOW_COMPLETE", fn: checkCtaWrongCopyAfterFlowComplete },
  { code: "STR_GENERIC_ERROR_COPY_ON_SPECIFIC_STATE", fn: checkGenericErrorCopyOnSpecificState },
  { code: "STR_INPUT_PLACEHOLDER_LOOKS_LIKE_VALUE", fn: checkInputPlaceholderLooksLikeValue },
  { code: "STR_PROGRESS_INDICATOR_COUNT", fn: checkProgressIndicatorCount },
  { code: "STR_DUPLICATE_PROGRESS_DISAGREES", fn: checkDuplicateProgressDisagrees },
  { code: "STR_TOOLTIP_TRIGGER_WITHOUT_ACCESSIBLE_LABEL", fn: checkTooltipTriggerWithoutAccessibleLabel },
  { code: "STR_SEGMENTED_CONTROL_NO_VISIBLE_BORDER", fn: checkSegmentedControlNoVisibleBorder },
  { code: "STR_NATIVE_CHECKBOX_IN_DARK_THEME", fn: checkNativeCheckboxInDarkTheme },
  { code: "STR_MULTI_SELECT_VISUALLY_AMBIGUOUS_VS_ENUM", fn: checkMultiSelectVisuallyAmbiguousVsEnum },
  { code: "STR_COLOR_USED_AS_SEMANTIC_WITHOUT_LEGEND", fn: checkColorUsedAsSemanticWithoutLegend },
  { code: "STR_BRAND_PALETTE_DRIFT_INTRA_PRODUCT", fn: checkBrandPaletteDriftIntraProduct },
  { code: "STR_TEXT_SIZE_BELOW_MIN", fn: checkTextSizeBelowMin },
  { code: "STR_HTML_LANG_MATCHES_LOCALE", fn: checkHtmlLangMatchesLocale },
  { code: "STR_CHILD_OVERFLOWS_PARENT_BOUNDS", fn: checkChildOverflowsParentBounds },
  { code: "STR_LIB_NOT_INSTANTIATED", fn: checkLibNotInstantiated },
  { code: "STR_ACTUAL_DISPLAYED_BACKGROUND_CONTRAST", fn: checkActualDisplayedBackgroundContrast },
  { code: "STR_RESPONSIVE_COLLAPSE_MISSING", fn: checkResponsiveCollapseMissing },
  { code: "STR_TEXT_OVERFLOWS_AT_BREAKPOINT", fn: checkTextOverflowsAtBreakpoint },
  { code: "STR_CHILD_OVERFLOWS_VISUAL_PARENT", fn: checkChildOverflowsVisualParent },
  { code: "STR_FULL_WIDTH_NOT_EDGE_TO_EDGE", fn: checkFullWidthNotEdgeToEdge },
  { code: "STR_CHILD_UNDERFILLS_PARENT_HEIGHT", fn: checkChildUnderfillsParentHeight },
  { code: "STR_KEEP_CLEAR_REGION_OVERLAPPED", fn: checkKeepClearRegionOverlapped },
  { code: "STR_INTERACTIVE_ELEMENT_VISUALLY_EMPTY", fn: checkInteractiveElementVisuallyEmpty },
  { code: "STR_TEXT_LEAKED_PROGRAMMING_VALUE", fn: checkTextLeakedProgrammingValue },
  { code: "STR_CANVAS_ZERO_SIZED", fn: checkCanvasZeroSized },
  { code: "STR_REPLACED_ELEMENT_ZERO_RECT", fn: checkReplacedElementZeroRect },
  { code: "STR_I18N_KEY_LEAKED", fn: checkI18nKeyLeaked },
  { code: "STR_DEAD_INTERACTIVE", fn: checkDeadInteractive },
  { code: "STR_DUPLICATE_ARIA_LABEL", fn: checkDuplicateAriaLabel },
  { code: "STR_THEME_TOKEN_NOT_THEMED", fn: checkThemeTokenNotThemed },
  { code: "STR_THEME_VARIANT_MISSING", fn: checkThemeVariantMissing },
  { code: "STR_ELEMENT_CLIPPED_BY_ANCESTOR", fn: checkElementClippedByAncestor },
  { code: "STR_CONTAINER_RENDERS_EMPTY", fn: checkContainerRendersEmpty },
  { code: "STR_RESPONSIVE_CONTRACT_MISSING", fn: checkResponsiveContractMissing },
  { code: "STR_PILL_CONTAINER_WRAPS", fn: checkPillContainerWraps },
  { code: "STR_CORNERS_NOT_CONCENTRIC", fn: checkCornersNotConcentric },
  { code: "STR_WRAPPED_FLEX_ORPHAN", fn: checkWrappedFlexOrphan },
  { code: "STR_TEXT_OVERFLOWS_CONTENT_BOX", fn: checkTextOverflowsContentBox },
  { code: "STR_TEXT_ASYMMETRIC_PADDING", fn: checkTextAsymmetricPadding },
  { code: "STR_ICU_TEMPLATE_LEAKED", fn: checkIcuTemplateLeaked },
  { code: "STR_MINIMAP_VIEWPORT_NOT_INVERTED", fn: checkMinimapViewportNotInverted },
  { code: "STR_ELEMENT_PAINTED_OFF_VIEWPORT", fn: checkElementPaintedOffViewport },
  { code: "STR_FLEX_NOWRAP_OVERFLOWS_PARENT", fn: checkFlexNowrapOverflowsParent },
  { code: "STR_LIB_FORMALLY_NO_RESPONSIVE", fn: checkLibFormallyNoResponsive },
  { code: "STR_SVG_RENDERS_PAINT_EMPTY", fn: checkSvgRendersPaintEmpty },
  { code: "STR_PILL_CONTAINER_ELLIPSE_AT_NARROW", fn: checkPillContainerEllipseAtNarrow },
  { code: "STR_CONTROL_LABEL_MID_WORD_BREAK", fn: checkControlLabelMidWordBreak },
  { code: "STR_VIEWER_TOOLBAR_MISSING", fn: checkViewerToolbarMissing },
  { code: "STR_VIEWER_MINIMAP_MISSING", fn: checkViewerMinimapMissing },
  { code: "STR_VIEWER_LEGEND_MISSING", fn: checkViewerLegendMissing },
  { code: "STR_VIEWER_SIDEBAR_MISSING", fn: checkViewerSidebarMissing },
  { code: "STR_VIEWER_BACKGROUND_PATTERN_MISSING", fn: checkViewerBackgroundPatternMissing },
  { code: "STR_VIEWER_BORDER_MISSING", fn: checkViewerBorderMissing },
  { code: "STR_BILLBOARD_OPAQUE_OCCLUDES_3D_EDGES", fn: checkBillboardOpaqueOccludes3dEdges },
  { code: "STR_BILLBOARD_TEXTURE_BLURRY_AT_ZOOM", fn: checkBillboardTextureBlurryAtZoom },
  { code: "STR_INTRA_PRODUCT_PANEL_OVERLAPS_PEER", fn: checkIntraProductPanelOverlapsPeer },
  { code: "STR_DECLARED_CHROME_NOT_VISIBLE", fn: checkDeclaredChromeNotVisible },
  { code: "STR_ICON_LOW_CONTRAST", fn: checkIconLowContrast },
  { code: "STR_ELEMENT_WIDTH_EXCEEDS_PARENT", fn: checkElementWidthExceedsParent },
  { code: "STR_TEXT_INVISIBLE", fn: checkTextInvisible },
  { code: "STR_FOCUSABLE_NO_VISIBLE_FOCUS_INDICATOR", fn: checkFocusableNoVisibleFocusIndicator },
  { code: "STR_Z_INDEX_STACKING_CONFLICT", fn: checkZIndexStackingConflict },
  { code: "STR_CLICK_TARGET_BELOW_MIN_SIZE", fn: checkClickTargetBelowMinSize },
  { code: "STR_LIB_ROOT_NO_DATA_NORKRON_LIB_STAMP", fn: checkLibRootNoDataNorkronLibStamp },
  { code: "STR_OVERLAPPING_CLICK_TARGETS", fn: checkOverlappingClickTargets },
  { code: "STR_TEXT_CROWDS_CONTAINER_EDGE", fn: checkTextCrowdsContainerEdge },
  { code: "STR_SIBLINGS_INSUFFICIENT_GAP", fn: checkSiblingsInsufficientGap },
  { code: "STR_CONTAINER_TEXT_NO_PADDING", fn: checkContainerTextNoPadding },
  { code: "STR_SPACING_NOT_TOKENIZED", fn: checkSpacingNotTokenized },
  { code: "STR_INTERACTIVE_NO_HOVER_FEEDBACK", fn: checkInteractiveNoHoverFeedback },
  { code: "STR_DISABLED_NOT_VISUALLY_DISTINCT", fn: checkDisabledNotVisuallyDistinct },
  { code: "STR_EXCESSIVE_LINE_HEIGHT", fn: checkExcessiveLineHeight },
  { code: "STR_SIBLINGS_EXCESSIVE_GAP", fn: checkSiblingsExcessiveGap },
  { code: "STR_TOKEN_REFERENCED_BUT_UNDEFINED", fn: checkTokenReferencedButUndefined },
  { code: "STR_ARIA_REFERENCE_BROKEN", fn: checkAriaReferenceBroken },
  { code: "STR_LABEL_FOR_BROKEN", fn: checkLabelForBroken },
  { code: "STR_DUPLICATE_ID", fn: checkDuplicateId },
  { code: "STR_BUTTON_NO_ACCESSIBLE_NAME", fn: checkButtonNoAccessibleName },
  { code: "STR_FORM_INPUT_NO_ACCESSIBLE_NAME", fn: checkFormInputNoAccessibleName },
  { code: "STR_HREF_FRAGMENT_BROKEN", fn: checkHrefFragmentBroken },
  // ── Document-surface family (gated: runs only for a DECLARED surface) ──
  { code: "STR_H1_MISSING", fn: documentSurfaceGated(checkHeadingOutline) },
  { code: "STR_TABLE_NO_HEADER_CELLS", fn: documentSurfaceGated(checkTableHeaders) },
  { code: "STR_DOCUMENT_ROLE_MISSING", fn: documentSurfaceGated(checkDocumentLandmark) },
  { code: "STR_DOCUMENT_LANG_MISSING", fn: documentSurfaceGated(checkDocumentLanguage) },
  { code: "STR_IMAGE_NO_ALT", fn: documentSurfaceGated(checkImageAlternatives) },
  { code: "STR_LINK_NO_ACCESSIBLE_NAME", fn: documentSurfaceGated(checkLinkNames) },
  { code: "STR_BLOCKED_LINK_TEXT_LOST", fn: documentSurfaceGated(checkBlockedHrefStillReadable) },
  { code: "STR_PAINTED_SURFACE_EXPOSED", fn: documentSurfaceGated(checkPaintedSurfaceHidden) },
  { code: "STR_MIRROR_REMOVED_FROM_AT_TREE", fn: documentSurfaceGated(checkMirrorStillInAtTree) },
  {
    code: "STR_DOCUMENT_DOUBLE_EXPOSED",
    fn: documentSurfaceGated(checkSingleExposedRepresentation)
  },
  { code: "STR_INVISIBLE_TAB_STOP", fn: documentSurfaceGated(checkNoInvisibleTabStops) }
];
function snippetElementForThrow(root, err) {
  const cause = err?.cause?.element;
  const target = cause ?? root;
  try {
    const html = target.outerHTML ?? "";
    return html.length > 256 ? `${html.slice(0, 256)}\u2026` : html;
  } catch {
    return void 0;
  }
}
function runOneRuleIsolated(rule, root, diagnostic, opts) {
  diagnostic.ruleRunsAttempted.push(rule.code);
  try {
    return rule.fn(root, opts);
  } catch (err) {
    const errorName = err instanceof Error ? err.name : typeof err;
    const errorMessage = err instanceof Error ? err.message : String(err);
    const elementOuterHtml = snippetElementForThrow(root, err);
    diagnostic.ruleThrows.push({
      ruleCode: rule.code,
      errorName,
      errorMessage,
      elementOuterHtml
    });
    return [
      {
        libId: "@norkron/web-conformance",
        code: "STR_RULE_THREW",
        category: "identity",
        severity: "critical",
        message: `structural rule ${rule.code} threw: ${errorName}: ${errorMessage}`,
        selector: void 0,
        extras: {
          ruleCode: rule.code,
          errorName,
          errorMessage,
          elementOuterHtml
        }
      }
    ];
  }
}
function runStructuralChecksWithDiagnostic(root, opts) {
  const diagnostic = {
    ruleRunsAttempted: [],
    ruleThrows: []
  };
  const findings = [];
  for (const rule of STRUCTURAL_RULES) {
    for (const f of runOneRuleIsolated(rule, root, diagnostic, opts)) findings.push(f);
  }
  const env = detectLayoutAvailable(root);
  if (!env.available) {
    const suppressed = [];
    const kept = [];
    for (const f of findings) {
      if (f.code === "STR_NEEDS_LAYOUT") {
        const forCode = f.extras?.forCode;
        if (typeof forCode === "string") suppressed.push(forCode);
        continue;
      }
      kept.push(f);
    }
    const gateFinding = gateLayoutUnavailableFinding(env.reason);
    const gateExtras = gateFinding.extras ?? {};
    gateExtras.suppressedForCodes = suppressed;
    gateExtras.suppressedCount = suppressed.length;
    kept.push(gateFinding);
    return { findings: kept, diagnostic };
  }
  return { findings, diagnostic };
}
function listStructuralRuleCodes() {
  return STRUCTURAL_RULES.map((r) => r.code);
}
function runStructuralChecks(root, opts) {
  return runStructuralChecksWithDiagnostic(root, opts).findings;
}
function mapWebErrorsSeverity(severity) {
  switch (severity) {
    case "info":
      return "info";
    case "warning":
      return "minor";
    case "critical-but-non-throwing":
      return "major";
    case "error":
      return "critical";
    default:
      return void 0;
  }
}
function registrySeverityFor(libBase, code, fallback) {
  try {
    const meta = getCodeMeta(libBase, code);
    const mapped = mapWebErrorsSeverity(meta?.severity);
    if (mapped !== void 0) return mapped;
  } catch {
  }
  return fallback;
}
async function ensureFoundationalWiring() {
  try {
    await import('@norkron/web-error-boundary');
    await import('@norkron/web-a11y');
  } catch {
  }
}
var DEFAULT_BUDGET_MS = 50;
function emptyTotals() {
  return { critical: 0, major: 0, minor: 0, info: 0 };
}
function addToTotals(totals, f) {
  totals[normalizeSeverity(f.severity)] += 1;
}
function detectLibId(el) {
  const raw = el.getAttribute("data-norkron-lib");
  if (!raw) return null;
  const at = raw.lastIndexOf("@");
  if (at <= 0) return raw;
  return raw;
}
function libIdBaseName(libIdWithVersion) {
  const at = libIdWithVersion.lastIndexOf("@");
  if (at <= 0) return libIdWithVersion;
  return libIdWithVersion.slice(0, at);
}
function detectVersionDrift(root) {
  const seen = /* @__PURE__ */ new Map();
  const elements = root.querySelectorAll("[data-norkron-lib]");
  elements.forEach((el) => {
    const raw = el.getAttribute("data-norkron-lib") ?? "";
    const at = raw.lastIndexOf("@");
    if (at <= 0) return;
    const base = raw.slice(0, at);
    const version = raw.slice(at + 1);
    if (!seen.has(base)) seen.set(base, /* @__PURE__ */ new Set());
    seen.get(base)?.add(version);
  });
  const findings = [];
  for (const [base, versions] of seen) {
    if (versions.size > 1) {
      findings.push({
        libId: base,
        code: "CF_LIB_VERSION_DRIFT",
        category: "identity",
        severity: "major",
        message: `lib ${base} loaded with ${versions.size} different versions on the page: ${[...versions].join(", ")}`,
        selector: `[data-norkron-lib^="${base}@"]`,
        extras: { versions: [...versions] }
      });
    }
  }
  return findings;
}
var CF_AUDIT_REFUSED_EMPTY_SURFACE = "CF_AUDIT_REFUSED_EMPTY_SURFACE";
var CF_AUDIT_REFUSED_RULE_NOT_RUN = "CF_AUDIT_REFUSED_RULE_NOT_RUN";
var REFUSAL_CODES = {
  EMPTY_SURFACE: CF_AUDIT_REFUSED_EMPTY_SURFACE,
  RULE_NOT_RUN: CF_AUDIT_REFUSED_RULE_NOT_RUN
};
function refuse(report, kind, detail, extras) {
  const code = REFUSAL_CODES[kind];
  report.refusal = { kind, detail, extras };
  const finding = {
    libId: "@norkron/web-conformance",
    code,
    category: "identity",
    severity: "critical",
    message: `audit REFUSED (${kind}): ${detail}`,
    suggestedFix: kind === "EMPTY_SURFACE" ? "Mount the surface (await the render / hydration) before auditing, or point `audit()` at the element that actually holds the content. A pass over an empty surface is not a pass." : "Fix the rule that threw, or drop it from `requiredRuleCodes`. A rule that did not execute has not found nothing \u2014 it has found nothing out.",
    extras: { refusalKind: kind, ...extras }
  };
  const own = report.libs.find((l) => l.libId === "@norkron/web-conformance");
  if (own) own.findings.push(finding);
  else report.libs.push({ libId: "@norkron/web-conformance", instances: 1, findings: [finding] });
  report.totals.critical += 1;
}
async function audit(root = void 0, opts = {}) {
  await ensureFoundationalWiring();
  flushPendingRegistrations();
  const realRoot = root ?? (typeof document !== "undefined" && document.body ? document.body : void 0);
  if (!realRoot) {
    const noRoot = {
      generatedAt: (/* @__PURE__ */ new Date()).toISOString(),
      productId: opts.productId ?? "unknown",
      productVersion: opts.productVersion ?? "0.0.0",
      url: opts.url,
      locale: opts.locale,
      libs: [],
      totals: emptyTotals()
    };
    if (opts.requireContent === true) {
      noRoot.census = {
        scope: "root",
        elements: 0,
        headings: 0,
        images: 0,
        links: 0,
        tables: 0,
        dataCells: 0,
        landmarks: 0,
        interactive: 0,
        libStamps: 0,
        textBearingElements: 0,
        textLength: 0,
        auditableNodes: 0
      };
      refuse(
        noRoot,
        "EMPTY_SURFACE",
        "no audit root: `audit()` received no element and no `document.body` was available, so nothing was examined.",
        { census: noRoot.census }
      );
    }
    return noRoot;
  }
  const selfCheckOpts = {
    budgetMs: opts.budgetMs ?? DEFAULT_BUDGET_MS,
    minSeverity: opts.minSeverity ?? "info",
    locale: opts.locale ?? (typeof document !== "undefined" ? document.documentElement.lang || void 0 : void 0),
    documentSurface: opts.documentSurface
  };
  const mountedLibs = /* @__PURE__ */ new Map();
  realRoot.querySelectorAll("[data-norkron-lib]").forEach((el) => {
    const id = detectLibId(el);
    if (!id) return;
    const existing = mountedLibs.get(id);
    if (existing) existing.instances += 1;
    else mountedLibs.set(id, { instances: 1, sample: el });
  });
  const libsOut = [];
  const totals = emptyTotals();
  const selfCheckThrows = [];
  const registry = listRegistered();
  const visited = /* @__PURE__ */ new Set();
  for (const [libIdWithVersion, mount] of mountedLibs) {
    const base = libIdBaseName(libIdWithVersion);
    visited.add(base);
    const fn = registry.get(base);
    const findings = [];
    if (fn) {
      const start = nowMs();
      try {
        const out = fn(realRoot, selfCheckOpts);
        const elapsed = nowMs() - start;
        const budget = selfCheckOpts.budgetMs ?? DEFAULT_BUDGET_MS;
        if (elapsed > budget) {
          const budgetCode = `${shortPrefixFor(base)}_SELFCHECK_BUDGET_EXCEEDED`;
          findings.push({
            libId: libIdWithVersion,
            code: budgetCode,
            category: "perf",
            // Cluster #5 substrate fix (v0.8.13): registry-driven severity
            // (every consumer lib registers this code at 'info' via
            // web-errors). Fallback 'info' matches the canonical
            // CODES.md documentation for the un-registered acronym-
            // fallback prefix path.
            severity: registrySeverityFor(base, budgetCode, "info"),
            message: `selfCheck for ${base} took ${elapsed}ms (budget ${budget}ms)`,
            extras: { elapsedMs: elapsed, budgetMs: budget }
          });
        }
        for (const f of out) findings.push(f);
      } catch (err) {
        selfCheckThrows.push(base);
        const errCode = `${shortPrefixFor(base)}_SELFCHECK_INTERNAL_ERROR`;
        findings.push({
          libId: libIdWithVersion,
          code: errCode,
          category: "identity",
          // Cluster #5 substrate fix (v0.8.13): registry-driven severity
          // (every consumer lib registers this code at 'info' via
          // web-errors). Fallback 'info' matches the canonical CODES.md
          // documentation for the un-registered acronym-fallback path.
          severity: registrySeverityFor(base, errCode, "info"),
          message: `selfCheck threw: ${err instanceof Error ? err.message : String(err)}`,
          extras: { errorType: err instanceof Error ? err.name : typeof err }
        });
      }
    }
    for (const f of findings) addToTotals(totals, f);
    libsOut.push({ libId: libIdWithVersion, instances: mount.instances, findings });
  }
  const { findings: structuralFindings, diagnostic } = runStructuralChecksWithDiagnostic(realRoot, selfCheckOpts);
  const driftFindings = detectVersionDrift(realRoot);
  const ownFindings = [...structuralFindings, ...driftFindings];
  for (const f of ownFindings) addToTotals(totals, f);
  libsOut.push({
    libId: "@norkron/web-conformance",
    instances: 1,
    findings: ownFindings
  });
  const report = {
    generatedAt: (/* @__PURE__ */ new Date()).toISOString(),
    productId: opts.productId ?? "unknown",
    productVersion: opts.productVersion ?? "0.0.0",
    url: opts.url ?? (typeof location !== "undefined" ? location.href : void 0),
    locale: selfCheckOpts.locale,
    libs: libsOut,
    totals,
    _diagnostic: diagnostic
  };
  if (opts.requireContent === true) {
    const census = censusSurface(realRoot, selfCheckOpts.documentSurface?.mirror ?? null);
    report.census = census;
    const emptyDetail = censusRefusal(census, {
      minAuditableNodes: opts.minAuditableNodes,
      minTextLength: opts.minTextLength
    });
    if (emptyDetail != null) {
      refuse(report, "EMPTY_SURFACE", emptyDetail, {
        census,
        censusSummary: summariseCensus(census),
        minAuditableNodes: opts.minAuditableNodes ?? 1,
        minTextLength: opts.minTextLength ?? 0
      });
      return report;
    }
    const missingRules = (opts.requiredRuleCodes ?? []).filter(
      (code) => !diagnostic.ruleRunsAttempted.includes(code)
    );
    const threwRules = diagnostic.ruleThrows.map((t) => t.ruleCode);
    if (threwRules.length > 0 || missingRules.length > 0 || selfCheckThrows.length > 0) {
      const parts = [];
      if (threwRules.length > 0) {
        parts.push(`structural rule(s) threw and did not complete: ${threwRules.join(", ")}`);
      }
      if (missingRules.length > 0) {
        parts.push(`required rule(s) never executed: ${missingRules.join(", ")}`);
      }
      if (selfCheckThrows.length > 0) {
        parts.push(`lib selfCheck(s) threw: ${selfCheckThrows.join(", ")}`);
      }
      refuse(
        report,
        "RULE_NOT_RUN",
        `${parts.join("; ")}. The surface was NOT audited for those criteria.`,
        {
          threwRules,
          missingRules,
          selfCheckThrows,
          ruleRunsAttempted: diagnostic.ruleRunsAttempted.length
        }
      );
    }
  }
  return report;
}
function isRefusal(report) {
  return report.refusal != null;
}
function censusOf(report) {
  return report.census ?? null;
}
function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}
function shortPrefixFor(base) {
  try {
    const codes = listCodes(base);
    for (const code of codes) {
      if (code.endsWith("_SELFCHECK_BUDGET_EXCEEDED")) {
        return code.slice(0, code.length - "_SELFCHECK_BUDGET_EXCEEDED".length);
      }
      if (code.endsWith("_SELFCHECK_INTERNAL_ERROR")) {
        return code.slice(0, code.length - "_SELFCHECK_INTERNAL_ERROR".length);
      }
    }
  } catch {
  }
  const tail = base.split("/").pop() ?? base;
  const stripped = tail.replace(/^web-/, "");
  const parts = stripped.split("-").filter(Boolean);
  if (parts.length === 0) return "LIB";
  if (parts.length === 1) {
    const seg = parts[0] ?? "";
    const out = seg.slice(0, 2).toUpperCase();
    return out.length >= 2 ? out : (out + "X").slice(0, 2);
  }
  const acronym = parts.map((p) => p[0]).filter(Boolean).join("").toUpperCase();
  return acronym.length >= 2 ? acronym : (acronym + "X").slice(0, 2);
}
var withConformanceSchema = z.object({
  libId: z.string().min(1).regex(/^@norkron\/web-[a-z0-9-]+$/, "libId must look like @norkron/web-<slug>"),
  version: z.string().min(1)
});

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
var ConformanceSeveritySchema = z.enum([
  "critical",
  "error",
  "major",
  "warning",
  "minor",
  "info"
]);
var ConformanceCategorySchema = z.enum([
  "theme",
  "stylesheet",
  "a11y",
  "layout",
  "rendering",
  "content",
  "i18n",
  "cardinality",
  "identity",
  "schema",
  "flow",
  "deprecation",
  "perf"
]);
var ConformanceFindingSchema = z.object({
  libId: z.string().min(1),
  code: z.string().min(1),
  category: ConformanceCategorySchema,
  severity: ConformanceSeveritySchema,
  message: z.string().min(1),
  /** WCAG 2.1 success criterion, e.g. "1.3.1". Populated by the a11y families. */
  wcag: z.string().optional(),
  selector: z.string().optional(),
  suggestedFix: z.string().optional(),
  docsUrl: z.string().optional(),
  extras: z.record(z.unknown()).optional()
});
var DocumentSurfaceOptionsSchema = z.object({
  mirror: z.custom(
    (v) => v === null || typeof v === "object" && v !== null && "getAttribute" in v,
    { message: "mirror must be an Element or null" }
  ).nullable().optional(),
  paintedSelector: z.string().min(1).optional(),
  expectedLang: z.string().min(1).optional()
});
var SurfaceRefusalSchema = z.enum(["EMPTY_SURFACE", "RULE_NOT_RUN"]);
var SurfaceCensusSchema = z.object({
  scope: z.enum(["mirror", "root"]),
  elements: z.number().int().nonnegative(),
  headings: z.number().int().nonnegative(),
  images: z.number().int().nonnegative(),
  links: z.number().int().nonnegative(),
  tables: z.number().int().nonnegative(),
  dataCells: z.number().int().nonnegative(),
  landmarks: z.number().int().nonnegative(),
  interactive: z.number().int().nonnegative(),
  libStamps: z.number().int().nonnegative(),
  textBearingElements: z.number().int().nonnegative(),
  textLength: z.number().int().nonnegative(),
  auditableNodes: z.number().int().nonnegative()
});
var SurfaceRefusalRecordSchema = z.object({
  kind: SurfaceRefusalSchema,
  detail: z.string().min(1),
  extras: z.record(z.unknown()).optional()
});
var ConformanceReportSchema = z.object({
  generatedAt: z.string(),
  productId: z.string(),
  productVersion: z.string(),
  url: z.string().optional(),
  locale: z.string().optional(),
  libs: z.array(
    z.object({
      libId: z.string(),
      instances: z.number().int().nonnegative(),
      findings: z.array(ConformanceFindingSchema)
    })
  ),
  totals: z.object({
    critical: z.number().int().nonnegative(),
    major: z.number().int().nonnegative(),
    minor: z.number().int().nonnegative(),
    info: z.number().int().nonnegative()
  }),
  refusal: SurfaceRefusalRecordSchema.optional(),
  census: SurfaceCensusSchema.optional()
});
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().positive().optional(),
  minSeverity: ConformanceSeveritySchema.optional(),
  locale: z.string().optional(),
  documentSurface: DocumentSurfaceOptionsSchema.optional()
});
var AuditOptionsSchema = z.object({
  productId: z.string().optional(),
  productVersion: z.string().optional(),
  url: z.string().optional(),
  locale: z.string().optional(),
  budgetMs: z.number().positive().optional(),
  minSeverity: ConformanceSeveritySchema.optional(),
  documentSurface: DocumentSurfaceOptionsSchema.optional(),
  requireContent: z.boolean().optional(),
  minAuditableNodes: z.number().int().nonnegative().optional(),
  minTextLength: z.number().int().nonnegative().optional(),
  requiredRuleCodes: z.array(z.string().min(1)).readonly().optional()
});
var SelfCheckFnSchema = z.function();
var auditSchema = z.object({
  root: z.unknown().optional(),
  opts: AuditOptionsSchema.optional()
});
var registerSchema = z.object({
  libId: z.string().min(1).regex(/^@norkron\/web-[a-z0-9-]+$/, "libId must look like @norkron/web-<slug>"),
  fn: SelfCheckFnSchema
});
var registerLazySchema = z.object({
  libId: z.string().min(1).regex(/^@norkron\/web-[a-z0-9-]+$/, "libId must look like @norkron/web-<slug>"),
  thunk: z.function()
});
var PendingRegistrationSchema = z.object({
  libId: z.string().min(1),
  thunk: z.function()
});
var flushPendingRegistrationsSchema = z.undefined();

// src/validate-input.ts
function validateInput(schema, fn, input) {
  const parsed = schema.safeParse(input);
  if (!parsed.success) {
    reportInvalidInput({
      lib: WEB_CONFORMANCE_LIB,
      libToken: WEB_CONFORMANCE_TOKEN,
      fn,
      parseResult: toZodLikeSafeParseError(parsed)
    });
    return input;
  }
  return parsed.data;
}

// src/helpers.ts
function withConformance(libId, version) {
  const checked = validateInput(
    withConformanceSchema,
    "withConformance",
    { libId, version }
  );
  return { "data-norkron-lib": `${checked.libId}@${checked.version}` };
}

// src/gate-self-test.ts
var CANONICAL_SELF_TEST_CASES = [
  {
    name: "hover-feedback-baseline-via-at-media",
    ruleCode: "STR_INTERACTIVE_NO_HOVER_FEEDBACK",
    // Baseline lives inside `@media (hover: hover)` — exactly the shape
    // web-themer/dist/base.css ships. Pre-efe4ff4 the matcher could not
    // see this rule and false-positived on the button.
    goodHtml: `<style>
			@media (hover: hover) {
				button:hover:not([disabled]) { filter: brightness(1.08); }
			}
		</style>
		<button type="button">Click me</button>`,
    // Same button, NO global hover declaration anywhere — the matcher
    // MUST detect this as a missing-hover-feedback finding.
    badHtml: `<style>
			/* deliberately empty \u2014 no hover rule of any kind */
			button { background: #444; color: white; }
		</style>
		<button type="button">Click me</button>`
  },
  {
    name: "focus-visible-baseline-via-at-media",
    ruleCode: "STR_FOCUSABLE_NO_VISIBLE_FOCUS_INDICATOR",
    goodHtml: `<style>
			@media (hover: hover) {
				:focus-visible { outline: 2px solid #06f; outline-offset: 2px; }
			}
		</style>
		<button type="button">Click me</button>`,
    badHtml: `<style>
			button { background: #444; color: white; outline: none; }
		</style>
		<button type="button">Click me</button>`
  },
  {
    // Token-wiring assumption: a lib that references --norkron-XYZ
    // assumes web-themer defines it. If web-themer doesn't (rename,
    // removal, typo), the widget paints WRONG silently. This case
    // proves the matcher catches that exact class of assumption.
    name: "token-referenced-but-undefined-namespace",
    ruleCode: "STR_TOKEN_REFERENCED_BUT_UNDEFINED",
    // GOOD: token IS defined in :root → reference resolves → no finding.
    goodHtml: `<style>
			:root { --norkron-defined-token: 16px; }
			div { padding: var(--norkron-defined-token); }
		</style>
		<div>I have padding</div>`,
    // BAD: token IS NOT defined anywhere → reference is dangling → finding.
    badHtml: `<style>
			div { padding: var(--norkron-undefined-token); }
		</style>
		<div>I have padding (or maybe I don't)</div>`
  },
  {
    // Aria-reference-broken assumption: a lib that writes
    // aria-labelledby="X" assumes id="X" exists in the same
    // document. Refactor/rename/typo breaks the reference silently.
    // Screen readers announce nothing — sighted user thinks it works.
    name: "aria-labelledby-references-existing-element",
    ruleCode: "STR_ARIA_REFERENCE_BROKEN",
    // GOOD: aria-labelledby points at a real id.
    goodHtml: `<span id="lbl">Save changes</span>
		<button type="button" aria-labelledby="lbl">Save</button>`,
    // BAD: aria-labelledby points at a nonexistent id.
    badHtml: `<button type="button" aria-labelledby="this-id-does-not-exist">Save</button>`
  },
  {
    // Label-for-broken assumption: a <label for="X"> assumes an
    // input id="X" exists. Refactor/typo silently breaks
    // association. Sighted devs still see the label next to the
    // input; screen reader users get an unlabeled control.
    name: "label-for-points-at-existing-input",
    ruleCode: "STR_LABEL_FOR_BROKEN",
    // GOOD: for-id matches a real input.
    goodHtml: `<label for="email">Email</label>
		<input id="email" type="email" />`,
    // BAD: for-id points at no element.
    badHtml: `<label for="email">Email</label>
		<input id="user-email" type="email" />`
  },
  {
    // Duplicate-id assumption: every id is document-unique. When
    // two elements share an id, every IDREF in the document
    // (aria-labelledby, label[for], href="#id") silently routes
    // to the FIRST occurrence — the aria-reference-broken and
    // label-for-broken rules see resolution succeed and report
    // nothing wrong, so the misrouting is invisible to them too.
    name: "every-id-is-document-unique",
    ruleCode: "STR_DUPLICATE_ID",
    // GOOD: distinct ids.
    goodHtml: `<div id="a">A</div><div id="b">B</div>`,
    // BAD: two elements share id="dup".
    badHtml: `<div id="dup">A</div><span id="dup">B</span>`
  },
  {
    // Button-no-accessible-name assumption: this <button> is
    // announced by screen readers. The icon-button trap: a
    // sighted dev sees the icon and assumes the button works,
    // but a screen reader announces only "button" with no name.
    name: "button-has-accessible-name",
    ruleCode: "STR_BUTTON_NO_ACCESSIBLE_NAME",
    // GOOD: textContent provides the accessible name.
    goodHtml: `<button type="button">Save changes</button>`,
    // BAD: icon-only button with no aria-label, no text, no nothing.
    badHtml: `<button type="button"><svg aria-hidden="true" width="16" height="16"><circle cx="8" cy="8" r="6" /></svg></button>`
  },
  {
    // Form-input-no-accessible-name assumption: the visible text
    // next to this input labels it. It doesn't — visual proximity
    // is NOT a screen-reader association. Sighted devs see
    // "Email" + input painted side-by-side and call it labeled.
    name: "form-input-has-accessible-name",
    ruleCode: "STR_FORM_INPUT_NO_ACCESSIBLE_NAME",
    // GOOD: <label for> + <input id> pair.
    goodHtml: `<label for="email-self-test">Email</label>
		<input id="email-self-test" type="email" />`,
    // BAD: nearby <span> but no actual label/aria-* on the input.
    badHtml: `<span>Email</span>
		<input type="email" />`
  },
  {
    // iter-4 STR_GATE_LAYOUT_UNAVAILABLE — fixture 1/3 (with-layout, GOOD).
    // In a real-layout env (chromium, jsdom-with-polyfill), a clean
    // element MUST produce zero STR_NEEDS_LAYOUT — every layout-dependent
    // rule had enough info to either run normally or stay silent.
    // Skipped under happy-dom/linkedom because the env-level collapse
    // strips per-rule STR_NEEDS_LAYOUT regardless of fixture content,
    // making the assertion non-discriminating. The realbrowser smoke
    // gate enforces this case.
    name: "layout-rules-clean-in-layout-env",
    ruleCode: "STR_NEEDS_LAYOUT",
    goodHtml: `<div style="width:200px;height:100px;background:#fff;color:#111;padding:8px">clean element</div>`,
    // For with-layout cases the BAD half asserts the inverse contract
    // for the SAME code on a fixture that legitimately stresses a
    // layout-dependent rule. Skipped under happy-dom — see flag below.
    badHtml: `<div style="width:200px;height:100px;background:#fff;color:#111;padding:8px">clean element</div>`,
    skipUnlessLayoutAvailable: true
  },
  {
    // iter-4 STR_GATE_LAYOUT_UNAVAILABLE — fixture 2/3 (with-layout, BAD).
    // In a real-layout env, an element that triggers a layout-dependent
    // rule (e.g. crowds-container-edge: text directly inside a bordered
    // container with <4px padding) MUST produce ≥1 finding from the
    // triggered rule — proving the rules DO fire when layout is
    // available, i.e. STR_GATE_LAYOUT_UNAVAILABLE is NOT a silent
    // fall-through for all conditions. Skipped under happy-dom.
    name: "layout-rules-bad-fires-in-layout-env",
    ruleCode: "STR_CONTAINER_TEXT_NO_PADDING",
    goodHtml: `<div style="border:1px solid #888;padding:16px;width:200px">comfortable padding</div>`,
    badHtml: `<div style="border:1px solid #888;padding:1px;width:200px">edge-glued text</div>`,
    skipUnlessLayoutAvailable: true
  },
  {
    // iter-4 paired case: degraded (no-layout) env MUST suppress every
    // per-rule STR_NEEDS_LAYOUT AND emit exactly 1 STR_GATE_LAYOUT_UNAVAILABLE.
    // Both fixtures use the same HTML — the env override is what we're
    // asserting. The "good" half proves the regression guard (zero
    // STR_NEEDS_LAYOUT noise); the "bad" half asserts the collapsed
    // envelope exists. Per task spec iter-4 task 3 fixture #3.
    name: "layout-rules-degrade-quiet-in-no-layout-env",
    ruleCode: "STR_NEEDS_LAYOUT",
    // Identical fixtures: under envOverride the runner forces zero rect.
    // Expected: 0 STR_NEEDS_LAYOUT on both, 1 STR_GATE_LAYOUT_UNAVAILABLE on both.
    goodHtml: `<button type="button">Trigger layout-dependent rules</button>`,
    badHtml: `<button type="button">Trigger layout-dependent rules</button>`,
    envOverride: "no-layout",
    companionCode: "STR_GATE_LAYOUT_UNAVAILABLE",
    expectGoodCompanion: 1,
    expectBadCompanion: 1
  },
  {
    // iter-7 STR_SEGMENTED_CONTROL_NO_VISIBLE_BORDER — role="tab" must
    // NOT trigger the segmented-control matcher. Tabs are governed by
    // indicator + label color + inter-tab gap, NOT per-tab borders.
    // Adding 1px borders to unselected tabs would re-introduce the
    // R1-A2 sub-grid cascade source iter1-6 just eliminated. The BAD
    // fixture is an actual `[role="radiogroup"]` with class
    // `segmented-control` containing a borderless `[role="radio"]`
    // option whose bg matches its parent — the real segmented-control
    // defect the rule was written to catch.
    // See FR str-segmented-control-no-visible-border-exclude-role-tab.md.
    name: "segmented-control-rule-excludes-role-tab",
    ruleCode: "STR_SEGMENTED_CONTROL_NO_VISIBLE_BORDER",
    // GOOD: a tablist with NO per-tab borders — zero findings (the
    // new excluded behavior).
    // The rule short-circuits when there are zero stylesheets attached
    // (SSR / no-cascade guard). Both fixtures include an inline <style>
    // so the rule actually runs against the inline declarations.
    goodHtml: `<style>div[role="tablist"] { background: #1a1a1a; }</style>
		<div role="tablist" style="background:#1a1a1a">
			<button type="button" role="tab" aria-selected="false" style="border-width:0;background:#1a1a1a">PLN</button>
			<button type="button" role="tab" aria-selected="false" style="border-width:0;background:#1a1a1a">USD</button>
			<button type="button" role="tab" aria-selected="true" style="border-width:0;background:#1a1a1a">EUR</button>
		</div>`,
    // BAD: an actual segmented control (radiogroup with /segmented/
    // class) containing a borderless transparent-bg radio option ≥1
    // finding (regression guard — the rule MUST still fire on the
    // case it was designed for).
    badHtml: `<style>.segmented-control { background: #1a1a1a; }</style>
		<div role="radiogroup" class="segmented-control" style="background:#1a1a1a">
			<button type="button" role="radio" aria-checked="false" style="border-width:0;background:#1a1a1a">PLN</button>
		</div>`
  },
  {
    // Href-fragment-broken assumption: this anchor link will
    // navigate to its target. Sighted devs see the link paint
    // normally, cursor flips to pointer on hover, but clicking
    // does nothing — the silent in-page nav defect.
    name: "href-fragment-resolves",
    ruleCode: "STR_HREF_FRAGMENT_BROKEN",
    // GOOD: anchor target id exists.
    goodHtml: `<a href="#section-self-test">Jump to section</a>
		<h2 id="section-self-test">Section</h2>`,
    // BAD: anchor target id does not exist.
    badHtml: `<a href="#section-self-test-missing">Jump to section</a>
		<h2 id="different-section">Section</h2>`
  },
  {
    // iter-19 STR_SPACING_NOT_TOKENIZED — the rule must recognise the
    // canonical Norkron token namespace `var(--norkron-space-*)` as
    // tokenized spacing. Pre-iter-19 the matcher only knew the legacy
    // `--space*` prefixes, so it false-positived on the ENTIRE ecosystem
    // (30+ libs migrated to `var(--norkron-space-N)` in iters 1-16).
    // See fix/iter19-spacing-rule-norkron-prefix.
    // Layout-dependent rule — skipped under happy-dom; the realbrowser
    // smoke gate enforces it.
    name: "spacing-tokenized-via-norkron-namespace",
    ruleCode: "STR_SPACING_NOT_TOKENIZED",
    // GOOD: container padding wired to a canonical `--norkron-space-*`
    // token → zero findings.
    goodHtml: `<style>
			.iter19-good { border: 1px solid #888; padding: var(--norkron-space-2); width: 200px; }
		</style>
		<div class="iter19-good">Themed card padding</div>`,
    // BAD: same container with a hardcoded `12px` literal → ≥1 finding
    // (regression guard — the rule still catches untokenized spacing).
    badHtml: `<style>
			.iter19-bad { border: 1px solid #888; padding: 12px; width: 200px; }
		</style>
		<div class="iter19-bad">Hardcoded card padding</div>`,
    skipUnlessLayoutAvailable: true
  },
  {
    // ITER-22 — STR_SIBLINGS_EXCESSIVE_GAP must measure ADJACENT siblings
    // only, never the cumulative child[0]→child[k] span. Pre-ITER-22 the
    // matcher ran an O(n²) all-pairs scan: in a tall stacked list the
    // first item vs a far-down item is naturally the SUM of every row +
    // gap between them, geometrically impossible for true neighbours, so
    // the matcher emitted N−1 false positives per list. This case proves
    // the adjacent-only contract: 3 uniformly-spaced siblings → 0
    // findings; 2 truly adjacent siblings stranded 200px apart → 1.
    // Layout-dependent — skipped under happy-dom; the realbrowser smoke
    // gate enforces it.
    name: "siblings-excessive-gap-adjacent-only",
    ruleCode: "STR_SIBLINGS_EXCESSIVE_GAP",
    // GOOD: 3 stacked siblings with a uniform 12px adjacent gap. The
    // all-pairs scan would have flagged span0→span2 (≈ 12 + 40 + 12 px)
    // as "excessive"; the adjacent-only scan sees only the real 12px
    // neighbour gaps → 0 findings.
    goodHtml: `<style>
			.iter22-eg-good { display: flex; flex-direction: column; gap: 12px; width: 220px; }
			.iter22-eg-good > div { height: 40px; background: #888; }
		</style>
		<div class="iter22-eg-good"><div></div><div></div><div></div></div>`,
    // BAD: 2 truly adjacent siblings, 200px apart on the vertical axis —
    // a real excessive gap between neighbours → exactly 1 finding.
    badHtml: `<style>
			.iter22-eg-bad > div { height: 40px; width: 40px; background: #888; }
			.iter22-eg-bad .iter22-eg-spacer { height: 200px; width: 1px; background: transparent; }
		</style>
		<div class="iter22-eg-bad">
			<div style="margin-bottom: 200px;"></div>
			<div></div>
		</div>`,
    skipUnlessLayoutAvailable: true
  },
  {
    // ITER-22 — STR_SIBLINGS_INSUFFICIENT_GAP shares the same all-pairs
    // bug class. It now sorts siblings on the dominant axis and compares
    // only consecutive neighbours, matching its documented "adjacent
    // pairs in the same axis" contract.
    // Layout-dependent — skipped under happy-dom; the realbrowser smoke
    // gate enforces it.
    name: "siblings-insufficient-gap-adjacent-only",
    ruleCode: "STR_SIBLINGS_INSUFFICIENT_GAP",
    // GOOD: 3 stacked siblings with a comfortable uniform 12px adjacent
    // gap → 0 findings.
    goodHtml: `<style>
			.iter22-ig-good { display: flex; flex-direction: column; gap: 12px; width: 220px; }
			.iter22-ig-good > div { height: 40px; background: #888; }
		</style>
		<div class="iter22-ig-good"><div></div><div></div><div></div></div>`,
    // BAD: 2 adjacent siblings only 2px apart — cramped, under the 4px
    // minimum → exactly 1 finding.
    badHtml: `<style>
			.iter22-ig-bad { display: flex; flex-direction: column; gap: 2px; width: 220px; }
			.iter22-ig-bad > div { height: 40px; background: #888; }
		</style>
		<div class="iter22-ig-bad"><div></div><div></div></div>`,
    skipUnlessLayoutAvailable: true
  }
];
function buildFixtureRoot(doc, html) {
  const container = doc.createElement("div");
  container.setAttribute("data-norkron-gate-self-test-fixture", "");
  container.innerHTML = html;
  doc.body.appendChild(container);
  return container;
}
function findingsForCode(findings, code) {
  let n = 0;
  for (const f of findings) {
    if (f.code === code) n++;
  }
  return n;
}
function stubNoLayoutEnv(doc, root) {
  const targets = [root];
  if (doc.body) targets.push(doc.body);
  if (doc.documentElement) targets.push(doc.documentElement);
  const originals = /* @__PURE__ */ new Map();
  const zeroRect = () => ({
    x: 0,
    y: 0,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: 0,
    height: 0,
    toJSON: () => ({})
  });
  for (const t of targets) {
    const orig = t.getBoundingClientRect.bind(t);
    originals.set(t, orig);
    t.getBoundingClientRect = zeroRect;
  }
  return () => {
    for (const [t, orig] of originals) {
      t.getBoundingClientRect = orig;
    }
  };
}
function runGateSelfTest(doc, cases = CANONICAL_SELF_TEST_CASES) {
  const failures = [];
  const probeEl = doc.body ?? doc.documentElement;
  const liveEnvHasLayout = probeEl ? detectLayoutAvailable(probeEl).available : false;
  for (const c of cases) {
    if (c.skipUnlessLayoutAvailable && !liveEnvHasLayout) {
      continue;
    }
    const goodRoot = buildFixtureRoot(doc, c.goodHtml);
    const restoreGood = c.envOverride === "no-layout" ? stubNoLayoutEnv(doc, goodRoot) : () => {
    };
    try {
      const goodFindings = runStructuralChecks(goodRoot);
      const goodCount = findingsForCode(goodFindings, c.ruleCode);
      if (goodCount > 0) {
        failures.push({
          case: c.name,
          ruleCode: c.ruleCode,
          reason: "false-positive-on-good",
          detail: `${goodCount} finding(s) of ${c.ruleCode} on the GOOD fixture (which is properly styled and MUST be accepted). The matcher is over-firing \u2014 likely a baseline rule the matcher cannot see in CSSOM. This was the @media-wrapped baseline regression at efe4ff4 (and the .filter(Boolean) regression at 279d980 / 6a7d8fe).`
        });
      }
      if (c.companionCode !== void 0) {
        const expected = c.expectGoodCompanion ?? 0;
        const actual = findingsForCode(goodFindings, c.companionCode);
        if (actual !== expected) {
          failures.push({
            case: c.name,
            ruleCode: c.companionCode,
            reason: "false-positive-on-good",
            detail: `Expected ${expected} finding(s) of companion ${c.companionCode} on GOOD fixture, got ${actual}. The paired envelope is misfiring.`
          });
        }
      }
    } finally {
      restoreGood();
      goodRoot.remove();
    }
    const badRoot = buildFixtureRoot(doc, c.badHtml);
    const restoreBad = c.envOverride === "no-layout" ? stubNoLayoutEnv(doc, badRoot) : () => {
    };
    try {
      const badFindings = runStructuralChecks(badRoot);
      const badCount = findingsForCode(badFindings, c.ruleCode);
      if (c.envOverride === "no-layout") {
        if (badCount > 0) {
          failures.push({
            case: c.name,
            ruleCode: c.ruleCode,
            reason: "false-positive-on-good",
            detail: `Expected 0 finding(s) of ${c.ruleCode} under envOverride='no-layout' (the env-level collapse MUST suppress every per-rule emission), got ${badCount}. The env-level layout-availability probe failed to fire \u2014 STR_NEEDS_LAYOUT noise will pour out.`
          });
        }
      } else if (badCount === 0) {
        failures.push({
          case: c.name,
          ruleCode: c.ruleCode,
          reason: "false-negative-on-bad",
          detail: `0 findings of ${c.ruleCode} on the BAD fixture (which has the baseline rule DELIBERATELY removed and MUST trigger \u22651 finding). The matcher is silently broken \u2014 it no longer detects the defect it claims to detect.`
        });
      }
      if (c.companionCode !== void 0) {
        const expected = c.expectBadCompanion ?? 0;
        const actual = findingsForCode(badFindings, c.companionCode);
        if (actual !== expected) {
          failures.push({
            case: c.name,
            ruleCode: c.companionCode,
            reason: "false-negative-on-bad",
            detail: `Expected ${expected} finding(s) of companion ${c.companionCode} on BAD fixture, got ${actual}. The paired envelope is misfiring.`
          });
        }
      }
    } finally {
      restoreBad();
      badRoot.remove();
    }
  }
  return {
    passed: failures.length === 0,
    cases: cases.length,
    failures
  };
}
function formatGateSelfTestResult(result) {
  if (result.passed) return "";
  const lines = [];
  lines.push(`STR_GATE_SELF_TEST_FAILED \u2014 ${result.failures.length}/${result.cases} case(s) failed.`);
  lines.push("The gate cannot judge libs until its own matchers are proven. Fix the matcher OR fix the fixture pair.");
  lines.push("");
  for (const f of result.failures) {
    lines.push(`  [${f.reason}] ${f.case} (${f.ruleCode})`);
    lines.push(`    ${f.detail}`);
  }
  return lines.join("\n");
}
var CONF_LIB = "@norkron/web-conformance";
var CONF_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-conformance");
var { warn: conformanceWarn } = createScopedErrors(CONF_LIB, CONF_TOKEN);
function buildElementIdentity(f) {
  const msg = f.message ?? "";
  const skeleton = msg.replace(/\d+(\.\d+)?/g, "#").replace(/\s+/g, " ").trim().slice(0, 200);
  const extras = f.extras;
  const cls = extras && typeof extras.className === "string" ? extras.className : "";
  const stamp = extras && typeof extras.stamp === "string" ? extras.stamp : "";
  let posBucket = "";
  if (extras && typeof extras === "object") {
    const q = (n) => {
      const v = typeof n === "number" ? n : Number(n);
      return Number.isFinite(v) ? String(Math.round(v / 64) * 64) : "_";
    };
    const pickRect = () => {
      for (const k of [
        "candidateRect",
        "rect",
        "childRect",
        "intruderRect",
        "elementRect"
      ]) {
        const r2 = extras[k];
        if (r2 && typeof r2 === "object") return r2;
      }
      return null;
    };
    const r = pickRect();
    if (r) posBucket = `${q(r.left ?? r.x)},${q(r.top ?? r.y)}`;
  }
  return `${f.code}|${skeleton}|${cls}|${stamp}|${posBucket}`;
}
function hashString(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)) >>> 0;
  }
  return h.toString(36);
}
function buildStateSignature(finding) {
  const parts = [];
  const extras = finding.extras;
  if (extras && typeof extras === "object") {
    const bucket = (n) => {
      const v = typeof n === "number" ? n : Number(n);
      if (!Number.isFinite(v)) return "_";
      return String(Math.round(v / 50) * 50);
    };
    const rectSig = (r) => {
      if (!r || typeof r !== "object") return "_";
      const o = r;
      return [o.left, o.top, o.width, o.height].map(bucket).join(",");
    };
    if ("clipAmounts" in extras) {
      const c = extras.clipAmounts;
      parts.push(
        `clip:${bucket(c.left)},${bucket(c.top)},${bucket(c.right)},${bucket(c.bottom)}`
      );
    }
    if ("intruderRect" in extras) parts.push(`intr:${rectSig(extras.intruderRect)}`);
    if ("keepClearRect" in extras) parts.push(`keep:${rectSig(extras.keepClearRect)}`);
    if ("candidateRect" in extras) parts.push(`cand:${rectSig(extras.candidateRect)}`);
    if ("rect" in extras) parts.push(`r:${rectSig(extras.rect)}`);
    if ("childHeight" in extras) parts.push(`ch:${bucket(extras.childHeight)}`);
    if ("parentContentHeight" in extras) parts.push(`ph:${bucket(extras.parentContentHeight)}`);
    if ("shortBy" in extras) parts.push(`sb:${bucket(extras.shortBy)}`);
    if ("backgroundColor" in extras) parts.push(`bg:${String(extras.backgroundColor)}`);
    if ("luminance" in extras) parts.push(`lum:${bucket(extras.luminance)}`);
    if ("theme" in extras) parts.push(`thm:${String(extras.theme)}`);
    if ("ratio" in extras) parts.push(`ra:${bucket(extras.ratio)}`);
    if ("threshold" in extras) parts.push(`th:${bucket(extras.threshold)}`);
    if ("stuckKeys" in extras) {
      try {
        parts.push(`sk:${extras.stuckKeys.join(",")}`);
      } catch {
      }
    }
    if ("stuckValues" in extras) {
      try {
        parts.push(`sv:${JSON.stringify(extras.stuckValues).slice(0, 80)}`);
      } catch {
      }
    }
    if ("sentinel" in extras) parts.push(`sen:${String(extras.sentinel)}`);
    if ("fontSize" in extras) parts.push(`fs:${String(extras.fontSize)}`);
  }
  const skeleton = (finding.message ?? "").replace(/\d+(\.\d+)?/g, "#").replace(/\s+/g, " ").trim();
  parts.push(`sk:${hashString(skeleton)}`);
  return parts.join("|");
}
var _installed = null;
var FORWARD_CODE = "CONF_LIVE_FINDING";
function installLiveAuditing(opts = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {
    };
  }
  if (_installed) return _installed.uninstall;
  const {
    root: providedRoot,
    watchResize = true,
    watchTheme = true,
    watchSubtreeMutations = false,
    debounceMs = 1500,
    // Default OFF — the edge-triggered cache already prevents
    // same-state churn, and rate-limiting genuine re-occurrences
    // (user drags slider into a bad state, out, back in) made the
    // audit appear broken ("not reporting new issues"). Set to a
    // finite value only when actively debugging a flapping rule.
    rateLimitPerCode = Infinity,
    rateLimitWindowMs = 2e3,
    maxFindingsPerPass = Infinity,
    ruleFilter,
    emitWarnings = true,
    excludeSelector = "[data-conformance-ignore]",
    onReport
  } = opts;
  const root = providedRoot ?? document.body;
  if (!root) return () => {
  };
  const codeEmits = /* @__PURE__ */ new Map();
  const _lastEmitted = /* @__PURE__ */ new Map();
  let resizeObserver = null;
  let themeObserver = null;
  let subtreeObserver = null;
  let _auditPhase = 0;
  let auditTimer = null;
  const scheduleAudit = () => {
    if (_auditPhase > 0) return;
    if (auditTimer) clearTimeout(auditTimer);
    auditTimer = setTimeout(runAudit, debounceMs);
  };
  function runAudit() {
    auditTimer = null;
    _auditPhase++;
    setTimeout(() => {
      _auditPhase = Math.max(0, _auditPhase - 1);
    }, 200);
    let findings;
    try {
      findings = runStructuralChecks(root);
    } catch {
      return;
    }
    findings = findings.filter((f) => {
      const m = f.message ?? "";
      if (m.includes("CONF_LIVE_FINDING")) return false;
      if (m.includes("@norkron/web-conformance")) return false;
      return true;
    });
    if (excludeSelector) {
      const ignoredRoots = /* @__PURE__ */ new Set();
      try {
        root.querySelectorAll(excludeSelector).forEach((el) => ignoredRoots.add(el));
      } catch {
      }
      if (ignoredRoots.size > 0) {
        findings = findings.filter((f) => {
          if (!f.selector) return true;
          try {
            const matches = root.querySelectorAll(f.selector);
            if (matches.length === 0) return true;
            for (const el of Array.from(matches)) {
              let cur = el;
              let underIgnored = false;
              while (cur) {
                if (ignoredRoots.has(cur)) {
                  underIgnored = true;
                  break;
                }
                cur = cur.parentElement;
              }
              if (!underIgnored) return true;
            }
            return false;
          } catch {
            return true;
          }
        });
      }
    }
    if (onReport) {
      try {
        onReport(findings);
      } catch {
      }
    }
    if (!emitWarnings) {
      return;
    }
    let emittedThisPass = 0;
    let suppressedThisPass = 0;
    const now = Date.now();
    for (const f of findings) {
      if (f.code === "STR_NEEDS_LAYOUT") continue;
      if (ruleFilter && !ruleFilter(f.code)) continue;
      const stateKey = buildElementIdentity(f);
      const sig = buildStateSignature(f);
      if (_lastEmitted.get(stateKey) === sig) continue;
      _lastEmitted.set(stateKey, sig);
      if (Number.isFinite(rateLimitPerCode) && rateLimitPerCode > 0) {
        const arr = codeEmits.get(f.code) ?? [];
        const cutoff = now - rateLimitWindowMs;
        const recent = arr.filter((t) => t >= cutoff);
        if (recent.length >= rateLimitPerCode) {
          suppressedThisPass++;
          codeEmits.set(f.code, recent);
          continue;
        }
        recent.push(now);
        codeEmits.set(f.code, recent);
      }
      if (emittedThisPass >= maxFindingsPerPass) {
        suppressedThisPass++;
        continue;
      }
      emittedThisPass++;
      try {
        conformanceWarn({
          fn: "installLiveAuditing",
          code: FORWARD_CODE,
          message: `${f.code}: ${f.message} [geom:${sig}]`,
          received: f.selector ?? "",
          dedupKey: {}
        });
      } catch {
      }
    }
    if (suppressedThisPass > 0) {
      try {
        conformanceWarn({
          fn: "installLiveAuditing",
          code: FORWARD_CODE,
          message: `[+${suppressedThisPass} findings suppressed by rate-limit / per-pass cap \u2014 raise maxFindingsPerPass or rateLimitPerCode to see all]`,
          received: "",
          dedupKey: {}
        });
      } catch {
      }
    }
  }
  if (watchResize && typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => scheduleAudit());
    try {
      resizeObserver.observe(root);
    } catch {
    }
  }
  if (watchTheme && typeof MutationObserver !== "undefined") {
    themeObserver = new MutationObserver(() => scheduleAudit());
    try {
      themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme", "class", "data-color-scheme"]
      });
    } catch {
    }
  }
  if (watchSubtreeMutations && typeof MutationObserver !== "undefined") {
    subtreeObserver = new MutationObserver(() => scheduleAudit());
    try {
      subtreeObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "data-norkron-lib"]
      });
    } catch {
    }
  }
  const _flushCache = () => {
    _lastEmitted.clear();
    codeEmits.clear();
    scheduleAudit();
  };
  const _onVisibility = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      _flushCache();
    }
  };
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", _onVisibility);
  }
  if (typeof window !== "undefined") {
    window.addEventListener("focus", _flushCache);
    window.addEventListener("pointerdown", _flushCache, { passive: true });
    window.addEventListener("keydown", _flushCache, { passive: true });
  }
  globalThis.__norkronConformanceFlush = _flushCache;
  scheduleAudit();
  const uninstall = () => {
    if (auditTimer) clearTimeout(auditTimer);
    resizeObserver?.disconnect();
    themeObserver?.disconnect();
    subtreeObserver?.disconnect();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", _onVisibility);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("focus", _flushCache);
      window.removeEventListener("pointerdown", _flushCache);
      window.removeEventListener("keydown", _flushCache);
    }
    globalThis.__norkronConformanceFlush = void 0;
    _installed = null;
  };
  _installed = { uninstall, options: opts };
  return uninstall;
}
function uninstallLiveAuditing() {
  if (_installed) _installed.uninstall();
}

// src/version.ts
var LIB_VERSION = "0.11.1";

// src/reporters/console.ts
function reportToConsole(report) {
  const t = report.totals;
  const n = t.critical + t.major + t.minor + t.info;
  console.log(
    `[norkron/conformance] ${n} findings \xB7 ${t.critical} critical \xB7 ${t.major} major \xB7 ${t.minor} minor \xB7 see window.__norkron_conformance__`
  );
}

// src/reporters/window.ts
function reportToWindow(report) {
  if (typeof window === "undefined") return;
  window.__norkron_conformance__ = report;
}

// src/reporters/http.ts
async function reportToHttp(report, opts = {}) {
  const isProd = typeof process !== "undefined" && (process.env?.NODE_ENV ?? "") === "production";
  if (isProd && !opts.allowProduction) return null;
  const endpoint = opts.endpoint ?? "/api/conformance";
  const fetchImpl = opts.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : void 0);
  if (!fetchImpl) return null;
  return fetchImpl(endpoint, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(report)
  });
}

export { AuditOptionsSchema, CANONICAL_SELF_TEST_CASES, CF_AUDIT_REFUSED_EMPTY_SURFACE, CF_AUDIT_REFUSED_RULE_NOT_RUN, ConformanceCategorySchema, ConformanceFindingSchema, ConformanceReportSchema, ConformanceSeveritySchema, DOCUMENT_SURFACE_ATTR, DocumentSurfaceOptionsSchema, KNOWN_PRIMITIVE_SIGNATURES, LIB_VERSION, PendingRegistrationSchema, SURFACE_REFUSAL_KINDS, SelfCheckFnSchema, SelfCheckOptionsSchema, SurfaceCensusSchema, SurfaceRefusalRecordSchema, SurfaceRefusalSchema, __resetRegistry, __resetResponsiveRegistry, audit, auditSchema, canonicalOwnerFor, censusOf, censusRefusal, censusSurface, checkActualDisplayedBackgroundContrast, checkBlockedHrefStillReadable, checkCanvasZeroSized, checkChildOverflowsVisualParent, checkChildUnderfillsParentHeight, checkContainerRendersEmpty, checkContrast, checkCornersNotConcentric, checkDeadInteractive, checkDocumentLandmark, checkDocumentLanguage, checkDuplicateAriaLabel, checkDuplicatePrimitiveBySimilarity, checkDuplicateVisibleText, checkElementClippedByAncestor, checkElementPaintedOffViewport, checkFlexNowrapOverflowsParent, checkFocusableInsideHeading, checkFullWidthNotEdgeToEdge, checkHandrolledDuplicatesLibPrimitive, checkHeadingHierarchy, checkHeadingOutline, checkI18nKeyLeaked, checkIcuTemplateLeaked, checkImageAlternatives, checkInlineStyleOnLibStamp, checkInteractiveElementVisuallyEmpty, checkKeepClearRegionOverlapped, checkLibFormallyNoResponsive, checkLibPrimitiveDuplicateEmitters, checkLinkNames, checkMinimapViewportNotInverted, checkMirrorStillInAtTree, checkNoInvisibleTabStops, checkPaintedSurfaceHidden, checkPaletteDrift, checkPillContainerWraps, checkProgressbarRedundancy, checkReplacedElementZeroRect, checkResponsiveCollapseMissing, checkResponsiveContractMissing, checkSingleExposedRepresentation, checkTableHeaders, checkTextAsymmetricPadding, checkTextClipped, checkTextLeakedProgrammingValue, checkTextOverflowsAtBreakpoint, checkTextOverflowsContentBox, checkThemeTokenNotThemed, checkThemeVariantMissing, checkUtilityClassOnLibStamp, checkViewportOverflow, checkWrappedFlexOrphan, compositeOver, computeFingerprint, contrastRatio, declaredDocumentSurface, displayedBgRgb, documentSurfaceGated, flushPendingRegistrations, flushPendingRegistrationsSchema, formatGateSelfTestResult, getResponsiveContract, hasResponsiveContract, installLiveAuditing, isRefusal, isUtilityClass, levenshtein, listRegistered, listStructuralRuleCodes, matchesClassNamePatterns, matchesPrimitive, nearestLibStamp, normalizeSeverity, parseCssColor, parseInlineStyle, register, registerLazy, registerLazySchema, registerResponsiveContract, registerSchema, reportToConsole, reportToHttp, reportToWindow, resolveBackgroundColor, resolveDisplayedBackground, rgbToHex, runGateSelfTest, runStructuralChecks, runStructuralChecksWithDiagnostic, severityForSimilarity, similarity, splitModifier, srgbLuminance, srgbToLinear, summariseCensus, uninstallLiveAuditing, withConformance, withConformanceSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map